import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react';
import { alpha, styled, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Box from '@mui/material/Box';
import MuiDrawer from '@mui/material/Drawer';
import MuiAppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import List from '@mui/material/List';
import CssBaseline from '@mui/material/CssBaseline';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { useNavigate, useLocation } from 'react-router-dom';
import { ADMIN_PENDING_DOCUMENT_NAME_KEY, useAuth } from '../../contexts/AuthContext';
import { FEATURE_FLAG_KEYS, useFeatureFlags } from '../../contexts/FeatureFlagsContext';
import { 
  Avatar, 
  Menu, 
  MenuItem, 
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid, 
  Card, 
  CardMedia, 
  CardContent,
  Paper,
  Tabs,
  Tab,
  Tooltip,
  FormControlLabel,
  Checkbox,
  Chip,
  CircularProgress,
  Select,
  FormControl,
  Badge,
  ToggleButton,
  Slider,
  Alert,
  Snackbar,
  RadioGroup,
  Radio,
} from '@mui/material';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonIcon from '@mui/icons-material/Person';
import { PREDEFINED_AVATARS } from '../../constants/avatars';
import { helpContent } from '../../constants/helpContent';
import api, { formatApiErrorMessage, skipAuthRedirectConfig, withSuppressedDocumentActivity } from '../../services/api';
import axios from 'axios';
import DocumentPanel from '../document/DocumentPanel';
import CloseIcon from '@mui/icons-material/Close';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import SplitPane from './SplitPane';
import CategoryTree from '../category/CategoryTree';
import CategoryContent, { InvertSelectionButton } from '../category/CategoryContent';
import { DropdownFieldTooltip } from '../DropdownFieldTooltip';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditNoteIcon from '@mui/icons-material/EditNote';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ContentCutIcon from '@mui/icons-material/ContentCut';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import { Document } from '../../types/Document';
import {
  DocumentShareInvitation,
  filterPendingIncomingShares,
} from '../../types/DocumentShare';
import {
  DocumentReadAccessGrant,
  OpenReadOnlyDocumentResponse,
  readOnlyDocumentTabId,
  readOnlyDocumentLabel,
  filterActiveReadAccessGrants,
  shouldRemoveReadAccessGrantAfterOpenError,
} from '../../types/DocumentReadAccess';
import DocumentCompareView from '../document/DocumentCompareView';
import {
  applyTypeConflictMergeRecord,
  createDocumentCompareTabId,
  documentCompareTabLabel,
  DocumentCompareSession,
  DocumentCompareSide,
  isDocumentCompareTabId,
  buildSelectiveMergeDisplaySession,
  createSelectiveMergeTabId,
  isSelectiveMergeTabId,
  selectiveMergeTabLabel,
  selectiveMergeActiveSourceSide,
  compareSessionUsesDeletedDocument,
  selectiveMergeSessionUsesDeletedDocument,
  SelectiveMergeBaseline,
  SelectiveMergeSession,
  SelectiveMergeSourceSide,
    CompareCopyUndoEntry,
    CompareDeleteContentUndoEntry,
    CompareUndoEntry,
    isCompareCopyUndoEntry,
    MergeCompareContentSelection,
} from '../../types/DocumentCompare';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import CallMergeIcon from '@mui/icons-material/CallMerge';
import KeyboardDoubleArrowUpIcon from '@mui/icons-material/KeyboardDoubleArrowUp';
import ViewListOutlinedIcon from '@mui/icons-material/ViewListOutlined';
import PlaylistAddOutlinedIcon from '@mui/icons-material/PlaylistAddOutlined';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import { mergeCompareActionErrorMessage, pruneStaleTypeConflictMergedSides } from '../../utils/documentCompareUtils';
import SummarizeIcon from '@mui/icons-material/Summarize';
import EditIcon from '@mui/icons-material/Edit';
import NotesIcon from '@mui/icons-material/Notes';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import ContentEditor, { type ContentEditorHandle } from '../content/ContentEditor';
import ContentRichPreviewDialog from '../content/ContentRichPreviewDialog';
import { rawStringHasPreviewableBody } from '../../utils/contentRichPreviewHtml';
import {
  mapAddMatchingContentTransfers,
  replayAddMatchingContentMoves,
  replayAddMatchingContentTransfers,
  collapseSubtreeToContentCategory,
  fetchCategoryForPane,
  fetchCategoryWithChildren,
  hydrateExpandedBranchesOnTree,
  loadLazyDocumentTree,
  mergeBranchFetchIntoTree,
  mergeContentsFetchIntoTree,
  restoreSelectedCategoryInTree,
  mergeCompareCategories,
  mergeCompareCategorySubtree,
  mergeCompareContent,
  mergeCompareTypeConflict,
  type MergeCompareCategoriesResult,
  type MergeCompareContentResult,
  updateCategoryInTree,
} from '../../services/CategoryService';
import {
  CategoryLoadFault,
  branchChildrenAreLoaded,
  categoryContentsAreLoaded,
  categoryShowsBranchExpandable,
  subtreeFullyLoaded,
  assertSubtreeFullyLoadedForDisplay,
  collectUnloadedNodesInSubtree,
  expandedNodesForLoadedTree,
  stubBranchChild,
} from '../../utils/categoryLoadState';
import { Content } from '../../types/Content';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import type { Operation } from '../../types/History';
import {
  historyService,
  collectContentIdsFromDeleteOperations,
  buildTreeAssistantBatchTooltip,
  historyReplayCategoryNames,
} from '../../services/HistoryService';
import { Logout } from '@mui/icons-material';
import { Outlet } from 'react-router-dom';
import { useDocument } from '../../contexts/DocumentContext';
import UploadIcon from '@mui/icons-material/Upload';
import { UserDetailsDto } from '../../types/UserDetailsDto';
import SettingsMenu from '../SettingsMenu/SettingsMenu';
import SemanticSearchWindow from '../search/SemanticSearchWindow';
import TreeAssistantWindow from '../assistant/TreeAssistantWindow';
import SearchIcon from '@mui/icons-material/Search';
import { SearchSettingsProvider } from '../../contexts/SearchSettingsContext';
import AiSparklesIcon, { AutoFixHighDirectionIcon } from '../icons/AiSparklesIcon';
import TreeAssistantIcon from '../icons/TreeAssistantIcon';
import { MergeMissingResolve, MergeTypeMismatchResolve } from '../../types/MergeTypes';
import { CustomDragLayer } from '../category/CustomDragLayer';
import RenameSubcaseDialog from '../dialogs/RenameSubcaseDialog';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import JoinFullIcon from '@mui/icons-material/JoinFull';
import VisibilityIcon from '@mui/icons-material/Visibility';
import HelpIcon from '@mui/icons-material/Help';
import SubTreeView from './SubTreeView';
import ExpirationWarning from '../auth/ExpirationWarning';
import AdminConsolePage from '../../pages/AdminConsolePage';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import UserProfileDialog from '../dialogs/UserProfileDialog';
import { SubcaseGroupsDialog } from '../dialogs/SubcaseGroupsDialog';
import HelpDialog from '../dialogs/HelpDialog';
import GroupIcon from '@mui/icons-material/Group';
import { Category } from '../../types/Category';
import FindInPageIcon from '@mui/icons-material/FindInPage';
import FilterListIcon from '@mui/icons-material/FilterList';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import FindCategoryDialog from '../dialogs/FindCategoryDialog';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import DuplicateCategoryAsDocumentDialog from '../dialogs/DuplicateCategoryAsDocumentDialog';
import ImportSubtreeFromDocumentDialog from '../dialogs/ImportSubtreeFromDocumentDialog';
import GenerateSubtreeDialog, {
  type OrganizeCategoriesMode,
  type OrganizeCategoriesPreset,
} from '../dialogs/GenerateSubtreeDialog';
import CategoryReportDialog from '../dialogs/CategoryReportDialog';
import WorkspaceMenuBar from './WorkspaceMenuBar';
import WorkspaceQuickToolbar from './WorkspaceQuickToolbar';
import {
  buildWorkspaceMenuSections,
  buildWorkspaceQuickActions,
} from '../../utils/buildWorkspaceMenuSections';
import {
  readWorkspaceChrome,
  writeWorkspaceChrome,
  WorkspaceChrome,
} from '../../utils/workspaceChrome';
import AddMatchingContentDialog from '../dialogs/AddMatchingContentDialog';
import type { AddMatchingContentTransfer } from '../dialogs/AddMatchingContentDialog';
import AdjustTextDialog from '../dialogs/AdjustTextDialog';
import CompareContentDialog, { CategoryPickerTreeLabel } from '../dialogs/CompareContentDialog';
import { TreeView, TreeItem } from '@mui/x-tree-view';
import DriveFolderUploadIcon from '@mui/icons-material/DriveFolderUpload';
import { useHelpContent } from '../../utils/HelpUtils';
import {
  hasDuplicateContent,
  formatDuplicateContentLabel,
  getDuplicateContentMessage,
  getContentIndex,
  normalizeCategoryName,
  normalizePresentation,
  isDuplicateName,
  categoryNameConflictsUnderParent,
  validateMultiCategoryPasteIntoBranchNames,
  isDescendantCategory,
  collectAddMatchingContentSourcesAlongAncestorPath,
  contentCategoryMayHaveRows,
  categoryReportScopeMayHaveRows,
  countContentRowsInCategorySubtree,
  categorySubtreeHasAllowedCategoryId,
  documentHasCompareTargets,
  findDefaultAddMatchingContentSourceIdAmongGroups,
  flattenAddMatchingContentSourceGroups,
  ORIGINAL_FILES_CONTENT_CATEGORY_NAME,
  TEMP_CONTENT_CATEGORY_PREFIX,
  invertSubcaseSelection,
  wouldLeaveAtLeastTwoChildrenAfterRemoving,
  canDeleteCategoryUnderParent,
  findCategoryInTree as findCategoryInTreeFromRoots,
} from '../../utils/CategoryUtils';
import {
  buildCutCategoryIdSet,
  buildCutContentIdSet,
  loadDocumentLastUsedMap,
  migrateDocumentLastUsed,
  recordDocumentUsed,
  removeDocumentLastUsed,
} from '../../utils/DocumentUtils';

const APP_BAR_HEIGHT = 64;
/** Two-row menubar + quick toolbar (tighter row gap than icon bar). */
const APP_BAR_HEIGHT_MENU_BAR = 72;
/** Fixed document sidebar width + gap before main pane (Pane width uses this constant). */
const DOCUMENT_PANEL_WIDTH_PX = 300;
/** Horizontal gap between document panel and main pane (px). Match `theme.spacing(1.5)` at default 8px density. */
const MAIN_PANE_GAP_PX = 12;
const MAIN_PANE_LEFT_OFFSET_PX = DOCUMENT_PANEL_WIDTH_PX + MAIN_PANE_GAP_PX;

/** Category toolbar icon priority (earlier = kept when space is tight). Drives visible icons and overflow. */
const CATEGORY_TOOLBAR_BUTTON_PRIORITY: readonly string[] = [
  'add', 'edit', 'adjustText', 'delete',
  'split', 'addMatchingContent', 'compareContent', 'group', 'flatten', 'collapseSubtree', 'merge',
  'generate', 'suggestParentStructure', 'rename',
  'moveUp', 'moveDown',
  'cut', 'copy', 'paste', 'importSubtree',
  'undo', 'redo',
  'duplicate', 'renameSubcase', 'subcaseGroups',
];

/** Lower-priority toolbar icons (hidden first when space is tight). Help/settings/profile stay pinned. */
const TOOLBAR_UTILITY_BUTTON_PRIORITY: readonly string[] = [
  'contentFilter', 'find', 'showSubTree', 'categoryAiReport', 'askAi', 'treeAssistant',
];

type ToolbarMeasurePhase = 'idle' | 'checkAllFit' | 'trimToFit';

/** Overflow (⋮) menu row — mirrors toolbar icon tooltips when actions are hidden. */
function ToolbarOverflowMenuItem({
  title,
  showTooltip,
  disabled,
  onClick,
  icon,
  label,
}: {
  title: string;
  showTooltip: boolean;
  disabled?: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}) {
  const menuItem = (
    <MenuItem
      onClick={onClick}
      disabled={disabled}
      sx={{
        fontSize: 'var(--app-font-size, 14px)',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}
    >
      <ListItemIcon>{icon}</ListItemIcon>
      <ListItemText primary={label} />
    </MenuItem>
  );
  if (!showTooltip || !title) {
    return menuItem;
  }
  return (
    <Tooltip title={title} placement="left">
      <span style={{ display: 'block' }}>{menuItem}</span>
    </Tooltip>
  );
}

const StyledAppBar = styled(MuiAppBar)(({ theme }) => {
  const isDark = theme.palette.mode === 'dark';
  /** Dark: top chrome slightly darker than `background.default` so the bar reads as its own layer. */
  const background = isDark
    ? `linear-gradient(180deg, ${alpha('#020408', 0.99)} 0%, ${alpha('#0a1018', 0.98)} 42%, ${alpha('#0d1520', 0.99)} 100%)`
    : `linear-gradient(180deg, ${alpha(theme.palette.grey[900], 0.97)} 0%, ${alpha('#0f172a', 0.98)} 100%)`;
  return {
    zIndex: theme.zIndex.drawer + 2,
    overflow: 'visible',
    color: theme.palette.common.white,
    background,
    borderBottom: `1px solid ${alpha(theme.palette.common.white, 0.08)}`,
    boxShadow: `0 1px 0 ${alpha(theme.palette.common.white, 0.06)} inset, 0 4px 24px ${alpha(theme.palette.common.black, 0.14)}`,
    '& .MuiToolbar-root': {
      minHeight: APP_BAR_HEIGHT,
      gap: theme.spacing(0.5),
    },
  };
});

// Prevent scrolling on the main pane
const MainContent = styled('main')(({ theme }) => ({
  flexGrow: 1,
  display: 'flex',
  flexDirection: 'column',
  marginTop: `${APP_BAR_HEIGHT}px`,
  backgroundColor: theme.palette.background.default,
  overflow: 'hidden',
  minWidth: 0,
}));

// Prevent scrolling on the container
const SplitPaneContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'row',
  height: '100%',
  width: '100%',
  minWidth: 0,
  position: 'relative',
  overflow: 'hidden',
});

const Pane = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  overflow: 'hidden',
});

const Resizer = styled('div')(({ theme }) => ({
  width: 4,
  backgroundColor: theme.palette.divider,
  cursor: 'col-resize',
  position: 'relative',
  userSelect: 'none',
  flexShrink: 0,
  transition: theme.transitions.create('background-color', { duration: theme.transitions.duration.shorter }),
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.55),
  },
}));

const TabPanel = styled('div')({
  height: '100%',
  display: 'none',
  '&.active': {
    display: 'flex',
    flexDirection: 'column',
  },
});

const DOC_TAB_STRIP_HEIGHT = 38;

const StyledTabs = styled(Tabs)(({ theme }) => ({
  width: '100%',
  maxWidth: '100%',
  boxSizing: 'border-box',
  backgroundColor: theme.palette.background.default,
  borderBottom: `1px solid ${theme.palette.divider}`,
  minHeight: DOC_TAB_STRIP_HEIGHT,
  overflow: 'hidden',
  paddingLeft: 0,
  paddingRight: theme.spacing(1.5),
  '& .MuiTabs-scroller': {
    maxWidth: '100%',
    boxSizing: 'border-box',
  },
  '& .MuiTabs-scrollButtons': {
    width: 28,
    color: theme.palette.text.secondary,
    '&.Mui-disabled': {
      opacity: 0.28,
    },
  },
  '& .MuiTabs-indicator': {
    display: 'none',
  },
  '& .MuiTabs-flexContainer': {
    alignItems: 'center',
    gap: theme.spacing(0.25),
    minHeight: DOC_TAB_STRIP_HEIGHT,
  },
  '& .MuiTab-root': {
    textTransform: 'none',
    minWidth: 0,
    maxWidth: 280,
    padding: theme.spacing(0.5, 1.125, 0.625),
    fontSize: 'inherit',
    lineHeight: 1.3,
    minHeight: 34,
    color: theme.palette.text.secondary,
    border: 'none',
    borderRadius: '6px 6px 0 0',
    transition: theme.transitions.create(['background-color', 'color', 'box-shadow'], {
      duration: theme.transitions.duration.shorter,
    }),
    '&:hover': {
      color: theme.palette.text.primary,
      backgroundColor: alpha(theme.palette.common.black, 0.04),
    },
    '&.Mui-selected': {
      color: theme.palette.text.primary,
      backgroundColor: theme.palette.background.paper,
      boxShadow: `inset 0 2px 0 0 ${alpha(theme.palette.primary.main, 0.38)}`,
      zIndex: 1,
    },
    '&:focus-visible': {
      outline: `2px solid ${alpha(theme.palette.primary.main, 0.45)}`,
      outlineOffset: -2,
    },
    '& .document-tab-title': {
      fontSize: '0.875rem',
      fontWeight: 500,
      letterSpacing: '-0.02em',
      lineHeight: 1.35,
      color: 'inherit',
      WebkitFontSmoothing: 'antialiased',
      transition: theme.transitions.create(['color', 'font-weight', 'letter-spacing'], {
        duration: theme.transitions.duration.shorter,
      }),
    },
    '&.Mui-selected .document-tab-title': {
      fontWeight: 600,
      letterSpacing: '-0.022em',
      color: theme.palette.text.primary,
    },
  },
}));

const TabLabel = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  minWidth: 0,
  width: '100%',
  '& .document-tab-title-wrap': {
    flex: 1,
    minWidth: 0,
    display: 'block',
    overflow: 'hidden',
  },
  '& .document-tab-title': {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    display: 'block',
    minWidth: 0,
  },
}));

function DocumentTabTitle({ label }: { label: string }) {
  const titleRef = useRef<HTMLSpanElement>(null);
  const [truncated, setTruncated] = useState(false);

  const updateTruncation = useCallback(() => {
    const el = titleRef.current;
    if (!el) {
      return;
    }
    setTruncated(el.scrollWidth > el.clientWidth + 1);
  }, []);

  useLayoutEffect(() => {
    updateTruncation();
  }, [label, updateTruncation]);

  useEffect(() => {
    const el = titleRef.current;
    if (!el || typeof ResizeObserver === 'undefined') {
      return undefined;
    }
    const observer = new ResizeObserver(() => updateTruncation());
    observer.observe(el);
    return () => observer.disconnect();
  }, [label, updateTruncation]);

  return (
    <Tooltip
      title={label}
      placement="top"
      enterDelay={400}
      disableHoverListener={!truncated}
      disableFocusListener={!truncated}
      disableTouchListener={!truncated}
    >
      <span className="document-tab-title-wrap">
        <span ref={titleRef} className="document-tab-title">
          {label}
        </span>
      </span>
    </Tooltip>
  );
}

const LogoImage = styled('img')(({ theme }) => ({
  height: 48,
  marginRight: theme.spacing(2),
  display: 'block',
  alignSelf: 'center',
}));

const StyledIconButton = styled(IconButton)(({ theme }) => ({
  color: 'inherit',
  padding: theme.spacing(0.75),
  minWidth: 'auto',
  '&.Mui-disabled': {
    color: 'rgba(255, 255, 255, 0.5)', // More visible gray color for disabled state
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.08)',
    },
  },
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.12)',
  },
  marginRight: theme.spacing(0.25),
}));

const ContentPanel = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  backgroundColor: theme.palette.background.paper,
  borderTop: `1px solid ${theme.palette.divider}`,
  borderRadius: `${theme.shape.borderRadius}px ${theme.shape.borderRadius}px 0 0`,
  boxShadow: theme.shadows[1],
  transition: theme.transitions.create(['height'], {
    duration: theme.transitions.duration.standard,
  }),
}));

const ContentPanelHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(1.5, 2),
  borderBottom: `1px solid ${theme.palette.divider}`,
  gap: theme.spacing(1),
  backgroundColor: alpha(theme.palette.primary.main, 0.03),
}));

const ContentPanelTitle = styled(Box)({
  flex: 1,
  fontWeight: 500,
  fontSize: 'var(--app-font-size, 14px)',
});

const ContentPanelContent = styled(Box)(({ theme }) => ({
  flex: 1,
  padding: theme.spacing(2),
  overflow: 'auto',
}));

const ResizeHandle = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '4px',
  backgroundColor: theme.palette.divider,
  cursor: 'row-resize',
  '&:hover': {
    backgroundColor: theme.palette.primary.main,
  },
}));

/** Default subcase in content panel (on paper background, not app bar). */
const PanelSubcaseSelect = styled(Select)(({ theme }) => ({
  minWidth: 180,
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.divider,
  },
}));

interface Tab {
  id: string;
  label: string;
  content: React.ReactNode;
}

function isContentPaneSubcaseFilterActive(filter: string[] | null): boolean {
  return filter != null && filter.length > 0;
}

/** macOS Dock–style falloff: pointer distance → icon scale (1 … 1+maxBoost). */
const TOOLBAR_DOCK_MAGNIFY_SIGMA_PX = 82;
const TOOLBAR_DOCK_MAGNIFY_MAX_BOOST = 0.38;

function resetToolbarDockMagnifyTransforms(root: HTMLElement | null) {
  if (!root) return;
  root.querySelectorAll<HTMLButtonElement>('button').forEach((btn) => {
    btn.style.transform = '';
    btn.style.transformOrigin = '';
    btn.style.zIndex = '';
  });
}

function applyToolbarDockMagnify(root: HTMLElement, clientX: number, clientY: number) {
  const sigma2 = 2 * TOOLBAR_DOCK_MAGNIFY_SIGMA_PX * TOOLBAR_DOCK_MAGNIFY_SIGMA_PX;
  root.querySelectorAll<HTMLButtonElement>('button:not([disabled])').forEach((btn) => {
    const r = btn.getBoundingClientRect();
    if (r.width === 0 || r.height === 0) return;
    const bx = r.left + r.width / 2;
    const by = r.top + r.height / 2;
    const dx = clientX - bx;
    const dy = clientY - by;
    const t = Math.exp(-(dx * dx + dy * dy) / sigma2);
    const s = 1 + TOOLBAR_DOCK_MAGNIFY_MAX_BOOST * t;
    btn.style.transformOrigin = '50% 0%';
    btn.style.transform = `scale(${s})`;
    btn.style.zIndex = t > 0.06 ? String(100 + Math.floor(t * 45)) : '';
  });
}

/** Matches backend category id prefix userId:documentId (first two numeric segments). Used for same-document cut checks. */
function appendMergeCompareCopyMessage(
  messages: string[],
  side: 'left' | 'right',
  result: MergeCompareContentResult | MergeCompareCategoriesResult,
  itemLabel: 'rows' | 'categories',
): void {
  const copied = result.copiedCount ?? 0;
  const skipped = result.skippedDuplicateCount ?? 0;
  const label = side === 'left' ? 'left' : 'right';
  if (copied === 0 && skipped > 0) {
    messages.push(`Copy to ${label}: all selected ${itemLabel} already existed there.`);
  } else if (copied > 0 && skipped > 0) {
    messages.push(
      `Copy to ${label}: copied ${copied} ${itemLabel}, skipped ${skipped} duplicate(s).`,
    );
  }
}

function getCategoryDocumentPrefix(categoryId: string): string | null {
  const m = /^(\d+:\d+)(?::|$)/.exec(categoryId);
  return m ? m[1] : null;
}

interface MainLayoutProps {
  showWarning: boolean;
}

const MainLayout: React.FC<MainLayoutProps> = ({ showWarning }) => {
  const theme = useTheme();
  const compactToolbar = useMediaQuery(theme.breakpoints.down('lg'));
  const { logout, user, fetchUserDetails, isImpersonating, impersonatorUsername, endImpersonation } = useAuth();
  const adminPendingDocumentOpenedRef = useRef(false);
  const { isEnabled: isFeatureEnabled } = useFeatureFlags();
  const documentSharingEnabled = isFeatureEnabled(FEATURE_FLAG_KEYS.DOCUMENT_SHARING);
  const documentMergeCompareEnabled = isFeatureEnabled(
    FEATURE_FLAG_KEYS.DOCUMENT_MERGE_COMPARE,
  );
  const [tabs, setTabs] = useState<Tab[]>([]);
  const [currentTab, setCurrentTab] = useState<string | null>(null);
  const [currentDocument, setCurrentDocument] = useState<Document | undefined>();
  const [readOnlyOpenDocuments, setReadOnlyOpenDocuments] = useState<Record<string, Document>>({});
  const effectiveDocumentReadOnly = useMemo(
    () => !!currentDocument?.readOnly || !!user?.readOnlyImpersonation,
    [currentDocument?.readOnly, user?.readOnlyImpersonation],
  );
  const [compareSessions, setCompareSessions] = useState<Record<string, DocumentCompareSession>>({});
  const compareSessionsRef = useRef(compareSessions);
  compareSessionsRef.current = compareSessions;
  const [selectiveMergeSessions, setSelectiveMergeSessions] = useState<
    Record<string, SelectiveMergeSession>
  >({});
  const selectiveMergeSessionsRef = useRef(selectiveMergeSessions);
  selectiveMergeSessionsRef.current = selectiveMergeSessions;
  const tabsRef = useRef(tabs);
  tabsRef.current = tabs;
  const currentTabRef = useRef(currentTab);
  currentTabRef.current = currentTab;
  const [mergeCompareApplyingTabId, setMergeCompareApplyingTabId] = useState<string | null>(null);
  const [splitPosition, setSplitPosition] = useState(30); // percentage
  const resizerRef = useRef<HTMLDivElement>(null);
  const isDraggingRef = useRef(false);
  const [selectedTab, setSelectedTab] = useState(0);
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  /** Always-current selection for keyboard shortcuts (effect closures lag one render behind). */
  const selectedCategoryRef = useRef<Category | null>(null);
  selectedCategoryRef.current = selectedCategory;
  const [clipboardCategory, setClipboardCategory] = useState<Category | null>(null);
  const [clipboardCategories, setClipboardCategories] = useState<Category[]>([]);
  const [clipboardContents, setClipboardCContents] = useState<Content[]>([]);
  const [isCutOperation, setIsCutOperation] = useState(false);
  const cutContentIds = useMemo(
    () => buildCutContentIdSet(isCutOperation, clipboardContents),
    [isCutOperation, clipboardContents],
  );
  const cutCategoryIds = useMemo(
    () => buildCutCategoryIdSet(isCutOperation, clipboardCategory, clipboardCategories),
    [isCutOperation, clipboardCategory, clipboardCategories],
  );
  const clearDocumentClipboard = useCallback(() => {
    setClipboardCategory(null);
    setClipboardCategories([]);
    setClipboardCContents([]);
    setIsCutOperation(false);
  }, []);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [addError, setAddError] = useState('');
  const [renameError, setRenameError] = useState('');
  const [expandedNodes, setExpandedNodes] = useState<string[]>([]);
  const [documentExpandedStates, setDocumentExpandedStates] = useState<{ [documentId: string]: string[] }>({});
  const [documentSelectedCategoryIds, setDocumentSelectedCategoryIds] = useState<{
    [documentId: string]: string;
  }>({});
  /** Per-tab snapshot of the in-memory category structure (branches + loaded content categories). */
  const documentCategoryTreesRef = useRef<{ [documentId: string]: Category[] }>({});
  /** Document tabs mutated from compare/merge — reload from server on next activation. */
  const documentTabsNeedRefreshRef = useRef<Set<string>>(new Set());
  const [documentLastUsedAt, setDocumentLastUsedAt] = useState<Record<string, number>>(() =>
    loadDocumentLastUsedMap(),
  );

  const touchDocumentUsed = useCallback((documentId: string) => {
    const ts = recordDocumentUsed(documentId);
    setDocumentLastUsedAt((prev) => ({ ...prev, [documentId]: ts }));
  }, []);
  const [fontSize, setFontSize] = useState(() => {
    const saved = localStorage.getItem('fontSize');
    return saved ? parseInt(saved) : 14;
  });

  // Initialize global font size on component mount
  useEffect(() => {
    document.documentElement.style.setProperty('--app-font-size', `${fontSize}px`);
  }, [fontSize]);
  const [splitDialogOpen, setSplitDialogOpen] = useState(false);
  const [splitNames, setSplitNames] = useState({ first: '', second: '' });
  const [splitError, setSplitError] = useState('');
  /** True when split rows were chosen via Compare content (empty first subcategory is valid). */
  const [splitFromCompare, setSplitFromCompare] = useState(false);
  const [addCategoryDialogOpen, setAddCategoryDialogOpen] = useState(false);
  const [groupCategoriesDialogOpen, setGroupCategoriesDialogOpen] = useState(false);
  const [organizeCategoriesDialogOpen, setOrganizeCategoriesDialogOpen] = useState(false);
  const [organizeCategoriesMode, setOrganizeCategoriesMode] = useState<OrganizeCategoriesMode>('fromContent');
  const [organizeCategoriesPreset, setOrganizeCategoriesPreset] = useState<OrganizeCategoriesPreset>(null);
  const [organizeBranchSiblingIds, setOrganizeBranchSiblingIds] = useState<string[]>([]);
  const [organizeSplittableChildren, setOrganizeSplittableChildren] = useState<Category[]>([]);
  const [organizeSplittableLoading, setOrganizeSplittableLoading] = useState(false);
  const organizeSplittableTokenRef = useRef(0);
  const [mergeCategoriesDialogOpen, setMergeCategoriesDialogOpen] = useState(false);
  const [addMatchingContentDialogOpen, setAddMatchingContentDialogOpen] = useState(false);
  const [adjustTextRow, setAdjustTextRow] = useState<Content | null>(null);
  const [adjustTextCategoryId, setAdjustTextCategoryId] = useState<string | null>(null);
  const [compareContentDialogOpen, setCompareContentDialogOpen] = useState(false);
  const [categoryReportDialogOpen, setCategoryReportDialogOpen] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState<Category[]>([]);
  const [selectedContents, setSelectedContents] = useState<Content[]>([]);
  const [isContentPanelOpen, setIsContentPanelOpen] = useState(true);
  const [contentPanelHeight, setContentPanelHeight] = useState(300);
  const [isEditingContent, setIsEditingContent] = useState(false);
  const contentPanelRef = useRef<HTMLDivElement>(null);
  const resizeStartY = useRef<number>(0);
  const resizeStartHeight = useRef<number>(0);
  const [rightPaneSplitPosition, setRightPaneSplitPosition] = useState(40); // percentage
  const rightPaneResizerRef = useRef<HTMLDivElement>(null);
  const isRightPaneDraggingRef = useRef(false);
  const [subcases, setSubcases] = useState<string[]>([]);
  const [currentContent, setCurrentContent] = useState<Content | null>(null);
  const [contentEditorResetNonce, setContentEditorResetNonce] = useState(0);
  const contentEditorRef = useRef<ContentEditorHandle | null>(null);
  const [contentPreviewOpen, setContentPreviewOpen] = useState(false);
  const [contentPreviewHtml, setContentPreviewHtml] = useState('');
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [avatarMenuAnchorEl, setAvatarMenuAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const avatarMenuOpen = Boolean(avatarMenuAnchorEl);
  const { documents, addDocument, createDocument, updateDocument, deleteDocument, refreshDocuments, openDocument, duplicateDocument, fetchSubcases, refreshDocumentSubcases, mergeDocument, createSelectiveMergeDocument } = useDocument();
  const [avatarDataUrl, setAvatarDataUrl] = useState<string | null>(null);
  const [predefinedAvatarDataUrls, setPredefinedAvatarDataUrls] = useState<{[key: string]: string}>({});
  const [loading, setLoading] = useState(false);
  const loadingRef = useRef(false);
  loadingRef.current = loading;
  /** Keep loadingRef in sync immediately so nested callers (e.g. handleNewDocument mid apply) see ownership before the next render. */
  const setLoadingSync = useCallback((next: boolean) => {
    loadingRef.current = next;
    setLoading(next);
  }, []);
  const [categoryPaneLoading, setCategoryPaneLoading] = useState(false);
  const [autoPasteEnabled, setAutoPasteEnabled] = useState<boolean>(() => {
    const saved = localStorage.getItem('autoPasteEnabled');
    return saved ? JSON.parse(saved) : false;
  });
  const [toolbarDockMagnify, setToolbarDockMagnify] = useState<boolean>(() => {
    const saved = localStorage.getItem('toolbarDockMagnify');
    if (!saved) return false;
    try {
      return Boolean(JSON.parse(saved));
    } catch {
      localStorage.removeItem('toolbarDockMagnify');
      return false;
    }
  });
  const [showContentIndexInPane, setShowContentIndexInPane] = useState<boolean>(() => {
    const saved = localStorage.getItem('showContentIndexInPane');
    if (!saved) return false;
    try {
      return Boolean(JSON.parse(saved));
    } catch {
      localStorage.removeItem('showContentIndexInPane');
      return false;
    }
  });
  const [clickToRenameCategoryInTree, setClickToRenameCategoryInTree] = useState<boolean>(() => {
    const saved = localStorage.getItem('clickToRenameCategoryInTree');
    if (!saved) return true;
    try {
      return Boolean(JSON.parse(saved));
    } catch {
      localStorage.removeItem('clickToRenameCategoryInTree');
      return true;
    }
  });
  const [workspaceChrome, setWorkspaceChrome] = useState<WorkspaceChrome>(() => readWorkspaceChrome());
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [treeAssistantOpen, setTreeAssistantOpen] = useState(false);
  const [isDocumentPanelVisible, setIsDocumentPanelVisible] = useState(true);
  const [incomingShareCount, setIncomingShareCount] = useState(0);
  const [shareLoginNoticeDismissed, setShareLoginNoticeDismissed] = useState(false);
  const [sharedWithMeFocusToken, setSharedWithMeFocusToken] = useState(0);
  const shareNoticeUserIdRef = useRef<string | null>(null);
  const knownIncomingShareIdsRef = useRef<Set<number>>(new Set());
  const incomingSharePollInitializedRef = useRef(false);
  const knownReadAccessGrantIdsRef = useRef<Set<number>>(new Set());
  const removeReadAccessGrantFromListRef = useRef<(grantId: number) => void>(() => {});
  const dismissStaleReadAccessGrantRef = useRef<(grantId: number) => void>(() => {});
  const removeIncomingShareFromListRef = useRef<(invitationId: number) => void>(() => {});
  const readAccessPollInitializedRef = useRef(false);
  const knownOutgoingShareStatusRef = useRef<Map<number, DocumentShareInvitation['status']>>(new Map());
  const outgoingSharePollInitializedRef = useRef(false);
  const [shareArrivalSnackbarOpen, setShareArrivalSnackbarOpen] = useState(false);
  const [shareArrivalSnackbarMessage, setShareArrivalSnackbarMessage] = useState('');
  const [shareAcceptedSnackbarOpen, setShareAcceptedSnackbarOpen] = useState(false);
  const [shareAcceptedSnackbarMessage, setShareAcceptedSnackbarMessage] = useState('');
  const [incomingSharesRefreshToken, setIncomingSharesRefreshToken] = useState(0);
  const [outgoingSharesRefreshToken, setOutgoingSharesRefreshToken] = useState(0);

  const syncKnownIncomingShares = useCallback((invitations: DocumentShareInvitation[]) => {
    knownIncomingShareIdsRef.current = new Set(invitations.map((inv) => inv.id));
    incomingSharePollInitializedRef.current = true;
  }, []);

  const syncKnownReadAccessGrants = useCallback((grants: DocumentReadAccessGrant[]) => {
    knownReadAccessGrantIdsRef.current = new Set(grants.map((g) => g.id));
    readAccessPollInitializedRef.current = true;
  }, []);

  const registerRemoveReadAccessGrantFromList = useCallback(
    (remove: (grantId: number) => void) => {
      removeReadAccessGrantFromListRef.current = remove;
    },
    [],
  );

  const registerRemoveIncomingShareFromList = useCallback(
    (remove: (invitationId: number) => void) => {
      removeIncomingShareFromListRef.current = remove;
    },
    [],
  );

  const syncKnownOutgoingShares = useCallback((invitations: DocumentShareInvitation[]) => {
    const next = new Map<number, DocumentShareInvitation['status']>();
    for (const inv of invitations) {
      next.set(inv.id, inv.status);
    }
    knownOutgoingShareStatusRef.current = next;
    outgoingSharePollInitializedRef.current = true;
  }, []);

  const handleIncomingSharesChanged = useCallback(
    (invitations: DocumentShareInvitation[]) => {
      setIncomingShareCount(invitations.length);
      syncKnownIncomingShares(invitations);
    },
    [syncKnownIncomingShares],
  );

  const handleOutgoingSharesChanged = useCallback(
    (invitations: DocumentShareInvitation[]) => {
      syncKnownOutgoingShares(invitations);
    },
    [syncKnownOutgoingShares],
  );

  const pollDocumentShares = useCallback(async () => {
    if (!documentSharingEnabled) {
      setIncomingShareCount(0);
      return;
    }
    try {
      const sharePollConfig = skipAuthRedirectConfig();
      const [incomingRes, outgoingRes, readAccessWithMeRes] = await Promise.all([
        api.get<DocumentShareInvitation[]>('/documents/shares/incoming', sharePollConfig),
        api.get<DocumentShareInvitation[]>('/documents/shares/outgoing', sharePollConfig),
        api.get<DocumentReadAccessGrant[]>('/documents/read-access/shared-with-me', sharePollConfig),
      ]);
      const pendingIncoming = filterPendingIncomingShares(incomingRes.data ?? []);
      const readAccessWithMe = filterActiveReadAccessGrants(readAccessWithMeRes.data ?? []);
      const outgoing = outgoingRes.data ?? [];
      setIncomingShareCount(pendingIncoming.length + readAccessWithMe.length);

      const currentIncomingIds = new Set(pendingIncoming.map((inv) => inv.id));
      if (!incomingSharePollInitializedRef.current) {
        syncKnownIncomingShares(pendingIncoming);
      } else {
        const revokedIncomingIds = Array.from(knownIncomingShareIdsRef.current).filter(
          (id) => !currentIncomingIds.has(id),
        );
        const newlyArrived = pendingIncoming.filter((inv) => !knownIncomingShareIdsRef.current.has(inv.id));
        knownIncomingShareIdsRef.current = currentIncomingIds;

        if (revokedIncomingIds.length > 0) {
          revokedIncomingIds.forEach((invitationId) => {
            removeIncomingShareFromListRef.current(invitationId);
          });
          setIncomingSharesRefreshToken((t) => t + 1);
        }

        if (newlyArrived.length > 0) {
          const first = newlyArrived[0];
          setShareArrivalSnackbarMessage(
            newlyArrived.length === 1
              ? `${first.sourceUsername} shared “${first.sourceDocumentName}” with you.`
              : `${newlyArrived.length} documents were shared with you.`,
          );
          setShareArrivalSnackbarOpen(true);
          setIncomingSharesRefreshToken((t) => t + 1);
        }
      }

      const currentReadAccessIds = new Set(readAccessWithMe.map((g) => g.id));
      if (!readAccessPollInitializedRef.current) {
        syncKnownReadAccessGrants(readAccessWithMe);
        setIncomingSharesRefreshToken((t) => t + 1);
      } else {
        const revokedReadAccessIds = Array.from(knownReadAccessGrantIdsRef.current).filter(
          (id) => !currentReadAccessIds.has(id),
        );
        const newlyArrivedReadAccess = readAccessWithMe.filter(
          (g) => !knownReadAccessGrantIdsRef.current.has(g.id),
        );
        knownReadAccessGrantIdsRef.current = currentReadAccessIds;

        if (revokedReadAccessIds.length > 0) {
          revokedReadAccessIds.forEach((grantId) => {
            dismissStaleReadAccessGrantRef.current(grantId);
          });
          setIncomingShareCount(pendingIncoming.length + readAccessWithMe.length);
          setIncomingSharesRefreshToken((t) => t + 1);
        }

        if (newlyArrivedReadAccess.length > 0) {
          const first = newlyArrivedReadAccess[0];
          setShareArrivalSnackbarMessage(
            newlyArrivedReadAccess.length === 1
              ? `${first.ownerUsername} granted you view-only access to “${first.documentName}”.`
              : `${newlyArrivedReadAccess.length} documents were shared with you for view-only access.`,
          );
          setShareArrivalSnackbarOpen(true);
          setIncomingSharesRefreshToken((t) => t + 1);
        }
      }

      const previousOutgoing = knownOutgoingShareStatusRef.current;
      if (!outgoingSharePollInitializedRef.current) {
        syncKnownOutgoingShares(outgoing);
      } else {
        const newlyAccepted = outgoing.filter(
          (inv) => previousOutgoing.get(inv.id) === 'PENDING' && inv.status === 'ACCEPTED',
        );
        syncKnownOutgoingShares(outgoing);
        if (newlyAccepted.length > 0) {
          const first = newlyAccepted[0];
          const recipient = first.targetUsername ?? 'The recipient';
          setShareAcceptedSnackbarMessage(
            newlyAccepted.length === 1
              ? first.acceptedDocumentName
                ? `${recipient} accepted “${first.sourceDocumentName}” as “${first.acceptedDocumentName}”.`
                : `${recipient} accepted “${first.sourceDocumentName}”.`
              : `${newlyAccepted.length} recipients accepted documents you shared.`,
          );
          setShareAcceptedSnackbarOpen(true);
          setOutgoingSharesRefreshToken((t) => t + 1);
        }
      }
    } catch {
      setIncomingShareCount(0);
    }
  }, [
    documentSharingEnabled,
    syncKnownIncomingShares,
    syncKnownOutgoingShares,
    syncKnownReadAccessGrants,
  ]);

  useEffect(() => {
    if (user?.id != null && shareNoticeUserIdRef.current !== user.id) {
      shareNoticeUserIdRef.current = user.id;
      setShareLoginNoticeDismissed(false);
      knownIncomingShareIdsRef.current = new Set();
      incomingSharePollInitializedRef.current = false;
      knownReadAccessGrantIdsRef.current = new Set();
      readAccessPollInitializedRef.current = false;
      knownOutgoingShareStatusRef.current = new Map();
      outgoingSharePollInitializedRef.current = false;
    }
    if (!user) {
      shareNoticeUserIdRef.current = null;
      setIncomingShareCount(0);
      setShareLoginNoticeDismissed(false);
      knownIncomingShareIdsRef.current = new Set();
      incomingSharePollInitializedRef.current = false;
      knownReadAccessGrantIdsRef.current = new Set();
      readAccessPollInitializedRef.current = false;
      knownOutgoingShareStatusRef.current = new Map();
      outgoingSharePollInitializedRef.current = false;
      setShareArrivalSnackbarOpen(false);
      setShareAcceptedSnackbarOpen(false);
    }
  }, [user]);

  const handleViewSharedDocumentsNotice = useCallback(() => {
    setIsDocumentPanelVisible(true);
    setSharedWithMeFocusToken((t) => t + 1);
    setShareLoginNoticeDismissed(true);
  }, []);
  const [openRenameSubcaseDialog, setOpenRenameSubcaseDialog] = useState(false);
  const [duplicateCategoryDialogOpen, setDuplicateCategoryDialogOpen] = useState(false);
  const [importSubtreeDialogOpen, setImportSubtreeDialogOpen] = useState(false);
  // Add state for delete confirmation dialogs
  const [openDeleteCategoryDialog, setOpenDeleteCategoryDialog] = useState(false);
  const [openDeleteContentDialog, setOpenDeleteContentDialog] = useState(false);
  const [includeDescriptions, setIncludeDescriptions] = useState(true);
  const [includeContent, setIncludeContent] = useState(true);
  /** Subcases shown in Show Subtree overlay (independent from content pane filter). */
  const [subTreeOverlayFilterSubcases, setSubTreeOverlayFilterSubcases] = useState<string[]>([]);
  const [subTreeOverlayFilterGroupNames, setSubTreeOverlayFilterGroupNames] = useState<string[]>([]);
  const [subTreeOverlayOpen, setSubTreeOverlayOpen] = useState(false);
  const [subTreeOverlayOpening, setSubTreeOverlayOpening] = useState(false);
  const [subTreeOverlayScopeLoading, setSubTreeOverlayScopeLoading] = useState(false);
  const overviewScopeLoadSeqRef = useRef(0);
  const [subTreeOverlayData, setSubTreeOverlayData] = useState<{
    category: Category | null;
    includeDescriptions: boolean;
  }>({ category: null, includeDescriptions: true });
  /** In-overview "Copy/Move to…": copy or move a content row to another category without leaving the overview. */
  const [overviewCopySource, setOverviewCopySource] = useState<{ content: Content; parentId: string; parentName: string } | null>(null);
  const [overviewCopyTargetId, setOverviewCopyTargetId] = useState<string>('');
  const [overviewCopyMode, setOverviewCopyMode] = useState<'copy' | 'move'>('copy');
  const [overviewCopyBusy, setOverviewCopyBusy] = useState(false);
  const [overviewCopyError, setOverviewCopyError] = useState<string | null>(null);
  const [overviewCopySnackbarOpen, setOverviewCopySnackbarOpen] = useState(false);
  const [overviewCopySnackbarMessage, setOverviewCopySnackbarMessage] = useState('');
  const [overviewDeleteSource, setOverviewDeleteSource] = useState<{
    content: Content;
    categoryId: string;
    parentName: string;
  } | null>(null);
  const [overviewDeleteBusy, setOverviewDeleteBusy] = useState(false);
  const [compareDeleteSource, setCompareDeleteSource] = useState<{
    tabId: string;
    kind: 'content' | 'category';
    side: 'left' | 'right';
    targetDocumentName: string;
    parentName: string;
    content?: Content;
    categoryId?: string;
    index?: number;
    category?: Category;
    parentId?: string | null;
  } | null>(null);
  const [compareDeleteBusy, setCompareDeleteBusy] = useState(false);
  const [categoryLoadFaultMessage, setCategoryLoadFaultMessage] = useState<string | null>(null);
  /** Show Subtree: subcase/group panel expanded (collapsed by default for maximum tree area). */
  const [subTreeOverlayFilterExpanded, setSubTreeOverlayFilterExpanded] = useState(false);
  const [subTreeOverlayLayoutMode, setSubTreeOverlayLayoutMode] = useState<'vertical' | 'horizontal'>(
    'vertical',
  );
  const [subTreeSearch, setSubTreeSearch] = useState('');
  const [subTreeCaseSensitive, setSubTreeCaseSensitive] = useState(false);
  const [subTreeWholeWord, setSubTreeWholeWord] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const searchTimeoutRef = useRef<NodeJS.Timeout>();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!user || !documentSharingEnabled || location.pathname.startsWith('/admin')) {
      knownIncomingShareIdsRef.current = new Set();
      incomingSharePollInitializedRef.current = false;
      knownReadAccessGrantIdsRef.current = new Set();
      readAccessPollInitializedRef.current = false;
      knownOutgoingShareStatusRef.current = new Map();
      outgoingSharePollInitializedRef.current = false;
      return;
    }
    void pollDocumentShares();
    const interval = window.setInterval(() => {
      void pollDocumentShares();
    }, 30_000);
    const onWindowFocus = () => {
      void pollDocumentShares();
    };
    window.addEventListener('focus', onWindowFocus);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener('focus', onWindowFocus);
    };
  }, [user, documentSharingEnabled, location.pathname, pollDocumentShares]);

  /** Split app bar: keep menu + logo over the document strip; align action icons with the main pane. */
  const isAdminRoute = location.pathname.startsWith('/admin');
  const effectiveAppBarHeight =
    workspaceChrome === 'menuBar' && !isAdminRoute ? APP_BAR_HEIGHT_MENU_BAR : APP_BAR_HEIGHT;
  const useMainToolbarGrid = isDocumentPanelVisible && !isAdminRoute;
  const [subcaseGroupsDialogOpen, setSubcaseGroupsDialogOpen] = useState(false);
  const [subcaseGroups, setSubcaseGroups] = useState<{ name: string; subcases: string[] }[]>([]);
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);
  const [loadingSubcaseGroups, setLoadingSubcaseGroups] = useState(false);
  /** First group chip in Show Subtree overlay: snapshot subcases to restore when last group cleared. */
  const subTreeOverlaySubcasesBaselineRef = useRef<string[] | null>(null);
  const contentPaneFilterSubcasesBaselineRef = useRef<string[] | null>(null);
  /** Filter value when dialog opened; Cancel restores this. */
  const contentPaneFilterSnapshotRef = useRef<string[] | null>(null);
  /** After inferring draft group chips from current draft (once per dialog open when filter was partial). */
  const contentPaneFilterGroupsInferredRef = useRef(false);
  const [selectedSubcase, setSelectedSubcase] = useState<string>('');
  /** null = show all contents; non-null = only rows whose subcase is in this list */
  const [contentPaneSubcaseFilter, setContentPaneSubcaseFilter] = useState<string[] | null>(null);
  const [contentPaneFilterDialogOpen, setContentPaneFilterDialogOpen] = useState(false);
  const [contentPaneFilterDraftSubcases, setContentPaneFilterDraftSubcases] = useState<string[]>([]);
  const [contentPaneFilterDraftGroupNames, setContentPaneFilterDraftGroupNames] = useState<string[]>([]);
  // Category find feature state
  const [findDialogOpen, setFindDialogOpen] = useState(false);
  const [findSearchString, setFindSearchString] = useState('');
  const [findResults, setFindResults] = useState<Category[]>([]);
  const [currentFindIndex, setCurrentFindIndex] = useState(0);
  const [findStarted, setFindStarted] = useState(false);
  const [showEndWarning, setShowEndWarning] = useState(false);
  const [showStartWarning, setShowStartWarning] = useState(false);
  const [hasWrappedAround, setHasWrappedAround] = useState(false);
  const [highlightedText, setHighlightedText] = useState<string>('');
  const [searchTerms, setSearchTerms] = useState<string[]>([]);
  const [searchMode, setSearchMode] = useState<'any' | 'all' | 'exact' | 'semantic'>('exact');
  const [wholeWord, setWholeWord] = useState<boolean>(false);
  const [caseSensitive, setCaseSensitive] = useState<boolean>(false);
  // Reactive variable for the current document (document in active tab)
  const [activeDocument, setActiveDocument] = useState<Document | undefined>();
  const [matchingContentIds, setMatchingContentIds] = useState<string[]>([]);
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [toolbarMenuAnchorEl, setToolbarMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [toolbarRef, setToolbarRef] = useState<HTMLDivElement | null>(null);
  const [toolbarRowRef, setToolbarRowRef] = useState<HTMLDivElement | null>(null);
  const [toolbarPinnedRef, setToolbarPinnedRef] = useState<HTMLDivElement | null>(null);
  const [showToolbarMore, setShowToolbarMore] = useState(false);
  const [visibleButtons, setVisibleButtons] = useState<Set<string>>(
    new Set(['add', 'edit', 'adjustText', 'delete']),
  );

  // Dialog position and drag state
  const [dialogPosition, setDialogPosition] = useState({ x: window.innerWidth - 350, y: 80 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  // Ref for scrolling to matched categories
  const categoryTreeRef = useRef<HTMLDivElement>(null);

  // Content pane ref for auto-scroll
  const contentPaneRef = useRef<HTMLDivElement>(null);
  const categoryContentRef = useRef<HTMLDivElement>(null);

  const { getToolbarTooltipTextForState, getToolbarTooltipText, shouldShowTooltip } = useHelpContent();
  const showToolbarOverflowTooltip = shouldShowTooltip();

  // Add state for unsaved changes confirmation dialog
  const [unsavedChangesDialog, setUnsavedChangesDialog] = useState<{
    open: boolean;
    action: (() => void) | null;
    message: string;
  }>({
    open: false,
    action: null,
    message: ''
  });

  // Add this near other state declarations
  const [showEditorWarning, setShowEditorWarning] = useState(false);
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);
  const pendingActionRef = useRef<(() => void) | null>(null);

  const toolbarMagnifyRootRef = useRef<HTMLDivElement | null>(null);
  const toolbarMagnifyRafRef = useRef<number | null>(null);
  const toolbarMagnifyMouseRef = useRef({ x: 0, y: 0 });
  const toolbarMeasurePhaseRef = useRef<ToolbarMeasurePhase>('idle');

  const _setPendingAction = (fn: (() => void) | null) => {
    pendingActionRef.current = fn;
    setPendingAction(() => fn);
  };

  const scheduleToolbarDockMagnify = useCallback(() => {
    if (toolbarMagnifyRafRef.current != null) return;
    toolbarMagnifyRafRef.current = requestAnimationFrame(() => {
      toolbarMagnifyRafRef.current = null;
      if (!toolbarDockMagnify) return;
      const root = toolbarMagnifyRootRef.current;
      if (!root) return;
      const { x, y } = toolbarMagnifyMouseRef.current;
      applyToolbarDockMagnify(root, x, y);
    });
  }, [toolbarDockMagnify]);

  const handleToolbarMagnifyMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!toolbarDockMagnify) return;
      toolbarMagnifyMouseRef.current = { x: e.clientX, y: e.clientY };
      scheduleToolbarDockMagnify();
    },
    [toolbarDockMagnify, scheduleToolbarDockMagnify],
  );

  const handleToolbarMagnifyLeave = useCallback(() => {
    if (toolbarMagnifyRafRef.current != null) {
      cancelAnimationFrame(toolbarMagnifyRafRef.current);
      toolbarMagnifyRafRef.current = null;
    }
    resetToolbarDockMagnifyTransforms(toolbarMagnifyRootRef.current);
  }, []);

  const handleToolbarMouseEnter = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const toolbarEl = e.currentTarget;
    const related = e.relatedTarget;
    if (related instanceof Node && toolbarEl.contains(related)) return;

    const active = document.activeElement;
    if (active instanceof HTMLElement) {
      if (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA' || active.tagName === 'SELECT') {
        return;
      }
      if (active.isContentEditable) return;
    }

    const nodes = toolbarEl.querySelectorAll('button:not([disabled])');
    for (let i = 0; i < nodes.length; i++) {
      const btn = nodes[i] as HTMLButtonElement;
      const style = window.getComputedStyle(btn);
      if (style.visibility === 'hidden' || style.display === 'none') continue;
      const r = btn.getBoundingClientRect();
      if (r.width === 0 || r.height === 0) continue;
      btn.focus({ preventScroll: true });
      break;
    }
  }, []);

  useEffect(() => {
    if (!toolbarDockMagnify) {
      if (toolbarMagnifyRafRef.current != null) {
        cancelAnimationFrame(toolbarMagnifyRafRef.current);
        toolbarMagnifyRafRef.current = null;
      }
      resetToolbarDockMagnifyTransforms(toolbarMagnifyRootRef.current);
    }
  }, [toolbarDockMagnify]);

  useEffect(
    () => () => {
      if (toolbarMagnifyRafRef.current != null) {
        cancelAnimationFrame(toolbarMagnifyRafRef.current);
      }
      resetToolbarDockMagnifyTransforms(toolbarMagnifyRootRef.current);
    },
    [],
  );

  // If a navigation/tab-close action was blocked due to unsaved edits, resume it only after
  // the editor has fully exited editing mode. This avoids race conditions with React state batching.
  useEffect(() => {
    if (isEditingContent) return;
    setShowEditorWarning(false);

    if (!pendingActionRef.current) return;
    const act = pendingActionRef.current;
    _setPendingAction(null);
    act();
  }, [isEditingContent]);

  // Read-only shared documents must never stay in content edit mode (Add was reachable before guards).
  useEffect(() => {
    if (!effectiveDocumentReadOnly || !isEditingContent) return;
    setIsEditingContent(false);
    setShowEditorWarning(false);
    setCurrentContent(selectedContents.length === 1 ? selectedContents[0] : null);
    setContentEditorResetNonce((n) => n + 1);
  }, [effectiveDocumentReadOnly, currentDocument?.id, isEditingContent, selectedContents]);

  useEffect(() => {
    if (!effectiveDocumentReadOnly) return;
    setTreeAssistantOpen(false);
  }, [effectiveDocumentReadOnly, currentDocument?.id]);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleAvatarMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAvatarMenuAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleAvatarMenuClose = () => {
    setAvatarMenuAnchorEl(null);
  };

  const handleToolbarMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setToolbarMenuAnchorEl(event.currentTarget);
  };

  const handleToolbarMenuClose = () => {
    setToolbarMenuAnchorEl(null);
  };

  const getCollapsibleToolbarButtonIds = useCallback((): string[] => {
    if (workspaceChrome === 'menuBar') {
      return [];
    }
    const ids: string[] = [];
    if (!effectiveDocumentReadOnly) {
      for (const id of CATEGORY_TOOLBAR_BUTTON_PRIORITY) {
        if (id === 'suggestParentStructure') continue;
        if (id === 'addMatchingContent' && !isFeatureEnabled(FEATURE_FLAG_KEYS.SEMANTIC_FIND)) continue;
        if (id === 'compareContent' && !isFeatureEnabled(FEATURE_FLAG_KEYS.COMPARE_CONTENT_SELECTION)) continue;
        if (id === 'generate' && !isFeatureEnabled(FEATURE_FLAG_KEYS.GENERATE_SUBTREE_FROM_CONTENT)) continue;
        ids.push(id);
      }
    }
    if (isFeatureEnabled(FEATURE_FLAG_KEYS.FILTER_CONTENT_PANE_BY_SUBCASE)) {
      ids.push('contentFilter');
    }
    ids.push('find', 'showSubTree');
    if (isFeatureEnabled(FEATURE_FLAG_KEYS.CATEGORY_AI_REPORT)) {
      ids.push('categoryAiReport');
    }
    if (isFeatureEnabled(FEATURE_FLAG_KEYS.ASK_AI)) {
      ids.push('askAi');
    }
    if (!effectiveDocumentReadOnly && isFeatureEnabled(FEATURE_FLAG_KEYS.TREE_ASSISTANT)) {
      ids.push('treeAssistant');
    }
    return ids;
  }, [workspaceChrome, effectiveDocumentReadOnly, isFeatureEnabled]);

  const handleLogout = async () => {
    historyService.clear();
    handleClose();
    await logout();
  };

  // Update undo/redo state
  useEffect(() => {
    const interval = setInterval(() => {
      setCanUndo(historyService.canUndo());
      setCanRedo(historyService.canRedo());
    }, 100);
    return () => clearInterval(interval);
  }, []);

  // Handle window resize and calculate visible buttons
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Calculate which buttons should be visible based on available space (pinned: help, settings, profile).
  const beginToolbarMeasure = useCallback(() => {
    const allEnabledIds = getCollapsibleToolbarButtonIds();
    if (!toolbarRef) {
      return;
    }
    if (allEnabledIds.length === 0) {
      toolbarMeasurePhaseRef.current = 'idle';
      setVisibleButtons(new Set());
      setShowToolbarMore(false);
      return;
    }
    toolbarMeasurePhaseRef.current = 'checkAllFit';
    setVisibleButtons(new Set(allEnabledIds));
    setShowToolbarMore(false);
  }, [toolbarRef, getCollapsibleToolbarButtonIds]);

  useEffect(() => {
    if (!toolbarRowRef) return;

    beginToolbarMeasure();

    const resizeObserver = new ResizeObserver(() => beginToolbarMeasure());
    resizeObserver.observe(toolbarRowRef);
    if (toolbarRef) {
      resizeObserver.observe(toolbarRef);
    }
    if (toolbarPinnedRef) {
      resizeObserver.observe(toolbarPinnedRef);
    }

    return () => resizeObserver.disconnect();
  }, [
    toolbarRowRef,
    toolbarRef,
    toolbarPinnedRef,
    windowWidth,
    beginToolbarMeasure,
  ]);

  useLayoutEffect(() => {
    if (!toolbarRef) return;

    const allEnabledIds = getCollapsibleToolbarButtonIds();
    if (allEnabledIds.length === 0) return;

    const phase = toolbarMeasurePhaseRef.current;
    if (phase === 'idle') return;

    const toolbarFits = toolbarRef.scrollWidth <= toolbarRef.clientWidth + 1;

    if (phase === 'checkAllFit') {
      if (toolbarFits) {
        toolbarMeasurePhaseRef.current = 'idle';
        setShowToolbarMore(false);
        setVisibleButtons(new Set(allEnabledIds));
        return;
      }
      toolbarMeasurePhaseRef.current = 'trimToFit';
      setShowToolbarMore(true);
      return;
    }

    if (phase === 'trimToFit') {
      if (toolbarFits) {
        toolbarMeasurePhaseRef.current = 'idle';
        return;
      }
      const visibleCount = allEnabledIds.filter((id) => visibleButtons.has(id)).length;
      const currentCount = visibleCount > 0 ? visibleCount : allEnabledIds.length;
      const nextCount = Math.max(0, currentCount - 1);
      if (nextCount === currentCount) {
        toolbarMeasurePhaseRef.current = 'idle';
        return;
      }
      setVisibleButtons(new Set(allEnabledIds.slice(0, nextCount)));
    }
  }, [
    toolbarRef,
    visibleButtons,
    showToolbarMore,
    getCollapsibleToolbarButtonIds,
    effectiveDocumentReadOnly,
    activeDocument?.subcases,
  ]);
  // Keep activeDocument in sync with current tab (owned docs + read-only shared sessions)
  useEffect(() => {
    const currentDoc = getActiveDocument();
    setActiveDocument(currentDoc);
  }, [currentTab, documents, readOnlyOpenDocuments]);

  /** Panel subcase is user-chosen / post-save only; clear if this document no longer lists it (e.g. last use deleted). */
  useEffect(() => {
    const list = activeDocument?.subcases ?? [];
    setSelectedSubcase(prev => {
      if (!prev) return prev;
      return list.includes(prev) ? prev : '';
    });
  }, [activeDocument?.id, activeDocument?.subcases]);

  /** After content delete is no longer undoable (stack trim / redo superseded / document history cleared), purge on-disk media. */
  useEffect(() => {
    const onPurge = (ops: Operation[]) => {
      const ids = collectContentIdsFromDeleteOperations(ops);
      if (ids.length === 0) return;
      void api.post('/content/purge-media', ids).catch((e) => console.error('Deferred media purge failed', e));
    };
    historyService.setDeferredDeleteMediaListener(onPurge);
    return () => historyService.setDeferredDeleteMediaListener(null);
  }, []);

  /** Sync toolbar/UI subcase list from Redis after any mutation that can change refcounts. */
  const refreshActiveDocumentSubcasesFromServer = useCallback(
    async (categoryIdOverride?: string) => {
      const categoryId = categoryIdOverride ?? activeDocument?.categoryId;
      if (!categoryId) return;
      try {
        const subcases = await refreshDocumentSubcases(categoryId);
        setActiveDocument((prev) =>
          prev?.categoryId === categoryId ? { ...prev, subcases } : prev,
        );
        setReadOnlyOpenDocuments((prev) => {
          let changed = false;
          const next = { ...prev };
          for (const [tabId, doc] of Object.entries(next)) {
            if (doc.categoryId === categoryId) {
              next[tabId] = { ...doc, subcases };
              changed = true;
            }
          }
          return changed ? next : prev;
        });
        setCurrentDocument((prev) =>
          prev?.categoryId === categoryId ? { ...prev, subcases } : prev
        );
        setSubTreeOverlayFilterSubcases(prev => prev.filter((s: string) => subcases.includes(s)));
        setSelectedSubcase(prev =>
          prev && subcases.includes(prev) ? prev : ''
        );
        setContentPaneSubcaseFilter(prev => {
          if (prev == null) return null;
          const next = prev.filter(s => subcases.includes(s));
          return next.length === 0 ? null : next;
        });
      } catch (e) {
        console.error('Failed to refresh subcases:', e);
      }
    },
    [activeDocument?.categoryId, refreshDocumentSubcases]
  );

  /**
   * Reload category structure for the open document (awaits network). Use instead of handleOpenDocument when you must
   * await completion — handleOpenDocument does not return a Promise because it delegates to checkForUnsavedChanges.
   */
  const reloadOpenDocumentCategoryTree = async (
    document: Document | undefined,
    preserveSelection = false,
  ) => {
    if (!document?.categoryId) return;
    try {
      const previousSelectedId = preserveSelection ? selectedCategory?.id : undefined;
      const rootCategory = await fetchCategoryWithChildren(document.categoryId);
      if (!rootCategory) return;
      setCategories([rootCategory]);
      // Re-select the previously selected category from the freshly loaded tree so its
      // content (e.g. a renamed subcase) reflects the change. Falls back to the root
      // if the previous selection no longer exists.
      const restoredSelection = previousSelectedId
        ? findCategoryInTree([rootCategory], previousSelectedId)
        : null;
      setSelectedCategory(restoredSelection ?? rootCategory);
      setSelectedCategories([]);
      setSelectedContents([]);
      setCurrentContent(null);
      const savedExpandedState = documentExpandedStates[document.id];
      setExpandedNodes(expandedNodesForLoadedTree(savedExpandedState, rootCategory));
    } catch (e) {
      console.error('Failed to reload category structure:', e);
    }
  };

  const findCategoryInTree = (categories: Category[], targetId: string): Category | null => {
    // First check root level
    const category = categories.find(cat => cat.id === targetId);
    if (category) return category;

    // Then check each category's children
    for (const parent of categories) {
      if (parent.children) {
        const found = findCategoryInTree(parent.children, targetId);
        if (found) return found;
      }
    }

    return null;
  };

  const persistDocumentCategoryTree = useCallback((documentId: string, tree: Category[]) => {
    documentCategoryTreesRef.current = {
      ...documentCategoryTreesRef.current,
      [documentId]: structuredClone(tree),
    };
  }, []);

  /** Remember expansion, selection, and loaded tree nodes before leaving a document tab. */
  const saveDocumentViewSnapshot = useCallback(
    (documentId: string) => {
      if (expandedNodes.length > 0) {
        setDocumentExpandedStates((prev) => ({
          ...prev,
          [documentId]: [...expandedNodes],
        }));
      }
      if (selectedCategory) {
        setDocumentSelectedCategoryIds((prev) => ({
          ...prev,
          [documentId]: selectedCategory.id,
        }));
      }
      if (categories.length > 0) {
        persistDocumentCategoryTree(documentId, categories);
      }
    },
    [categories, expandedNodes, selectedCategory, persistDocumentCategoryTree],
  );

  /** Lazy-load tree + restore saved expansion and selection for a document tab. */
  const loadDocumentViewForTab = useCallback(
    async (document: Document, hadOpenTab: boolean, options?: { skipCache?: boolean }) => {
      if (!document.categoryId) {
        throw new Error('Document has no category root');
      }
      const cachedTree =
        hadOpenTab && !options?.skipCache
          ? documentCategoryTreesRef.current[document.id]
          : undefined;
      if (cachedTree && cachedTree.length > 0) {
        const tree = structuredClone(cachedTree);
        const savedExpanded = documentExpandedStates[document.id];
        const savedCategoryId = documentSelectedCategoryIds[document.id];
        const rootCategory = tree[0];
        const expandedIds = expandedNodesForLoadedTree(savedExpanded, rootCategory);
        const selectedCategory = savedCategoryId
          ? findCategoryInTree(tree, savedCategoryId) ?? rootCategory
          : rootCategory;
        return {
          tree,
          expandedIds,
          selectedCategory,
        };
      }
      const savedExpanded = hadOpenTab ? documentExpandedStates[document.id] : undefined;
      const savedCategoryId = hadOpenTab ? documentSelectedCategoryIds[document.id] : undefined;
      const { tree, expandedIds } = await loadLazyDocumentTree(document.categoryId, savedExpanded);
      const rootCategory = tree[0];
      if (!rootCategory) {
        throw new Error('Could not fetch root category');
      }
      const restored = await restoreSelectedCategoryInTree(tree, expandedIds, savedCategoryId);
      return {
        tree: restored.tree,
        expandedIds: restored.expandedIds,
        selectedCategory: restored.selectedCategory ?? rootCategory,
      };
    },
    [documentExpandedStates, documentSelectedCategoryIds],
  );

  const reportCategoryLoadFault = useCallback((err: unknown, context: string) => {
    const message =
      err instanceof CategoryLoadFault
        ? err.message
        : `${context}: ${err instanceof Error ? err.message : String(err)}`;
    console.error('[category-load]', message, err);
    setCategoryLoadFaultMessage(message);
  }, []);

  const loadBranchChildrenIntoTree = useCallback(
    async (categoryId: string): Promise<Category | null> => {
      setCategories((prev) =>
        updateCategoryInTree(prev, categoryId, (cat) => ({
          ...cat,
          childrenLoadState: 'loading',
          loadError: undefined,
        })),
      );
      try {
        const loaded = await fetchCategoryForPane(categoryId);
        setCategories((prev) => mergeBranchFetchIntoTree(prev, categoryId, loaded));
        setSelectedCategory((prev) => (prev?.id === categoryId ? loaded : prev));
        return loaded;
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        setCategories((prev) =>
          updateCategoryInTree(prev, categoryId, (cat) => ({
            ...cat,
            childrenLoadState: 'error',
            loadError: msg,
          })),
        );
        reportCategoryLoadFault(err, 'Failed to load subcategories');
        return null;
      }
    },
    [reportCategoryLoadFault],
  );

  const loadContentRowsIntoTree = useCallback(
    async (categoryId: string): Promise<Category | null> => {
      setCategories((prev) =>
        updateCategoryInTree(prev, categoryId, (cat) => ({
          ...cat,
          contentsLoadState: 'loading',
          loadError: undefined,
        })),
      );
      try {
        const loaded = await fetchCategoryForPane(categoryId);
        setCategories((prev) => mergeContentsFetchIntoTree(prev, categoryId, loaded));
        setSelectedCategory((prev) => (prev?.id === categoryId ? loaded : prev));
        return loaded;
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        setCategories((prev) =>
          updateCategoryInTree(prev, categoryId, (cat) => ({
            ...cat,
            contentsLoadState: 'error',
            loadError: msg,
          })),
        );
        reportCategoryLoadFault(err, 'Failed to load content rows');
        return null;
      }
    },
    [reportCategoryLoadFault],
  );

  const ensureOverviewScopeLoaded = useCallback(
    async (category: Category): Promise<Category | null> => {
      const seq = ++overviewScopeLoadSeqRef.current;
      const scope = findCategoryInTree(categories, category.id) ?? category;
      if (subtreeFullyLoaded(scope)) {
        return scope;
      }
      setSubTreeOverlayScopeLoading(true);
      try {
        const full = await fetchCategoryWithChildren(scope.id);
        if (seq !== overviewScopeLoadSeqRef.current) {
          return null;
        }
        setCategories((prev) => updateCategoryInTree(prev, scope.id, () => full));
        assertSubtreeFullyLoadedForDisplay(full, 'ensureOverviewScopeLoaded');
        return full;
      } catch (err) {
        if (seq === overviewScopeLoadSeqRef.current) {
          reportCategoryLoadFault(err, 'Failed to load Category Structure Overview scope');
        }
        return null;
      } finally {
        if (seq === overviewScopeLoadSeqRef.current) {
          setSubTreeOverlayScopeLoading(false);
        }
      }
    },
    [categories, reportCategoryLoadFault],
  );

  const resolveParentCategoryIdsAsync = useCallback(
    async (category: Category): Promise<string[]> => {
      const parentIds: string[] = [];
      let parentId = category.parentId;
      while (parentId) {
        parentIds.unshift(parentId);
        let parent = findCategoryInTree(categories, parentId);
        if (!parent) {
          try {
            parent = await fetchCategoryForPane(parentId);
          } catch {
            break;
          }
        }
        parentId = parent.parentId;
      }
      return parentIds;
    },
    [categories],
  );

  const expandParentsToMakeVisible = useCallback(
    async (targetCategory: Category, expandTargetBranch = false) => {
      const parentIds = await resolveParentCategoryIdsAsync(targetCategory);
      const idsToExpand = [...parentIds];
      if (expandTargetBranch && !targetCategory.contentCategory) {
        idsToExpand.push(targetCategory.id);
      }

      for (const id of idsToExpand) {
        const cat = findCategoryInTree(categories, id);
        if (cat && !cat.contentCategory && !branchChildrenAreLoaded(cat)) {
          const loaded = await loadBranchChildrenIntoTree(id);
          if (!loaded) return;
        }
      }

      setExpandedNodes((prev) => {
        const next = new Set(prev);
        idsToExpand.forEach((id) => next.add(id));
        return Array.from(next);
      });

      const liveTarget = findCategoryInTree(categories, targetCategory.id) ?? targetCategory;
      if (liveTarget.contentCategory && !categoryContentsAreLoaded(liveTarget)) {
        await loadContentRowsIntoTree(liveTarget.id);
      }

      scrollToCategory(targetCategory.id);
      setTimeout(() => scrollToCategory(targetCategory.id), 1000);
      setTimeout(() => scrollToCategory(targetCategory.id), 2000);
    },
    [
      categories,
      resolveParentCategoryIdsAsync,
      loadBranchChildrenIntoTree,
      loadContentRowsIntoTree,
    ],
  );

  const addSubTree = async(target: Category, source: Category) => {
    if ( source.contentCategory && source.contents) {
    let newContents: Content[] = [];
      for (const content of source.contents ) {
         const response = await api.post('/content', {
           ...buildContentCopyPayload(content, target.id),
           skipDuplicateCheck: true,
         });
         const newContent = response.data;
         newContents.push(newContent);
      };

      target.contents = newContents;
    }
    else if ( source.children ){
      for (const cat of source.children ) {
        const response = await api.post('/categories', {
          parentId: target.id,
          name: cat.name,
          description: cat.description
        });
        if (!response.data) throw new Error('Failed to create category');
        const newCategory = response.data;

        target.children = [...(target.children || []), newCategory];
        target.contentCategory = false;

        await addSubTree(newCategory, cat);
       }
    }
  };

  /** Shared clone path for paste-copy and import-from-document (new IDs under parentCategory). */
  const cloneCopiedSubtreeUnderCategory = async (
    sourceRoot: Category,
    parentCategory: Category,
    options?: { skipHistory?: boolean },
  ): Promise<Category> => {
    const response = await api.post('/categories', {
      name: sourceRoot.name,
      description: sourceRoot.description,
      parentId: parentCategory.id,
    });
    const newCategory = response.data;

    if (!options?.skipHistory) {
      historyService.recordOperation({
        type: 'pasteCategory',
        data: {
          category: newCategory,
          oldParentId: null,
          newParentId: newCategory.parentId,
          oldIndex: -1,
          newIndex: -1,
        },
      });
    }

    await addSubTree(newCategory, sourceRoot);

    setCategories(prevCategories => {
      const updatedCategories = [...prevCategories];

      const withChild = updateCategoryInTree(
        updatedCategories,
        parentCategory.id,
        cat => ({
          ...cat,
          children: [...(cat.children || []), newCategory],
        }),
      );

      const refreshedTargetParent = findCategoryInTree(withChild, parentCategory.id);
      if (
        refreshedTargetParent &&
        parentCategory.id === refreshedTargetParent.id
      ) {
        const targetId = refreshedTargetParent.id;
        setSelectedCategory(refreshedTargetParent);
        setExpandedNodes(prev =>
          prev.includes(targetId) ? prev : [...prev, targetId],
        );
      }

      return withChild;
    });

    await refreshActiveDocumentSubcasesFromServer();
    return newCategory;
  };

  /** Pre-edit row from the pane/selection; currentContent is mutated live while editing (e.g. subcase). */
  const contentRowBeforeSave = (contentId?: string | null): Content | null => {
    const id = contentId?.trim();
    if (!id) {
      return selectedContents.length === 1 ? selectedContents[0] : null;
    }
    const fromPane = selectedCategory?.contents?.find((c) => c.id === id);
    if (fromPane) return fromPane;
    const fromSelection = selectedContents.find((c) => c.id === id);
    if (fromSelection) return fromSelection;
    return currentContent?.id === id ? currentContent : null;
  };

  /** Prefer the pane/tree copy so editor/preview stay aligned with the content table. */
  const resolveContentRowForDisplay = (content: Content): Content => {
    const id = content.id;
    const fromPane = selectedCategory?.contents?.find((c) => c.id === id);
    if (fromPane) return fromPane;
    const parentId = content.parentId ?? selectedCategory?.id;
    if (parentId) {
      const parent = findCategoryInTree(categories, parentId);
      const fromTree = parent?.contents?.find((c) => c.id === id);
      if (fromTree) return fromTree;
    }
    return content;
  };

  /** Always send rich-text fields so undo/redo clears HTML when reverting plain-only rows. */
  const buildContentSavePayload = (row: Content, parentId: string): Record<string, unknown> => ({
    parentId,
    id: row.id,
    subcase: row.subcase ?? '',
    text: row.text ?? '',
    richTextHtml: row.richTextHtml ?? '',
    richTextDelta: row.richTextDelta ?? '',
    ...(typeof row.attachmentsJson === 'string' ? { attachmentsJson: row.attachmentsJson } : {}),
  });

  /**
   * Payload for copying a content row into a category as a NEW row (no source id reuse).
   * Always carries rich text / media so copy and move keep full fidelity (only Adjust text
   * intentionally works on plain text).
   */
  const buildContentCopyPayload = (row: Content, parentId: string): Record<string, unknown> => ({
    parentId,
    text: row.text ?? '',
    subcase: row.subcase ?? '',
    richTextHtml: row.richTextHtml ?? '',
    richTextDelta: row.richTextDelta ?? '',
    ...(typeof row.attachmentsJson === 'string' ? { attachmentsJson: row.attachmentsJson } : {}),
  });

  const applyContentUpdateInTree = (restored: Content, categoryId: string) => {
    let updatedParent: Category | null = null;
    setCategories((prev) => {
      const newCategories = [...prev];
      const parent = findCategoryInTree(newCategories, categoryId);
      if (parent?.contents) {
        const index = parent.contents.findIndex((child) => child.id === restored.id);
        if (index >= 0) {
          const newContents = [...parent.contents];
          newContents[index] = restored;
          updatedParent = { ...parent, contents: newContents };
          return updateCategoryInTree(newCategories, categoryId, () => updatedParent!);
        }
      }
      return newCategories;
    });
    if (updatedParent && selectedCategory?.id === categoryId) {
      setSelectedCategory(updatedParent);
    }
  };

  const finalizeContentHistoryUpdate = async (restored: Content, categoryId: string) => {
    try {
      const fresh = await fetchCategoryForPane(categoryId);
      setCategories((prev) => updateCategoryInTree([...prev], categoryId, () => fresh));
      const freshRow = fresh.contents?.find((c) => c.id === restored.id) ?? restored;
      if (selectedCategory?.id === categoryId) {
        setSelectedCategory(fresh);
      }
      syncContentEditorIfSelected(freshRow);
    } catch (e) {
      console.warn('Failed to refresh content category after history update', e);
      applyContentUpdateInTree(restored, categoryId);
      syncContentEditorIfSelected(restored);
    }
  };

  /** After undo/redo of a content edit, refresh selection + editor when that row stays selected. */
  const syncContentEditorIfSelected = (restored: Content) => {
    const inSelection = selectedContents.some((c) => c.id === restored.id);
    if (!inSelection) return;
    setSelectedContents((prev) => prev.map((c) => (c.id === restored.id ? restored : c)));
    const soleSelection =
      selectedContents.length === 1 && selectedContents[0].id === restored.id;
    const isViewed = currentContent?.id === restored.id;
    if (soleSelection || isViewed) {
      setCurrentContent(restored);
      setSelectedSubcase(restored.subcase ?? '');
      setContentEditorResetNonce((n) => n + 1);
    }
  };

  /** Pure sibling reorder so React Strict Mode's double updater call cannot swap twice (no-op). */
  const applySiblingReorder = (
    prev: Category[],
    parentId: string,
    mutateCopy: (children: Category[]) => void,
  ): Category[] => {
    const parent = findCategoryInTree(prev, parentId);
    if (!parent?.children) return prev;
    const children = [...parent.children];
    mutateCopy(children);
    const next = updateCategoryInTree(prev, parentId, (p) => ({ ...p, children }));
    const newParent = findCategoryInTree(next, parentId);
    if (newParent) {
      // Content pane renders selectedCategory.children, not the tree copy — keep them in sync.
      setSelectedCategory((sel) => {
        if (!sel) return sel;
        if (sel.id === parentId) return newParent;
        return findCategoryInTree(next, sel.id) ?? sel;
      });
      setSelectedCategories((sels) => {
        if (sels.length === 0) return sels;
        return sels
          .map((c) => findCategoryInTree(next, c.id))
          .filter((c): c is Category => c != null);
      });
    }
    return next;
  };

  const moveChildFormBody = (
    categoryId: string,
    firstIndex: number,
    lastIndex: number,
    direction: string,
  ) =>
    new URLSearchParams({
      categoryId,
      firstIndex: String(firstIndex),
      lastIndex: String(lastIndex),
      direction,
    }).toString();

  const logDocumentHistoryReplay = async (direction: 'UNDO' | 'REDO', operation: Operation) => {
    const documentId = activeDocument?.id;
    if (!documentId) return;
    const { sourceCategoryName, targetCategoryName } = historyReplayCategoryNames(operation, categories);
    try {
      await api.post(`/documents/${documentId}/activity/history-replay`, {
        direction,
        operationType: operation.type,
        operationLabel: historyService.getOperationDisplayName(operation),
        sourceCategoryName,
        targetCategoryName,
      });
    } catch (err) {
      console.warn('Failed to log document history replay:', err);
    }
  };

  const handleUndo = async () => {
    const operation = historyService.undo();
    if (!operation) return;
    setLoading(true);
    try {
      await withSuppressedDocumentActivity(async () => {
      const subcaseRefreshCategoryId = activeDocument?.categoryId;
      switch (operation.type) {
        case 'splitCategory': {
            const { firstCategory, secondCategory, parentId, tempCategory, movedContentsFirst, movedContentsSecond} = operation.data;
            await deleteCategory(firstCategory.id);
            await deleteCategory(secondCategory.id);

            if ( tempCategory.id !== firstCategory.id ) {
                await deleteCategory(tempCategory.id);
            }

             // Restore every stashed row in a single request (rows are appended in the order sent, so the
             // original ordering is preserved without per-row reorder calls). This collapses what used to be
             // ~2 HTTP round-trips per row into one — undo of a large split is otherwise very slow.
             const rowsToRestore = [...movedContentsFirst, ...movedContentsSecond]
                 .slice()
                 .sort((a, b) => a.index - b.index);

             let newContents: {content: Content, index: number}[] = [];
             if (rowsToRestore.length > 0) {
                 const restorePayload = rowsToRestore.map(({ content, oldContentId }) => ({
                     id: oldContentId,
                     parentId: parentId,
                     subcase: content.subcase,
                     text: content.text,
                     richTextHtml: content.richTextHtml,
                     richTextDelta: content.richTextDelta,
                     attachmentsJson: content.attachmentsJson,
                 }));
                 const response = await api.post('/content/bulk', restorePayload);
                 const restored = response.data as Content[];
                 newContents = restored.map((c, i) => ({ content: c, index: rowsToRestore[i].index }));
             }

            newContents.sort((a, b) => a.index - b.index);
            setCategories(prev => {
                const newCategories = [...prev];
                const parent = findParentCategoryInTree(newCategories, firstCategory.id);
                if (parent) {
                  parent.contents = newContents.map(c => c.content);
                  parent.children = [];
                  parent.contentCategory = true;

                  // Select the new category and expand the parent
                  setTimeout(() => {
                    setSelectedCategory(parent);
                    setSelectedCategories([]);
                    setExpandedNodes(prev => [...prev, parent.id]);
                  }, 0);
                }

                return newCategories;
            });
            break;
        }
        case 'deleteCategory': {
          const items = Array.isArray(operation.data) ? operation.data : [operation.data];
          const parentIds = new Set<string>();
          const sorted = [...items].sort((a, b) => {
            const pa = a.parentId ?? '';
            const pb = b.parentId ?? '';
            if (pa !== pb) return pa.localeCompare(pb);
            return b.index - a.index;
          });

          for (const { category, parentId, index } of sorted) {
            if (parentId) {
              parentIds.add(parentId);
            }
            const response = await api.post('/categories', {
              parentId,
              name: category.name,
              description: category.description,
            });
            if (!response.data) throw new Error('Failed to create category');
            const newCategory = response.data;
            if (index >= 0) {
              await api.put(
                '/categories/moveto',
                {
                  categoryId: newCategory.id,
                  moveTo: index,
                },
                {
                  headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                  },
                },
              );
            }
            if (
              (category.children && category.children.length > 0) ||
              (category.contents && category.contents.length > 0)
            ) {
              await addSubTree(newCategory, category);
            }
          }

          const refreshed = (
            await Promise.all(
              Array.from(parentIds).map((id) => fetchCategoryWithChildren(id)),
            )
          ).filter((c): c is Category => c != null);

          setCategories((prev) => {
            let next = [...prev];
            for (const parent of refreshed) {
              next = updateCategoryInTree(next, parent.id, () => parent);
            }
            return next;
          });

          const primaryParentId = items[0]?.parentId;
          const refreshedPrimary = primaryParentId
            ? refreshed.find((p) => p.id === primaryParentId) ?? null
            : null;
          if (refreshedPrimary) {
            setSelectedCategory(refreshedPrimary);
          }
          setSelectedCategories([]);
          break;
        }
        case 'addCategory': {
          const { category } = operation.data;
          await deleteCategory(category.id);
          setCategories(prev => {
            const newCategories = [...prev];
            const parent = findParentCategoryInTree(newCategories, category.id);
            if (parent) {
              parent.children = parent.children?.filter(child => child.id !== category.id);
            }
            return newCategories;
          });
          break;
        }
        case 'renameCategory': {
            const { category, oldName, oldDescription } = operation.data;
            const response = await api.put(`/categories`, {
                    parentId: category.id,
                    name: oldName,
                    description: oldDescription
                }
              );
            const renamedCategory = response.data;
            const updatedCategory = await fetchCategoryWithChildren(renamedCategory.parentId);

           setCategories(prevCategories => {
             const updatedCategories = [...prevCategories];
                const newCategories = updateCategoryInTree(updatedCategories, updatedCategory.id, () => updatedCategory);

                // Find and update the selected category within the same state update
                if (selectedCategory) {
                    const cat = findCategoryInTree(newCategories, selectedCategory.id);
           if (cat) {
                        // Use setTimeout to ensure this happens after the state update
               setTimeout(() => {
                    setSelectedCategory(cat);
                  }, 0);
            }
                }

                return newCategories;
            });
            break;
        }
        case 'pasteCategory':
        case 'dropCategory': {
          const { category, oldCategoryId, oldParentId, newParentId, oldIndex, newIndex } = operation.data;
          const deletedCategoryId = category.id;
          await deleteCategory(deletedCategoryId);

          let parentAfterUndo: Category | null = null;
          if (oldIndex < 0 && newParentId) {
            parentAfterUndo = await fetchCategoryWithChildren(newParentId);
          }

          setCategories(prev => {
            const newCategories = [...prev];
            const parent = findCategoryInTree(newCategories, newParentId);
            if (parent) {
              parent.children = parent.children?.filter(child => child.id !== deletedCategoryId);
            }

            const selectionWasOnDeletedSubtree =
              !!selectedCategory &&
              (selectedCategory.id === deletedCategoryId ||
                selectedCategory.id.startsWith(`${deletedCategoryId}:`));

            // Import branch / copy-paste: always return to the target parent so the content pane
            // does not keep showing rows from the removed clone.
            if (oldIndex < 0 && newParentId) {
              const refreshedTargetParent =
                parentAfterUndo ?? findCategoryInTree(newCategories, newParentId);
              if (refreshedTargetParent) {
                setTimeout(() => {
                  setSelectedCategory(refreshedTargetParent);
                  setSelectedCategories([]);
                  setSelectedContents([]);
                  setCurrentContent(null);
                  setExpandedNodes(prev =>
                    prev.includes(newParentId) ? prev : [...prev, newParentId],
                  );
                }, 0);
              }
            } else if (selectedCategory?.id === newParentId) {
              const refreshedTargetParent = findCategoryInTree(newCategories, newParentId);
              if (refreshedTargetParent) {
                setTimeout(() => {
                  setSelectedCategory(refreshedTargetParent);
                  setExpandedNodes(prev =>
                    prev.includes(newParentId) ? prev : [...prev, newParentId],
                  );
                }, 0);
              }
            } else if (selectionWasOnDeletedSubtree && newParentId) {
              const refreshedTargetParent = findCategoryInTree(newCategories, newParentId);
              if (refreshedTargetParent) {
                setTimeout(() => {
                  setSelectedCategory(refreshedTargetParent);
                  setSelectedCategories([]);
                  setSelectedContents([]);
                  setCurrentContent(null);
                  setExpandedNodes(prev =>
                    prev.includes(newParentId) ? prev : [...prev, newParentId],
                  );
                }, 0);
              }
            }

            if (parentAfterUndo) {
              const refreshedParent = parentAfterUndo;
              return updateCategoryInTree(newCategories, newParentId, () => refreshedParent);
            }
            return newCategories;
          });

          if ( oldIndex >= 0 ) {
            const response = await api.post('/categories', {
              parentId: oldParentId,
              name: category.name,
              description: category.description
            });

            if (!response.data) throw new Error('Failed to create category');
            const newCategory = response.data;

            await api.put(`/categories/moveto`,
                {
                    categoryId: newCategory.id,
                    moveTo: oldIndex
                },
                {
                  headers: {
                    'Content-Type': 'application/x-www-form-urlencoded'
                  }
                }
             );

               await addSubTree(newCategory, category);


              setCategories(prevCategories => {
                const updatedCategories = [...prevCategories];
                const parent = findCategoryInTree(updatedCategories, oldParentId);
                if (parent) {
                    // Remove any existing reference to the dropped category
                    if (parent.children) {
                        parent.children = parent.children.filter(child => child.id !== category.id && child.id !== newCategory.id);
                    }
                    // Now insert the new category at the correct index
                    if (parent.children) {
                        parent.children.splice(oldIndex, 0, newCategory);
                    }
                }

                // If the (old) parent is currently selected, refresh it so the content pane updates.
                if (selectedCategory?.id === oldParentId) {
                  const refreshedOldParent = findCategoryInTree(updatedCategories, oldParentId);
                  if (refreshedOldParent) {
                    setTimeout(() => {
                      setSelectedCategory(refreshedOldParent);
                      setExpandedNodes(prev => (prev.includes(oldParentId) ? prev : [...prev, oldParentId]));
                    }, 0);
                  }
                }

                return updatedCategories;
              });

           }
          break;
        }
        case 'groupCategories': {
          const { originalParentId, newParent, groupedCategories } = operation.data
          const movedCategories: { category: Category; index: number }[] = [];
          for ( const {name, index} of groupedCategories ) {
            const response = await api.put(`/categories/move`, {
                categoryId: newParent.id + ":" +name,
                newParentId: originalParentId
              },
              {
                headers: {
                  'Content-Type': 'application/x-www-form-urlencoded'
              }
             });
             const movedCategory = response.data;
             const movedCategoryWithChildren = await fetchCategoryWithChildren(movedCategory.id);
               if (!movedCategoryWithChildren) {
                 throw new Error('Could not fetch updated category structure');
               }
             movedCategories.push({category: movedCategoryWithChildren, index: index});
          }

          for ( const {category, index} of movedCategories ) {
            await api.put(`/categories/moveto`,
                {
                    categoryId: category.id,
                    moveTo: index
                },
                {
                  headers: {
                    'Content-Type': 'application/x-www-form-urlencoded'
                  }
                }
             );
              setCategories(prev => {
                 const newCategories = [...prev];
                 const parent = findCategoryInTree(newCategories, originalParentId);
                 if (parent) {
                     if (parent.children) {
                       parent.children.splice(index, 0, category);
                     }
                 }
                 return newCategories;
             });
            }

            await deleteCategory(newParent.id);

            setCategories(prev => {
                const newCategories = [...prev];
                const parent = findParentCategoryInTree(newCategories, newParent.id);
                if (parent) {
                  parent.children = parent.children?.filter(child => child.id !== newParent.id);
                }
                return newCategories;
              });
          setSelectedCategory(selectedCategory);
          setSelectedCategories([]);
          break;
        }
        case 'pasteCategoriesIntoBranch': {
          const {
            targetBranchId,
            copy,
            moveRecords = [],
            copiedTopIds = [],
          } = operation.data;
          if (copy) {
            for (const id of [...copiedTopIds].reverse()) {
              await deleteCategory(id);
            }
          } else {
            const updatedMoveRecords: {
              categoryId: string;
              oldParentId: string;
              oldIndex: number;
              categoryName?: string;
            }[] = [];
            for (const rec of [...moveRecords].reverse()) {
              const response = await api.put(
                '/categories/move',
                {
                  categoryId: rec.categoryId,
                  newParentId: rec.oldParentId,
                },
                {
                  headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                  },
                },
              );
              const movedBack = response.data as Category;
              if (rec.oldIndex >= 0) {
                await api.put(
                  '/categories/moveto',
                  {
                    categoryId: movedBack.id,
                    moveTo: rec.oldIndex,
                  },
                  {
                    headers: {
                      'Content-Type': 'application/x-www-form-urlencoded',
                    },
                  },
                );
              }
              updatedMoveRecords.unshift({
                ...rec,
                categoryId: movedBack.id,
              });
            }
            operation.data.moveRecords = updatedMoveRecords;
          }
          const refreshIds = new Set<string>([targetBranchId]);
          if (!copy) {
            for (const rec of moveRecords) {
              refreshIds.add(rec.oldParentId);
            }
          }
          const refreshed = (
            await Promise.all(
              Array.from(refreshIds).map((id) => fetchCategoryWithChildren(id)),
            )
          ).filter((c): c is Category => c != null);
          setCategories((prev) => {
            let next = [...prev];
            for (const node of refreshed) {
              next = updateCategoryInTree(next, node.id, () => node);
            }
            return next;
          });
          const targetRefreshed = refreshed.find((c) => c.id === targetBranchId);
          if (targetRefreshed) {
            setSelectedCategory(targetRefreshed);
          }
          setSelectedCategories([]);
          break;
        }
        case 'absorbIntoContentCategory': {
          const {
            parentId,
            copy,
            tempCategory,
            moveRecords = [],
            copiedTopIds = [],
          } = operation.data;
          await api.post('/categories/revert-absorb-into-content-category', {
            parentId,
            copy,
            moveRecords,
            copiedTopIds,
            tempChildId: tempCategory?.id ?? undefined,
          });
          const refreshed = await fetchCategoryWithChildren(parentId);
          const sourceParentIds = Array.from(
            new Set(
              (moveRecords as { oldParentId?: string }[])
                .map((r) => r.oldParentId)
                .filter((id): id is string => !!id),
            ),
          );
          const refreshedSources = (
            await Promise.all(sourceParentIds.map((id) => fetchCategoryWithChildren(id)))
          ).filter((c): c is Category => c != null);
          if (refreshed) {
            setCategories((prev) => {
              let next = [...prev];
              for (const srcParent of refreshedSources) {
                next = updateCategoryInTree(next, srcParent.id, () => srcParent);
              }
              return updateCategoryInTree(next, parentId, () => refreshed);
            });
            setSelectedCategory(refreshed);
            setSelectedCategories([]);
          }
          break;
        }
        case 'addMatchingContent': {
          const {
            targetCategoryId,
            sourceCategoryId,
            move,
            transfers,
          } = operation.data as {
            targetCategoryId: string;
            sourceCategoryId: string;
            move: boolean;
            transfers: AddMatchingContentTransfer[];
          };
          const updatedTransfers: AddMatchingContentTransfer[] = [];
          for (const t of transfers ?? []) {
            try {
              if (move) {
                const params: Record<string, string> = {
                  contentId: t.contentId,
                  newParentId: sourceCategoryId,
                };
                // Restore the row to its ORIGINAL source id (a move otherwise re-creates it with a
                // fresh id). This keeps a prior in-place text edit's updateContent undo — which is
                // keyed by that source id — valid, so undo-move then undo-edit reverts cleanly.
                if (t.sourceContentId) {
                  params.newContentId = t.sourceContentId;
                }
                const response = await api.put('/content/move', null, {
                  params,
                  headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                });
                const moved = response.data as { id: string };
                updatedTransfers.push({
                  ...t,
                  contentId: moved.id,
                  sourceContentId: t.sourceContentId ?? t.contentId,
                  moved: true,
                });
              } else {
                await api.delete('/content', { params: { contentId: t.contentId } });
                updatedTransfers.push({ ...t, moved: false });
              }
            } catch (err) {
              console.error('Failed to undo add matching content row:', t.contentId, err);
              updatedTransfers.push(t);
            }
          }
          operation.data = {
            ...operation.data,
            transfers: updatedTransfers,
          };
          const restoredTarget = await fetchCategoryWithChildren(targetCategoryId);
          const restoredSource = await fetchCategoryWithChildren(sourceCategoryId);
          setCategories((prev) => {
            const replace = (cats: Category[]): Category[] =>
              cats.map((cat) => {
                if (cat.id === targetCategoryId) return restoredTarget;
                if (cat.id === sourceCategoryId) return restoredSource;
                if (cat.children?.length) return { ...cat, children: replace(cat.children) };
                return cat;
              });
            return replace(prev);
          });
          setSelectedCategory(restoredTarget);
          setSelectedCategories([]);
          break;
        }
        case 'collapseSubtreeToContentCategory': {
          const { branchCategoryId, snapshot } = operation.data;
          const branch = await fetchCategoryWithChildren(branchCategoryId);
          for (const row of branch.contents || []) {
            try {
              await api.delete('/content', { params: { contentId: row.id } });
            } catch (err) {
              console.error('Failed to delete content during collapse undo:', row.id, err);
            }
          }
          if (snapshot?.children?.length) {
            const branchAfterClear = await fetchCategoryWithChildren(branchCategoryId);
            await addSubTree(branchAfterClear, snapshot);
          }
          const restored = await fetchCategoryWithChildren(branchCategoryId);
          setCategories(prev => {
            const replace = (cats: Category[]): Category[] =>
              cats.map(cat => {
                if (cat.id === branchCategoryId) return restored;
                if (cat.children?.length) return { ...cat, children: replace(cat.children) };
                return cat;
              });
            return replace(prev);
          });
          setSelectedCategory(restored);
          setSelectedCategories([]);
          break;
        }
        case 'collapseSubtreeToContentCategoryBulk': {
          const { items } = operation.data as {
            items: { branchCategoryId: string; snapshot: Category }[];
          };
          for (const { branchCategoryId, snapshot } of items) {
            const branch = await fetchCategoryWithChildren(branchCategoryId);
            for (const row of branch.contents || []) {
              try {
                await api.delete('/content', { params: { contentId: row.id } });
              } catch (err) {
                console.error('Failed to delete content during bulk collapse undo:', row.id, err);
              }
            }
            if (snapshot?.children?.length) {
              const branchAfterClear = await fetchCategoryWithChildren(branchCategoryId);
              await addSubTree(branchAfterClear, snapshot);
            }
            const restored = await fetchCategoryWithChildren(branchCategoryId);
            setCategories(prev => {
              const replace = (cats: Category[]): Category[] =>
                cats.map(cat => {
                  if (cat.id === branchCategoryId) return restored;
                  if (cat.children?.length) return { ...cat, children: replace(cat.children) };
                  return cat;
                });
              return replace(prev);
            });
          }
          setSelectedCategories([]);
          break;
        }
        case 'flattenCategory': {
            const { parentId, category, index, flattenCategories, originalCategories } = operation.data;
             const response = await api.post('/categories', {
                  parentId,
                  name: category.name,
                  description: category.description
                });

             const createdCategory = response.data;

             await api.put(`/categories/moveto`,
                 {
                     categoryId: createdCategory.id,
                     moveTo: index
                 },
                 {
                     headers: {
                         'Content-Type': 'application/x-www-form-urlencoded'
                     }
                 }
             );

            // After moving categories, wait a bit and fetch again
            // Move categories sequentially instead of in parallel
            for (const cat of flattenCategories) {
                try {
                    await api.put(`/categories/move`, {
                        categoryId: cat.id,
                        newParentId: createdCategory.id
                    },
                    {
                        headers: {
                            'Content-Type': 'application/x-www-form-urlencoded'
                        }
                    });
                } catch (error) {
                    console.error(`Failed to move category ${cat.id}:`, error);
                    throw error;
                }
            }

            const restoredFlattened = await fetchCategoryWithChildren(createdCategory.id);
            if (!restoredFlattened) {
              throw new Error('Could not fetch restored category after undo flatten');
            }
            const refreshedGrandparent = await fetchCategoryWithChildren(parentId);

            setCategories(prev => {
              if (!refreshedGrandparent) {
                return prev;
              }
              const refreshedParent = refreshedGrandparent;
              return updateCategoryInTree(prev, parentId, () => refreshedParent);
            });

            setTimeout(() => {
              if (refreshedGrandparent) {
                setSelectedCategory(refreshedGrandparent);
                setSelectedCategories([]);
                setSelectedContents([]);
                setCurrentContent(null);
                setExpandedNodes(prevNodes => {
                  const next = prevNodes.includes(parentId) ? prevNodes : [...prevNodes, parentId];
                  return restoredFlattened.id && !next.includes(restoredFlattened.id)
                    ? [...next, restoredFlattened.id]
                    : next;
                });
              }
            }, 0);
            break;
        }
        case 'mergeCategories': {
            const { parentId, newCategory, mergedCategories } = operation.data;
            await deleteCategory(newCategory.id);
            setCategories(prev => {
                const newCategories = [...prev];
                const parent = findParentCategoryInTree(newCategories, newCategory.id);
                if (parent) {
                    parent.children = parent.children?.filter(child => child.id !== newCategory.id);
                }
                return newCategories;
            });

            // Create a map to store created categories with their indices
            const createdCategories = new Map<number, Category>();

            for (const {category, index} of mergedCategories) {
                const response = await api.post('/categories', {
                  parentId,
                  name: category.name,
                  description: category.description
                });

                const createdCategory = response.data;
                createdCategories.set(index, createdCategory);

                const sourceContents = category.contents ?? [];
                if (sourceContents.length > 0) {
                let newContents: Content[] = [];
                    for (const content of sourceContents) {
                    const response = await api.post('/content', {
                      ...buildContentCopyPayload(content, createdCategory.id),
                      skipDuplicateCheck: true,
                    });
                    const newContent = response.data;
                    newContents.push(newContent);
                    }

                    // Update the created category with its contents
                    createdCategory.contents = newContents;
                }
            }

            // Add all categories at once in the correct order
            setCategories(prev => {
                const newCategories = [...prev];
                const parent = findCategoryInTree(newCategories, parentId);
                if (parent) {
                    // Sort the indices to maintain order
                    const sortedIndices = Array.from(createdCategories.keys()).sort((a, b) => a - b);
                    for (const index of sortedIndices) {
                        const category = createdCategories.get(index);
                        if (category) {
                            if (parent.children) {
                                parent.children.splice(index, 0, category);
                            }
                        }
                    }
                }
                return newCategories;
              });

            // Update backend order — use recreated category IDs (merge deleted the originals).
            const sortedMoveIndices = Array.from(createdCategories.keys()).sort((a, b) => a - b);
            for (const idx of sortedMoveIndices) {
                const recreated = createdCategories.get(idx);
                if (!recreated) continue;
                await api.put(`/categories/moveto`,
                    {
                        categoryId: recreated.id,
                        moveTo: idx
                    },
                    {
                        headers: {
                            'Content-Type': 'application/x-www-form-urlencoded'
                        }
                    }
                );
            }

            const parentCategory = findCategoryInTree(categories, parentId);
            if (parentCategory) {
                setSelectedCategory(parentCategory);
            }
            setSelectedCategories([]);
            break;
        }
        case 'generateSubtree':
        case 'generateInitialCategories': {
            const generateSubtreeItems = Array.isArray(operation.data)
                ? operation.data
                : [operation.data];
            // Undo multi-category apply in reverse order so nested/sibling updates stay consistent.
            for (const item of [...generateSubtreeItems].reverse()) {
            const { originalCategory, originalContents, tempCategory, generatedTree, renamedCategoryId, originalCategoryName } = item;

            // Find the current category (either renamed or original)
            const currentCategoryId = renamedCategoryId || originalCategory.id;
            const currentCategory = findCategoryInTree(categories, currentCategoryId);

            if (!currentCategory) {
                throw new Error('Could not find current category for undo');
            }

            // Step 1: Delete all categories under the main category
            if (currentCategory.children && currentCategory.children.length > 0) {
                for (const child of currentCategory.children) {
                    try {
                        await deleteCategory(child.id);
                    } catch (err) {
                        console.error('Error deleting generated category:', err);
                    }
                }
            }

            // Step 1b: Remove any content rows left on the category itself (AI may have left stragglers)
            try {
                const mid = await fetchCategoryWithChildren(currentCategoryId);
                for (const row of mid.contents || []) {
                    try {
                        await api.delete('/content', { params: { contentId: row.id } });
                    } catch (err) {
                        console.error('Error deleting leftover content:', row.id, err);
                    }
                }
            } catch (err) {
                console.error('Error fetching category to clear content:', err);
            }

            // After organize + optional rename, the anchor category id may differ from the pre-apply id.
            // Renaming back on the server restores ids derived from the original name; use that id for
            // content restore and fetch — not renamedCategoryId (stale after rename-back).
            let categoryIdForRestore = currentCategoryId;

            // Step 2: Rename it back if it was renamed
            if (renamedCategoryId && originalCategoryName) {
                try {
                    // parentId is actually the category ID being renamed (see CategoryController PUT /categories)
                    const renameBackRes = await api.put('/categories', {
                        parentId: renamedCategoryId,
                        name: originalCategoryName,
                        description: originalCategory.description || ''
                    });
                    const back = renameBackRes.data as Category;
                    if (back?.id) {
                        categoryIdForRestore = back.id;
                    }
                } catch (err) {
                    console.error('Error renaming category back:', err);
                }
            }

            // Step 3: Restore original content under the anchor category's current id
            for (const content of originalContents) {
                try {
                    await api.post('/content', {
                        parentId: categoryIdForRestore,
                        text: content.text,
                        subcase: content.subcase,
                        id: content.id,
                        ...(content.richTextHtml != null ? { richTextHtml: content.richTextHtml } : {}),
                        ...(content.richTextDelta != null ? { richTextDelta: content.richTextDelta } : {}),
                        ...(typeof content.attachmentsJson === 'string' ? { attachmentsJson: content.attachmentsJson } : {}),
                    });
                } catch (err) {
                    console.error('Error restoring original content:', err);
                }
            }

            // Step 4: Refresh the categories from the backend to sync the frontend state
            try {
                    const updatedCategory = await fetchCategoryWithChildren(categoryIdForRestore);

                    setCategories(prevCategories => {
                        const updatedCategories = [...prevCategories];

                        if ( !updatedCategory.parentId ) return [updatedCategory];

                        // Category has a parent - find and update the parent
                        const parentCategory = findCategoryInTree(updatedCategories, updatedCategory.parentId);
                        if (parentCategory && parentCategory.children) {
                            // Find and replace the old category with the updated one
                            const childIndex = parentCategory.children.findIndex(
                                (child) => child.id === currentCategoryId || child.id === categoryIdForRestore
                            );
                            if (childIndex !== -1) {
                                parentCategory.children[childIndex] = updatedCategory;
                            }
                        }


                        return updatedCategories;
                    });

                    // Select the restored category
                    setSelectedCategory(updatedCategory);
                    setSelectedCategories([]);
                    setExpandedNodes((prev) => [...prev, categoryIdForRestore]);

                } catch (err) {
                    console.error('Error refreshing category:', err);
                }
            }

            break;
        }
        case 'proposeParentCategories': {
            const data = operation.data as {
              anchorCategoryId: string;
              anchorCategoryIdBeforeApply?: string;
              createdCategoryIds: string[];
              movesInApplyOrder: (
                | { originalId: string; previousParentId: string }
                | { categoryId: string; previousParentId: string }
              )[];
              finalIdsByOriginalId?: Record<string, string>;
              optionalAnchorRename?: {
                previousName: string;
                previousDescription: string;
                newName: string;
              };
            };
            const trimId = (s: string) => s.replace(/^\uFEFF/g, '').trim();
            /** Only when the anchor was renamed on apply: recorded previousParentId may still use pre-rename paths. */
            const rewriteUndoPreviousParentId = (
              prevId: string | undefined,
              anchorBefore: string | undefined,
              anchorAfter: string | undefined,
              didRenameAnchor: boolean,
            ): string => {
              if (typeof prevId !== 'string' || !prevId) return '';
              const p = trimId(prevId);
              if (!didRenameAnchor) return p;
              const oldA = anchorBefore ? trimId(anchorBefore) : '';
              const newA = anchorAfter ? trimId(anchorAfter) : '';
              if (!oldA || !newA || oldA === newA) return p;
              if (p === oldA) return newA;
              const prefix = `${oldA}:`;
              if (p.startsWith(prefix)) return `${newA}:${p.slice(prefix.length)}`;
              return p;
            };
            const moveHeaders = { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } };
            const curByOrig = new Map<string, string>();
            for (const [k, v] of Object.entries(data.finalIdsByOriginalId || {})) {
              if (typeof v === 'string' && v) curByOrig.set(trimId(k), trimId(v));
            }
            if (!data.finalIdsByOriginalId) {
              for (const m of data.movesInApplyOrder) {
                const legacy = m as { categoryId?: string; originalId?: string };
                const oid = legacy.originalId || legacy.categoryId;
                if (oid && legacy.categoryId) curByOrig.set(trimId(oid), trimId(legacy.categoryId));
              }
            }
            for (let i = data.movesInApplyOrder.length - 1; i >= 0; i--) {
              const m = data.movesInApplyOrder[i] as {
                originalId?: string;
                categoryId?: string;
                previousParentId: string;
              };
              const origKey = trimId((m.originalId || m.categoryId) as string);
              const cid = curByOrig.get(origKey) || (m.categoryId ? trimId(m.categoryId) : '');
              if (!cid) continue;
              const newParentId = trimId(
                rewriteUndoPreviousParentId(
                  m.previousParentId,
                  data.anchorCategoryIdBeforeApply,
                  data.anchorCategoryId,
                  !!data.optionalAnchorRename,
                ),
              );
              const res = await api.put(
                '/categories/move',
                new URLSearchParams({ categoryId: cid, newParentId }).toString(),
                moveHeaders,
              );
              const moved = res.data as Category;
              if (moved?.id) curByOrig.set(origKey, trimId(moved.id));
            }
            for (let i = data.createdCategoryIds.length - 1; i >= 0; i--) {
              await deleteCategory(data.createdCategoryIds[i]);
            }
            if (data.optionalAnchorRename) {
              const anchorIdForRename = data.anchorCategoryId;
              await api.put('/categories', {
                parentId: anchorIdForRename,
                name: data.optionalAnchorRename.previousName,
                description: data.optionalAnchorRename.previousDescription,
              });
            }
            const refreshId = data.anchorCategoryIdBeforeApply || data.anchorCategoryId;
            const updatedTree = await fetchCategoryWithChildren(refreshId);
            if (updatedTree) {
              setCategories((prev) => {
                const newCategories = [...prev];
                const parent = findParentCategoryInTree(newCategories, refreshId);
                if (!parent) return [updatedTree];
                if (parent.children) {
                  const index = parent.children.findIndex((cat) => cat.id === updatedTree.id);
                  if (index !== -1) {
                    parent.children[index] = updatedTree;
                  } else {
                    parent.children.push(updatedTree);
                  }
                }
                return newCategories;
              });
              setSelectedCategory(updatedTree);
              setSelectedCategories([]);
              setExpandedNodes((prev) => [...prev, refreshId]);
            }
            break;
        }
        case 'moveCategoryUp': {
          const { parentId, firstIndex, lastIndex } = operation.data
          await api.put('/categories/movechild',
            moveChildFormBody(parentId, firstIndex - 1, lastIndex - 1, 'down'),
            { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } },
          );

          setCategories(prev => applySiblingReorder(prev, parentId, (children) => {
            for (let i = lastIndex; i >= firstIndex; i--) {
              [children[i - 1], children[i]] = [children[i], children[i - 1]];
            }
          }));
          break;
        }
         case 'moveCategoryDown': {
              const { parentId, firstIndex, lastIndex } = operation.data

              await api.put('/categories/movechild',
                moveChildFormBody(parentId, firstIndex + 1, lastIndex + 1, 'up'),
                { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } },
              );

              setCategories(prev => applySiblingReorder(prev, parentId, (children) => {
                for (let i = firstIndex; i <= lastIndex; i++) {
                  [children[i], children[i + 1]] = [children[i + 1], children[i]];
                }
              }));
              break;
            }
        case 'moveCategoryTo' : {
            const { categoryId, fromIndex, toIndex } = operation.data
            await api.put('/categories/moveto',
                {
                    categoryId: categoryId,
                    moveTo: fromIndex
                },
                {
                  headers: {
                    'Content-Type': 'application/x-www-form-urlencoded'
                  }
                }
            );

              setCategories(prev => {
                 const newCategories = [...prev];
                 const parent = findParentCategoryInTree(newCategories, categoryId);
                 if (parent && parent.children) {
                   const child = parent.children[toIndex];
                   parent.children?.splice(toIndex, 1);
                   parent.children?.splice(fromIndex, 0, child);
                 }
                 return newCategories;
               });
               break;
        }
        case 'deleteContent': {
          const contents = Array.isArray(operation.data) ? operation.data : [operation.data];
          const undoContents: { content: Content; index: number; categoryId: string }[] = [];
          for (const { content, categoryId, index } of contents) {
            const response = await api.post('/content', {
              parentId: categoryId,
              text: content.text,
              richTextHtml: content.richTextHtml,
              richTextDelta: content.richTextDelta,
              subcase: content.subcase,
              id: content.id,
              ...(typeof content.attachmentsJson === 'string' ? { attachmentsJson: content.attachmentsJson } : {}),
            });
            if (!response.data) throw new Error('Failed to create content');
            const newContent = response.data;
            undoContents.push({ content: newContent, index, categoryId });
          }

          for (const { content, index } of undoContents) {
            await api.put(`/content/moveto`,
            {
                contentId: content.id,
                moveTo: index
            },
            {
              headers: {
                'Content-Type': 'application/x-www-form-urlencoded'
              }
            });
          }

          setCategories(prev => {
            const newCategories = [...prev];
            for (const { content, index, categoryId } of undoContents) {
              const parent = findCategoryInTree(newCategories, categoryId);
              if (!parent) continue;
              if (index >= 0) {
                parent.contents = parent.contents ?? [];
                parent.contents.splice(index, 0, content);
              } else {
                parent.contents = [...(parent.contents || []), content];
              }
            }
            return newCategories;
          });
          break;
        }
        case 'addContent': {
          const { content, categoryId } = operation.data;
          await api.delete('/content', { params: { contentId: content.id } });

          setCategories(prev => {
            const newCategories = [...prev];
            const parent = findCategoryInTree(newCategories, categoryId);
            if (parent) {
              parent.contents = parent.contents?.filter(child => child.id !== content.id);
            }
            return newCategories;
          });
          setSelectedCategory(selectedCategory);
          setSelectedContents(prev => prev.filter(c => c.id !== content.id));
          if (currentContent?.id === content.id) {
            setCurrentContent(null);
          }
          break;
        }
        case 'pasteContent':
        case 'dropContent': {
          const contents = Array.isArray(operation.data) ? operation.data : [operation.data];
          for (const { content, oldContentId, oldParentId, oldIndex } of contents) {
            await api.delete('/content', { params: { contentId: content.id } });
            setCategories(prev => {
              const newCategories = [...prev];
              const parent = findCategoryInTree(newCategories, content.parentId);
              if (parent) {
                parent.contents = parent.contents?.filter(child => child.id !== content.id);
              }
              return newCategories;
            });
          }

          if ( operation.isBatch ) {
             const undoContents: { content: Content; index: number }[] = [];
             for (const { content, oldContentId, oldParentId, oldIndex } of contents) {
                const response = await api.post('/content', {
                   parentId: oldParentId,
                   text: content.text,
                   richTextHtml: content.richTextHtml,
                   richTextDelta: content.richTextDelta,
                   subcase: content.subcase,
                   id: oldContentId,
                   ...(typeof content.attachmentsJson === 'string' ? { attachmentsJson: content.attachmentsJson } : {}),
                 });
                 if (!response.data) throw new Error('Failed to create content');
                 const newContent = response.data;
                 undoContents.push({content: newContent, index: oldIndex});
             }

             for (const { content, index } of undoContents) {
                await api.put(`/content/moveto`,
                {
                    contentId: content.id,
                    moveTo: index
                },
                {
                  headers: {
                    'Content-Type': 'application/x-www-form-urlencoded'
                  }
                });

                setCategories(prev => {
                    const newCategories = [...prev];
                    const parent = findCategoryInTree(newCategories, content.parentId);

                    if (parent) {
                      if (index >= 0) {
                        if (parent.contents) {
                          parent.contents.splice(index, 0, content);
                        }
                      } else {
                        if (parent.contents) {
                          parent.contents.push(content);
                        }
                      }
                    }

                    return newCategories;
                });

                const newParent = findCategoryInTree(categories, contents[0].oldParentId);
                setSelectedCategory(newParent);
             }
          }

          break;
        }
        case 'updateContent': {
          const { prevContent, categoryId } = operation.data;
          const response = await api.post('/content', buildContentSavePayload(prevContent, categoryId));
          const restored: Content = response.data ?? prevContent;
          await finalizeContentHistoryUpdate(restored, categoryId);
          break;
        }
        case 'moveContentUp': {
          const { parentId, firstIndex, lastIndex, direction } = operation.data
          await api.put('/categories/movechild', {
              categoryId: parentId,
              firstIndex: firstIndex - 1,
              lastIndex: lastIndex - 1,
              direction: "down"
            },
            {
              headers: {
                'Content-Type': 'application/x-www-form-urlencoded'
              }
            });

            {
              let reorderedParent: Category | null = null;
              setCategories(prev => {
                const newCategories = [...prev];
                const parent = findCategoryInTree(newCategories, parentId);
                if (parent && typeof parent.contents !== 'undefined') {
                  const newContents = [...parent.contents];
                  for (let i = lastIndex; i >= firstIndex; i--) {
                    [newContents[i - 1], newContents[i]] = [newContents[i], newContents[i - 1]];
                  }
                  reorderedParent = { ...parent, contents: newContents };
                  return updateCategoryInTree(newCategories, parentId, () => reorderedParent!);
                }
                return newCategories;
              });
              setTimeout(() => { if (reorderedParent) setSelectedCategory(reorderedParent); }, 0);
            }

            break;
        }
         case 'moveContentDown': {
          const { parentId, firstIndex, lastIndex, direction } = operation.data
          await api.put('/categories/movechild', {
              categoryId: parentId,
              firstIndex: firstIndex + 1,
              lastIndex: lastIndex + 1,
              direction: "up"
            },
            {
              headers: {
                'Content-Type': 'application/x-www-form-urlencoded'
              }
            });

            {
              let reorderedParent: Category | null = null;
              setCategories(prev => {
                const newCategories = [...prev];
                const parent = findCategoryInTree(newCategories, parentId);
                if (parent && typeof parent.contents !== 'undefined') {
                  const newContents = [...parent.contents];
                  for (let i = firstIndex; i <= lastIndex; i++) {
                    [newContents[i], newContents[i + 1]] = [newContents[i + 1], newContents[i]];
                  }
                  reorderedParent = { ...parent, contents: newContents };
                  return updateCategoryInTree(newCategories, parentId, () => reorderedParent!);
                }
                return newCategories;
              });
              setTimeout(() => { if (reorderedParent) setSelectedCategory(reorderedParent); }, 0);
            }
            break;
        }
        case 'dragCategoryTo':
        case 'dragContentTo': {
              const { parentId, firstIndex, lastIndex, toIndex } = operation.data

              await api.put('/categories/movechildto', {
                  categoryId: parentId,
                  firstIndex: toIndex > lastIndex ? toIndex - (lastIndex - firstIndex) : toIndex,
                  lastIndex: toIndex > lastIndex ? toIndex : toIndex + (lastIndex - firstIndex),
                  moveToIndex: toIndex > lastIndex ? firstIndex : lastIndex
                },
                {
                  headers: {
                    'Content-Type': 'application/x-www-form-urlencoded'
                  }
                });

                const updatedCategory = await fetchCategoryWithChildren(parentId);

                setCategories(prevCategories => {
                   const updatedCategories = [...prevCategories];
                   return updateCategoryInTree(updatedCategories, parentId, () => updatedCategory);
                 });

                 setTimeout(() => {
                      setSelectedCategory(updatedCategory);
                      //setSelectedCategories([]);
                      setExpandedNodes(prev => [...prev, updatedCategory.id]);
                    }, 0);
                break;
            }
            case 'renameSubcase': {
              const { oldName, newName } = operation.data;
              const docForRename = activeDocument;
              const response = await api.put('/documents/subcases/rename', {
                oldSubcase: newName,
                newSubcase: oldName
              }, { params: { documentId: docForRename?.id } });

              if (response.status === 200 && docForRename) {
                await reloadOpenDocumentCategoryTree(docForRename, true);
              }
              break;
            }
            case 'treeAssistantBatch': {
              const pack = operation.data as { undoSteps?: unknown[]; redoSteps?: unknown[] };
              const steps = pack?.undoSteps ?? [];
              for (const raw of steps) {
                await dispatchTreeAssistantHistoryStep(raw);
              }
              if (activeDocument) {
                await reloadOpenDocumentCategoryTree(activeDocument);
              }
              break;
            }
      }

      await refreshActiveDocumentSubcasesFromServer(subcaseRefreshCategoryId);
      });
      await logDocumentHistoryReplay('UNDO', operation);
    } catch (error) {
      console.error('Error undoing operation:', error);
      alert('Failed to undo operation');
    }
    setLoading(false);
  };

  const handleRedo = async () => {
    const operation = historyService.redo();
    if (!operation) return;

    setLoading(true);
    try {
      await withSuppressedDocumentActivity(async () => {
      const subcaseRefreshCategoryId = activeDocument?.categoryId;
      switch (operation.type) {
        case 'splitCategory': {
            const { firstCategory, secondCategory, parentId, tempCategory, movedContentsFirst, movedContentsSecond} = operation.data;

             let newChildren: Category[] = [];
             let newChildrenIds: string[] = [];
             let newMovedContentsToFirst: Content[] = [];
             let newMovedChildrenIdsToFirst: string[] = [];
             let newMovedContentsToSecond: Content[] = [];
             let newMovedChildrenIdsToSecond: string[] = [];

             // Recreate the temp category first (when one existed) so it stays the first sibling.
             const hasSeparateTemp = tempCategory.id !== firstCategory.id;
             let response;
             let newTempCategory: Category | null = null;
             if ( hasSeparateTemp ) {
                 response = await api.post('/categories', {
                   parentId,
                   name: tempCategory.name,
                   description: tempCategory.description
                 });
                 if (!response.data) throw new Error('Failed to create category');
                 const createdTempCategory = response.data as Category;
                 newTempCategory = createdTempCategory;
                 newChildren.push(createdTempCategory);
                 newChildrenIds.push(createdTempCategory.id);
             }

             response = await api.post('/categories', {
                parentId,
                name: firstCategory.name,
                description: firstCategory.description
              });
              if (!response.data) throw new Error('Failed to create category');
              const newFirstCategory = response.data;
              response = await api.post('/categories', {
                  parentId,
                  name: secondCategory.name,
                  description: secondCategory.description
              });
              if (!response.data) throw new Error('Failed to create category');
              const newSecondCategory = response.data;

             newChildren.push(newFirstCategory);
             newChildrenIds.push(newFirstCategory.id);
             newChildren.push(newSecondCategory);
             newChildrenIds.push(newSecondCategory.id);

             if (!newTempCategory) newTempCategory = newFirstCategory;


             // Move all rows for each destination in a single request (order preserved) rather than one
             // HTTP round-trip per row — redo of a large split is otherwise very slow.
             if (movedContentsFirst.length > 0) {
                 const response = await api.post('/content/bulk-move',
                   movedContentsFirst.map(({content, oldContentId}: {content: Content, oldContentId: string}) => ({
                     contentId: oldContentId,
                     newParentId: newTempCategory?.id,
                     newContentId: content.id,
                   })),
                 );
                 const moved = response.data as Content[];
                 for (const movedContent of moved) {
                     newMovedContentsToFirst.push(movedContent);
                     newMovedChildrenIdsToFirst.push(movedContent.id);
                 }
             }

             if (movedContentsSecond.length > 0) {
                 const response = await api.post('/content/bulk-move',
                   movedContentsSecond.map(({content, oldContentId}: {content: Content, oldContentId: string}) => ({
                     contentId: oldContentId,
                     newParentId: newSecondCategory?.id,
                     newContentId: content.id,
                   })),
                 );
                 const moved = response.data as Content[];
                 for (const movedContent of moved) {
                     newMovedContentsToSecond.push(movedContent);
                     newMovedChildrenIdsToSecond.push(movedContent.id);
                 }
             }



            const parent = findCategoryInTree(categories, parentId);
            if ( !parent )
                throw new Error('Failed to find category');

            const updatedParent = {
               ...parent,
               children: newChildren,
               contentCategory: false,
               contents: []
            };

             // Update the categories state with the new tree
             setCategories(prevCategories => {
               const updatedCategories = [...prevCategories];

               // Update the tree with the modified parent
               return updateCategoryInTree(updatedCategories, parentId, () => updatedParent);
             });

             const updateTemp: Category = {
               ...newTempCategory!,
               contents: newMovedContentsToFirst
             };

             const tempId = newTempCategory!.id;
             // Update the categories state with the new tree
             setCategories(prevCategories => {
               const updatedCategories = [...prevCategories];

               // Update the tree with the modified parent
               return updateCategoryInTree(updatedCategories, tempId, () => updateTemp);
             });

             const updateSecond = {
                ...newSecondCategory,
                contents: newMovedContentsToSecond
              };

              // Update the categories state with the new tree
              setCategories(prevCategories => {
                const updatedCategories = [...prevCategories];

                // Update the tree with the modified parent
                return updateCategoryInTree(updatedCategories, newSecondCategory?.id, () => updateSecond);
              });

             // Select the parent category and expand it
             setSelectedCategory(updatedParent);
             setSelectedCategories([]);
             setExpandedNodes(prev => [...prev, parentId]);
             break;
        }
        case 'deleteCategory': {
          const items = Array.isArray(operation.data) ? operation.data : [operation.data];
          const parentIds = new Set<string>();
          for (const { category, parentId } of items) {
            if (parentId) {
              parentIds.add(parentId);
            }
            await deleteCategory(category.id);
          }

          const refreshed = (
            await Promise.all(
              Array.from(parentIds).map((id) => fetchCategoryWithChildren(id)),
            )
          ).filter((c): c is Category => c != null);

          setCategories((prev) => {
            let next = [...prev];
            for (const parent of refreshed) {
              next = updateCategoryInTree(next, parent.id, () => parent);
            }
            return next;
          });

          const primaryParentId = items[0]?.parentId;
          const refreshedPrimary = primaryParentId
            ? refreshed.find((p) => p.id === primaryParentId) ?? null
            : null;
          setSelectedCategory(refreshedPrimary);
          setSelectedCategories([]);
          break;
        }
        case 'addCategory': {
          const { category, parentId } = operation.data;
          const response = await api.post('/categories', {
            parentId,
            name: category.name,
            description: category.description
          });
          if (!response.data) throw new Error('Failed to create category');
          const newCategory = stubBranchChild(response.data);
          setCategories(prev => {
            const newCategories = [...prev];
            const parent = findCategoryInTree(newCategories, parentId);
            if (parent) {
              parent.children = [...(parent.children || []), newCategory];
              parent.hasChildCategories = true;
            }
            return newCategories;
          });
          break;
        }
        case 'renameCategory': {
            const { category, oldId } = operation.data;
            const response = await api.put('/categories', {
                    parentId: oldId,
                    name: category.name,
                    description: category.description
                }
              );

            const renamedCategory = response.data;
            const updatedCategory = await fetchCategoryWithChildren(renamedCategory.parentId);

           setCategories(prevCategories => {
             const updatedCategories = [...prevCategories];
               const newCategories = updateCategoryInTree(updatedCategories, updatedCategory.id, () => updatedCategory);

               // Find and update the selected category within the same state update
               if (selectedCategory) {
                   const cat = findCategoryInTree(newCategories, selectedCategory.id);
                   if (cat) {
                       // Use setTimeout to ensure this happens after the state update
            setTimeout(() => {
                 setSelectedCategory(cat);
               }, 0);
            }
               }

               return newCategories;
           });
            break;
        }
        case 'pasteCategory':
        case 'dropCategory':  {
          const { category, oldCategoryId, oldParentId, newParentId, oldIndex, newIndex } = operation.data;
          var newCategory:Category = category;
          if ( oldIndex >= 0) {
           const response = await api.put('/categories/move', {
             categoryId: oldCategoryId,
             newParentId: newParentId
           },
            {
             headers: {
               'Content-Type': 'application/x-www-form-urlencoded'
             }
           });
           const moved = response.data;
           const updatedCategory = await fetchCategoryWithChildren(moved.id);
           newCategory = updatedCategory ?? moved;
           setCategories(prev => {
               const newCategories = [...prev];
               const parent = findCategoryInTree(newCategories, oldParentId);
               if (parent) {
                 parent.children = parent.children?.filter(child => child.id !== oldCategoryId);
               }
               return newCategories;
           });
          }
          else {
              const response = await api.post('/categories', {
                parentId: newParentId,
                name: category.name,
                description: category.description
              });
              if (!response.data) throw new Error('Failed to create category');
              newCategory = response.data;
              await addSubTree(newCategory, category);
          }

          setCategories(prev => {
            const updatedCategories = [...prev];
           const targetCategory = findCategoryInTree(updatedCategories, newParentId);
           if ( targetCategory) {
              // Update the target parent with the new category
             const newCategories = updateCategoryInTree(updatedCategories, targetCategory.id, (cat) => ({
               ...cat,
               children: [...(cat.children || []), newCategory],
             }));

             // If the target parent is currently selected, refresh it so the content pane shows the pasted category.
             if (selectedCategory?.id === newParentId) {
               const refreshedTargetParent = findCategoryInTree(newCategories, newParentId);
               if (refreshedTargetParent) {
                 setTimeout(() => {
                   setSelectedCategory(refreshedTargetParent);
                   setExpandedNodes(prev => (prev.includes(newParentId) ? prev : [...prev, newParentId]));
                 }, 0);
               }
             }

             return newCategories;
            }
             return [];
          });

          break;
        }
        case 'groupCategories': {
          const { originalParentId, newParent, groupedCategories } = operation.data
          const response = await api.post('/categories', {
              parentId: originalParentId,
              name: newParent.name,
              description: newParent.description
            });
          if (!response.data) throw new Error('Failed to create category');
          const newCategory = response.data;

          const movedCategories: Category[] = [];
          for ( const {name, index} of groupedCategories ) {
            const response = await api.put('/categories/move', {
                categoryId: originalParentId + ":" + name,
                newParentId: newCategory?.id
              },
              {
                headers: {
                  'Content-Type': 'application/x-www-form-urlencoded'
              }
             });
             const movedCategory = response.data;
             movedCategories.push(movedCategory);
          }

          const updatedTree = await fetchCategoryWithChildren(originalParentId);
            if (!updatedTree) {
              throw new Error('Could not fetch updated category structure');
            }

            // Update the state with the complete tree
            setCategories(prev => {
              const newCategories = [...prev];
              const parent = findParentCategoryInTree(newCategories, originalParentId);
              if ( !parent ) return [updatedTree];
              if (parent && parent.children) {
                const index = parent.children.findIndex(cat => cat.id === updatedTree.id);
                if (index !== -1) {
                  parent.children[index] = updatedTree;
                } else {
                  parent.children.push(updatedTree);
                }
              }
              return newCategories;
            });
          setSelectedCategory(newCategory);
          setSelectedCategories([]);
          setExpandedNodes(prev => [...prev, newCategory]);
          break;
        }
        case 'pasteCategoriesIntoBranch': {
          const {
            targetBranchId,
            copy,
            moveRecords = [],
            copiedSources = [],
          } = operation.data;
          if (copy) {
            let targetBranch = findCategoryInTree(categories, targetBranchId);
            if (!targetBranch) {
              targetBranch = await fetchCategoryWithChildren(targetBranchId);
            }
            const newCopiedTopIds: string[] = [];
            for (const snapshot of copiedSources) {
              const created = await cloneCopiedSubtreeUnderCategory(
                snapshot,
                targetBranch,
                { skipHistory: true },
              );
              newCopiedTopIds.push(created.id);
            }
            operation.data.copiedTopIds = newCopiedTopIds;
            const updatedTarget = await fetchCategoryWithChildren(targetBranchId);
            if (updatedTarget) {
              setCategories((prev) =>
                updateCategoryInTree(prev, targetBranchId, () => updatedTarget),
              );
              setSelectedCategory(updatedTarget);
            }
          } else {
            const updatedMoveRecords: {
              categoryId: string;
              oldParentId: string;
              oldIndex: number;
              categoryName?: string;
            }[] = [];
            for (const rec of moveRecords) {
              const response = await api.put(
                '/categories/move',
                {
                  categoryId: rec.categoryId,
                  newParentId: targetBranchId,
                },
                {
                  headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                  },
                },
              );
              const moved = response.data as Category;
              updatedMoveRecords.push({
                ...rec,
                categoryId: moved.id,
              });
            }
            operation.data.moveRecords = updatedMoveRecords;
            const refreshIds = new Set<string>([targetBranchId]);
            for (const rec of updatedMoveRecords) {
              refreshIds.add(rec.oldParentId);
            }
            const refreshed = (
              await Promise.all(
                Array.from(refreshIds).map((id) => fetchCategoryWithChildren(id)),
              )
            ).filter((c): c is Category => c != null);
            setCategories((prev) => {
              let next = [...prev];
              for (const node of refreshed) {
                next = updateCategoryInTree(next, node.id, () => node);
              }
              return next;
            });
            const targetRefreshed = refreshed.find((c) => c.id === targetBranchId);
            if (targetRefreshed) {
              setSelectedCategory(targetRefreshed);
            }
          }
          setSelectedCategories([]);
          setExpandedNodes((prev) =>
            prev.includes(targetBranchId) ? prev : [...prev, targetBranchId],
          );
          break;
        }
        case 'absorbIntoContentCategory': {
          const { parentId, copy, absorbedSourceIds = [] } = operation.data;
          if (absorbedSourceIds.length < 2) {
            break;
          }
          const redoResponse = await api.post('/categories/absorb-into-content-category', {
            contentCategoryId: parentId,
            sourceCategoryIds: absorbedSourceIds,
            copy,
          });
          const redoData = redoResponse.data as {
            tempCategory?: Category;
            copiedTopIds?: string[];
            moveRecords?: { categoryId: string; oldParentId: string; oldIndex: number; categoryName: string }[];
          };
          operation.data.tempCategory = redoData?.tempCategory ?? null;
          operation.data.copiedTopIds = redoData?.copiedTopIds ?? operation.data.copiedTopIds ?? [];
          if (!copy && redoData?.moveRecords) {
            operation.data.moveRecords = redoData.moveRecords;
          }
          const emptiedBranchParentIds =
            (redoResponse.data?.emptiedBranchParentIds as string[] | undefined) ?? [];
          const refreshed = await fetchCategoryWithChildren(parentId);
          const refreshedEmptiedParents = (
            await Promise.all(
              emptiedBranchParentIds.map((id) => fetchCategoryWithChildren(id)),
            )
          ).filter((c): c is Category => c != null);
          if (refreshed) {
            setCategories((prev) => {
              let next = [...prev];
              if (!copy) {
                for (const srcId of absorbedSourceIds) {
                  const sourceParent = findParentCategoryInTree(next, srcId);
                  if (sourceParent) {
                    next = updateCategoryInTree(next, sourceParent.id, (cat) => ({
                      ...cat,
                      children: (cat.children || []).filter((child) => child.id !== srcId),
                    }));
                  }
                }
                for (const emptiedParent of refreshedEmptiedParents) {
                  next = updateCategoryInTree(next, emptiedParent.id, () => emptiedParent);
                }
              }
              return updateCategoryInTree(next, parentId, () => refreshed);
            });
            setSelectedCategory(refreshed);
            setSelectedCategories([]);
            setExpandedNodes((prev) => (prev.includes(parentId) ? prev : [...prev, parentId]));
          }
          break;
        }
        case 'addMatchingContent': {
          const {
            targetCategoryId,
            sourceCategoryId,
            move,
            transfers,
          } = operation.data as {
            targetCategoryId: string;
            sourceCategoryId: string;
            move: boolean;
            transfers: AddMatchingContentTransfer[];
          };
          const replayed = move
            ? await replayAddMatchingContentMoves(transfers ?? [])
            : await replayAddMatchingContentTransfers(
                targetCategoryId,
                sourceCategoryId,
                false,
                transfers ?? [],
              );
          operation.data = {
            ...operation.data,
            transfers: replayed,
          };
          const updated = await fetchCategoryWithChildren(targetCategoryId);
          const updatedSource = await fetchCategoryWithChildren(sourceCategoryId);
          setCategories((prev) => {
            const replace = (cats: Category[]): Category[] =>
              cats.map((cat) => {
                if (cat.id === targetCategoryId) return updated;
                if (cat.id === sourceCategoryId) return updatedSource;
                if (cat.children?.length) return { ...cat, children: replace(cat.children) };
                return cat;
              });
            return replace(prev);
          });
          setSelectedCategory(updated);
          setSelectedCategories([]);
          break;
        }
        case 'collapseSubtreeToContentCategory': {
          const { branchCategoryId } = operation.data;
          await collapseSubtreeToContentCategory(branchCategoryId);
          const updated = await fetchCategoryWithChildren(branchCategoryId);
          setCategories(prev => {
            const replace = (cats: Category[]): Category[] =>
              cats.map(cat => {
                if (cat.id === branchCategoryId) return updated;
                if (cat.children?.length) return { ...cat, children: replace(cat.children) };
                return cat;
              });
            return replace(prev);
          });
          setSelectedCategory(updated);
          setSelectedCategories([]);
          break;
        }
        case 'collapseSubtreeToContentCategoryBulk': {
          const { items } = operation.data as { items: { branchCategoryId: string }[] };
          for (const { branchCategoryId } of items) {
            await collapseSubtreeToContentCategory(branchCategoryId);
            const updated = await fetchCategoryWithChildren(branchCategoryId);
            setCategories(prev => {
              const replace = (cats: Category[]): Category[] =>
                cats.map(cat => {
                  if (cat.id === branchCategoryId) return updated;
                  if (cat.children?.length) return { ...cat, children: replace(cat.children) };
                  return cat;
                });
              return replace(prev);
            });
          }
          setSelectedCategories([]);
          break;
        }
        case 'flattenCategory': {
            const { parentId, category, index, flattenCategories, originalCategories } = operation.data;
             var movedCategories: Category[] = [];
             for (const cat of originalCategories) {
                 try {
                     const response = await api.put(`/categories/move`, {
                         categoryId: cat.id,
                         newParentId: parentId
                     },
                     {
                         headers: {
                             'Content-Type': 'application/x-www-form-urlencoded'
                         }
                     });

                  const movedCat = response.data;
                  const updatedCat = await fetchCategoryWithChildren(movedCat.id);
                  movedCategories.push(updatedCat)

                 } catch (error) {
                     console.error(`Failed to move category ${cat.id}:`, error);
                     throw error;
                 }
             }

           await deleteCategory(category.id);

           const parentBeforeReorder = findCategoryInTree(categories, parentId);
           if (!parentBeforeReorder?.children) return;
          const firstIndex = parentBeforeReorder.children.length - 1;
          const lastIndex = firstIndex + flattenCategories.length - 1;
          await api.put(`/categories/movechildto`, {
                   categoryId: parentId,
                   firstIndex,
                   lastIndex,
                   moveToIndex: index
                 },
                 {
                   headers: {
                     'Content-Type': 'application/x-www-form-urlencoded'
                   }
                 });

             const refreshedGrandparent = await fetchCategoryWithChildren(parentId);

             setCategories(prev => {
               if (!refreshedGrandparent) {
                 return prev;
               }
               const refreshedParent = refreshedGrandparent;
               return updateCategoryInTree(prev, parentId, () => refreshedParent);
             });

             setTimeout(() => {
               if (refreshedGrandparent) {
                 setSelectedCategory(refreshedGrandparent);
                 setSelectedCategories([]);
                 setSelectedContents([]);
                 setCurrentContent(null);
               }
             }, 0);
            break;
        }
        case 'mergeCategories': {
            const { parentId, newCategory, mergedCategories } = operation.data;
            const categories = mergedCategories.map((cat: {category:Category, index:number}) => cat.category);
            for (const category of categories) {
              await deleteCategory(category.id);  // ← Sequential deletion
            }

             const response = await api.post('/categories', {
                parentId: parentId,
                name: newCategory.name,
                description: newCategory.description
              });

              const addedCategory = response.data;

            setCategories(prev => {
                 const newCategories = [...prev];
                 const parent = findCategoryInTree(newCategories, parentId);
                 if (parent && parent.children) {
                   const removeIds = categories.map((obj: Category) => obj.id);
                   parent.children = [
                     ...(parent.children || []).filter((child: Category) => !removeIds.includes(child.id)),
                     addedCategory
                   ];
                 }
                 return newCategories;
               });

               let aggregatedContent: Content[] = []
              for ( const category of categories) {
                   if ( !category.contents ) continue;
                   for (const content of category.contents) {
                       if ( !aggregatedContent.some(c => c.subcase === content.subcase && c.text === content.text) )
                           aggregatedContent.push(content);
                   }
               }

               let newContents: Content[] = [];
               for (const content of aggregatedContent) {
                  const response = await api.post(`/content`, {
                    ...buildContentCopyPayload(content, addedCategory.id),
                    skipDuplicateCheck: true,
                  });
                  newContents.push(response.data);
               }

                setCategories(prev => {
                  const newCategories = [...prev];
                  const parent = findCategoryInTree(newCategories, addedCategory.id);
                  if (parent) {
                    parent.contents = newContents;
                  }
                  return newCategories;
                });
                setSelectedCategory(addedCategory);
                setSelectedCategories([]);
            break;
        }
        case 'generateSubtree':
        case 'generateInitialCategories': {
            const generateSubtreeItems = Array.isArray(operation.data)
                ? operation.data
                : [operation.data];
            for (const item of generateSubtreeItems) {
            const { originalCategory, originalContents, tempCategory, generatedTree, renamedCategoryId, originalCategoryName } = item;

            const contentsToDelete = originalContents || [];
            if (contentsToDelete.length > 0) {
                for (const content of contentsToDelete) {
                    try {
                        await api.delete('/content', {
                            params: { contentId: content.id }
                        });
                    } catch (err) {
                        console.error('Failed to delete content:', content.id, err);
                    }
                }
            }

            let targetCategory = originalCategory
            // Step 1: Rename category to the generated name if it was renamed in the original operation
            if (renamedCategoryId && originalCategoryName) {
                try {
                    const response = await api.put('/categories', {
                        parentId: originalCategory.id, // This is actually the category ID being renamed
                        name: generatedTree.name, // Use the generated name from the tree
                        description: generatedTree.description || ''
                    });

                    targetCategory = response.data;
                } catch (err) {
                    console.error('✗ Failed to rename category to generated name:', err);

                }
            }

            // Step 2: Create temp category and move original content if it was created in the original operation
            if (tempCategory) {
                try {

                    // Create temp category
                    const tempCategoryRes = await api.post('/categories', {
                        parentId: targetCategory.id,
                        name: `Original Content - ${originalCategory.name}`,
                        description: 'Temporary category containing original content'
                    });
                    const newTempCategory = tempCategoryRes.data;

                    // Move all original content to temp category
                    const contentsToMove = originalContents || [];
                    for (const content of contentsToMove) {
                        try {
                            await api.put('/content/move', { params: { contentId: content.id,  newParentId: newTempCategory.id} });
                        } catch (err) {
                            console.error('✗ Failed to move content:', content.id, err);
                            // Continue with other content even if one fails
                        }
                    }
                } catch (err) {
                    console.error('✗ Failed to create temp category or move content:', err);
                }
            }

            // Use addSubTree to build the tree
            await addSubTree(targetCategory, generatedTree);

            // Step 4: Update frontend state
            try {
                const updatedCategory = await fetchCategoryWithChildren(targetCategory.id);

                setCategories(prevCategories => {
                    const updatedCategories = [...prevCategories];

                    if ( !updatedCategory.parentId ) return [updatedCategory];

                    // Category has a parent - find and update the parent
                    const parentCategory = findCategoryInTree(updatedCategories, targetCategory.parentId);
                    if (parentCategory && parentCategory.children) {
                        // Find and replace the old category with the updated one
                        const childIndex = parentCategory.children.findIndex(child => child.id === originalCategory.id);
                        if (childIndex !== -1) {
                            parentCategory.children[childIndex] = updatedCategory;
                        }
                    }


                    return updatedCategories;
                });

                // Select the restored category
                setSelectedCategory(updatedCategory);
                setSelectedCategories([]);
                setExpandedNodes((prev) => [...prev, updatedCategory.id]);

            } catch (err) {
                console.error('Error refreshing category:', err);
            }
            }

            break;
        }
        case 'proposeParentCategories': {
            const data = operation.data as {
              anchorCategoryId: string;
              anchorCategoryIdBeforeApply?: string;
              previewTreeRoot: { children?: unknown[] };
              createdCategoryIds: string[];
              movesInApplyOrder: (
                | { originalId: string; previousParentId: string }
                | { categoryId: string; previousParentId: string }
              )[];
              finalIdsByOriginalId?: Record<string, string>;
              optionalAnchorRename?: {
                previousName: string;
                previousDescription: string;
                newName: string;
              };
            };
            const tracking: ProposeParentApplyTracking = {
              createdCategoryIds: [],
              movesInApplyOrder: [],
            };
            const idMap = seedIdMapFromProposePreviewRoot(data.previewTreeRoot as { children?: any[] });
            const anchorStoredBefore = data.anchorCategoryIdBeforeApply || data.anchorCategoryId;
            let workAnchor = stripBomAndTrimCategoryId(anchorStoredBefore);
            const rChildren = (data.previewTreeRoot?.children || []) as unknown[];
            for (const sub of rChildren) {
              if (sub && typeof sub === 'object' && (sub as { type?: string }).type === 'category') {
                await applySuggestParentNode(workAnchor, sub as any, idMap, tracking);
              }
            }
            let anchorAfter = workAnchor;
            if (data.optionalAnchorRename) {
              const anchorBeforeUmbrellaRename = anchorAfter;
              const ren = await api.put('/categories', {
                parentId: anchorAfter,
                name:
                  normalizePresentation(data.optionalAnchorRename.newName.trim()) ||
                  data.optionalAnchorRename.newName,
                description: data.optionalAnchorRename.previousDescription,
              });
              anchorAfter = stripBomAndTrimCategoryId((ren.data as Category).id);
              const oldA = stripBomAndTrimCategoryId(anchorBeforeUmbrellaRename);
              const newA = stripBomAndTrimCategoryId(anchorAfter);
              if (oldA !== newA) {
                const rebasePath = (p: string) => {
                  const v = stripBomAndTrimCategoryId(p);
                  if (v === oldA) return newA;
                  if (v.startsWith(`${oldA}:`)) return newA + v.slice(oldA.length);
                  return v;
                };
                for (const [k, v] of Array.from(idMap.entries())) {
                  idMap.set(k, rebasePath(v));
                }
                for (let i = 0; i < tracking.createdCategoryIds.length; i++) {
                  tracking.createdCategoryIds[i] = rebasePath(tracking.createdCategoryIds[i]);
                }
              }
            }
            data.createdCategoryIds = tracking.createdCategoryIds;
            data.movesInApplyOrder = tracking.movesInApplyOrder;
            data.finalIdsByOriginalId = Object.fromEntries(idMap);
            data.anchorCategoryId = anchorAfter;
            const updatedTree = await fetchCategoryWithChildren(anchorAfter);
            if (updatedTree) {
              setCategories((prev) => {
                const newCategories = [...prev];
                const parent = findParentCategoryInTree(newCategories, anchorAfter);
                if (!parent) return [updatedTree];
                if (parent.children) {
                  const index = parent.children.findIndex((cat) => cat.id === updatedTree.id);
                  if (index !== -1) {
                    parent.children[index] = updatedTree;
                  } else {
                    parent.children.push(updatedTree);
                  }
                }
                return newCategories;
              });
              setSelectedCategory(updatedTree);
              setSelectedCategories([]);
              setExpandedNodes((prev) => [...prev, anchorAfter]);
            }
            break;
        }
        case 'moveCategoryUp': {
          const { parentId, firstIndex, lastIndex } = operation.data;
          await api.put('/categories/movechild',
            moveChildFormBody(parentId, firstIndex, lastIndex, 'up'),
            { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } },
          );

          setCategories(prev => applySiblingReorder(prev, parentId, (children) => {
            for (let i = firstIndex; i <= lastIndex; i++) {
              [children[i - 1], children[i]] = [children[i], children[i - 1]];
            }
          }));
          break;
        }
        case 'moveCategoryDown': {
          const { parentId, firstIndex, lastIndex } = operation.data
          await api.put('/categories/movechild',
            moveChildFormBody(parentId, firstIndex, lastIndex, 'down'),
            { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } },
          );

          setCategories(prev => applySiblingReorder(prev, parentId, (children) => {
            for (let i = lastIndex; i >= firstIndex; i--) {
              [children[i], children[i + 1]] = [children[i + 1], children[i]];
            }
          }));
          break;
        }
        case 'moveCategoryTo' : {
            const { categoryId, fromIndex, toIndex } = operation.data
            await api.put('/categories/moveto',
                {
                    categoryId: categoryId,
                    moveTo: toIndex
                },
                {
                  headers: {
                    'Content-Type': 'application/x-www-form-urlencoded'
                  }
                }
            );

              setCategories(prev => {
                 const newCategories = [...prev];
                 const parent = findParentCategoryInTree(newCategories, categoryId);
                 if (parent && parent.children) {
                   const child = parent.children[fromIndex];
                   parent.children?.splice(fromIndex, 1);
                   parent.children?.splice(toIndex, 0, child);
                 }
                 return newCategories;
               });
               break;
        }
        case 'deleteContent': {
          const contents = Array.isArray(operation.data) ? operation.data : [operation.data];
          const deletingIds = new Set(contents.map(({ content }) => content.id));
          for (const { content } of contents) {
            await api.delete('/content', { params: { contentId: content.id } });
          }
          setCategories(prev => {
            const newCategories = [...prev];
            for (const { content, categoryId } of contents) {
              const category = findCategoryInTree(newCategories, categoryId);
              if (category?.contents) {
                category.contents = category.contents.filter(c => c.id !== content.id);
              }
            }
            return newCategories;
          });
          setSelectedContents(prev => prev.filter(c => !deletingIds.has(c.id)));
          setCurrentContent(prev => {
            if (prev?.id && deletingIds.has(prev.id)) {
              queueMicrotask(() => {
                setIsEditingContent(false);
                setShowEditorWarning(false);
              });
              return null;
            }
            return prev;
          });
          break;
        }
        case 'addContent': {
          const { content, categoryId } = operation.data;
          const response = await api.post('/content', {
            parentId: categoryId,
            subcase: content.subcase,
            text: content.text,
            richTextHtml: content.richTextHtml,
            richTextDelta: content.richTextDelta,
            id: content.id,
            ...(typeof content.attachmentsJson === 'string' ? { attachmentsJson: content.attachmentsJson } : {}),
          });
          if (!response.data) throw new Error('Failed to create category');
          const newContent = response.data;
          setCategories(prev => {
            const newCategories = [...prev];
            const parent = findCategoryInTree(newCategories, categoryId);
            if (parent) {
             parent.contents = [...(parent.contents || []), newContent];
            }
            return newCategories;
          });
          setSelectedCategory(selectedCategory);
          // If this content was being shown in the editor (e.g. undo after create), keep UI consistent.
          // A delete undo should show the restored/created content only if it is selected.
          if (currentContent?.id === content.id) {
            setCurrentContent(newContent);
          }
          break;
        }
        case 'pasteContent':
        case 'dropContent': {
          const contents = Array.isArray(operation.data) ? operation.data : [operation.data];
          if ( operation.isBatch) {
            for (const { content, oldContentId, oldParentId, oldIndex } of contents) {
                await api.delete('/content', { params: { contentId: oldContentId } });

                setCategories(prev => {
                  const newCategories = [...prev];
                  const parent = findCategoryInTree(newCategories, oldParentId);
                  if (parent) {
                    parent.contents = parent.contents?.filter(child => child.id !== oldContentId);
                  }
                  return newCategories;
                });
              }
          }

         const newContents: Content[] = [];
         for (const { content, categoryId, index } of contents) {
            const response = await api.post('/content', {
               parentId: content.parentId,
               text: content.text,
               richTextHtml: content.richTextHtml,
               richTextDelta: content.richTextDelta,
               subcase: content.subcase,
               id: content.id,
               ...(typeof content.attachmentsJson === 'string' ? { attachmentsJson: content.attachmentsJson } : {}),
             });
             if (!response.data) throw new Error('Failed to create content');
             newContents.push(response.data);
         }

          setCategories(prev => {
              const newCategories = [...prev];
              const parent = findCategoryInTree(newCategories, contents[0].content.parentId);
              if (parent) {
                parent.contents = [...(parent.contents || []), ...newContents];
              }
              return newCategories;
            });

          const newParent = findCategoryInTree(categories, newContents[0].parentId);
          setSelectedCategory(newParent);
          break;
        }
        case 'updateContent': {
          const { content, categoryId } = operation.data;
          const response = await api.post('/content', buildContentSavePayload(content, categoryId));
          const restored: Content = response.data ?? content;
          await finalizeContentHistoryUpdate(restored, categoryId);
          break;
        }
        case 'moveContentUp': {
          const { parentId, firstIndex, lastIndex, direction } = operation.data
          await api.put('/categories/movechild', {
              categoryId: parentId,
              firstIndex: firstIndex,
              lastIndex: lastIndex,
              direction: "up"
            },
            {
              headers: {
                'Content-Type': 'application/x-www-form-urlencoded'
              }
            });

            {
              let reorderedParent: Category | null = null;
              setCategories(prev => {
                const newCategories = [...prev];
                const parent = findCategoryInTree(newCategories, parentId);
                if (parent && typeof parent.contents !== 'undefined') {
                  const newContents = [...parent.contents];
                  for (let i = firstIndex; i <= lastIndex; i++) {
                    [newContents[i - 1], newContents[i]] = [newContents[i], newContents[i - 1]];
                  }
                  reorderedParent = { ...parent, contents: newContents };
                  return updateCategoryInTree(newCategories, parentId, () => reorderedParent!);
                }
                return newCategories;
              });
              setTimeout(() => { if (reorderedParent) setSelectedCategory(reorderedParent); }, 0);
            }
            break;
        }
        case 'moveContentDown': {
          const { parentId, firstIndex, lastIndex, direction } = operation.data
          await api.put('/categories/movechild', {
              categoryId: parentId,
              firstIndex: firstIndex,
              lastIndex: lastIndex,
              direction: "down"
            },
            {
              headers: {
                'Content-Type': 'application/x-www-form-urlencoded'
              }
            });

            {
              let reorderedParent: Category | null = null;
              setCategories(prev => {
                const newCategories = [...prev];
                const parent = findCategoryInTree(newCategories, parentId);
                if (parent && typeof parent.contents !== 'undefined') {
                  const newContents = [...parent.contents];
                  for (let i = lastIndex; i >= firstIndex; i--) {
                    [newContents[i], newContents[i + 1]] = [newContents[i + 1], newContents[i]];
                  }
                  reorderedParent = { ...parent, contents: newContents };
                  return updateCategoryInTree(newCategories, parentId, () => reorderedParent!);
                }
                return newCategories;
              });
              setTimeout(() => { if (reorderedParent) setSelectedCategory(reorderedParent); }, 0);
            }
            break;
        }
        case 'dragCategoryTo':
        case 'dragContentTo': {
              const { parentId, firstIndex, lastIndex, toIndex } = operation.data
              await api.put('/categories/movechildto', {
                  categoryId: parentId,
                  firstIndex: firstIndex,
                  lastIndex: lastIndex,
                  moveToIndex: toIndex
                },
                {
                  headers: {
                    'Content-Type': 'application/x-www-form-urlencoded'
                  }
                });

                const updatedCategory = await fetchCategoryWithChildren(parentId);

                setCategories(prevCategories => {
                   const updatedCategories = [...prevCategories];
                   return updateCategoryInTree(updatedCategories, parentId, () => updatedCategory);
                 });

                 setTimeout(() => {
                      setSelectedCategory(updatedCategory);
                      //setSelectedCategories([]);
                      setExpandedNodes(prev => [...prev, updatedCategory.id]);
                    }, 0);
                break;
            }
            case 'renameSubcase': {
              const { oldName, newName } = operation.data;
              const docForRename = activeDocument;
              const response = await api.put('/documents/subcases/rename', {
                oldSubcase: oldName,
                newSubcase: newName
              }, { params: { documentId: docForRename?.id } });

              if (response.status === 200 && docForRename) {
                await reloadOpenDocumentCategoryTree(docForRename, true);
              }
              break;
            }
            case 'treeAssistantBatch': {
              const pack = operation.data as { undoSteps?: unknown[]; redoSteps?: unknown[] };
              const steps = pack?.redoSteps ?? [];
              const us = pack?.undoSteps;
              for (const raw of steps) {
                await dispatchTreeAssistantHistoryStep(raw, us ? { undoSteps: us } : undefined);
              }
              if (activeDocument) {
                await reloadOpenDocumentCategoryTree(activeDocument);
              }
              break;
            }
      }

      await refreshActiveDocumentSubcasesFromServer(subcaseRefreshCategoryId);
      });
      await logDocumentHistoryReplay('REDO', operation);
    } catch (error) {
      console.error('Error redoing operation:', error);
      alert('Failed to redo operation');
    }
    setLoading(false);
  };

  // Add keyboard shortcuts at the top level
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Avoid interfering with typing/paste inside form fields or rich text editors.
      // Quill uses contenteditable divs (not inputs), so we must explicitly exclude those too.
      const target = e.target as HTMLElement | null;
      const activeEl = document.activeElement as HTMLElement | null;
      const isTextInput =
        (e.target instanceof HTMLInputElement && e.target.type !== 'checkbox') ||
        e.target instanceof HTMLTextAreaElement;
      const isContentEditable =
        (!!target && (target.isContentEditable || !!target.closest('[contenteditable="true"]'))) ||
        (!!activeEl && (activeEl.isContentEditable || !!activeEl.closest('[contenteditable="true"]')));
      const isQuillEditor =
        (!!target && !!target.closest('.ql-editor')) ||
        (!!activeEl && !!activeEl.closest('.ql-editor'));

      if (isTextInput || isContentEditable || isQuillEditor) {
        return;
      }

      // Escape: cancel pending cut/copy clipboard (Finder-style), without pasting.
      if (e.key === 'Escape') {
        if (
          target?.closest('.MuiMenu-root, .MuiPopover-root, .MuiDialog-root') ||
          openDeleteCategoryDialog ||
          openDeleteContentDialog
        ) {
          return;
        }
        const hasClipboard =
          clipboardCategory != null ||
          clipboardCategories.length > 0 ||
          clipboardContents.length > 0;
        if (hasClipboard) {
          e.preventDefault();
          clearDocumentClipboard();
        }
        return;
      }

      // No open document tab — category/content shortcuts must not run on stale selection.
      if (tabs.length === 0 || currentTab == null) {
        return;
      }

      // Check for Ctrl/Cmd + key combinations
      if (e.ctrlKey || e.metaKey) {
        switch (e.key.toLowerCase()) {
          case 'z':
            if (currentTab && isDocumentCompareTabId(currentTab)) {
              // Compare edits are not on the document undo stack; avoid surprising undo.
              break;
            }
            if (e.shiftKey) {
              // Ctrl/Cmd + Shift + Z for redo
              e.preventDefault();
              if (canRedo) handleRedo();
            } else {
              // Ctrl/Cmd + Z for undo
              e.preventDefault();
              if (canUndo) handleUndo();
            }
            break;
          case 'x':
            // Ctrl/Cmd + X for cut
            e.preventDefault();
            handleCutCategory();
            break;
          case 'c':
            // Ctrl/Cmd + C for copy
            e.preventDefault();
            handleCopyCategory();
            break;
          case 'v':
            // Ctrl/Cmd + V for paste — use ref so click→paste in the same tick sees the new target.
            e.preventDefault();
            void (async () => {
              let target = selectedCategoryRef.current;
              if (!target) return;
              if (!target.contentCategory && !branchChildrenAreLoaded(target)) {
                const loaded = await loadBranchChildrenIntoTree(target.id);
                if (loaded) target = loaded;
              }
              if (canPaste(target)) {
                await handlePasteCategory(target);
              }
            })();
            break;
        }
      }

      // Move selection up/down among siblings (same as toolbar arrows).
      // Plain Arrow keys only when focus is outside the category structure so TreeView keeps ↑/↓ navigation.
      // ⌘/Ctrl+↑ / ⌘/Ctrl+↓ works even when the tree is focused.
      if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
        // Don't move siblings underneath an open modal dialog.
        if (document.querySelector('.MuiDialog-container')) return;
        const dir = e.key === 'ArrowUp' ? 'up' : 'down';
        const chordMove =
          (e.ctrlKey || e.metaKey) && !e.shiftKey && !e.altKey;
        const treeLike =
          !!activeEl?.closest('[role="tree"]') ||
          !!activeEl?.closest('.MuiTreeView-root');
        const plainMove =
          !e.ctrlKey && !e.metaKey && !e.altKey && !e.shiftKey && !treeLike;

        if (chordMove || plainMove) {
          if (!canMoveSelectedCategories(dir)) return;
          e.preventDefault();
          void handleMoveCategory(dir);
        }
      }

      // Delete: open the same confirmation dialog as the toolbar (not while typing in fields/editor).
      if (openDeleteCategoryDialog || openDeleteContentDialog) {
        return;
      }
      // Include Backspace: on many Mac keyboards the key labeled Delete sends Backspace.
      if (e.key === 'Delete' || e.key === 'Backspace') {
        if (!canDeleteSelectedCategories()) return;
        e.preventDefault();
        if (selectedContents.length > 0) {
          setOpenDeleteContentDialog(true);
        } else if (selectedCategories.length > 0 || selectedCategory) {
          setOpenDeleteCategoryDialog(true);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [
    canUndo,
    canRedo,
    selectedCategory,
    clipboardCategory,
    clipboardCategories,
    clipboardContents,
    isCutOperation,
    selectedContents,
    selectedCategories,
    openDeleteCategoryDialog,
    openDeleteContentDialog,
    clearDocumentClipboard,
    categories,
    tabs,
    currentTab,
  ]);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDraggingRef.current || !resizerRef.current) return;

      const container = resizerRef.current.parentElement;
      if (!container) return;

      const containerRect = container.getBoundingClientRect();
      const newPosition = ((e.clientX - containerRect.left) / containerRect.width) * 100;

      // Limit the split position between 20% and 80% of the container width
      const limitedPosition = Math.min(Math.max(newPosition, 20), 80);
      setSplitPosition(limitedPosition);
    };

    const handleMouseUp = () => {
      isDraggingRef.current = false;
      document.body.style.cursor = 'default';
      document.body.style.userSelect = 'auto';
    };

    if (isDraggingRef.current) {
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'default';
      document.body.style.userSelect = 'auto';
    };
  }, []);

  useEffect(() => {
    const handleRightPaneMouseMove = (e: MouseEvent) => {
      if (!isRightPaneDraggingRef.current || !rightPaneResizerRef.current) return;

      const container = rightPaneResizerRef.current.parentElement;
      if (!container) return;

      const containerRect = container.getBoundingClientRect();
      const newPosition = ((e.clientY - containerRect.top) / containerRect.height) * 100;

      // Limit the split position between 30% and 90% of the container height
      const limitedPosition = Math.min(Math.max(newPosition, 30), 90);
      setRightPaneSplitPosition(limitedPosition);
    };

    const handleRightPaneMouseUp = () => {
      isRightPaneDraggingRef.current = false;
      document.body.style.cursor = 'default';
      document.body.style.userSelect = 'auto';
    };

    if (isRightPaneDraggingRef.current) {
      document.body.style.cursor = 'row-resize';
      document.body.style.userSelect = 'none';
      document.addEventListener('mousemove', handleRightPaneMouseMove);
      document.addEventListener('mouseup', handleRightPaneMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleRightPaneMouseMove);
      document.removeEventListener('mouseup', handleRightPaneMouseUp);
      document.body.style.cursor = 'default';
      document.body.style.userSelect = 'auto';
    };
  }, []);

  const handleResizerMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    isDraggingRef.current = true;
  };

  const handleTabChange = async (event: React.SyntheticEvent, newValue: string) => {
    const performTabChange = async () => {
    // Save current document's expanded state, selection, and loaded tree before switching
    if (shouldSaveDocumentViewSnapshotOnTabLeave()) {
      saveDocumentViewSnapshot(currentDocument!.id);
    }

    setCurrentTab(newValue);
    if (isDocumentCompareTabId(newValue)) {
      await refreshCompareSession(newValue);
      return;
    }
    if (isSelectiveMergeTabId(newValue)) {
      await refreshSelectiveMergeSession(newValue);
      return;
    }
    const document = resolveTabDocument(newValue);
    if (document && !document.readOnly) {
      touchDocumentUsed(document.id);
    }
    if ( document && document.categoryId ) {
     try {
          setLoading(true);
          if (!document.readOnly) {
            await openDocument(document.name);
          }
          const hadOpenTab = tabs.some((tab) => tab.id === document.id);
          const needsRefresh = consumeDocumentTabRefreshFlag(document.id);
          const { tree, expandedIds, selectedCategory: restoredSelection } =
            await loadDocumentViewForTab(document, hadOpenTab, { skipCache: needsRefresh });
          if (!tree[0]) {
            throw new Error('Could not fetch root category');
          }

          setLoading(false);
          setCategories(tree);
          setSelectedCategory(restoredSelection);
            setSelectedCategories([]);
            setSelectedContents([]);
            setCurrentContent(null);
        setCurrentDocument(document);
      setActiveDocument(document);
        historyService.setCurrentDocument(document.id);
        setExpandedNodes(expandedIds);
         }
         catch (error) {
            console.error('Error switching tabs:', error);
            setLoading(false);
         }
      }
    };

    checkForUnsavedChanges(
      performTabChange,
      'The content editor is currently open with unsaved changes.'
    );
  };

  const resolveTabDocument = useCallback(
    (tabId: string): Document | undefined =>
      documents.find((doc) => doc.id === tabId) ?? readOnlyOpenDocuments[tabId],
    [documents, readOnlyOpenDocuments],
  );

  const handleCloseTab = (event: React.MouseEvent, tabId: string) => {
    event.stopPropagation();
    if (isDocumentCompareTabId(tabId)) {
      const closedTabIndex = tabs.findIndex((tab) => tab.id === tabId);
      const newTabs = tabs.filter((tab) => tab.id !== tabId);
      setTabs(newTabs);
      setCompareSessions((prev) => {
        const next = { ...prev };
        delete next[tabId];
        return next;
      });
      if (currentTab === tabId) {
        let newCurrentTabId: string | null = null;
        if (newTabs.length > 0) {
          newCurrentTabId =
            closedTabIndex > 0 ? newTabs[closedTabIndex - 1].id : newTabs[0].id;
        }
        setCurrentTab(newCurrentTabId);
        if (newCurrentTabId && !isDocumentCompareTabId(newCurrentTabId)) {
          void handleTabChange({} as React.SyntheticEvent, newCurrentTabId);
        } else if (!newCurrentTabId) {
          setCurrentDocument(undefined);
          setCategories([]);
          setSelectedCategory(null);
        }
      }
      return;
    }
    if (isSelectiveMergeTabId(tabId)) {
      const closedTabIndex = tabs.findIndex((tab) => tab.id === tabId);
      const newTabs = tabs.filter((tab) => tab.id !== tabId);
      setTabs(newTabs);
      setSelectiveMergeSessions((prev) => {
        const next = { ...prev };
        delete next[tabId];
        return next;
      });
      if (currentTab === tabId) {
        let newCurrentTabId: string | null = null;
        if (newTabs.length > 0) {
          newCurrentTabId =
            closedTabIndex > 0 ? newTabs[closedTabIndex - 1].id : newTabs[0].id;
        }
        setCurrentTab(newCurrentTabId);
        if (
          newCurrentTabId &&
          !isDocumentCompareTabId(newCurrentTabId) &&
          !isSelectiveMergeTabId(newCurrentTabId)
        ) {
          void handleTabChange({} as React.SyntheticEvent, newCurrentTabId);
        } else if (!newCurrentTabId) {
          setCurrentDocument(undefined);
          setCategories([]);
          setSelectedCategory(null);
        }
      }
      return;
    }
    const doc = resolveTabDocument(tabId);
    if (doc) {
      checkForUnsavedChanges(
        () => handleCloseDocument(doc),
        'The content editor is currently open with unsaved changes.',
      );
    }
  };

  const handleCloseDocument = (document: Document) => {
    const existingTab = tabs.find((tab) => tab.id === document.id);
    if (existingTab) {
      const closedTabIndex = tabs.findIndex(tab => tab.id === existingTab.id);
      const newTabs = tabs.filter(tab => tab.id !== existingTab.id);

      // Update the document's open state
      const doc = documents.find(doc => doc.id === existingTab.id);
      if (doc) {
        doc.hasOpenTab = false;
      }

      setTabs(newTabs);

      setDocumentExpandedStates((prev) => {
        const next = { ...prev };
        delete next[document.id];
        delete next[existingTab.id];
        return next;
      });
      setDocumentSelectedCategoryIds((prev) => {
        const next = { ...prev };
        delete next[document.id];
        delete next[existingTab.id];
        return next;
      });
      const nextCategoryTrees = { ...documentCategoryTreesRef.current };
      delete nextCategoryTrees[document.id];
      delete nextCategoryTrees[existingTab.id];
      documentCategoryTreesRef.current = nextCategoryTrees;

      if (document.readOnly) {
        setReadOnlyOpenDocuments((prev) => {
          const next = { ...prev };
          delete next[document.id];
          return next;
        });
      }

      // Only update currentTab if the closed tab was the current one
      if (tabs.length > 0 && currentTab === existingTab.id) {
        let newCurrentTabId: string | null = null;
        if (newTabs.length > 0) {
          if (closedTabIndex > 0) {
            newCurrentTabId = newTabs[closedTabIndex - 1].id;
          } else {
            newCurrentTabId = newTabs[0].id;
          }
        }
        setCurrentTab(newCurrentTabId);
        const newCurrentDocument = newCurrentTabId
          ? resolveTabDocument(newCurrentTabId)
          : undefined;
        if (newCurrentDocument && !newCurrentDocument.readOnly) {
          touchDocumentUsed(newCurrentDocument.id);
        }
        setCurrentDocument(newCurrentDocument);
        setActiveDocument(newCurrentDocument);
        if (!newCurrentDocument) {
          // Closing the last tab: previously returned here and never cleared selection/categories.
          historyService.setCurrentDocument(null);
          setCategories([]);
          setSelectedCategory(null);
          setSelectedCategories([]);
          setSelectedContents([]);
          setCurrentContent(null);
          setExpandedNodes([]);
          return;
        }
        // Switch the current document and load its tree directly (avoid going through
        // handleOpenDocument/checkForUnsavedChanges during tab-close flows).
        historyService.setCurrentDocument(newCurrentDocument.id);
        (async () => {
          try {
            if (!newCurrentDocument.categoryId) return;
            setLoading(true);
            const { tree, expandedIds, selectedCategory: restoredSelection } =
              await loadDocumentViewForTab(newCurrentDocument, true);
            if (!tree[0]) throw new Error('Could not fetch root category');
            setCategories(tree);
            setSelectedCategory(restoredSelection);
            setSelectedCategories([]);
            setSelectedContents([]);
            setCurrentContent(null);
            setExpandedNodes(expandedIds);
          } catch (err) {
            console.error('Error switching to next tab after close:', err);
          } finally {
            setLoading(false);
          }
        })();
      }

      setSelectedCategory(null);
      setSelectedCategories([]);
      setSelectedContents([]);
    }
  };

  const dismissStaleReadAccessGrant = (grantId: number) => {
    removeReadAccessGrantFromListRef.current(grantId);
    knownReadAccessGrantIdsRef.current.delete(grantId);
    const tabId = readOnlyDocumentTabId(grantId);
    const openDoc = readOnlyOpenDocuments[tabId];
    if (openDoc) {
      handleCloseDocument(openDoc);
    }
  };
  dismissStaleReadAccessGrantRef.current = dismissStaleReadAccessGrant;

  const handleNewDocument = async (name: string, open: boolean):Promise<Document> => {
      // When a caller already owns the global logo spinner (e.g. New document from files apply),
      // do not clear it when the empty document is created mid-flow.
      const alreadyLoading = loadingRef.current;
      try {
          if (!alreadyLoading) {
            setLoadingSync(true);
          }
          const newDocument = await createDocument(name);
          // Tab creation / activation is centralized in handleOpenDocument to avoid duplicate tabs
          // and inconsistent tab ids (name vs id) across different open flows.
          if (open) {
            await handleOpenDocument(newDocument);
          }
          return newDocument;
      } catch (error) {
          console.error('Error creating document:', error);
          alert(
            formatApiErrorMessage(
              (error as { response?: { data?: unknown } })?.response?.data,
              'Failed to create document',
            ),
          );
          throw error;
      } finally {
          if (!alreadyLoading) {
            setLoadingSync(false);
          }
      }
  };
  const dismissMergeCompareTabsReferencingDocument = (
    deletedDocumentId: string,
    deletedDocumentName: string,
    deletedDisplayName: string,
  ) => {
    const compareSessionsSnapshot = compareSessionsRef.current;
    const selectiveMergeSessionsSnapshot = selectiveMergeSessionsRef.current;
    const tabsSnapshot = tabsRef.current;
    const currentTabSnapshot = currentTabRef.current;

    const tabIdsToClose = new Set<string>();
    for (const [tabId, session] of Object.entries(compareSessionsSnapshot)) {
      if (compareSessionUsesDeletedDocument(session, deletedDocumentId, deletedDocumentName)) {
        tabIdsToClose.add(tabId);
      }
    }
    for (const [tabId, session] of Object.entries(selectiveMergeSessionsSnapshot)) {
      if (
        selectiveMergeSessionUsesDeletedDocument(
          session,
          deletedDocumentId,
          deletedDocumentName,
        )
      ) {
        tabIdsToClose.add(tabId);
      }
    }
    if (tabIdsToClose.size === 0) {
      for (const tab of tabsSnapshot) {
        if (!isDocumentCompareTabId(tab.id) && !isSelectiveMergeTabId(tab.id)) {
          continue;
        }
        const label = tab.label ?? '';
        if (
          label.includes(deletedDocumentName) ||
          label.includes(deletedDocumentId) ||
          (deletedDisplayName.trim() && label.includes(deletedDisplayName.trim()))
        ) {
          tabIdsToClose.add(tab.id);
        }
      }
    }
    if (tabIdsToClose.size === 0) {
      return;
    }

    const closedLabels = tabsSnapshot
      .filter((tab) => tabIdsToClose.has(tab.id))
      .map((tab) => tab.label);
    const closedTabIndex = tabsSnapshot.findIndex((tab) => tab.id === currentTabSnapshot);
    const newTabs = tabsSnapshot.filter((tab) => !tabIdsToClose.has(tab.id));

    setTabs(newTabs);
    setCompareSessions((prev) => {
      const next = { ...prev };
      Array.from(tabIdsToClose).forEach((tabId) => {
        delete next[tabId];
      });
      return next;
    });
    setSelectiveMergeSessions((prev) => {
      const next = { ...prev };
      Array.from(tabIdsToClose).forEach((tabId) => {
        delete next[tabId];
      });
      return next;
    });

    if (currentTabSnapshot && tabIdsToClose.has(currentTabSnapshot)) {
      let newCurrentTabId: string | null = null;
      if (newTabs.length > 0) {
        newCurrentTabId =
          closedTabIndex > 0
            ? newTabs[Math.min(closedTabIndex - 1, newTabs.length - 1)]?.id ?? newTabs[0].id
            : newTabs[0].id;
      }
      setCurrentTab(newCurrentTabId);
      if (
        newCurrentTabId &&
        !isDocumentCompareTabId(newCurrentTabId) &&
        !isSelectiveMergeTabId(newCurrentTabId)
      ) {
        void handleTabChange({} as React.SyntheticEvent, newCurrentTabId);
      } else if (!newCurrentTabId) {
        setCurrentDocument(undefined);
        setCategories([]);
        setSelectedCategory(null);
      }
    }

    const docLabel = deletedDisplayName.trim() || deletedDocumentName;
    alert(
      `Closed compare or merge tab(s) because "${docLabel}" was deleted:\n${closedLabels.join('\n')}`,
    );
  };

  const handleDeleteDocument = async (documentId: string) => {
    try {
      const doc = documents.find(doc => doc.id === documentId)
      setLoading(true);
      await deleteDocument(documentId);
      removeDocumentLastUsed(documentId);
      setDocumentLastUsedAt((prev) => {
        if (!(documentId in prev)) return prev;
        const next = { ...prev };
        delete next[documentId];
        return next;
      });

      // Clear the document's history
      historyService.clearDocumentHistory(documentId);

      // If the deleted document was the current one, set history to null
      if (activeDocument?.id === documentId) {
        historyService.setCurrentDocument(null);
      }

      if ( currentDocument?.id == documentId ) {
          setCurrentDocument(undefined);
      }
      dismissMergeCompareTabsReferencingDocument(
        documentId,
        doc?.name ?? documentId,
        doc?.label || doc?.name || documentId,
      );
      setLoading(false);
      if (doc)
        handleCloseDocument(doc);
      // Remove the document from tabs list
    } catch (error) {
      setLoading(false);
      console.error('Error deleting document:', error);
      alert('Failed to delete document');
    }
  };

  const handleRenameDocument = async (documentId: string, newName: string) => {
    try {
      setLoading(true);
      
      // Preserve the current document's subcases before renaming
      const currentDoc = currentDocument;
      const existingSubcases = currentDoc?.subcases || [];
      
      const updatedDoc = await updateDocument(documentId, { name: newName });
      migrateDocumentLastUsed(documentId, updatedDoc.name);
      setDocumentLastUsedAt((prev) => {
        if (documentId === updatedDoc.name) return prev;
        const ts = prev[documentId];
        if (ts == null) return prev;
        const next = { ...prev };
        next[updatedDoc.name] = Math.max(next[updatedDoc.name] ?? 0, ts);
        delete next[documentId];
        return next;
      });
      setLoading(false);

      const renamedDoc = {
        ...updatedDoc,
        subcases: existingSubcases,
      };

      // Functional update so tab ids stay in sync; avoid handleOpenDocument here — it reads stale `tabs`
      // from its closure right after rename and can create a duplicate tab.
      setTabs(prev =>
        prev.map(tab =>
          tab.id === documentId ? { ...tab, id: renamedDoc.id, label: renamedDoc.name } : tab
        )
      );

      const renamedDocHadOpenTab = tabs.some(tab => tab.id === documentId);

      // Only load tree / switch tab when the renamed document was actually open.
      // Renaming a closed document (e.g. selected in sidebar only) must not open it.
      if (renamedDocHadOpenTab && currentDoc?.id === documentId) {
        checkForUnsavedChanges(async () => {
          const savedExpandedState =
            documentExpandedStates[documentId] ?? documentExpandedStates[renamedDoc.id];
          const savedCategoryId =
            documentSelectedCategoryIds[documentId] ?? documentSelectedCategoryIds[renamedDoc.id];
          setDocumentExpandedStates(prev => {
            const next = { ...prev };
            if (documentId !== renamedDoc.id && next[documentId]) {
              next[renamedDoc.id] = next[documentId];
              delete next[documentId];
            }
            return next;
          });
          setDocumentSelectedCategoryIds(prev => {
            const next = { ...prev };
            if (documentId !== renamedDoc.id && next[documentId]) {
              next[renamedDoc.id] = next[documentId];
              delete next[documentId];
            }
            return next;
          });
          if (documentId !== renamedDoc.id && documentCategoryTreesRef.current[documentId]) {
            const nextCategoryTrees = { ...documentCategoryTreesRef.current };
            nextCategoryTrees[renamedDoc.id] = nextCategoryTrees[documentId];
            delete nextCategoryTrees[documentId];
            documentCategoryTreesRef.current = nextCategoryTrees;
          }

          setCurrentDocument(renamedDoc);
          setCurrentTab(renamedDoc.id);
          setActiveDocument(renamedDoc);
          historyService.setCurrentDocument(renamedDoc.id);

          setLoading(true);
          try {
            const { tree, expandedIds } = await loadLazyDocumentTree(
              renamedDoc.categoryId!,
              savedExpandedState,
            );
            const rootCategory = tree[0];
            if (!rootCategory) {
              throw new Error('Could not fetch root category');
            }
            const restored = await restoreSelectedCategoryInTree(
              tree,
              expandedIds,
              savedCategoryId,
            );
            setCategories(restored.tree);
            setSelectedCategory(restored.selectedCategory ?? rootCategory);
            setSelectedCategories([]);
            setSelectedContents([]);
            setCurrentContent(null);
            setExpandedNodes(restored.expandedIds);
          } finally {
            setLoading(false);
          }
        }, 'The content editor is currently open with unsaved changes.');
      } else if (!renamedDocHadOpenTab && currentDoc?.id === documentId) {
        setCurrentDocument(renamedDoc);
      }
    } catch (error) {
      setLoading(false);
      console.error('Error renaming document:', error);
      alert('Failed to rename document');
    }
  };

  const normalize = (str: string) =>
    str.replace(/[\u200B-\u200D\uFEFF]/g, '').trim();

  const handleLoadFromFile = async (fileName: string, file?: File) => {
    try {
      // Find the file input element
      const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement;
      if (!fileInput || (!file && (!fileInput.files || fileInput.files.length === 0))) {
        throw new Error('No file selected');
      }

      const fileToUpload = file || fileInput.files![0];

      // Create FormData and append the file
      const formData = new FormData();
      formData.append('file', fileToUpload);
      formData.append('customName', fileName);

      // Send the file to the server
      setLoading(true);
      const response = await api.post('/documents/load-from-file', formData);
      setLoading(false);
      // Open the newly loaded document
      const newDocument = response.data;
       // Fetch subcases for the loaded document before opening it
        if (newDocument.categoryId) {
          const response = await api.get('/documents/subcases', { params: { documentId: newDocument.categoryId} });
          newDocument.subcases = response.data;
        }
      if (newDocument) {
        newDocument.id = normalize(newDocument.name);
        newDocument.name = normalize(newDocument.name);
        newDocument.label = normalize(newDocument.name);
        addDocument(newDocument);

        // Let handleOpenDocument create/activate the tab. Creating a tab here and then calling
        // handleOpenDocument can race with state updates and result in duplicate tabs.
        await handleOpenDocument(newDocument);
      }
    } catch (error) {
      setLoading(false);
      console.error('Error loading document from file:', error);
      alert('Failed to import .nbr archive');
    }
  };

  const handleExportDocument = async (documentId: string) => {
    if (!isFeatureEnabled(FEATURE_FLAG_KEYS.EXPORT_TO_FILE)) {
      alert('Export .nbr archive is turned off for this installation.');
      return;
    }
    try {
      setLoading(true);
      
      // Get the document to get its name
      const doc = documents.find(doc => doc.id === documentId);
      if (!doc) {
        throw new Error('Document not found');
      }

      // Call the export endpoint
      const response = await api.get('/documents/export', {
        params: { documentId },
        responseType: 'blob'
      });

      // Create a download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${doc.name}.nbr`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error('Error exporting document:', error);
      alert('Failed to export document');
    }
  };

  const getActiveDocument = (): Document | undefined => {
    if (!currentTab) return undefined;
    return resolveTabDocument(currentTab);
  };

  const handleDuplicateDocument = async (
    documentId: string,
    newName: string,
    subcases: string[],
    sourceReadAccessGrantId: number | null = null,
  ) => {
    try {
      setLoading(true);
      const newDocument = await duplicateDocument(
        documentId,
        newName,
        subcases,
        sourceReadAccessGrantId,
      );

      // Fetch subcases for the new document before opening it
        const response = await api.get('/documents/subcases', { params: { documentId: newDocument.categoryId } });
        newDocument.subcases = response.data;

      setLoading(false);
      // Tab creation / activation is centralized in handleOpenDocument to avoid duplicate tabs.
      await handleOpenDocument(newDocument);
    } catch (error) {
      setLoading(false);
      console.error('Error duplicating document:', error);
      alert('Failed to duplicate document');
    }
  };

  const addNewTab = (documentName: string, document?: Document) => {
    try {
      // If a document is provided, match by tab id (read-only sessions use distinct ids/labels)
      if (document) {
        const tabLabel = document.label ?? document.name;
        const existingById = tabs.find((tab) => tab.id === document.id);
        if (existingById) {
          setCurrentTab(existingById.id);
          return;
        }

        const newTab: Tab = {
          id: document.id,
          label: tabLabel,
          content: (
            <Typography>
              Content for {documentName}
            </Typography>
          ),
        };
        setTabs(prevTabs => [...prevTabs, newTab]);
        setCurrentTab(document.id);
        setCurrentDocument(document);
        setActiveDocument(document);
        document.hasOpenTab = true;
        return;
      }

      // Check if document already exists in tabs (owned docs: label === name)
      const existingTab = tabs.find((tab) => tab.label === documentName);
      if (existingTab) {
        setCurrentTab(existingTab.id);
        return;
      }

      // Otherwise, check if document exists in documents but not in tabs
      const existingDocument = documents.find(doc => doc.id === documentName || doc.name === documentName);
      if (existingDocument) {
        const newTab: Tab = {
          id: existingDocument.id,
          label: existingDocument.name,
          content: (
            <Typography>
              Content for {documentName}
            </Typography>
          ),
        };
        setTabs(prevTabs => [...prevTabs, newTab]);
        setCurrentTab(existingDocument.id);
        setCurrentDocument(existingDocument);
        setActiveDocument(existingDocument);
        existingDocument.hasOpenTab = true;
        return;
      }
    } catch (error) {
      console.error('Error adding new tab:', error);
    }
  };



  const handleSelectDocument = async (document?: Document) => {
   const performDocumentSelect = async () => {
    if (!document) {
      setCurrentDocument(undefined);
      return;
    }
    if (!document || !document.categoryId) {
      throw new Error('Document not found');
    }

    const existingTab = tabs.find(tab => tab.id === document.id);

    // If the document is already open in a tab, switch to it
    if (existingTab && existingTab.id !== currentTab) {
      if (currentDocument) {
        saveDocumentViewSnapshot(currentDocument.id);
      }
      touchDocumentUsed(document.id);
      try {
        setLoading(true);
        // Set the current document in the history service
        historyService.setCurrentDocument(document.id);

        await openDocument(document.name);
        const { tree, expandedIds, selectedCategory: restoredSelection } =
          await loadDocumentViewForTab(document, true);
        if (!tree[0]) {
          throw new Error('Could not fetch root category');
        }

        setLoading(false);
        setCategories(tree);
        setSelectedCategory(restoredSelection);
          setSelectedCategories([]);
          setSelectedContents([]);
          setCurrentContent(null);
          setCurrentTab(existingTab.id);
          setCurrentDocument(document);
      setActiveDocument(document);
        setExpandedNodes(expandedIds);
      } catch (error) {
          console.error('Error selecting document:', error);
        setLoading(false);
        }
    } else {
      // For closed documents, just set the current document without opening it
      // This allows menu operations to work on selected closed documents
      setCurrentDocument(document);
    }
  };

    checkForUnsavedChanges(
      performDocumentSelect,
      'The content editor is currently open with unsaved changes.'
    );
  };

  /**
   * Awaitable open used by AI apply flows that own the global spinner until the tree is loaded.
   * Unlike handleOpenDocument, this does not go through checkForUnsavedChanges and the Promise
   * only settles after loadDocumentViewForTab + UI state updates complete.
   */
  const openDocumentFully = async (
    document: Document,
    options?: { forceExpandRoot?: boolean }
  ): Promise<void> => {
    // When a caller already owns the global logo spinner, leave it on until that caller finishes.
    const alreadyLoading = loadingRef.current;
    try {
      if (!document || !document.categoryId) {
        console.error('Document not found:', document.name);
        return;
      }

      touchDocumentUsed(document.id);

      // Same as clicking a tab: remember expansion, selection, and loaded tree for the document we are leaving
      if (shouldSaveDocumentViewSnapshotOnTabLeave()) {
        saveDocumentViewSnapshot(currentDocument!.id);
      }

      // Check if the document is already open in a tab before creating a new one
      const existingTab = tabs.find(tab => tab.id === document.id || tab.label === document.name);
      const hadOpenTab = !!existingTab;

      if (!existingTab) {
        // Pass document so a tab is created even if context `documents` has not updated yet (e.g. just created).
        addNewTab(document.name, document);
        document.hasOpenTab = true;
      }

      if (!alreadyLoading) {
        setLoadingSync(true);
      }
      await openDocument(document.name);
      const subcases = await refreshDocumentSubcases(document.categoryId);
      const documentWithSubcases = { ...document, subcases };

      const needsRefresh = consumeDocumentTabRefreshFlag(document.id);
      const { tree, expandedIds, selectedCategory: restoredSelection } =
        await loadDocumentViewForTab(
          documentWithSubcases,
          hadOpenTab && !options?.forceExpandRoot,
          { skipCache: needsRefresh || !!options?.forceExpandRoot },
        );
      if (!tree[0]) {
        throw new Error('Could not fetch root category');
      }

      if (!alreadyLoading) {
        setLoadingSync(false);
      }

      // Set the document content in the UI
      setCategories(tree);
      persistDocumentCategoryTree(document.id, tree);
      setSelectedCategory(restoredSelection);
      setSelectedCategories([]);
      setSelectedContents([]);
      setCurrentContent(null);
      setCurrentTab(document.id);
      setCurrentDocument(documentWithSubcases);
      setActiveDocument(documentWithSubcases);
      historyService.setCurrentDocument(document.id);
      setExpandedNodes(expandedIds);
    } catch (error) {
      console.error('Error opening document:', error);
      if (!alreadyLoading) {
        setLoadingSync(false);
      }
      // 401 is handled by the api interceptor (redirect to /login). Do not alert over it.
      if (axios.isAxiosError(error) && error.response?.status === 401) {
        return;
      }
      alert('Failed to open document');
      throw error;
    }
  };

  const handleOpenDocument = async (
    document: Document,
    options?: { forceExpandRoot?: boolean }
  ) => {
    checkForUnsavedChanges(
      () => {
        void openDocumentFully(document, options);
      },
      'The content editor is currently open with unsaved changes.'
    );
  };

  const handleOpenReadOnlyDocument = (grant: DocumentReadAccessGrant): Promise<void> =>
    new Promise((resolve) => {
      const performOpenReadOnly = async () => {
      try {
        setLoading(true);
        const response = await api.post<OpenReadOnlyDocumentResponse>(
          `/documents/read-access/${grant.id}/open`,
        );
        const data = response.data;
        const tabId = readOnlyDocumentTabId(data.grantId);
        const readOnlyDoc: Document = {
          id: tabId,
          name: data.documentName,
          label: readOnlyDocumentLabel(data.documentName, data.ownerUsername),
          categoryId: data.categoryId,
          subcases: [],
          readOnly: true,
          readOnlyGrantId: data.grantId,
          ownerUsername: data.ownerUsername,
          hasOpenTab: true,
        };

        setReadOnlyOpenDocuments((prev) => ({ ...prev, [tabId]: readOnlyDoc }));

        if (currentDocument) {
          saveDocumentViewSnapshot(currentDocument.id);
        }

        const existingTab = tabs.find((tab) => tab.id === tabId);
        const hadOpenTab = !!existingTab;
        if (!existingTab) {
          addNewTab(readOnlyDoc.label, readOnlyDoc);
        } else {
          setCurrentTab(tabId);
        }

        const subcases = await refreshDocumentSubcases(data.categoryId);
        const documentWithSubcases = { ...readOnlyDoc, subcases };

        const { tree, expandedIds, selectedCategory: restoredSelection } =
          await loadDocumentViewForTab(documentWithSubcases, hadOpenTab);
        if (!tree[0]) {
          throw new Error('Could not fetch root category');
        }

        setCategories(tree);
        setSelectedCategory(restoredSelection);
        setSelectedCategories([]);
        setSelectedContents([]);
        setCurrentContent(null);
        setCurrentTab(tabId);
        setCurrentDocument(documentWithSubcases);
        setActiveDocument(documentWithSubcases);
        historyService.setCurrentDocument(tabId);
        setReadOnlyOpenDocuments((prev) => ({ ...prev, [tabId]: documentWithSubcases }));

        setExpandedNodes(expandedIds);
      } catch (error) {
        console.error('Error opening read-only document:', error);
        const ax = error as { response?: { data?: unknown } };
        if (shouldRemoveReadAccessGrantAfterOpenError(error)) {
          dismissStaleReadAccessGrant(grant.id);
        }
        alert(
          formatApiErrorMessage(
            ax?.response?.data,
            'Could not open the shared document. The owner may have revoked your view-only access.',
          ),
        );
      } finally {
        setLoading(false);
        resolve();
      }
    };

      checkForUnsavedChanges(
        () => {
          void performOpenReadOnly();
        },
        'The content editor is currently open with unsaved changes.',
      );
    });

  const handleCategorySelect = async (category: Category | null) => {
    const performCategorySelect = async () => {
    if (!category) {
      selectedCategoryRef.current = null;
      setSelectedCategory(null);
      setSelectedCategories([]);
      setSelectedContents([]);
      setCurrentContent(null);
      return;
    }
    let resolved = findCategoryInTree(categories, category.id) ?? category;
    const idChanged = resolved.id !== selectedCategoryRef.current?.id;
    // Select immediately (before await) so Ctrl/Cmd+V in the same tick targets this node.
    selectedCategoryRef.current = resolved;
    setSelectedCategory(resolved);
    if (idChanged) {
      setSelectedCategories([]);
      setSelectedContents([]);
      setCurrentContent(null);
      if (currentDocument) {
        setDocumentSelectedCategoryIds((prev) => ({
          ...prev,
          [currentDocument.id]: resolved.id,
        }));
      }
    }
    // Hydrate pane data so enablement and paste see real children/rows.
    if (resolved.contentCategory && !categoryContentsAreLoaded(resolved)) {
      const loaded = await loadContentRowsIntoTree(resolved.id);
      if (!loaded || selectedCategoryRef.current?.id !== loaded.id) return;
      selectedCategoryRef.current = loaded;
      setSelectedCategory(loaded);
    } else if (!resolved.contentCategory && !branchChildrenAreLoaded(resolved)) {
      const loaded = await loadBranchChildrenIntoTree(resolved.id);
      if (!loaded || selectedCategoryRef.current?.id !== loaded.id) return;
      selectedCategoryRef.current = loaded;
      setSelectedCategory(loaded);
    }
    };

    checkForUnsavedChanges(
      performCategorySelect,
      'The content editor is currently open with unsaved changes.'
    );
  };

  /** @deprecated Prefer handleCategorySelect — kept for any direct callers. */
  const handleCategoryClick = async (category: Category) => {
    await handleCategorySelect(category);
  };

  const categoryHasPaneData = (cat: Category): boolean =>
    cat.contentCategory
      ? categoryContentsAreLoaded(cat)
      : branchChildrenAreLoaded(cat);

  const applyCategoryNavigation = (category: Category) => {
    setSelectedCategory(category);
    setSelectedCategories([]);
    setSelectedContents([]);
    setCurrentContent(null);
    void expandParentsToMakeVisible(category, true);
  };

  const handleCategoryNavigate = (category: Category) => {
    checkForUnsavedChanges(
      () => {
        const cached = findCategoryInTree(categories, category.id) ?? category;
        if (categoryHasPaneData(cached)) {
          applyCategoryNavigation(cached);
          return;
        }

        void (async () => {
          setCategoryPaneLoading(true);
          try {
            const updatedCategory = await fetchCategoryForPane(category.id);
            setCategories((prev) =>
              updateCategoryInTree(prev, updatedCategory.id, (existing) => {
                if (updatedCategory.contentCategory) {
                  return {
                    ...existing,
                    ...updatedCategory,
                    contents: updatedCategory.contents,
                  };
                }
                const mergedChildren = (updatedCategory.children ?? []).map((child) => {
                  const prior = existing.children?.find((c) => c.id === child.id);
                  return prior ? { ...prior, ...child } : child;
                });
                return {
                  ...existing,
                  ...updatedCategory,
                  children: mergedChildren,
                };
              }),
            );
            applyCategoryNavigation(updatedCategory);
          } catch (error) {
            console.error('Error navigating to category:', error);
          } finally {
            setCategoryPaneLoading(false);
          }
        })();
      },
      'The content editor is currently open with unsaved changes.',
    );
  };

  const handleNavigateToParent = () => {
    if (!selectedCategory?.parentId) return;
    const parent =
      findCategoryInTree(categories, selectedCategory.parentId) ??
      findParentCategoryInTree(categories, selectedCategory.id);
    if (parent) {
      handleCategoryNavigate(parent);
    }
  };

  const handleCategoryMultiSelect = (category: Category, event: React.MouseEvent) => {
    if (event.ctrlKey || event.metaKey) {
      setSelectedCategories(prev => {
        const isSelected = prev.some(c => c.id === category.id);
        if (isSelected) {
          return prev.filter(c => c.id !== category.id);
        } else {
          return [...prev, category];
        }
      });
    }
    else if (event.shiftKey) {
      if (!category?.parentId) return;
      const parent =
        selectedCategory && category.parentId === selectedCategory.id
          ? selectedCategory
          : findCategoryInTree(categories, category.parentId);
      const siblingsList = parent?.children;
      if (!siblingsList?.length) return;

      const siblings = siblingsList.map((c) => c.id);
      const categoryIndex = siblings.indexOf(category.id);
      if (categoryIndex < 0) return;

      const validIndices = selectedCategories
        .map((c) => siblings.indexOf(c.id))
        .filter((i) => i >= 0);

      let anchorIndex = categoryIndex;
      if (validIndices.length > 0) {
        let closest = validIndices[validIndices.length - 1];
        for (let i = validIndices.length - 1; i >= 0; i--) {
          closest = validIndices[i];
          if (validIndices[i] < categoryIndex) break;
        }
        anchorIndex = closest;
      }

      const start = Math.min(anchorIndex, categoryIndex);
      const end = Math.max(anchorIndex, categoryIndex);
      setSelectedCategories(siblingsList.slice(start, end + 1));
    }
    else {
      setSelectedCategories(prev => {
        const isSelected = prev.some(c => c.id === category.id);
        if (isSelected) {
          return [];
        } else {
          return [category];
        }
      });
    }
  };

  const previewContentRow = (content: Content) => {
    if (isEditingContent) {
      setIsEditingContent(false);
      setShowEditorWarning(false);
    }
    const row = resolveContentRowForDisplay(content);
    setCurrentContent(row);
    setSelectedSubcase(row.subcase ?? '');
    setContentEditorResetNonce((n) => n + 1);
  };

  const handleContentCheckboxToggle = (content: Content) => {
    setSelectedContents((prev) => {
      const isSelected = prev.some((c) => c.id === content.id);
      const next = isSelected ? prev.filter((c) => c.id !== content.id) : [...prev, content];
      if (next.length === 1) {
        const row = resolveContentRowForDisplay(next[0]);
        previewContentRow(row);
        return [row];
      }
      if (isSelected && currentContent?.id === content.id) {
        setCurrentContent(null);
      }
      return next;
    });
  };

  const handleContentMultiSelect = (content: Content, event: React.MouseEvent) => {
    if (event.ctrlKey || event.metaKey) {
      setSelectedContents((prev) => {
        const isSelected = prev.some((c) => c.id === content.id);
        const next = isSelected ? prev.filter((c) => c.id !== content.id) : [...prev, content];
        if (currentContent?.id === content.id && isSelected) {
          setCurrentContent(next.length > 0 ? resolveContentRowForDisplay(next[0]) : null);
        } else if (!isSelected) {
          setCurrentContent(resolveContentRowForDisplay(content));
        }
        return next;
      });
    } else if (event.shiftKey) {
      if (!content?.parentId) return;
      const parent = findCategoryInTree(categories, content.parentId);
      if (!parent?.contents) return;
      const siblings = parent.contents.map((c) => c.id);
      const selectedIds = selectedContents.map((c) => c.id);
      const indices = selectedIds.map((id) => siblings.indexOf(id)).sort((a, b) => a - b);
      const contentIndex = siblings.indexOf(content.id);

      let closest = -1;
      for (let i = indices.length - 1; i >= 0; i--) {
        closest = indices[i];
        if (indices[i] < contentIndex) break;
      }

      const range = parent.contents.slice(
        Math.min(closest, contentIndex),
        Math.max(closest, contentIndex) + 1,
      );
      setSelectedContents(range);
      previewContentRow(content);
    } else if (selectedContents.length > 1) {
      const isInSelection = selectedContents.some((c) => c.id === content.id);
      if (isInSelection) {
        previewContentRow(content);
      } else {
        previewContentRow(content);
        setSelectedContents([resolveContentRowForDisplay(content)]);
      }
    } else {
      setSelectedContents((prev) => {
        const isSelected = prev.some((c) => c.id === content.id);
        if (isSelected) {
          setCurrentContent(null);
          return [];
        }
        previewContentRow(content);
        return [resolveContentRowForDisplay(content)];
      });
    }
  };

  const handleSelectAllCategories = (checked: boolean) => {
      if (checked) {
        setSelectedCategories(selectedCategory?.children ?? []);
      } else {
        setSelectedCategories([]);
      }
    };


  const handleSelectAllContents = (checked: boolean, contentsScope?: Content[]) => {
    const pool = contentsScope ?? selectedCategory?.contents ?? [];
    if (checked) {
      setCurrentContent(null);
      setSelectedContents(pool);
    } else {
      setSelectedContents([]);
    }
  };

  const handleInvertSelectionCategories = () => {
    const children = selectedCategory?.children ?? [];
    setSelectedCategories((prev) =>
      children.filter((child) => !prev.some((c) => c.id === child.id)),
    );
  };

  const handleInvertSelectionContents = (contentsScope?: Content[]) => {
    const pool = contentsScope ?? selectedCategory?.contents ?? [];
    setCurrentContent(null);
    setSelectedContents((prev) =>
      pool.filter((c) => !prev.some((s) => s.id === c.id)),
    );
  };

  const handleCategoriesChange = (updatedCategories: Category[]) => {
    setCategories(updatedCategories);
  };

  const handleNewCategoryNameChange = (newName: string) => {
    if ( !selectedCategory || !selectedCategory.children ) return;
    if (!newName.trim()) {
       setAddError('Category name is required');
    } else if ( isDuplicateName(newName, selectedCategory.children) ) {
        setAddError('A category with this name already exists');
    } else if ( normalizeCategoryName(newName) === normalizeCategoryName(selectedCategory.name) ) {
        setAddError('Name must be different than the parent category');
    }else {
        setAddError('');
    }
    setNewName(newName);
  }

  const getNonSelectedCategories = (category: Category, selectedCategories: Category[]): Category[] => {
    if (!category.children) return [];
    return category.children.filter(child => !selectedCategories.some(selected => selected.id === child.id));
  };

  const handleMergeCategoriesNameChange = (newName: string) => {
    if (!selectedCategory || !selectedCategory.children) return;
    if (!newName.trim()) {
      setAddError('Category name is required');
    } else if (isDuplicateName(newName, getNonSelectedCategories(selectedCategory, selectedCategories))) {
      setAddError('A category with this name already exists');
    } else if (normalizeCategoryName(newName) === normalizeCategoryName(selectedCategory.name)) {
      setAddError('Name must be different than the parent category');
    } else {
      setAddError('');
    }
    setNewName(newName);
  };

  const handleRenameCategoryNameChange = (newName: string) => {
      if (!selectedCategory) return;
      setRenameError(getCategoryRenameValidationError(selectedCategory, newName));
      setNewName(newName);
    }
  const handleRenameCategory = (explicitCategory?: Category) => {
    const categoryToRename =
      explicitCategory ??
      (selectedCategories.length > 0 ? selectedCategories[0] : selectedCategory);

    if (!categoryToRename || !canRenameCategory(categoryToRename)) return;
    setSelectedCategory(categoryToRename);
    setSelectedCategories([]);
    setNewName(categoryToRename.name);
    setNewDescription(categoryToRename.description);
    setRenameError('');
    setRenameDialogOpen(true);
  };

  const handleRenameDialogClose = () => {
    setRenameDialogOpen(false);
    setNewName('');
    setNewDescription('');
    setRenameError('');
  };

  const handleRenameDialogConfirm = async () => {
    if (!selectedCategory || !newName.trim()) return;

    const err = getCategoryRenameValidationError(selectedCategory, newName);
    if (err) {
      setRenameError(err);
      return;
    }

    try {
      if (
        normalizePresentation(newName) === normalizePresentation(selectedCategory.name) &&
        normalizePresentation(newDescription) === normalizePresentation(selectedCategory.description ?? '')
      ) {
        handleRenameDialogClose();
        return;
      }

      await performCategoryRename(selectedCategory, newName.trim(), newDescription);
      handleRenameDialogClose();
    } catch (error) {
      console.error('Error renaming category:', error);
      alert('Failed to rename category: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  };

  const findParentCategoryInTree = (categories: Category[], targetId: string): Category | null => {
    for (const category of categories) {
      // Check if this category has the target as a child in the children array
      if (category.children && category.children.some(child => child.id === targetId)) {
        return category;
      }

      if (category.contents && category.contents.some(child => child.id === targetId)) {
         return category;
      }

      // Recursively check children
      if (category.children && category.children.length > 0) {
        const found = findParentCategoryInTree(category.children, targetId);
        if (found) return found;
      }
    }
    return null;
  };

  const canMoveCategory = (category: Category | null, direction: 'up' | 'down'): boolean => {
    if (!category) return false;
    if (category.parentId === null) return false;

    // For nested categories, find the parent
    const findParent = (cats: Category[], targetId: string): Category | null => {
        for (const cat of cats) {
        if (!cat) continue;
        if (cat.children && Array.isArray(cat.children)) {
          if (cat.children.some(child => child.id === targetId)) {
          return cat;
        }
          const found = findParent(cat.children, targetId);
            if (found) return found;
          }
        }
        return null;
      };

    const parent = findParent(categories, category.id);
    if (!parent || !parent.children || !Array.isArray(parent.children)) return false;

    const index = parent.children.findIndex((child: Category) => child.id === category.id);
    if (direction === 'up' && index === 0) return false;
    if (direction === 'down' && index === parent.children.length - 1) return false;

    return true;
  };

  const handleMoveCategory = async (direction: 'up' | 'down') => {
    if (!canMoveSelectedCategories(direction)) return;

    if ( selectedContents.length > 0 )
        return handleMoveContent(direction);
    // If there are selected categories in the right pane, use those
    const categoriesToMove = selectedCategories.length > 0 ? selectedCategories :
      (selectedCategory ? [selectedCategory] : []);


    try {
      const parentId = categoriesToMove[0].parentId;
      if (!parentId) return;

      const parent = findParentCategoryInTree(categories, categoriesToMove[0].id);
      if (!parent || !parent.children) return;

      const allIds = [...parent.children].map(c => c.id);
      const selectedIds = categoriesToMove.map(c => c.id);

      // Find the indices of the selected categories
      const indices = selectedIds.map(id => allIds.indexOf(id)).sort((a, b) => a - b);
       // Update the backend with the new order
       await api.put('/categories/movechild',
         moveChildFormBody(parentId, indices[0], indices[indices.length - 1], direction),
         { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } },
       );

      // Move the categories
      if (direction === 'up') {
        historyService.recordOperation({
            type: 'moveCategoryUp',
            data: {
              parentId: parentId,
              firstIndex: indices[0],
              lastIndex: indices[indices.length - 1],
              direction: direction
            }
          });

        // Move up by swapping with the previous category
        indices.forEach(index => {
          if (index > 0) {
            [allIds[index], allIds[index - 1]] = [allIds[index - 1], allIds[index]];
          }
        });



      } else {

        historyService.recordOperation({
            type: 'moveCategoryDown',
            data: {
              parentId: parentId,
              firstIndex: indices[0],
              lastIndex: indices[indices.length - 1],
              direction: direction
            }
          });

        // Move down by swapping with the next category
        indices.reverse().forEach(index => {
          if (index < allIds.length - 1) {
            [allIds[index], allIds[index + 1]] = [allIds[index + 1], allIds[index]];
          }
        });

      }

      // Update the categories state with the new tree
      setCategories(prevCategories => {
        const updatedCategories = [...prevCategories];

        // Create the updated parent with new children order
        const updatedParent = {
          ...parent,
          children: allIds
            .map(id => parent.children?.find(child => child.id === id))
            .filter((child): child is Category => child !== undefined)
        };

        // Update the tree with the modified parent
        const newCategories = updateCategoryInTree(updatedCategories, parent.id, () => updatedParent);

        // Find the moved categories in their new positions
        const findCategoryInTree = (cats: Category[], targetId: string): Category | null => {
          for (const cat of cats) {
            if (cat.id === targetId) return cat;
          if (cat.children) {
              const found = findCategoryInTree(cat.children, targetId);
            if (found) return found;
          }
        }
        return null;
      };

        // Get the updated categories with their new positions
        const updatedCategoriesToSelect = categoriesToMove.map(cat =>
          findCategoryInTree(newCategories, cat.id)
        ).filter((cat): cat is Category => cat !== null);

        // Schedule the selection updates after the state update
        setTimeout(() => {
          if (selectedCategories.length > 0) {
            setSelectedCategories(updatedCategoriesToSelect);
            setSelectedCategory(updatedParent);
          } else {
            setSelectedCategory(updatedCategoriesToSelect[0]);
          }
        }, 0);

        return newCategories;
      });
    } catch (error) {
      console.error('Error moving categories:', error);
      alert('Failed to move categories: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  };

  const handleMoveContent = async (direction: 'up' | 'down') => {
      if (isContentPaneSubcaseFilterActive(contentPaneSubcaseFilter)) return;

      const contentToMove = selectedContents;

      try {
        const parentId = contentToMove[0].parentId;
        if (!parentId) return;

        const parent = findParentCategoryInTree(categories, contentToMove[0].id);
        if (!parent || !parent.contents) return;

        const allIds = [...parent.contents].map(c => c.id);
        const selectedIds = contentToMove.map(c => c.id);

        // Find the indices of the selected categories
        const indices = selectedIds.map(id => allIds.indexOf(id)).sort((a, b) => a - b);
         // Update the backend with the new order
         await api.put('/categories/movechild', {
                categoryId: parentId,
                firstIndex: indices[0],
                lastIndex: indices[indices.length - 1],
                direction: direction
              },
              {
                headers: {
                  'Content-Type': 'application/x-www-form-urlencoded'
                }
              });

         // Move the categories
          if (direction === 'up') {
             historyService.recordOperation({
                type: 'moveContentUp',
                data: {
                  parentId: parentId,
                  firstIndex: indices[0],
                  lastIndex: indices[indices.length - 1],
                  direction: direction
                }
              });
            // Move up by swapping with the previous category
            indices.forEach(index => {
              if (index > 0) {
                [allIds[index], allIds[index - 1]] = [allIds[index - 1], allIds[index]];
              }
            });
          } else {
           historyService.recordOperation({
              type: 'moveContentDown',
              data: {
                parentId: parentId,
                firstIndex: indices[0],
                lastIndex: indices[indices.length - 1],
                direction: direction
              }
            });
            // Move down by swapping with the next category
            indices.reverse().forEach(index => {
              if (index < allIds.length - 1) {
                [allIds[index], allIds[index + 1]] = [allIds[index + 1], allIds[index]];
              }
            });

          }

          // Update the categories state with the new tree
        setCategories(prevCategories => {
          const updatedCategories = [...prevCategories];

          // Create the updated parent with new children order
          const updatedParent = {
            ...parent,
            contents: allIds
              .map(id => parent.contents?.find(child => child.id === id))
              .filter((child): child is Content => child !== undefined)
          };

          // Update the tree with the modified parent
          const newCategories = updateCategoryInTree(updatedCategories, parentId, () => updatedParent);

          // Get the updated categories with their new positions
          const updatedContentToSelect = contentToMove.map(cont =>
            parent.contents?.find(child => child.id === cont.id)
          ).filter((cont): cont is Content => cont !== null);

          // Schedule the selection updates after the state update
          setTimeout(() => {
            if (selectedContents.length > 0) {
              setSelectedContents(updatedContentToSelect);
              setSelectedCategory(updatedParent);
            }
          }, 0);

          return newCategories;
        });
      } catch (error) {
        setLoading(false);
        console.error('Error moving categories:', error);
        alert('Failed to move categories: ' + (error instanceof Error ? error.message : 'Unknown error'));
      }
    };

  const getSiblings = (categories: Category[], categoryId: string): Category[] => {
    const parent = findParentCategoryInTree(categories, categoryId);
    if (!parent) return categories;
    return parent.children || [];
  };

  const getCategoryRenameValidationError = (category: Category | null, newName: string): string => {
    if (!category || !category.parentId) return '';
    const parent = findCategoryInTree(categories, category.parentId);
    if (!parent) return 'Invalid category';
    if (!newName.trim()) return 'Category name is required';
    const trimmed = newName.trim();
    if (
      normalizeCategoryName(trimmed) !== normalizeCategoryName(category.name) &&
      normalizeCategoryName(trimmed) === normalizeCategoryName(parent.name)
    ) {
      return `Cannot use "${trimmed}" as the name — it matches the parent folder "${parent.name}".`;
    }
    const siblingsExcludingSelf = getSiblings(categories, category.id).filter((c) => c.id !== category.id);
    if (
      normalizeCategoryName(trimmed) !== normalizeCategoryName(category.name) &&
      isDuplicateName(trimmed, siblingsExcludingSelf)
    ) {
      const other = siblingsExcludingSelf.find((c) => normalizeCategoryName(c.name) === normalizeCategoryName(trimmed));
      return other
        ? `Cannot use "${trimmed}" — "${other.name}" is already at the same level (same naming rules ignoring spaces/case).`
        : `Cannot use "${trimmed}" — another folder at the same level already uses that name.`;
    }
    const directChildren = category.children ?? [];
    if (
      normalizeCategoryName(trimmed) !== normalizeCategoryName(category.name) &&
      isDuplicateName(trimmed, directChildren)
    ) {
      return `Cannot use "${trimmed}" — this category already has a subcategory with this name.`;
    }
    return '';
  };

  const performCategoryRename = async (
    category: Category,
    newName: string,
    newDescription: string,
  ): Promise<void> => {
    const response = await api.put('/categories', {
      parentId: category.id,
      name: newName,
      description: newDescription,
    });
    const renamedCategory = response.data;
    historyService.recordOperation({
      type: 'renameCategory',
      data: {
        category: renamedCategory,
        oldId: category.id,
        oldName: category.name,
        oldDescription: category.description,
      },
    });
    const updatedCategory = await fetchCategoryWithChildren(renamedCategory.parentId);
    if (!updatedCategory) {
      throw new Error('Could not fetch root category');
    }
    setCategories(prevCategories => {
      const updatedCategories = [...prevCategories];
      return updateCategoryInTree(updatedCategories, updatedCategory.id, () => updatedCategory);
    });
    const refreshed = await fetchCategoryWithChildren(renamedCategory.id);
    if (refreshed) {
      setTimeout(() => {
        setSelectedCategory(refreshed);
        setExpandedNodes(prev => (prev.includes(refreshed.id) ? prev : [...prev, refreshed.id]));
      }, 0);
    }
  };

  const handleCategoryInlineRenameCommit = async (category: Category, newName: string) => {
    const trimmed = newName.trim();
    const validationErr = getCategoryRenameValidationError(category, trimmed);
    if (validationErr) return;

    if (normalizePresentation(trimmed) === normalizePresentation(category.name)) {
      return;
    }

    try {
      await performCategoryRename(category, trimmed, category.description);
    } catch (error) {
      console.error('Error renaming category:', error);
      alert('Failed to rename category: ' + (error instanceof Error ? error.message : 'Unknown error'));
      throw error;
    }
  };

  const updateCategoryName = (categories: Category[], targetId: string, newName: string, newDescription: string): Category[] => {
    return categories.map(cat =>
      cat.id === targetId ? { ...cat, name: newName, description: newDescription } : cat
    );
  };

  const createCategory = async (parentId: string | undefined, name: string, description: string): Promise<Category | null> => {
    try {
      const response = await api.post('/categories', {
        name,
        description,
        parentId: parentId || null,  // Ensure we send null if parentId is undefined
        content: '',  // Add empty content
        childrenIds: [],  // Initialize with empty children array
        children: []  // Initialize with empty children array
      });
      return response.data;
    } catch (error) {
      console.error('Error creating category:', error);
      if (axios.isAxiosError(error)) {
        console.error('Axios error details:', error.response?.data);
      }
      throw error; // Rethrow the error to handle it in the calling function
    }
  };

  const handleAddCategory = async () => {
    if (!selectedCategory || !newName) return;

    try {
      const response = await api.post('/categories', {
        parentId: selectedCategory.id,
        name: newName,
        description: newDescription
      });
      if (!response.data) throw new Error('Failed to create category');
      // Create response has no hasContents/load-state; stub so empty icon is correct immediately.
      const newCategory = stubBranchChild(response.data);
      const parentId = newCategory.parentId ?? selectedCategory.id;

      // Record the operation
      historyService.recordOperation({
        type: 'addCategory',
        data: {
          category: newCategory,
          parentId: parentId
        }
      });

      // Update the tree immutably and, if the parent is selected, point selectedCategory to the updated node.
      setCategories(prev => {
        let updatedParent: Category | null = null;
        const updated = updateCategoryInTree([...prev], parentId, (cat) => {
          const next = {
            ...cat,
            hasChildCategories: true,
            children: [...(cat.children || []), newCategory],
          };
          updatedParent = next;
          return next;
        });
        if (selectedCategory?.id === parentId && updatedParent) {
          setSelectedCategory(updatedParent);
          setExpandedNodes(nodes => (nodes.includes(parentId) ? nodes : [...nodes, parentId]));
        }
        return updated;
      });
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 401) {
        localStorage.removeItem('token');
        window.location.replace('/login');
        return;
      }
      console.error('Error adding category:', error);
      alert('Failed to add category');
    }
  };

  const handleCutCategory = () => {
    if (!canCutSelectedCategories()) return;

    setClipboardCategory(null);
    setClipboardCategories([]);
    setClipboardCContents([]);
    if (selectedContents.length > 0) {
      setClipboardCContents(selectedContents);
    } else if (selectedCategories.length >= 2) {
      setClipboardCategories([...selectedCategories]);
    } else {
      const categoryToCut =
        selectedCategories.length > 0 ? selectedCategories[0] : selectedCategory;
      setClipboardCategory(categoryToCut);
    }
    setIsCutOperation(true);
  };

  const handleCopyCategory = () => {
    if (!canCopySelectedCategories()) return;
    setClipboardCategory(null);
    setClipboardCategories([]);
    setClipboardCContents([]);
    if (selectedContents.length > 0) {
      setClipboardCContents(selectedContents);
    } else if (selectedCategories.length >= 2) {
      setClipboardCategories([...selectedCategories]);
    } else {
      const categoryToCopy =
        selectedCategories.length > 0 ? selectedCategories[0] : selectedCategory;
      setClipboardCategory(categoryToCopy);
    }
    setIsCutOperation(false);
  };

  const resolvePasteTargetCategory = (target: Category | null): Category | null => {
    if (!target) return null;
    const fromTree = findCategoryInTree(categories, target.id);
    if (!fromTree) return target;
    // Prefer a caller-hydrated node (e.g. after loadBranchChildrenIntoTree) over a
    // stale unloaded stub still sitting in React state.
    if (
      !target.contentCategory &&
      branchChildrenAreLoaded(target) &&
      !branchChildrenAreLoaded(fromTree)
    ) {
      return target;
    }
    if (
      target.contentCategory &&
      categoryContentsAreLoaded(target) &&
      !categoryContentsAreLoaded(fromTree)
    ) {
      return target;
    }
    if (
      !fromTree.contentCategory &&
      (!fromTree.children || fromTree.children.length === 0) &&
      fromTree.parentId
    ) {
      const parent = findCategoryInTree(categories, fromTree.parentId);
      const fromParent = parent?.children?.find((c) => c.id === fromTree.id);
      if (fromParent?.children?.length) {
        return { ...fromTree, children: fromParent.children };
      }
    }
    return fromTree;
  };

  const branchChildCount = (branch: Category): number => {
    const resolved = resolvePasteTargetCategory(branch);
    if (!resolved) return 0;
    if (branchChildrenAreLoaded(resolved)) {
      return resolved.children?.length ?? 0;
    }
    // Unloaded branch: app rules require ≥2 children when hasChildCategories is set.
    return resolved.hasChildCategories === true ? 2 : 0;
  };

  const handlePasteCategory = async (pasteTarget?: Category | null) => {
    if (selectedCategories.length > 0) return;

    const targetCategory = resolvePasteTargetCategory(pasteTarget ?? selectedCategory);
    if (!targetCategory) return;

    if (clipboardContents.length > 0) {
      await handlePasteContents(targetCategory);
      return;
    }
    if (clipboardCategories.length >= 2 && targetCategory.contentCategory) {
      const docCategoryId = activeDocument?.categoryId;
      if (
        isCutOperation &&
        !canCutCategoriesLeavingParentValid(clipboardCategories)
      ) {
        alert(
          'Cannot cut: the source parent must keep at least two subcategories, unless you cut every subcategory (parent becomes an empty content category).',
        );
        return;
      }
      if (
        isCutOperation &&
        docCategoryId &&
        !clipboardCategories.every((c) => {
          const clipPrefix = getCategoryDocumentPrefix(c.id);
          const docPrefix = getCategoryDocumentPrefix(docCategoryId);
          return !clipPrefix || !docPrefix || clipPrefix === docPrefix;
        })
      ) {
        alert('Cut categories can only be pasted in the same document.');
        return;
      }
      await handleDropCategoriesIntoContent(
        clipboardCategories,
        targetCategory,
        !isCutOperation,
      );
      setClipboardCategories([]);
      setClipboardCategory(null);
      setIsCutOperation(false);
      return;
    }
    if (clipboardCategories.length >= 2 && !targetCategory.contentCategory) {
      await handleDropCategoriesIntoBranch(
        clipboardCategories,
        targetCategory,
        !isCutOperation,
      );
      setClipboardCategories([]);
      setClipboardCategory(null);
      setIsCutOperation(false);
      return;
    }
    if (!clipboardCategory) return;

    if (
      isCutOperation &&
      activeDocument?.categoryId &&
      getCategoryDocumentPrefix(clipboardCategory.id) &&
      getCategoryDocumentPrefix(activeDocument.categoryId)
    ) {
      const clipPrefix = getCategoryDocumentPrefix(clipboardCategory.id)!;
      const docPrefix = getCategoryDocumentPrefix(activeDocument.categoryId)!;
      if (clipPrefix !== docPrefix) {
        alert('Cut categories can only be pasted in the same document.');
        return;
      }
    }

    if (
      categoryNameConflictsUnderParent(
        clipboardCategory.name,
        targetCategory,
      )
    ) {
      alert(
        'Cannot paste category here: name must differ from the target category and from names of categories already under it.'
      );
      return;
    }
    setLoading(true);
    var newCategory = clipboardCategory;
    try {
      // Create a new category in the backend
      if (isCutOperation) {
        const response = await api.put('/categories/move', {
          categoryId: clipboardCategory.id,
          newParentId: targetCategory.id
        },
         {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded'
          }
        });
        newCategory = response.data;

        const parent  = findParentCategoryInTree(categories, clipboardCategory.id);

          // Fetch the complete category structure starting from the root
        const updatedCategory = await fetchCategoryWithChildren(newCategory.id);
        if (!updatedCategory) {
          throw new Error('Could not fetch root category');
        }

        historyService.recordOperation({
            type: 'pasteCategory',
            data: {
              category: updatedCategory,
              oldCategoryId: clipboardCategory.id,
              oldParentId: parent!.id,
              newParentId: updatedCategory.parentId,
              oldIndex: parent!.children!.findIndex(c => c.id === clipboardCategory.id) ?? -1,
              newIndex: -1
            }
          });

        const originalParentForPaste = findParentCategoryInTree(categories, clipboardCategory.id);
        const remainingSiblingsAfterPaste = originalParentForPaste
          ? (originalParentForPaste.children || []).filter(
              (child) => child.id !== clipboardCategory.id,
            )
          : null;
        const refreshedOldParentAfterPaste =
          remainingSiblingsAfterPaste?.length === 0 && originalParentForPaste
            ? await fetchCategoryWithChildren(originalParentForPaste.id)
            : null;

        setCategories(prevCategories => {
            let updatedCategories = [...prevCategories];

            // Update the target parent with the new category
            updatedCategories = updateCategoryInTree(updatedCategories, targetCategory.id, (cat) => ({
              ...cat,
              children: [...(cat.children || []), updatedCategory],
            }));

            // Find and update the original parent to remove the category
            const originalParent = findParentCategoryInTree(updatedCategories, clipboardCategory.id);
            if (originalParent) {
              const remainingChildren = (originalParent.children || []).filter(
                (child) => child.id !== clipboardCategory.id,
              );
              if (remainingChildren.length === 0) {
                if (refreshedOldParentAfterPaste) {
                  updatedCategories = updateCategoryInTree(
                    updatedCategories,
                    originalParent.id,
                    () => refreshedOldParentAfterPaste,
                  );
                } else {
                  updatedCategories = updateCategoryInTree(updatedCategories, originalParent.id, (cat) => ({
                    ...cat,
                    contentCategory: true,
                    children: [],
                  }));
                }
              } else {
                updatedCategories = updateCategoryInTree(updatedCategories, originalParent.id, (cat) => ({
                  ...cat,
                  children: remainingChildren,
                }));
              }
            }

            // If the target parent is currently selected, update it so the content pane shows the pasted category.
            const refreshedTargetParent = findCategoryInTree(updatedCategories, targetCategory.id);
            if (refreshedTargetParent && targetCategory.id === refreshedTargetParent.id) {
              const targetId = refreshedTargetParent.id;
              setSelectedCategory(refreshedTargetParent);
              setExpandedNodes(prev => (prev.includes(targetId) ? prev : [...prev, targetId]));
            }

            return updatedCategories;
          });
    } else {
        // Clipboard may be a lazy stub — fetch the full subtree before cloning.
        const snapshot = await fetchCategoryWithChildren(clipboardCategory.id);
        await cloneCopiedSubtreeUnderCategory(snapshot, targetCategory);
      }

       setLoading(false);

      // Clear the clipboard
      setClipboardCategory(null);
      setIsCutOperation(false);

      await refreshActiveDocumentSubcasesFromServer();
    } catch (error) {
      setLoading(false);
      console.error('Error pasting category:', error);
      alert('Failed to paste category: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  };

  const handleImportSubtreeFromDocumentConfirm = async (sourceRoot: Category) => {
    // Prefer ref — dialog confirm can run while React state is one frame behind selection.
    const target = selectedCategoryRef.current ?? selectedCategory;
    if (!target) return;
    if (categoryNameConflictsUnderParent(sourceRoot.name, target)) {
      alert(
        'Cannot import here: name must not match categories already under the target.',
      );
      return;
    }
    setLoading(true);
    try {
      await cloneCopiedSubtreeUnderCategory(sourceRoot, target);
    } finally {
      setLoading(false);
    }
  };

  const pasteContent = async (
    content: Content,
    parentCategory: Category,
  ): Promise<Content | null> => {
    var response;
    setLoading(true);
    if (isCutOperation) {
      response = await api.put(`/content/move`, {
        contentId: content.id,
        newParentId: parentCategory.id
      },
      {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        }
      });
    } else {
      response = await api.post(`/content`, buildContentCopyPayload(content, parentCategory.id));
    }
    setLoading(false);
    return response.data;
  };


  const handlePasteContents = async (pasteTarget?: Category | null) => {
    if (clipboardContents.length == 0) return;

    let target: Category | null =
      findCategoryInTree(categories, (pasteTarget ?? selectedCategory)?.id ?? '') ??
      pasteTarget ??
      selectedCategory;
    if (!target || !target.contentCategory) return;

    // Duplicate checks need loaded rows — unloaded targets look empty and allow partial pastes.
    if (!categoryContentsAreLoaded(target)) {
      const loaded = await loadContentRowsIntoTree(target.id);
      if (!loaded) return;
      target = loaded;
    }

    if (
      isCutOperation &&
      activeDocument?.categoryId &&
      clipboardContents[0]?.parentId &&
      getCategoryDocumentPrefix(clipboardContents[0].parentId) &&
      getCategoryDocumentPrefix(activeDocument.categoryId)
    ) {
      const clipPrefix = getCategoryDocumentPrefix(clipboardContents[0].parentId)!;
      const docPrefix = getCategoryDocumentPrefix(activeDocument.categoryId)!;
      if (clipPrefix !== docPrefix) {
        alert('Cut contents can only be pasted in the same document.');
        return;
      }
    }

    const pasteTargetCategory = target;
    const duplicates = clipboardContents.filter(content =>
        hasDuplicateContent(
          pasteTargetCategory,
          content.subcase,
          content.text,
          undefined,
          content.richTextHtml,
        )
    );

    if (duplicates.length > 0) {
        const duplicateNames = duplicates.map(c => formatDuplicateContentLabel(c)).join(', ');
        const message = `The following contents already exist in this category: ${duplicateNames}. Please modify the subcases or choose a different category.`;
        alert(message);
        return;
    }

    let newContents: Content[] = [];
    try {
      const operationData: { content: Content;
        oldContentId: string;
        oldParentId: string;
        oldIndex: number;
       }[] = [];

      for (const content of clipboardContents) {
        const sourceParentInCurrentDoc = findParentCategoryInTree(categories, content.id);
        let oldIndex = -1;
        let oldParentId = content.parentId ?? '';

        if (sourceParentInCurrentDoc?.contents) {
          const idx = sourceParentInCurrentDoc.contents.findIndex(c => c.id === content.id);
          if (idx >= 0) {
            oldIndex = idx;
            oldParentId = sourceParentInCurrentDoc.id;
          }
        }

        if (isCutOperation && (oldIndex < 0 || !sourceParentInCurrentDoc)) {
          alert(
            'Cut contents could not be found in this document. Cut contents can only be pasted within the same document.'
          );
          return;
        }

        const newContent = await pasteContent(content, pasteTargetCategory);
        if ( !newContent )
            throw new Error('Failed to paste content');
        newContents.push(newContent);
        operationData.push({
          content: newContent,
          oldContentId: content.id,
          oldParentId,
          oldIndex,
        });
      };

      historyService.recordOperation({
        type: 'pasteContent',
        data: operationData,
        isBatch: isCutOperation
      });

      const targetId = pasteTargetCategory.id;
      const targetSnapshot = pasteTargetCategory;

      // Update the categories state with the new tree
      setCategories(prevCategories => {
        let updatedCategories = [...prevCategories];

        // If it's a cut operation, remove the category from its original location
        if (isCutOperation) {
          const originalParent = findParentCategoryInTree(updatedCategories, clipboardContents[0].id);
          if (originalParent) {
            // Update the original parent to remove the category
            const updatedOriginalParent = {
              ...originalParent,
              contents: (originalParent.contents || []).filter(child => !clipboardContents.includes(child))
            };
            // Update the tree with the modified original parent
            updatedCategories = updateCategoryInTree(updatedCategories, originalParent.id, () => updatedOriginalParent);
          }
        }

        // Update the target parent with the new category
        const liveTarget =
          findCategoryInTree(updatedCategories, targetId) ?? targetSnapshot;
        const updatedTargetParent = {
          ...liveTarget,
          contents: [...(liveTarget.contents || []), ...newContents]
        };

        // Update the tree with the modified target parent
        const newCategories = updateCategoryInTree(updatedCategories, targetId, () => updatedTargetParent);

        // Select the new category and expand the parent
        setTimeout(() => {
          setSelectedCategory(updatedTargetParent);
          selectedCategoryRef.current = updatedTargetParent;
          setSelectedCategories([]);
          setExpandedNodes(prev => [...prev, targetId]);
        }, 0);

        return newCategories;
      });


      // Clear the clipboard
      setClipboardCContents([]);
      setIsCutOperation(false);

      await refreshActiveDocumentSubcasesFromServer();
    } catch (error) {
      console.error('Error pasting category:', error);
      alert('Failed to paste category: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  };

  /**
   * Cut/move may leave two+ siblings, or remove every child so the parent becomes an empty content category.
   */
  const canCutCategoriesLeavingParentValid = (categoriesToCut: Category[]): boolean => {
    if (categoriesToCut.length === 0) return false;
    if (!categoriesToCut.every((c) => c.parentId)) return false;

    const idsByParent = new Map<string, Set<string>>();
    for (const c of categoriesToCut) {
      const parentId = c.parentId!;
      if (!idsByParent.has(parentId)) {
        idsByParent.set(parentId, new Set());
      }
      idsByParent.get(parentId)!.add(c.id);
    }

    for (const [parentId, idsToRemove] of Array.from(idsByParent.entries())) {
      const parent = findCategoryInTree(categories, parentId);
      if (!parent?.children) return false;
      if (!wouldLeaveAtLeastTwoChildrenAfterRemoving(parent, idsToRemove)) {
        return false;
      }
    }
    return true;
  };

  const canDeleteCategory = (category: Category | null): boolean => {
    if (!category?.parentId) return false;

    const parent = findParentCategoryInTree(categories, category.id);
    if (!parent?.children) return false;

    return wouldLeaveAtLeastTwoChildrenAfterRemoving(parent, new Set([category.id]));
  };

  const canCutCategory = (category: Category | null): boolean => {
    if (!category) return false;
    return canCutCategoriesLeavingParentValid([category]);
  };

  const canDelete = () : boolean => {
    if (selectedContents.length > 0) {
      return true;
    }
    return canDeleteSelectedCategories();
  };

  const canDeleteSelectedCategories = (): boolean => {
    if (selectedContents.length > 0) {
      return true;
    }

    if (selectedCategories.length >= 1) {
      return canCutCategoriesLeavingParentValid(selectedCategories);
    }

    return selectedCategory ? canDeleteCategory(selectedCategory) : false;
  };

  const canCutSelectedCategories = (): boolean => {
    if (selectedContents.length > 0) {
      return true;
    }
    if (selectedCategories.length >= 1) {
      return canCutCategoriesLeavingParentValid(selectedCategories);
    }
    return selectedCategory ? canCutCategory(selectedCategory) : false;
  };

  const canCopyCategory = (category: Category | null): boolean => {
    if (!category) return false;
    // Including root: useful for paste/import into another document (or under a branch here).
    // Paste still enforces sibling uniqueness via categoryNameConflictsUnderParent.
    return true;
  };

  const canCopySelectedCategories = (): boolean => {
    if (selectedContents.length > 0) {
      return true;
    }
    if (selectedCategories.length >= 2) {
      return selectedCategories.every((c) => canCopyCategory(c));
    }
    if (selectedCategories.length === 1) {
      return canCopyCategory(selectedCategories[0]);
    }
    return selectedCategory ? canCopyCategory(selectedCategory) : false;
  };

  const canImportSubtreeFromDocument = (category: Category | null): boolean => {
    if (!category || tabs.length === 0) return false;
    if (selectedCategories.length > 0 || selectedContents.length > 0) return false;
    if (category.contentCategory) return false;
    const live = findCategoryInTree(categories, category.id) ?? category;
    // Need at least two direct children; use load hints when the branch is not fetched yet.
    if (branchChildrenAreLoaded(live)) {
      return (live.children?.length ?? 0) >= 2;
    }
    return live.hasChildCategories === true;
  };

  const canPaste = (category: Category | null): boolean => {
    if (!category) return false;
    if (selectedContents.length > 0) return false;
    if (selectedCategories.length > 0) return false;

    const targetCategory = resolvePasteTargetCategory(category);
    if (!targetCategory) return false;

    if (targetCategory.contentCategory) {
      if (clipboardCategories.length >= 2) {
        const docCategoryId = activeDocument?.categoryId;
        if (
          isCutOperation &&
          docCategoryId &&
          !clipboardCategories.every((c) => {
            const clipPrefix = getCategoryDocumentPrefix(c.id);
            const docPrefix = getCategoryDocumentPrefix(docCategoryId);
            return !clipPrefix || !docPrefix || clipPrefix === docPrefix;
          })
        ) {
          return false;
        }
        return clipboardCategories.every(
          (s) =>
            !categoryNameConflictsUnderParent(
              s.name,
              targetCategory,
              isCutOperation ? undefined : s.id,
            ),
        );
      }
      if (clipboardContents.length === 0) return false;
      if (
        isCutOperation &&
        activeDocument?.categoryId &&
        clipboardContents[0]?.parentId &&
        getCategoryDocumentPrefix(clipboardContents[0].parentId) &&
        getCategoryDocumentPrefix(activeDocument.categoryId)
      ) {
        const c = getCategoryDocumentPrefix(clipboardContents[0].parentId)!;
        const d = getCategoryDocumentPrefix(activeDocument.categoryId)!;
        if (c !== d) return false;
      }
      return true;
    }

    if (clipboardCategories.length >= 2) {
      if (isCutOperation) {
        const docCategoryId = activeDocument?.categoryId;
        if (
          docCategoryId &&
          !clipboardCategories.every((c) => {
            const clipPrefix = getCategoryDocumentPrefix(c.id);
            const docPrefix = getCategoryDocumentPrefix(docCategoryId);
            return !clipPrefix || !docPrefix || clipPrefix === docPrefix;
          })
        ) {
          return false;
        }
        if (!canCutCategoriesLeavingParentValid(clipboardCategories)) {
          return false;
        }
      }
      return canDropCategoriesIntoBranch(
        clipboardCategories,
        targetCategory,
        !isCutOperation,
      );
    }

    if (!clipboardCategory) return false;

    if (
      isCutOperation &&
      activeDocument?.categoryId &&
      getCategoryDocumentPrefix(clipboardCategory.id) &&
      getCategoryDocumentPrefix(activeDocument.categoryId)
    ) {
      const c = getCategoryDocumentPrefix(clipboardCategory.id)!;
      const d = getCategoryDocumentPrefix(activeDocument.categoryId)!;
      if (c !== d) return false;
    }

    return (
        branchChildCount(targetCategory) >= 2 &&
        !categoryNameConflictsUnderParent(clipboardCategory.name, targetCategory)
    );
  };

  const handleEditDocument = (document: any) => {
    setCurrentTab(document.id);
    setCurrentDocument(documents.find(doc => doc.id === document.id));
  };

  const canAddCategory = (category: Category | null): boolean => {
    if (!category || category.contentCategory) return false;
    const resolved = findCategoryInTree(categories, category.id) ?? category;
    if (branchChildrenAreLoaded(resolved)) {
      return (resolved.children?.length ?? 0) >= 2;
    }
    return resolved.hasChildCategories === true;
  };

  const handleDelete = () => {
    if (selectedContents.length > 0) {
      setOpenDeleteContentDialog(true);
    } else if (selectedCategories.length > 0 || selectedCategory) {
      setOpenDeleteCategoryDialog(true);
    }
  };

  const openSplitDialog = (
    defaultNames?: { first: string; second: string },
    options?: { fromCompare?: boolean },
  ) => {
    if (!selectedCategory) return;

    setSplitNames(
      defaultNames ?? {
        first: `${selectedCategory.name} - 1`,
        second: `${selectedCategory.name} - 2`,
      },
    );
    setSplitError('');
    setSplitFromCompare(options?.fromCompare ?? false);
    setSplitDialogOpen(true);
  };

  const handleSplitDialogOpen = () => {
    openSplitDialog();
  };

  const handleSplitDialogClose = () => {
    setSplitDialogOpen(false);
    setSplitNames({ first: '', second: '' });
    setSplitError('');
    setSplitFromCompare(false);
  };

  const validateSplitNames = (first: string, second: string) => {
    if (normalizeCategoryName(first) === normalizeCategoryName(second)) {
      setSplitError('The two categories cannot have the same name');
      return false;
    }
    if (
      selectedCategory &&
      (normalizeCategoryName(first) === normalizeCategoryName(selectedCategory.name) ||
       normalizeCategoryName(second) === normalizeCategoryName(selectedCategory.name))
    ) {
      setSplitError('Sub categories cannot have the same name as the parent category');
      return false;
    }
    setSplitError('');
    return true;
  };

  const handleSplitDialogConfirm = async () => {
    if (!selectedCategory || !splitNames.first || !splitNames.second) return;
    if (splitError) return;

    try {
      // Use the selected category's ID as the parent ID for the new categories
      const parentCategoryId = selectedCategory.id;

      let newChildren = [];
      let newChildrenIds = [];
      setLoading(true);

      // When the original category has content but no explicit selection (and we are not
      // splitting from compare), all rows are moved wholesale into a "Temp - …" content
      // category. Create that temp category first so it becomes the first sibling.
      const needsTempCategory =
        !!selectedCategory.contents &&
        selectedCategory.contents.length > 0 &&
        selectedContents.length === 0 &&
        !splitFromCompare;

      let tempCategory: Category | null = null;
      if (needsTempCategory) {
        tempCategory = await createCategory(parentCategoryId, "Temp - " + selectedCategory.name, "Temporary category for " + selectedCategory.name + " content");
        if (tempCategory === null) throw new Error('Failed to create temporary category');
        newChildren.push(tempCategory);
        newChildrenIds.push(tempCategory.id);
      }

      // Create the first new category with the correct parent ID
      const firstCategory = await createCategory(parentCategoryId, splitNames.first, "");
      if (!firstCategory) throw new Error('Failed to create first category');

      // Create the second new category with the correct parent ID
      const secondCategory = await createCategory(parentCategoryId, splitNames.second, "");
      if (!secondCategory) throw new Error('Failed to create second category');

      newChildren.push(firstCategory, secondCategory);
      newChildrenIds.push(firstCategory.id, secondCategory.id);

      let movedContentsFirst: {content: Content, oldContentId: string, index:number}[]  = [];
      let movedContentsSecond: {content: Content, oldContentId: string, index:number}[]  = [];

      let contentsToMoveToFirst: Content[];
      let contentsToMoveToSecond: Content[];
      if (needsTempCategory) {
        // All original content goes into the temp category.
        contentsToMoveToFirst = selectedCategory.contents ?? [];
        contentsToMoveToSecond = [];
      } else {
        // Selection-based split: matched rows go into the first category.
        contentsToMoveToFirst = selectedCategory && selectedCategory.contents ? selectedCategory.contents.filter(c => selectedContents.includes(c)) : [];
        contentsToMoveToSecond = selectedCategory && selectedCategory.contents ? selectedCategory.contents.filter(c => !selectedContents.includes(c)) : [];
      }
      // Rows destined for "first" actually land in the temp category when one was created;
      // otherwise they go straight into the first category.
      if (!tempCategory) tempCategory = firstCategory;

      // Move all rows for each destination in a single request (order preserved), instead of one
      // HTTP round-trip per row — splitting a large content category is otherwise very slow.
      if (contentsToMoveToFirst.length > 0) {
        try {
          const response = await api.post('/content/bulk-move',
            contentsToMoveToFirst.map(content => ({
              contentId: content.id,
              newParentId: tempCategory?.id,
            })),
          );
          const moved = response.data as Content[];
          movedContentsFirst = moved.map((mc, i) => ({
            content: mc,
            oldContentId: contentsToMoveToFirst[i].id,
            index: selectedCategory.contents!.findIndex(c => c.id === contentsToMoveToFirst[i].id),
          }));
        } catch (error) {
          console.error('Error moving content into first/temp category:', error);
        }
      }

      if (contentsToMoveToSecond.length > 0) {
        try {
          const response = await api.post('/content/bulk-move',
            contentsToMoveToSecond.map(content => ({
              contentId: content.id,
              newParentId: secondCategory?.id,
            })),
          );
          const moved = response.data as Content[];
          movedContentsSecond = moved.map((mc, i) => ({
            content: mc,
            oldContentId: contentsToMoveToSecond[i].id,
            index: selectedCategory.contents!.findIndex(c => c.id === contentsToMoveToSecond[i].id),
          }));
        } catch (error) {
          console.error('Error moving content into second category:', error);
        }
    }

    setLoading(false);

      historyService.recordOperation({
        type: 'splitCategory',
        data: {
           firstCategory,
           secondCategory,
           parentId: parentCategoryId,
           tempCategory,
           movedContentsFirst,
           movedContentsSecond
        },
      });

      // Close the dialog
      setSplitDialogOpen(false);
      setSplitNames({ first: '', second: '' });
      setSplitError('');
      setSplitFromCompare(false);

      selectedCategory.contentCategory = false;

        const updatedParent = {
          ...selectedCategory,
          children: newChildren
        };

        // Update the categories state with the new tree
        setCategories(prevCategories => {
          const updatedCategories = [...prevCategories];

          // Update the tree with the modified parent
          return updateCategoryInTree(updatedCategories, parentCategoryId, () => updatedParent);
        });


        const updateTemp = {
          ...tempCategory,
          contents: movedContentsFirst.map(c => c.content)
        };

        const tempId = tempCategory.id;
        // Update the categories state with the new tree
        setCategories(prevCategories => {
          const updatedCategories = [...prevCategories];

          // Update the tree with the modified parent
          return updateCategoryInTree(updatedCategories, tempId, () => updateTemp);
        });

        const updateSecond = {
          ...secondCategory,
          contents: movedContentsSecond.map(c => c.content)
        };

        // Update the categories state with the new tree
        setCategories(prevCategories => {
          const updatedCategories = [...prevCategories];

          // Update the tree with the modified parent
          return updateCategoryInTree(updatedCategories, secondCategory.id, () => updateSecond);
        });

        // Select the parent category and expand it
        setSelectedCategory(updatedParent);
        setSelectedCategories([]);
        setExpandedNodes(prev => [...prev, parentCategoryId]);
      } catch (error) {
        setLoading(false);
        console.error('Error splitting category:', error);
        alert('Failed to split category: ' + (error instanceof Error ? error.message : 'Unknown error'));
      }
  };

  const canGroupCategories = (): boolean => {
    if (!selectedCategory || selectedCategory.contentCategory) return false;
    const live = findCategoryInTree(categories, selectedCategory.id) ?? selectedCategory;
    if (!branchChildrenAreLoaded(live) || !live.children) return false;
    return selectedCategories.length > 1 && live.children.length > selectedCategories.length;
  };

  /** Direct child categories of the tree selection that are also checked in the content pane. */
  const getSelectedDirectChildCategoriesOfSelectedCategory = (): Category[] => {
    if (!selectedCategory?.children?.length) return [];
    const childIds = new Set(selectedCategory.children.map((c) => c.id));
    return selectedCategories.filter((c) => childIds.has(c.id));
  };

  const trimCategoryId = (id: string) => id.replace(/^\uFEFF/g, '').trim();

  /**
   * Direct children of the selected branch included in Structure categories (AI).
   * Content pane: checked rows only when at least one is checked; otherwise all direct children
   * (unless the selection includes categories from outside this branch).
   */
  const getBranchChildrenForOrganize = (branch: Category | null = selectedCategory): Category[] => {
    if (!branch || branch.contentCategory) return [];
    const children = branch.children ?? [];
    if (children.length === 0) return [];

    const childIds = new Set(children.map((ch) => trimCategoryId(ch.id)));
    const selectedDirect = selectedCategories.filter((c) => {
      if (childIds.has(trimCategoryId(c.id))) return true;
      const nn = normalizePresentation((c.name || '').trim());
      if (!nn) return false;
      return children.some((ch) => normalizePresentation((ch.name || '').trim()) === nn);
    });

    if (selectedDirect.length > 0) {
      return selectedDirect.map((c) => findCategoryInTree(categories, c.id) ?? c);
    }

    const unrelatedCategorySelections = selectedCategories.filter(
      (sc) => !childIds.has(trimCategoryId(sc.id)),
    );
    if (unrelatedCategorySelections.length > 0) return [];

    return children.map((ch) => findCategoryInTree(categories, ch.id) ?? ch);
  };

  /**
   * Category IDs for bottom-up grouping (parent layers). Branch must have more than two direct
   * children in total; the operation uses {@link getBranchChildrenForOrganize} (selected only when
   * any are checked). Fewer than three in that set is not allowed (build from selected siblings up).
   */
  const getProposeParentCategoryIds = (): string[] => {
    if (!isFeatureEnabled(FEATURE_FLAG_KEYS.GENERATE_SUBTREE_FROM_CONTENT)) return [];
    if (!selectedCategory || selectedCategory.contentCategory) return [];
    if ((selectedCategory.children?.length ?? 0) <= 2) return [];

    const scope = getBranchChildrenForOrganize();
    if (scope.length <= 2) return [];

    const children = selectedCategory.children ?? [];
    const resolveToBranchChildId = (c: Category): string | null => {
      const tid = trimCategoryId(c.id);
      const exact = children.find((ch) => trimCategoryId(ch.id) === tid);
      if (exact) return trimCategoryId(exact.id);
      const nn = normalizePresentation((c.name || '').trim());
      if (nn) {
        const byName = children.find(
          (ch) => normalizePresentation((ch.name || '').trim()) === nn,
        );
        if (byName) return trimCategoryId(byName.id);
      }
      const inTree = findCategoryInTree(categories, tid);
      return inTree ? trimCategoryId(inTree.id) : null;
    };

    const resolved = new Set<string>();
    for (const c of scope) {
      const rid = resolveToBranchChildId(c);
      if (rid) resolved.add(rid);
    }
    return Array.from(resolved);
  };

  const canProposeParentCategories = (): boolean => {
    return getProposeParentCategoryIds().length >= 2;
  };

  const canSuggestParentStructureForCategory = (category: Category | null): boolean => {
    if (!isFeatureEnabled(FEATURE_FLAG_KEYS.GENERATE_SUBTREE_FROM_CONTENT)) return false;
    if (!category || category.contentCategory) return false;
    return (category.children?.length ?? 0) > 2;
  };

  /** True when the branch has at least one direct child that is a folder (not a content bucket). */
  const branchHasNestedBranchChildren = (branch: Category | null): boolean => {
    if (!branch || branch.contentCategory || !branch.children?.length) return false;
    return branch.children.some((c) => !c.contentCategory);
  };

  /** Content-category direct children checked in the content pane (not “select all” implicit scope). */
  const getSelectedContentChildrenInPane = (branch: Category | null = selectedCategory): Category[] => {
    if (!branch?.children?.length) return [];
    const childIds = new Set(branch.children.map((ch) => trimCategoryId(ch.id)));
    return selectedCategories.filter((c) => {
      if (!c.contentCategory) return false;
      if (childIds.has(trimCategoryId(c.id))) return true;
      const nn = normalizePresentation((c.name || '').trim());
      if (!nn) return false;
      return branch.children?.some(
        (ch) =>
          ch.contentCategory &&
          normalizePresentation((ch.name || '').trim()) === nn,
      );
    });
  };

  const countContentRowsForOrganize = (cat: Category): number => {
    const resolved = findCategoryInTree(categories, cat.id) ?? cat;
    return resolved.contents?.length ?? cat.contents?.length ?? 0;
  };

  const getSplittableChildCategoriesForOrganize = (branch: Category | null = selectedCategory): Category[] => {
    return getBranchChildrenForOrganize(branch).filter(
      (c) => c.contentCategory && countContentRowsForOrganize(c) >= 2,
    );
  };

  /** Load row counts for content subcategories in scope (content pane often has no contents[] on children). */
  const resolveSplittableChildrenForOrganize = async (
    branch: Category | null = selectedCategory,
  ): Promise<Category[]> => {
    const scope = getBranchChildrenForOrganize(branch).filter((c) => c.contentCategory);
    const resolved = await Promise.all(
      scope.map(async (child) => {
        if (countContentRowsForOrganize(child) >= 2) {
          return findCategoryInTree(categories, child.id) ?? child;
        }
        try {
          const full = await fetchCategoryWithChildren(child.id);
          return (full.contents?.length ?? 0) >= 2 ? full : null;
        } catch {
          return null; // not enough rows or fetch failed
        }
      }),
    );
    return resolved.filter((c): c is Category => c !== null);
  };

  const canOrganizeBranch = (category: Category | null): boolean => {
    if (!isFeatureEnabled(FEATURE_FLAG_KEYS.GENERATE_SUBTREE_FROM_CONTENT)) return false;
    if (!category || category.contentCategory || !(category.children?.length ?? 0)) return false;

    const nestedBranchChildren = branchHasNestedBranchChildren(category);
    const selectedContentChildren = getSelectedContentChildrenInPane(category);

    if (nestedBranchChildren) {
      if (selectedContentChildren.length < 2) return false;
    }

    const scope = getBranchChildrenForOrganize(category);
    const contentInScope = scope.filter((c) => c.contentCategory);
    const canSplit = contentInScope.some(
      (c) => countContentRowsForOrganize(c) >= 2 || countContentRowsForOrganize(c) === 0,
    );
    const canGroup =
      !nestedBranchChildren &&
      (category.children?.length ?? 0) > 2 &&
      scope.length > 2;
    return canSplit || canGroup;
  };

  const getOrganizeCategoriesMode = (): OrganizeCategoriesMode | null => {
    if (!isFeatureEnabled(FEATURE_FLAG_KEYS.GENERATE_SUBTREE_FROM_CONTENT)) return null;
    if (canGenerateSubtree(selectedCategory)) return 'fromContent';
    if (canOrganizeBranch(selectedCategory)) return 'branch';
    return null;
  };

  const canOrganizeCategories = (): boolean =>
    selectedContents.length === 0 && getOrganizeCategoriesMode() !== null;

  const handleOrganizeCategoriesOpen = async (preset?: OrganizeCategoriesPreset) => {
    const mode = getOrganizeCategoriesMode();
    if (!mode) return;
    if (preset === 'initialCategories' && mode !== 'fromContent') return;
    setOrganizeCategoriesMode(mode);
    setOrganizeCategoriesPreset(preset ?? null);
    if (mode === 'branch') {
      const branch = selectedCategory;
      const token = ++organizeSplittableTokenRef.current;
      setOrganizeBranchSiblingIds(getProposeParentCategoryIds());
      setOrganizeSplittableChildren([]);
      setOrganizeSplittableLoading(true);
      // Open the dialog immediately; resolve splittable children in the
      // background so the UI is responsive even for branches with many
      // content subcategories (each may require a fetch for its row count).
      setOrganizeCategoriesDialogOpen(true);
      void (async () => {
        try {
          const children = await resolveSplittableChildrenForOrganize(branch);
          if (organizeSplittableTokenRef.current === token) {
            setOrganizeSplittableChildren(children);
          }
        } finally {
          if (organizeSplittableTokenRef.current === token) {
            setOrganizeSplittableLoading(false);
          }
        }
      })();
    } else {
      organizeSplittableTokenRef.current++;
      setOrganizeBranchSiblingIds([]);
      setOrganizeSplittableChildren([]);
      setOrganizeSplittableLoading(false);
      setOrganizeCategoriesDialogOpen(true);
    }
  };

  const handleOrganizeCategoriesClose = () => {
    setOrganizeCategoriesDialogOpen(false);
    setOrganizeCategoriesPreset(null);
    setOrganizeBranchSiblingIds([]);
    setOrganizeSplittableChildren([]);
    organizeSplittableTokenRef.current++;
    setOrganizeSplittableLoading(false);
  };

  const handleOrganizeCategoriesFromTree = () => {
    handleOrganizeCategoriesOpen();
  };

  const canCategoryAiReport = useCallback(
    (cat: Category | null) => {
      if (!cat) return false;
      return categoryReportScopeMayHaveRows(cat);
    },
    [],
  );

  const handleCategoryAiReportFromTree = () => {
    if (!selectedCategory || !canCategoryAiReport(selectedCategory)) return;
    setCategoryReportDialogOpen(true);
  };

  const handleCategoryAiReportDialogClose = () => {
    setCategoryReportDialogOpen(false);
  };

  const handleCategoryCheckboxToggle = (category: Category) => {
    setSelectedCategories((prev) => {
      const isSelected = prev.some((c) => c.id === category.id);
      if (isSelected) return prev.filter((c) => c.id !== category.id);
      return [...prev, category];
    });
  };

  const handleGroupCategoriesDialogOpen = () => {
       if (!canGroupCategories()) return;
      setNewName('');
      setNewDescription('');
      setAddError('');
      setGroupCategoriesDialogOpen(true);
  };

  const handleGroupCategoriesDialogClose = () => {
      setGroupCategoriesDialogOpen(false);
      setNewName('');
      setNewDescription('');
      setAddError('');
  };

    const handleGroupCategoriesDialogConfirm = async () => {
       if (!newName.trim()) {
        setAddError('Category name is required');
        return;
      }
      setGroupCategoriesDialogOpen(false);
      await handleGroupCategories();
  };

  const handleGroupCategories = async () => {
    try {
      setLoading(true);
      if (!selectedCategory) {
        throw new Error('No category selected');
      }

      const response = await api.post('/categories', {
        parentId: selectedCategory.id,
        name: newName,
        description: newDescription
      });
      if (!response.data) throw new Error('Failed to create category');
      const newCategory = response.data;

      const historyCategories: {name: string, index: number}[] = [];
      const movedCategories: Category[] = [];
      for (const category of selectedCategories) {
        const index = selectedCategory.children!.findIndex(c => c.id === category.id);
        const response = await api.put('/categories/move', {
          categoryId: category.id,
          newParentId: newCategory.id
        },
        {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded'
          }
        });
        const movedCategory = response.data;
        historyCategories.push({name: movedCategory.name, index: index!});
        const categoryWithChildren = await fetchCategoryWithChildren(newCategory.id);
        if (!categoryWithChildren) {
          throw new Error('Could not fetch root category');
        }
        movedCategories.push(categoryWithChildren);
      }

      historyService.recordOperation({
        type: 'groupCategories',
        data: {
          originalParentId: selectedCategory.id,
          newParent: newCategory,
          groupedCategories: historyCategories
        }
      });

      // Fetch the complete updated category structure
      const updatedTree = await fetchCategoryWithChildren(selectedCategory.id);
      if (!updatedTree) {
        throw new Error('Could not fetch updated category structure');
      }

      // Update the state with the complete tree
      setCategories(prev => {
        const newCategories = [...prev];
        const parent = findParentCategoryInTree(newCategories, selectedCategory.id);
        if ( !parent ) return [updatedTree];
        if (parent && parent.children) {
          const index = parent.children.findIndex(cat => cat.id === updatedTree.id);
          if (index !== -1) {
            parent.children[index] = updatedTree;
          } else {
            parent.children.push(updatedTree);
          }
        }
        return newCategories;
      });

      setLoading(false);
    } catch (error) {
      setLoading(false);
      if (axios.isAxiosError(error) && error.response?.status === 401) {
        localStorage.removeItem('token');
        window.location.replace('/login');
        return;
      }
      console.error('Error adding category:', error);
      alert('Failed to add category');
    }
  };

  const SOURCE_CAT_MARKER = /\n__SRC_CAT__=([^\n]+)$/;

  type ProposeParentApplyTracking = {
    createdCategoryIds: string[];
    movesInApplyOrder: { originalId: string; previousParentId: string }[];
  };

  const proposeParentFormHeaders = { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } };

  /** Form-encode move params so ids with quotes (e.g. תנ"ך) are not mangled in the request body. */
  const putCategoryMoveForm = (categoryId: string, newParentId: string) =>
    api.put(
      '/categories/move',
      new URLSearchParams({ categoryId, newParentId }).toString(),
      proposeParentFormHeaders,
    );

  /** BOM / stray whitespace on ids breaks Redis keys and axios param serialization for Hebrew paths. */
  const stripBomAndTrimCategoryId = (id: string) => id.replace(/^\uFEFF/g, '').trim();

  /** GET /categories with explicit query string (stable encoding for long Hebrew category ids). */
  const fetchCategoryByIdForProposeParent = (categoryId: string) => {
    const qs = new URLSearchParams();
    qs.set('categoryId', stripBomAndTrimCategoryId(categoryId));
    return api.get<Category>(`/categories?${qs.toString()}`);
  };

  /** Map original id from preview markers → current id (paths change after each move). */
  const seedIdMapFromProposePreviewRoot = (previewRoot: { children?: any[] }) => {
    const map = new Map<string, string>();
    const walk = (node: any) => {
      if (!node || node.type !== 'category') return;
      const ch = node.children || [];
      if (
        ch.length === 1 &&
        ch[0]?.type === 'content' &&
        typeof ch[0]?.text === 'string'
      ) {
        const m = ch[0].text.match(SOURCE_CAT_MARKER);
        if (m?.[1]) {
          const clean = stripBomAndTrimCategoryId(m[1]);
          map.set(clean, clean);
        }
      }
      for (const sub of ch) {
        if (sub?.type === 'category') walk(sub);
      }
    };
    for (const sub of previewRoot?.children || []) {
      if (sub?.type === 'category') walk(sub);
    }
    return map;
  };

  const applySuggestParentNode = async (
    parentId: string,
    node: any,
    idMap: Map<string, string>,
    tracking?: ProposeParentApplyTracking,
  ): Promise<void> => {
    if (!node || node.type !== 'category') return;
    const ch = node.children || [];
    if (
      ch.length === 1 &&
      ch[0]?.type === 'content' &&
      typeof ch[0]?.text === 'string' &&
      ch[0].text.includes('__SRC_CAT__=')
    ) {
      const m = ch[0].text.match(SOURCE_CAT_MARKER);
      if (!m?.[1]) return;
      const origFromMarker = stripBomAndTrimCategoryId(m[1]);
      const catId = stripBomAndTrimCategoryId(idMap.get(origFromMarker) ?? origFromMarker);
      // __SRC_CAT__ marks an existing category to place under `parentId`. Do not POST a wrapper folder:
      // the preview leaf uses `new: true` for the AI subtree, but that bucket would nest each real
      // category under an empty duplicate (wrong tree vs preview, and invalid-looking hierarchy).
      const moveTargetParentId = stripBomAndTrimCategoryId(parentId);
      if (tracking) {
        let prevParentId = findCategoryInTree(categories, catId)?.parentId;
        if (typeof prevParentId !== 'string' || !prevParentId) {
          const catGet = await fetchCategoryByIdForProposeParent(catId);
          prevParentId = catGet.data?.parentId;
        }
        if (typeof prevParentId !== 'string' || !prevParentId) {
          throw new Error(`Could not resolve previous parent for category ${catId}`);
        }
        tracking.movesInApplyOrder.push({
          originalId: origFromMarker,
          previousParentId: stripBomAndTrimCategoryId(prevParentId),
        });
      }
      const moveRes = await putCategoryMoveForm(catId, moveTargetParentId);
      const moved = moveRes.data as Category;
      if (moved?.id) idMap.set(origFromMarker, moved.id);
      return;
    }
    const res = await api.post('/categories', {
      parentId: stripBomAndTrimCategoryId(parentId),
      name: normalizePresentation((node.name || 'New category').trim()) || 'New category',
      description: '',
    });
    const newPid = res.data.id as string;
    if (tracking) {
      tracking.createdCategoryIds.push(newPid);
    }
    for (const sub of ch) {
      if (sub?.type === 'category') {
        await applySuggestParentNode(newPid, sub, idMap, tracking);
      }
    }
  };

  const canFlattenCategory = (): boolean => {
          if (!selectedCategory || !selectedCategory.parentId || selectedCategory.contentCategory) return false;
          const live = findCategoryInTree(categories, selectedCategory.id) ?? selectedCategory;
          // Name-conflict checks need direct children loaded.
          if (!branchChildrenAreLoaded(live) || !live.children?.length) return false;

          const grandparent = findCategoryInTree(categories, selectedCategory.parentId);
          if (!grandparent || !branchChildrenAreLoaded(grandparent) || !grandparent.children) return false;

          // Siblings under grandparent after removing the category being flattened (not vs the flatten node itself).
          const siblingCategories = grandparent.children.filter((c) => c.id !== selectedCategory.id);

          const liftedNormalized = new Set<string>();
          for (const category of live.children) {
            const nn = normalizeCategoryName(category.name);
            if (!nn) return false;
            if (liftedNormalized.has(nn)) return false;
            liftedNormalized.add(nn);

            if (nn === normalizeCategoryName(grandparent.name)) return false;
            if (isDuplicateName(category.name, siblingCategories)) return false;
          }

          return true;
     };

  const handleFlattenCategory = async () => {
          if (!selectedCategory || !selectedCategory.parentId) return;
          let live = findCategoryInTree(categories, selectedCategory.id) ?? selectedCategory;
          if (!branchChildrenAreLoaded(live)) {
            const loaded = await loadBranchChildrenIntoTree(live.id);
            if (!loaded) return;
            live = loaded;
          }
          if (!live.children?.length) return;
          if (!canFlattenCategory()) {
            alert(
              'Cannot flatten: moving subcategories up would duplicate an existing sibling or parent name (comparison ignores letter case). Rename one of them first.'
            );
            return;
          }

           setLoading(true);

           var flattenCategories: Category[] = [];
            for (const cat of live.children) {
                try {
                    const response = await api.put(`/categories/move`, {
                        categoryId: cat.id,
                        newParentId: selectedCategory.parentId
                    },
                    {
                        headers: {
                            'Content-Type': 'application/x-www-form-urlencoded'
                        }
                    });

                 const movedCat = response.data;
                 const updatedCat = await fetchCategoryWithChildren(movedCat.id);
                 flattenCategories.push(updatedCat)

                } catch (error) {
                    console.error(`Failed to move category ${cat.id}:`, error);
                    throw error;
                }
            }

            await deleteCategory(selectedCategory.id);

            const parent  = findParentCategoryInTree(categories, selectedCategory.id);
           if ( !parent || !parent.children) return;
           const index = parent.children.findIndex(c => c.id === selectedCategory.id);
           const firstIndex = parent.children.length - 1;
           const lastIndex = firstIndex + flattenCategories.length - 1;

           await api.put(`/categories/movechildto`, {
                    categoryId: parent.id,
                    firstIndex,
                    lastIndex,
                    moveToIndex: index
                  },
                  {
                    headers: {
                      'Content-Type': 'application/x-www-form-urlencoded'
                    }
                  });


          historyService.recordOperation({
            type: 'flattenCategory',
            data: {
                parentId: selectedCategory.parentId,
                category: live,
                index: index,
                flattenCategories: flattenCategories,
                originalCategories: live.children
            }
          });

            setCategories(prev => {
                const newCategories = [...prev];
                const parent = findParentCategoryInTree(newCategories, selectedCategory.id);
                if (parent && parent.children) {

                    // Remove the selected category from its current position
                    const filteredChildren = parent.children.filter(child => child.id !== selectedCategory.id);

                    // Insert the children at the specific index
                    filteredChildren.splice(index, 0, ...(flattenCategories || []));

                    parent.children = filteredChildren;
                }

                setSelectedCategory(parent);
                return newCategories;
            });

            setLoading(false);


       };

  const canCollapseSubtreeToContentCategory = (category: Category | null): boolean => {
    if (!category || category.contentCategory) return false;
    if (!category.parentId) return false;
    const live = findCategoryInTree(categories, category.id) ?? category;
    if (branchChildrenAreLoaded(live)) {
      return (live.children?.length ?? 0) > 0;
    }
    return live.hasChildCategories === true;
  };

  const handleCollapseSubtreeToContentCategory = async () => {
    if (!selectedCategory || !canCollapseSubtreeToContentCategory(selectedCategory)) return;
    const branchId = selectedCategory.id;
    try {
      setLoading(true);
      const snapshot = await fetchCategoryWithChildren(branchId);
      await collapseSubtreeToContentCategory(branchId);
      const updated = await fetchCategoryWithChildren(branchId);
      historyService.recordOperation({
        type: 'collapseSubtreeToContentCategory',
        data: { branchCategoryId: branchId, snapshot },
      });
      setCategories(prev => {
        const replace = (cats: Category[]): Category[] =>
          cats.map(cat => {
            if (cat.id === branchId) return updated;
            if (cat.children?.length) return { ...cat, children: replace(cat.children) };
            return cat;
          });
        return replace(prev);
      });
      setSelectedCategory(updated);
      setSelectedCategories([]);
    } catch (error) {
      console.error('Failed to collect subtree content:', error);
      if (axios.isAxiosError(error) && error.response?.data) {
        const msg = typeof error.response.data === 'string'
          ? error.response.data
          : (error.response.data as { message?: string }).message;
        alert(msg || 'Failed to collect branch content.');
      } else {
        alert('Failed to collect branch content.');
      }
    } finally {
      setLoading(false);
    }
  };

  /**
   * Multi-selected branches (resolved to full tree nodes) that can each be collected.
   * Excludes any branch nested under another selected branch — collapsing the ancestor
   * already absorbs it, and collapsing both would conflict.
   */
  const getCollapsibleSelectedBranches = (): Category[] => {
    if (selectedCategories.length === 0) return [];
    const resolved = selectedCategories
      .map((c) => findCategoryInTree(categories, c.id) ?? c)
      .filter((c) => canCollapseSubtreeToContentCategory(c));
    const ids = resolved.map((c) => trimCategoryId(c.id));
    return resolved.filter((c) => {
      const cid = trimCategoryId(c.id);
      return !ids.some((other) => other !== cid && cid.startsWith(other + ':'));
    });
  };

  /**
   * Enabled when the content-pane selection contains at least one collapsible branch
   * (content categories are ignored). With no multi-selection, falls back to the single
   * tree-selected branch.
   */
  const canCollectBranchContent = (): boolean => {
    if (selectedCategories.length > 0) {
      return getCollapsibleSelectedBranches().length >= 1;
    }
    return canCollapseSubtreeToContentCategory(selectedCategory);
  };

  /** Collapse each given branch into a content category; records one history entry for the batch. */
  const collapseBranchesToContent = async (branchIds: string[]) => {
    if (branchIds.length === 0) return;
    try {
      setLoading(true);
      const items: { branchCategoryId: string; snapshot: Category }[] = [];
      for (const branchId of branchIds) {
        const snapshot = await fetchCategoryWithChildren(branchId);
        await collapseSubtreeToContentCategory(branchId);
        const updated = await fetchCategoryWithChildren(branchId);
        items.push({ branchCategoryId: branchId, snapshot });
        setCategories(prev => {
          const replace = (cats: Category[]): Category[] =>
            cats.map(cat => {
              if (cat.id === branchId) return updated;
              if (cat.children?.length) return { ...cat, children: replace(cat.children) };
              return cat;
            });
          return replace(prev);
        });
      }
      if (items.length === 1) {
        historyService.recordOperation({
          type: 'collapseSubtreeToContentCategory',
          data: items[0],
        });
      } else {
        historyService.recordOperation({
          type: 'collapseSubtreeToContentCategoryBulk',
          data: { items },
        });
      }
      setSelectedCategories([]);
    } catch (error) {
      console.error('Failed to collect branch content:', error);
      if (axios.isAxiosError(error) && error.response?.data) {
        const msg = typeof error.response.data === 'string'
          ? error.response.data
          : (error.response.data as { message?: string }).message;
        alert(msg || 'Failed to collect branch content.');
      } else {
        alert('Failed to collect branch content.');
      }
    } finally {
      setLoading(false);
    }
  };

  /**
   * Collect branch content for the current selection. Operates on the collapsible branches
   * in the content-pane selection (ignoring content categories); with no such selection,
   * falls back to the single tree-selected branch.
   */
  const handleCollectBranchContent = async () => {
    const branches = getCollapsibleSelectedBranches();
    if (branches.length >= 1) {
      await collapseBranchesToContent(branches.map(b => b.id));
    } else if (selectedCategories.length === 0) {
      await handleCollapseSubtreeToContentCategory();
    }
  };

  const getAddMatchingContentSourceGroups = (category: Category | null) => {
    if (!category?.contentCategory) return [];
    return collectAddMatchingContentSourcesAlongAncestorPath(categories, category);
  };

  const getAddMatchingContentSources = (category: Category | null): Category[] =>
    flattenAddMatchingContentSourceGroups(getAddMatchingContentSourceGroups(category));

  const getAddMatchingContentDefaultSourceId = (category: Category | null): string | undefined => {
    if (!category?.contentCategory) return undefined;
    return findDefaultAddMatchingContentSourceIdAmongGroups(
      getAddMatchingContentSourceGroups(category),
      category.id,
    );
  };

  const canAddMatchingContentFor = (category: Category | null): boolean => {
    if (!isFeatureEnabled(FEATURE_FLAG_KEYS.SEMANTIC_FIND)) return false;
    if (!category?.contentCategory) return false;
    // Known path sources (loaded rows or hasContents hints).
    if (getAddMatchingContentSources(category).length > 0) return true;
    // Parent not fully loaded yet — siblings may still be valid sources once fetched.
    if (!category.parentId) return false;
    const parent = findCategoryInTree(categories, category.parentId);
    if (!parent) return true;
    if (!branchChildrenAreLoaded(parent)) {
      return parent.hasChildCategories !== false;
    }
    // Sibling content categories, or branches that may contain sources deeper down.
    return (parent.children ?? []).some(
      (c) =>
        c.id !== category.id &&
        (contentCategoryMayHaveRows(c) ||
          (!c.contentCategory &&
            (c.hasChildCategories === true || (c.children?.length ?? 0) > 0))),
    );
  };

  const canAddMatchingContent = (): boolean => canAddMatchingContentFor(selectedCategory);

  const handleAddMatchingContentDialogOpen = () => {
    if (!canAddMatchingContent()) return;
    setAddMatchingContentDialogOpen(true);
  };

  const handleAddMatchingContentDialogClose = () => {
    setAddMatchingContentDialogOpen(false);
  };

  // "Adjust text" on a single plain-text content row: borrow a suffix from the previous row and/or
  // a prefix from the next row (document order) without modifying those neighbours.
  // "Rich" means the row carries formatting/media that plain-text adjust would discard — not merely a
  // plain paragraph wrapper (<p>…</p>/<br>) that the editor adds to every row. Rows with attachments
  // or any non-trivial markup/Quill-delta are excluded; plain editor text is allowed.
  const isRichContentRow = (row: Content | null | undefined): boolean => {
    if (!row) return false;
    const attachments = row.attachmentsJson?.trim();
    if (attachments && attachments !== '[]') return true;
    const html = row.richTextHtml?.trim();
    if (!html) return false;
    if (html.startsWith('{')) return true; // Quill delta JSON — treat as rich.
    // Strip plain paragraph/line-break wrappers; any remaining tag (img, strong, ul, …) is real rich content.
    const withoutSimpleWrappers = html.replace(/<\/?(?:p|br)\b[^>]*>/gi, '');
    return /<[a-z]/i.test(withoutSimpleWrappers);
  };

  // Adjacent document-order neighbours within a category; a side is only offered when that
  // neighbour shares the row's subcase (borrowing across subcases doesn't make sense).
  const getNeighboursInCategory = (
    row: Content | null,
    category: Category | null | undefined,
  ): { prev?: string; next?: string } => {
    if (!row || !category?.contents) return {};
    const rows = category.contents;
    const idx = rows.findIndex((c) => c.id === row.id);
    if (idx < 0) return {};
    const selfSubcase = rows[idx].subcase ?? '';
    const sameSubcase = (other?: Content) => !!other && (other.subcase ?? '') === selfSubcase;
    const prevRow = idx > 0 ? rows[idx - 1] : undefined;
    const nextRow = idx < rows.length - 1 ? rows[idx + 1] : undefined;
    return {
      prev: sameSubcase(prevRow) ? prevRow!.text : undefined,
      next: sameSubcase(nextRow) ? nextRow!.text : undefined,
    };
  };

  const canAdjustContentRowInCategory = (row: Content, category: Category): boolean => {
    if (isRichContentRow(row)) return false;
    const { prev, next } = getNeighboursInCategory(row, category);
    return !!(prev && prev.length > 0) || !!(next && next.length > 0);
  };

  // Adjacent document-order neighbours for the content pane. Hidden when a subcase filter is
  // active so the neighbours always reflect the unfiltered document order.
  const getContentRowNeighbours = (row: Content | null): { prev?: string; next?: string } => {
    if (!row) return {};
    if (isContentPaneSubcaseFilterActive(contentPaneSubcaseFilter)) return {};
    // Find whichever live array actually contains the row (the pane's selectedCategory is freshest
    // for just-created rows; the tree copy can briefly lag). Compare subcases within that same array
    // so a stale selection snapshot can't disagree with the live rows about the subcase.
    let rows: Content[] | undefined;
    let idx = -1;
    for (const candidate of [selectedCategory, findCategoryInTree(categories, row.parentId)]) {
      const candidateRows = candidate?.contents;
      if (!candidateRows) continue;
      const i = candidateRows.findIndex((c) => c.id === row.id);
      if (i >= 0) {
        rows = candidateRows;
        idx = i;
        break;
      }
    }
    if (!rows || idx < 0) return {};
    const selfSubcase = rows[idx].subcase ?? '';
    const sameSubcase = (other?: Content) => !!other && (other.subcase ?? '') === selfSubcase;
    const prevRow = idx > 0 ? rows[idx - 1] : undefined;
    const nextRow = idx < rows.length - 1 ? rows[idx + 1] : undefined;
    return {
      prev: sameSubcase(prevRow) ? prevRow!.text : undefined,
      next: sameSubcase(nextRow) ? nextRow!.text : undefined,
    };
  };

  const getAdjustTextNeighbours = (row: Content | null): { prev?: string; next?: string } => {
    if (!row) return {};
    const categoryId = adjustTextCategoryId ?? selectedCategory?.id;
    if (!categoryId) return {};
    if (!adjustTextCategoryId && isContentPaneSubcaseFilterActive(contentPaneSubcaseFilter)) {
      return {};
    }
    return getNeighboursInCategory(row, findCategoryInTree(categories, categoryId));
  };

  const canAdjustTextContent = (): boolean => {
    if (effectiveDocumentReadOnly) return false;
    if (isContentPaneSubcaseFilterActive(contentPaneSubcaseFilter)) return false;
    if (selectedContents.length !== 1) return false;
    const row = selectedContents[0];
    if (isRichContentRow(row)) return false;
    const { prev, next } = getContentRowNeighbours(row);
    return !!(prev && prev.length > 0) || !!(next && next.length > 0);
  };

  const handleAdjustTextContent = () => {
    if (!canAdjustTextContent()) return;
    setAdjustTextCategoryId(selectedCategory?.id ?? null);
    setAdjustTextRow(selectedContents[0]);
  };

  const handleAdjustTextSave = async (newText: string) => {
    const row = adjustTextRow;
    const categoryId = adjustTextCategoryId ?? selectedCategory?.id;
    if (!row || !categoryId) {
      setAdjustTextRow(null);
      setAdjustTextCategoryId(null);
      return;
    }
    if (!newText.trim() || newText === row.text) {
      setAdjustTextRow(null);
      setAdjustTextCategoryId(null);
      return;
    }
    const payload: Record<string, unknown> = {
      parentId: categoryId,
      id: row.id,
      subcase: row.subcase ?? '',
      text: newText,
      richTextHtml: '',
      richTextDelta: '',
      ...(typeof row.attachmentsJson === 'string' ? { attachmentsJson: row.attachmentsJson } : {}),
    };
    // Throws on failure — AdjustTextDialog surfaces the error and stays open.
    const response = await api.post<Content>('/content', payload);
    const updated = response.data;
    applyContentUpdateInTree(updated, categoryId);
    syncContentEditorIfSelected(updated);
    historyService.recordOperation({
      type: 'updateContent',
      data: { content: updated, prevContent: row, categoryId },
    });
    setAdjustTextRow(null);
    setAdjustTextCategoryId(null);
  };

  const canCompareContentFor = (category: Category | null): boolean => {
    if (!isFeatureEnabled(FEATURE_FLAG_KEYS.COMPARE_CONTENT_SELECTION)) return false;
    if (!category?.contentCategory) return false;
    // Source must have rows (loaded or server hint).
    if (!contentCategoryMayHaveRows(category)) return false;
    if (documentHasCompareTargets(categories, category)) return true;
    // Parent may have unloaded siblings / branches that are valid compare targets.
    if (!category.parentId) return false;
    const parent = findCategoryInTree(categories, category.parentId);
    if (!parent) return true;
    if (!branchChildrenAreLoaded(parent)) {
      return parent.hasChildCategories !== false;
    }
    return (parent.children ?? []).some(
      (c) =>
        c.id !== category.id &&
        (contentCategoryMayHaveRows(c) ||
          (!c.contentCategory && (c.hasChildCategories || (c.children?.length ?? 0) > 0))),
    );
  };

  const canCompareContent = (): boolean => canCompareContentFor(selectedCategory);

  const canFilterContentPaneBySubcase =
    tabs.length > 0 && !!(activeDocument?.subcases?.length);

  const handleCompareContentDialogOpen = () => {
    if (!canCompareContent()) return;
    setCompareContentDialogOpen(true);
  };

  const handleCompareContentDialogClose = () => {
    setCompareContentDialogOpen(false);
  };

  const handleCompareContentSelect = (contentIds: string[]) => {
    if (!selectedCategory?.contentCategory) return;
    const fresh = findCategoryInTree(categories, selectedCategory.id) ?? selectedCategory;
    const idSet = new Set(contentIds);
    const rows = (fresh.contents ?? []).filter(c => idSet.has(c.id));
    setSelectedCategories([]);
    setSelectedContents(rows);
    setCurrentContent(rows.length > 0 ? rows[0] : null);
    setMatchingContentIds([]);
  };

  const handleCompareContentSplit = (
    contentIdsForFirst: string[],
    whichInFirst: 'matched' | 'unmatched',
  ) => {
    if (!selectedCategory?.contentCategory) return;
    handleCompareContentSelect(contentIdsForFirst);
    setCompareContentDialogOpen(false);
    const baseName = selectedCategory.name.trim();
    const matchedName = `${baseName} - matched`;
    const unmatchedName = `${baseName} - unmatched`;
    openSplitDialog(
      whichInFirst === 'matched'
        ? { first: matchedName, second: unmatchedName }
        : { first: unmatchedName, second: matchedName },
      { fromCompare: true },
    );
  };

  const handleAddMatchingContentSuccess = async (
    targetCategoryId: string,
    transfers: AddMatchingContentTransfer[],
    move: boolean,
  ) => {
    const sourceCategoryId = transfers[0]?.sourceCategoryId;
    historyService.recordOperation({
      type: 'addMatchingContent',
      data: {
        targetCategoryId,
        sourceCategoryId,
        move,
        transfers: transfers.map((t) => ({ ...t, moved: move })),
      },
    });
    const updatedTarget = await fetchCategoryWithChildren(targetCategoryId);
    let updatedSource: Category | null = null;
    if (sourceCategoryId) {
      updatedSource = await fetchCategoryWithChildren(sourceCategoryId);
    }
    setCategories((prev) => {
      const replace = (cats: Category[]): Category[] =>
        cats.map((cat) => {
          if (cat.id === targetCategoryId) return updatedTarget;
          if (sourceCategoryId && cat.id === sourceCategoryId && updatedSource) return updatedSource;
          if (cat.children?.length) return { ...cat, children: replace(cat.children) };
          return cat;
        });
      return replace(prev);
    });
    setSelectedCategory(updatedTarget);
    setSelectedCategories([]);
  };

    const canMergeCategories = (): boolean => {
        if (!selectedCategory || selectedCategory.contentCategory) return false;
        const live = findCategoryInTree(categories, selectedCategory.id) ?? selectedCategory;
        if (!branchChildrenAreLoaded(live) || !live.children) return false;
        if (selectedCategories.length < 2 || live.children.length === selectedCategories.length) return false;

       for ( const category of selectedCategories ) {
             if ( !category.contentCategory )
                return false;
       }
       return true;
   };

 const handleMergeCategoriesDialogOpen = () => {
       if (!canMergeCategories()) return;
      setNewName('');
      setNewDescription('');
      setAddError('');
      setMergeCategoriesDialogOpen(true);
  };

  const handleMergeCategoriesDialogClose = () => {
      setMergeCategoriesDialogOpen(false);
      setNewName('');
      setNewDescription('');
      setAddError('');
  };

    const handleMergeCategoriesDialogConfirm = async () => {
       if (!newName.trim()) {
        setAddError('Category name is required');
        return;
      }
      setMergeCategoriesDialogOpen(false);
      await handleMergeCategories();
  };

   const handleMergeCategories = async () => {
      if (!selectedCategory) return;
      const liveParent = findCategoryInTree(categories, selectedCategory.id) ?? selectedCategory;
      if (!liveParent.children) return;
       setLoading(true);

       // Load content rows for each selected category before deleting — stubs look empty.
       const categoriesToMerge: Category[] = [];
       for (const category of selectedCategories) {
         let live = findCategoryInTree(categories, category.id) ?? category;
         if (live.contentCategory && !categoryContentsAreLoaded(live)) {
           const loaded = await loadContentRowsIntoTree(live.id);
           if (!loaded) {
             setLoading(false);
             return;
           }
           live = loaded;
         }
         categoriesToMerge.push(live);
       }

       var mergedCategories: {category: Category, index: number}[] = [];
       for (const category of categoriesToMerge) {
         const index = liveParent.children.findIndex(c => c.id === category.id);
         await deleteCategory(category.id);  // ← Sequential deletion
         mergedCategories.push({category: category, index: index});
       }

      const response = await api.post('/categories', {
        parentId: selectedCategory?.id,
        name: newName,
        description: newDescription
      });

      if (!response.data) throw new Error('Failed to create category');
      const newCategory = response.data;

      historyService.recordOperation({
        type: 'mergeCategories',
        data: {
            parentId: selectedCategory.id,
            newCategory: newCategory,
            mergedCategories: mergedCategories
        }
      });

        const mergedIds = new Set(categoriesToMerge.map((c) => c.id));
        setCategories(prev => {
             const newCategories = [...prev];
             const parent = findCategoryInTree(newCategories, selectedCategory.id);
             if (parent && parent.children) {
               parent.children = [
                 ...(parent.children || []).filter((child) => !mergedIds.has(child.id)),
                 newCategory,
               ];
             }
             return newCategories;
           });


       let aggregatedContent: Content[] = []
       for ( const category of categoriesToMerge) {
            if ( !category.contents ) continue;
            for (const content of category.contents) {
                if ( !aggregatedContent.some(c => c.subcase === content.subcase && c.text === content.text) )
                    aggregatedContent.push(content);
            }
        }

        let newContents: Content[] = [];
        for (const content of aggregatedContent) {
           const response = await api.post(`/content`, {
             ...buildContentCopyPayload(content, newCategory.id),
             skipDuplicateCheck: true,
           });
           newContents.push(response.data);
        }

        setCategories(prev => {
          const newCategories = [...prev];
          const parent = findCategoryInTree(newCategories, newCategory.id);
          if (parent) {
            parent.contents = newContents;
          }
          return newCategories;
        });

        setSelectedCategory(newCategory);
        setLoading(false);

        await refreshActiveDocumentSubcasesFromServer();

   };

  const canRenameCategory = (category: Category | null): boolean => {
    if (!category) return false;

    // Cannot rename root category
    if (!category.parentId) return false;

    return true;
  };

  const canMoveSelectedCategories = (direction: 'up' | 'down'): boolean => {
    if (selectedCategories.length === 0 && selectedContents.length === 0)
        return canMoveCategory(selectedCategory, direction);

    if (selectedContents.length > 0)
        return canMoveContent(direction);

    if (selectedCategories.length === 1)
            return canMoveCategory(selectedCategories[0], direction);

    const parentId = selectedCategories[0].parentId;
    if (!parentId) return false;

    const parent = findParentCategoryInTree(categories, selectedCategories[0].id);

    if (!parent || !parent.children) return false;

    if ( parent.children.length === selectedCategories.length )
        return false;

    // Check if selected categories are consecutive
    const selectedIds = new Set(selectedCategories.map(c => c.id));
    const allIds = parent.children.map(c => c.id);

    // Check if we can move in the requested direction
    const firstSelectedIndex = allIds.indexOf(selectedCategories[0].id);
    const lastSelectedIndex = allIds.indexOf(selectedCategories[selectedCategories.length - 1].id);

    const isNotConsecutive = selectedCategories.length <= Math.abs(lastSelectedIndex - firstSelectedIndex);
    if (isNotConsecutive) return false;

    if (direction === 'up') {
      return Math.min(firstSelectedIndex, lastSelectedIndex) > 0;
    } else {
      return Math.max(firstSelectedIndex, lastSelectedIndex) < allIds.length - 1;
    }
  };

  const canMoveContent = (direction: 'up' | 'down'): boolean => {
    if (isContentPaneSubcaseFilterActive(contentPaneSubcaseFilter)) return false;

    const parentId = selectedContents[0].parentId;
    if (!parentId) return false;


    const parent = findParentCategoryInTree(categories, selectedContents[0].id);
    if (!parent || !parent.contents) return false;

    if ( parent.contents.length === selectedContents.length )
        return false;

    // Check if selected categories are consecutive
    const selectedIds = new Set(selectedContents.map(c => c.id));
    const allIds = parent.contents.map(c => c.id);

    // Check if we can move in the requested direction
    const firstSelectedIndex = allIds.indexOf(selectedContents[0].id);
    const lastSelectedIndex = allIds.indexOf(selectedContents[selectedContents.length - 1].id);

    const isNotConsecutive = selectedContents.length <= Math.abs(lastSelectedIndex - firstSelectedIndex);
    if (isNotConsecutive) return false;

    if (direction === 'up') {
      return Math.min(firstSelectedIndex, lastSelectedIndex) > 0;
    } else {
      return Math.max(firstSelectedIndex, lastSelectedIndex) < allIds.length - 1;
    }
  };

  const canRenameSelectedCategory = (): boolean => {
    return selectedCategories.length === 1 || selectedCategories.length === 0 && !!selectedCategory && !!selectedCategory.parentId;
  };

  const handleMoveSelectedCategories = async (direction: 'up' | 'down') => {
    if (!canMoveSelectedCategories(direction)) return;

    try {
      const parentId = selectedCategories[0].parentId;
      if (!parentId) return;

      const parent = findParentCategoryInTree(categories, parentId);
      if (!parent || !parent.children) return;

      const allIds = [...parent.children].map(c => c.id);
      const selectedIds = selectedCategories.map(c => c.id);

      // Find the indices of the selected categories
      const indices = selectedIds.map(id => allIds.indexOf(id));

      // Move the categories
      if (direction === 'up') {
        // Move up by swapping with the previous category
        indices.forEach(index => {
          if (index > 0) {
            [allIds[index], allIds[index - 1]] = [allIds[index - 1], allIds[index]];
          }
        });
    } else {
        // Move down by swapping with the next category
        indices.reverse().forEach(index => {
          if (index < allIds.length - 1) {
            [allIds[index], allIds[index + 1]] = [allIds[index + 1], allIds[index]];
          }
        });
      }

      // Update the categories list
      setCategories(prev => [...prev]);
    } catch (error) {
      console.error('Error moving categories:', error);
      alert('Failed to move categories');
    }
  };

  const deleteCategory = async (categoryId: string) => {
    try {
      await api.delete('/categories', { params: { categoryId } });
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 401) {
        localStorage.removeItem('token');
        window.location.replace('/login');
        return;
      }
      console.error('Error deleting category:', error);
      throw error;
    }
  };

  const runRestoreCategorySubtreeFromSnapshot = async (
    parentId: string,
    index: number,
    category: Category,
  ) => {
    const response = await api.post('/categories', {
      parentId,
      name: category.name,
      description: category.description ?? '',
    });
    if (!response.data) throw new Error('Failed to create category');
    const newCategory = response.data as Category;
    await api.put(
      '/categories/moveto',
      { categoryId: newCategory.id, moveTo: index },
      {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      },
    );
    if (parentId) {
      const parent = findCategoryInTree(categories, parentId);
      if (parent) {
        if (index >= 0) {
          if (parent.children) {
            parent.children.splice(index, 0, newCategory);
          }
        } else {
          if (parent.children) {
            parent.children.push(newCategory);
          }
        }
        setCategories([...categories]);
      }
    }

    if (
      (category.children && category.children.length > 0) ||
      (category.contents && category.contents.length > 0)
    ) {
      await addSubTree(newCategory, category);
    }
  };

  /** After redo replays APPLY_CATEGORY_OUTLINE, new category/temp ids must replace the matching undo slice. */
  const replaceOutlineUndoGroupAfterRedo = (
    undoSteps: unknown[],
    outlineUndoGroupId: string,
    anchorCategoryId: string,
    apiResult: { createdCategoryIds?: string[]; tempStashCategoryId?: string },
  ) => {
    const createdRaw = apiResult.createdCategoryIds;
    const created = Array.isArray(createdRaw)
      ? createdRaw.filter((x): x is string => typeof x === 'string' && x.length > 0)
      : [];
    const temp =
      typeof apiResult.tempStashCategoryId === 'string' && apiResult.tempStashCategoryId.trim()
        ? apiResult.tempStashCategoryId.trim()
        : '';
    const newSteps: Record<string, unknown>[] = [...created]
      .reverse()
      .map((categoryId) => ({
        type: 'DELETE_CATEGORY',
        categoryId,
        outlineUndoGroupId,
      }));
    if (temp) {
      newSteps.push({
        type: 'UNSTASH_OUTLINE_TEMP',
        anchorCategoryId,
        tempCategoryId: temp,
        outlineUndoGroupId,
      });
    }
    const next: unknown[] = [];
    let inserted = false;
    for (const st of undoSteps) {
      const o = st as Record<string, unknown>;
      if (o?.outlineUndoGroupId === outlineUndoGroupId) {
        if (!inserted) {
          next.push(...newSteps);
          inserted = true;
        }
      } else {
        next.push(st);
      }
    }
    undoSteps.splice(0, undoSteps.length, ...next);
  };

  /** After redo replays SPLIT_CATEGORY, child/temp ids change — refresh the matching UNSPLIT undo step. */
  const replaceSplitUndoStepAfterRedo = (
    undoSteps: unknown[],
    splitUndoGroupId: string,
    apiResult: {
      childIds?: string[];
      firstChildId?: string;
      secondChildId?: string;
      tempChildId?: string;
    },
  ) => {
    const childIds = Array.isArray(apiResult.childIds)
      ? apiResult.childIds.filter((x): x is string => typeof x === 'string' && x.trim().length > 0)
      : [];
    const firstChildId =
      typeof apiResult.firstChildId === 'string' && apiResult.firstChildId.trim()
        ? apiResult.firstChildId.trim()
        : childIds[0] ?? '';
    const secondChildId =
      typeof apiResult.secondChildId === 'string' && apiResult.secondChildId.trim()
        ? apiResult.secondChildId.trim()
        : childIds[1] ?? '';
    const tempChildId =
      typeof apiResult.tempChildId === 'string' && apiResult.tempChildId.trim()
        ? apiResult.tempChildId.trim()
        : '';

    const matchesGroup = (o: Record<string, unknown>) => {
      if (o?.type !== 'UNSPLIT_ASSISTANT_SPLIT') {
        return false;
      }
      const undoGroup =
        typeof o.splitUndoGroupId === 'string' && o.splitUndoGroupId.trim()
          ? o.splitUndoGroupId.trim()
          : '';
      if (undoGroup) {
        return undoGroup === splitUndoGroupId;
      }
      return (
        typeof o.parentContentCategoryId === 'string' &&
        o.parentContentCategoryId === splitUndoGroupId
      );
    };

    for (let i = 0; i < undoSteps.length; i++) {
      const o = undoSteps[i] as Record<string, unknown>;
      if (!matchesGroup(o)) {
        continue;
      }
      const updated: Record<string, unknown> = {
        ...o,
        childIds: childIds.length >= 2 ? [...childIds] : o.childIds,
      };
      if (firstChildId) updated.firstChildId = firstChildId;
      if (secondChildId) updated.secondChildId = secondChildId;
      if (tempChildId) {
        updated.tempChildId = tempChildId;
      } else {
        delete updated.tempChildId;
      }
      undoSteps[i] = updated;
      break;
    }
  };

  const dispatchTreeAssistantHistoryStep = async (
    raw: unknown,
    treeAssistantRedoCtx?: { undoSteps: unknown[] },
  ) => {
    const step = raw as Record<string, unknown>;
    const t = typeof step.type === 'string' ? step.type : '';
    const formHeaders = { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } } as const;

    switch (t) {
      case 'UNSPLIT_ASSISTANT_SPLIT': {
        const body: Record<string, unknown> = {
          parentContentCategoryId: String(step.parentContentCategoryId ?? ''),
        };
        const rawChildIds = step.childIds;
        if (Array.isArray(rawChildIds) && rawChildIds.length >= 2) {
          body.childIds = rawChildIds.filter(
            (x): x is string => typeof x === 'string' && x.trim().length > 0,
          );
        } else {
          body.firstChildId = String(step.firstChildId ?? '');
          body.secondChildId = String(step.secondChildId ?? '');
        }
        const tc = step.tempChildId;
        if (typeof tc === 'string' && tc.trim()) {
          body.tempChildId = tc.trim();
        }
        await api.post('/categories/unsplit-assistant-split', body);
        break;
      }
      case 'UNSTASH_OUTLINE_TEMP': {
        await api.post('/categories/unstash-outline-temp', {
          anchorCategoryId: String(step.anchorCategoryId ?? ''),
          tempCategoryId: String(step.tempCategoryId ?? ''),
        });
        break;
      }
      case 'SPLIT_CATEGORY': {
        const rawSel = step.selectedContentIds;
        const selectedContentIds = Array.isArray(rawSel)
          ? rawSel.filter((x): x is string => typeof x === 'string' && x.trim().length > 0)
          : [];
        const rawChildNames = step.childNames;
        const childNames = Array.isArray(rawChildNames)
          ? rawChildNames.filter((x): x is string => typeof x === 'string' && x.trim().length > 0)
          : [];
        const body: Record<string, unknown> = {
          contentCategoryId: String(step.contentCategoryId ?? ''),
        };
        if (childNames.length >= 2) {
          body.childNames = childNames;
          const byChild = step.contentByChildName;
          if (byChild && typeof byChild === 'object' && !Array.isArray(byChild)) {
            body.contentByChildName = byChild;
          }
        } else {
          body.firstName = String(step.firstName ?? '');
          body.secondName = String(step.secondName ?? '');
          body.selectedContentIds = selectedContentIds;
        }
        const response = await api.post('/categories/split-content-category', body);
        const parentId = String(step.contentCategoryId ?? '').trim();
        const gid =
          typeof step.splitUndoGroupId === 'string' && step.splitUndoGroupId.trim()
            ? step.splitUndoGroupId.trim()
            : parentId;
        if (gid && treeAssistantRedoCtx?.undoSteps) {
          replaceSplitUndoStepAfterRedo(
            treeAssistantRedoCtx.undoSteps,
            gid,
            response.data as {
              childIds?: string[];
              firstChildId?: string;
              secondChildId?: string;
              tempChildId?: string;
            },
          );
        }
        break;
      }
      case 'RESTORE_CATEGORY_SUBTREE': {
        const cat = step.category as Category;
        await runRestoreCategorySubtreeFromSnapshot(
          String(step.parentId ?? ''),
          typeof step.index === 'number' ? step.index : Number(step.index ?? -1),
          cat,
        );
        break;
      }
      case 'DELETE_CATEGORY_FOR_REDO': {
        const pid = String(step.parentId ?? '');
        const nm = String(step.categoryName ?? '');
        const parent = findCategoryInTree(categories, pid);
        const child = parent?.children?.find((c) => c.name === nm);
        if (child) await deleteCategory(child.id);
        break;
      }
      case 'RESTORE_CONTENT': {
        const parentId = String(step.parentId ?? '');
        const contentId = String(step.contentId ?? '');
        const idx = typeof step.index === 'number' ? step.index : Number(step.index ?? 0);
        const body: Record<string, unknown> = {
          parentId,
          text: step.text ?? '',
          subcase: step.subcase ?? '',
          id: contentId,
        };
        if (typeof step.richTextHtml === 'string') body.richTextHtml = step.richTextHtml;
        if (typeof step.richTextDelta === 'string') body.richTextDelta = step.richTextDelta;
        if (typeof step.attachmentsJson === 'string') body.attachmentsJson = step.attachmentsJson;
        const response = await api.post('/content', body);
        if (!response.data) throw new Error('Failed to restore content');
        await api.put(`/content/moveto`, { contentId, moveTo: idx }, formHeaders);
        break;
      }
      case 'DELETE_CONTENT': {
        const contentId = String(step.contentId ?? '');
        if (contentId) await api.delete('/content', { params: { contentId } });
        break;
      }
      case 'DELETE_ALL_CONTENT_IN_CATEGORY': {
        await api.post('/categories/delete-all-content-in-category', {
          contentCategoryId: String(step.contentCategoryId ?? ''),
        });
        break;
      }
      case 'APPLY_CATEGORY_OUTLINE': {
        const rawNodes = step.nodes ?? step.outline;
        const nodes = Array.isArray(rawNodes) ? rawNodes : [];
        const response = await api.post('/categories/apply-category-outline', {
          parentCategoryId: String(step.parentCategoryId ?? ''),
          nodes,
        });
        const gid = step.outlineUndoGroupId;
        if (typeof gid === 'string' && gid && treeAssistantRedoCtx?.undoSteps) {
          replaceOutlineUndoGroupAfterRedo(
            treeAssistantRedoCtx.undoSteps,
            gid,
            String(step.parentCategoryId ?? ''),
            response.data as { createdCategoryIds?: string[]; tempStashCategoryId?: string },
          );
        }
        break;
      }
      case 'DELETE_CATEGORY': {
        const id = String(step.categoryId ?? '');
        if (id) await deleteCategory(id);
        break;
      }
      case 'ADD_CATEGORY': {
        await api.post('/categories', {
          parentId: String(step.parentCategoryId ?? ''),
          name: String(step.name ?? ''),
          description: String(step.description ?? ''),
        });
        break;
      }
      case 'MOVE_CATEGORY': {
        await api.put(
          '/categories/move',
          {
            categoryId: String(step.categoryId ?? ''),
            newParentId: String(step.newParentCategoryId ?? ''),
          },
          formHeaders,
        );
        break;
      }
      case 'MOVE_CONTENT': {
        const payload: Record<string, string> = {
          contentId: String(step.contentId ?? ''),
          newParentId: String(step.newParentCategoryId ?? ''),
        };
        const rc = step.restoreContentId;
        if (typeof rc === 'string' && rc.trim()) payload.newContentId = rc.trim();
        await api.put('/content/move', payload, formHeaders);
        break;
      }
      case 'COPY_CATEGORY': {
        const body: Record<string, string> = {
          categoryId: String(step.categoryId ?? ''),
          newParentCategoryId: String(step.newParentCategoryId ?? ''),
        };
        const nn = step.newName;
        if (typeof nn === 'string' && nn.trim()) {
          body.newName = nn.trim();
        }
        await api.post('/categories/copy-subtree', body);
        break;
      }
      case 'COPY_CONTENT': {
        await api.post('/content/copy', {
          contentId: String(step.contentId ?? ''),
          newParentCategoryId: String(step.newParentCategoryId ?? ''),
        });
        break;
      }
      case 'ORGANIZE_CONTENT_CATEGORY_ONE_LAYER': {
        const body: Record<string, string> = {
          contentCategoryId: String(step.contentCategoryId ?? ''),
        };
        const ctx = step.context;
        if (typeof ctx === 'string' && ctx.trim()) {
          body.context = ctx.trim();
        }
        const plan = step.plan;
        if (plan !== null && plan !== undefined && typeof plan === 'object') {
          body.planJson = JSON.stringify(plan);
        }
        await api.post('/categories/organize-content-one-layer', body);
        break;
      }
      case 'RENAME_CATEGORY': {
        await api.put('/categories', {
          parentId: String(step.categoryId ?? ''),
          name: String(step.newName ?? ''),
          description: String(step.description ?? ''),
        });
        break;
      }
      case 'UPDATE_CATEGORY_DESCRIPTION': {
        await api.put('/categories', {
          parentId: String(step.categoryId ?? ''),
          name: String(step.name ?? ''),
          description: String(step.description ?? ''),
        });
        break;
      }
      case 'RENAME_SUBCASE': {
        const docId = activeDocument?.id;
        if (!docId) throw new Error('No open document for subcase rename');
        await api.put(
          '/documents/subcases/rename',
          {
            oldSubcase: String(step.oldSubcase ?? ''),
            newSubcase: String(step.newSubcase ?? ''),
          },
          { params: { documentId: docId } },
        );
        break;
      }
      default:
        console.warn('Unknown structure builder history step:', t);
    }
  };

  const deleteContent = async (contentId: string) => {
      try {
        await api.delete('/content', { params: { contentId } });
      } catch (error) {
        console.error('Error deleting category:', error);
        throw error;
      }
  };


  const handleDragStart = (category: Category) => {
    setClipboardCategories([]);
    setClipboardCategory(category);
    setIsCutOperation(true);
  };

  const handleDragOver = (category: Category) => {
    // Prevent dropping on itself or its children
    if (clipboardCategory &&
        (category.id === clipboardCategory.id ||
         isDescendant(category, clipboardCategory))) {
      return;
    }
    // The visual feedback is handled by the CategoryTree component
  };


  const handleDropCategory = async (sourceCategory: Category, targetCategory: Category, position: 'before' | 'after' | 'inside', copy: boolean) => {
    try {
     var  newCategory: Category = sourceCategory;
      if (position === 'inside') {
        let target =
          findCategoryInTree(categories, targetCategory.id) ?? targetCategory;
        // Name checks need loaded children — unloaded targets look empty and allow conflicts.
        if (!target.contentCategory && !branchChildrenAreLoaded(target)) {
          const loaded = await loadBranchChildrenIntoTree(target.id);
          if (!loaded) return;
          target = loaded;
        }
        if (
          categoryNameConflictsUnderParent(
            sourceCategory.name,
            target,
            copy ? undefined : sourceCategory.id,
          )
        ) {
          alert(
            'Cannot drop here: name must not match categories already under the target (same rule as Paste).'
          );
          return;
        }
        const parent = findParentCategoryInTree(categories, sourceCategory.id);
        const oldIndex =
          parent?.children?.findIndex((c) => c.id === sourceCategory.id) ?? -1;

        setLoading(true);
        if ( copy ) {
          const response = await api.post(`/categories`, {
            name: sourceCategory.name,
            description: sourceCategory.description,
            parentId: targetCategory.id
          });
          newCategory = response.data;

          // Source may be a lazy stub — fetch the full subtree before cloning.
          const snapshot = await fetchCategoryWithChildren(sourceCategory.id);
          await addSubTree(newCategory, snapshot ?? sourceCategory);
          // Record after clone so undo/redo restores contents and nested categories.
          const categoryForHistory =
            (await fetchCategoryWithChildren(newCategory.id)) ?? newCategory;
          newCategory = categoryForHistory;

          historyService.recordOperation({
              type: 'dropCategory',
              data: {
                category: categoryForHistory,
                oldParentId: null,
                newParentId: categoryForHistory.parentId,
                oldIndex: -1,
                newIndex: -1
              }
            });
        }
        else {
            if (!parent || oldIndex < 0) {
              setLoading(false);
              return;
            }
            const response = await api.put(`/categories/move`, {
              categoryId: sourceCategory.id,
              newParentId: targetCategory.id
            },
            {
              headers: {
                'Content-Type': 'application/x-www-form-urlencoded'
              }
            });

            newCategory = response.data;

            const updatedCategory = await fetchCategoryWithChildren(newCategory.id);
            const categoryForHistory = updatedCategory ?? newCategory;
            newCategory = categoryForHistory;

            historyService.recordOperation({
                type: 'dropCategory',
                data: {
                  category: categoryForHistory,
                  oldCategoryId: sourceCategory.id,
                  oldParentId: parent.id,
                  newParentId: categoryForHistory.parentId,
                  oldIndex,
                  newIndex: -1
                }
              });
        }

        // Re-fetch affected parents from the server so React state matches Redis
        // (avoids stale child lists after move/copy + undo/redo).
        const refreshedTarget = await fetchCategoryForPane(target.id);
        const refreshedOldParent =
          !copy && parent ? await fetchCategoryForPane(parent.id) : null;

        setCategories(prevCategories => {
            let updatedCategories = [...prevCategories];
            if (refreshedOldParent) {
              updatedCategories = updateCategoryInTree(
                updatedCategories,
                refreshedOldParent.id,
                () => refreshedOldParent,
              );
            }
            if (refreshedTarget) {
              updatedCategories = updateCategoryInTree(
                updatedCategories,
                refreshedTarget.id,
                () => refreshedTarget,
              );
            }

            setTimeout(() => {
              setSelectedCategory(newCategory);
              setSelectedCategories([]);
              setExpandedNodes(prev => [...prev, target.id]);
            }, 0);

            return updatedCategories;
        });
        setLoading(false);
    } else {
        // For before/after positions, we need to determine how many positions to move
        const siblings = getSiblings(categories, targetCategory.id);

       const sourceIndex = siblings.findIndex(cat => cat.id === sourceCategory.id);

       let targetIndex = position === 'after' ?
            siblings.findIndex(cat => cat.id === targetCategory.id) :
            siblings.findIndex(cat => cat.id === targetCategory.id) - 1;

       if ( sourceIndex > targetIndex)
            targetIndex++;
        if ( targetIndex < 0 )
            targetIndex = 0;

        if ( sourceIndex === targetIndex )
            return;

        setLoading(true);
        await api.put(`/categories/moveto`,
            {
                categoryId: sourceCategory.id,
                moveTo: targetIndex
            },
            {
              headers: {
                'Content-Type': 'application/x-www-form-urlencoded'
              }
            }
         );

         historyService.recordOperation({
             type: 'moveCategoryTo',
             data: {
               categoryId: sourceCategory.id,
               fromIndex: sourceIndex,
               toIndex: targetIndex
             }
           });

          setCategories(prev => {
             const newCategories = [...prev];
             const parent = findParentCategoryInTree(newCategories, sourceCategory.id);
             if (parent && parent.children) {
               const child = parent.children[sourceIndex];
               parent.children?.splice(sourceIndex, 1);
               parent.children?.splice(targetIndex, 0, child);
             }
             return newCategories;
           });
           setLoading(false);
      }

    } catch (error) {
      setLoading(false);
      if (axios.isAxiosError(error) && error.response?.status === 401) {
        localStorage.removeItem('token');
        window.location.replace('/login');
        return;
      }
      console.error('Error moving category:', error);
    }
  };

  const handleDropCategoriesIntoContent = async (
    sourceCategories: Category[],
    targetContentCategory: Category,
    copy: boolean,
  ) => {
    if (sourceCategories.length < 2 || !targetContentCategory.contentCategory) {
      return;
    }
    if (!copy && !canCutCategoriesLeavingParentValid(sourceCategories)) {
      alert(
        'Cannot cut: the source parent must keep at least two subcategories, unless you cut every subcategory (parent becomes an empty content category).',
      );
      return;
    }
    for (const s of sourceCategories) {
      if (
        categoryNameConflictsUnderParent(
          s.name,
          targetContentCategory,
          copy ? undefined : s.id,
        )
      ) {
        alert(
          'Cannot drop here: each category name must not conflict with siblings already under the target.',
        );
        return;
      }
    }
    try {
      setLoading(true);
      const priorContents = targetContentCategory.contents ?? [];
      const response = await api.post('/categories/absorb-into-content-category', {
        contentCategoryId: targetContentCategory.id,
        sourceCategoryIds: sourceCategories.map((c) => c.id),
        copy,
      });
      const data = response.data as {
        tempCategory?: Category;
        moveRecords?: {
          categoryId: string;
          oldParentId: string;
          oldIndex: number;
          categoryName: string;
        }[];
        copiedTopIds?: string[];
        emptiedBranchParentIds?: string[];
      };
      const updatedTree = await fetchCategoryWithChildren(targetContentCategory.id);
      if (!updatedTree) {
        throw new Error('Could not fetch updated category structure');
      }
      const emptiedBranchParentIds = data.emptiedBranchParentIds ?? [];
      const refreshedEmptiedParents = (
        await Promise.all(
          emptiedBranchParentIds.map((id) => fetchCategoryWithChildren(id)),
        )
      ).filter((c): c is Category => c != null);

      const movedContentsToTemp =
        priorContents.length > 0 && data.tempCategory
          ? priorContents.map((content, index) => ({
              content,
              oldContentId: content.id,
              index,
            }))
          : undefined;

      historyService.recordOperation({
        type: 'absorbIntoContentCategory',
        data: {
          parentId: targetContentCategory.id,
          copy,
          absorbedSourceIds: sourceCategories.map((c) => c.id),
          tempCategory: data.tempCategory ?? null,
          movedContentsToTemp,
          moveRecords: data.moveRecords ?? [],
          copiedTopIds: data.copiedTopIds ?? [],
        },
      });

      setCategories((prev) => {
        let next = [...prev];
        if (!copy) {
          for (const src of sourceCategories) {
            const sourceParent = findParentCategoryInTree(next, src.id);
            if (sourceParent) {
              next = updateCategoryInTree(next, sourceParent.id, (cat) => ({
                ...cat,
                children: (cat.children || []).filter((child) => child.id !== src.id),
              }));
            }
          }
          for (const emptiedParent of refreshedEmptiedParents) {
            next = updateCategoryInTree(next, emptiedParent.id, () => emptiedParent);
          }
        }
        return updateCategoryInTree(next, targetContentCategory.id, () => updatedTree);
      });
      setSelectedCategory(updatedTree);
      setSelectedCategories([]);
      setExpandedNodes((prev) =>
        prev.includes(targetContentCategory.id) ? prev : [...prev, targetContentCategory.id],
      );
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 401) {
        localStorage.removeItem('token');
        window.location.replace('/login');
        return;
      }
      const msg =
        axios.isAxiosError(error) && typeof error.response?.data === 'string'
          ? error.response.data
          : axios.isAxiosError(error) && error.response?.data?.message
            ? String(error.response.data.message)
            : null;
      alert(msg ?? 'Failed to move categories into content category.');
      console.error('Error absorbing categories into content category:', error);
    } finally {
      setLoading(false);
    }
  };

  const getMultiCategoryPasteIntoBranchStructureError = (
    sourceCategories: Category[],
    targetBranch: Category,
  ): string | null => {
    const resolvedTarget =
      resolvePasteTargetCategory(targetBranch) ?? targetBranch;
    if (sourceCategories.length < 2) {
      return 'At least two categories are required.';
    }
    if (resolvedTarget.contentCategory) {
      return 'Target must be a branch category.';
    }
    if (branchChildCount(resolvedTarget) < 2) {
      return 'Target branch must have at least two subcategories.';
    }
    for (const source of sourceCategories) {
      if (source.id === resolvedTarget.id) {
        return 'Cannot paste a category into itself.';
      }
    }
    for (const source of sourceCategories) {
      if (
        sourceCategories.some(
          (other) => other.id !== source.id && isDescendantCategory(source, other),
        )
      ) {
        return 'Cannot paste nested categories together.';
      }
    }
    return null;
  };

  const getMultiCategoryPasteIntoBranchNameError = (
    sourceCategories: Category[],
    targetBranch: Category,
  ): string | null => {
    const resolvedTarget =
      resolvePasteTargetCategory(targetBranch) ?? targetBranch;
    const parentOfTarget = resolvedTarget.parentId
      ? findCategoryInTree(categories, resolvedTarget.parentId)
      : null;
    return validateMultiCategoryPasteIntoBranchNames(
      sourceCategories,
      resolvedTarget,
      parentOfTarget,
    );
  };

  const canDropCategoriesIntoBranch = (
    sourceCategories: Category[],
    targetBranch: Category,
    copy: boolean,
  ): boolean => {
    if (getMultiCategoryPasteIntoBranchStructureError(sourceCategories, targetBranch)) {
      return false;
    }
    if (!copy && !canCutCategoriesLeavingParentValid(sourceCategories)) {
      return false;
    }
    if (getMultiCategoryPasteIntoBranchNameError(sourceCategories, targetBranch)) {
      return false;
    }
    return true;
  };

  const handleDropCategoriesIntoBranch = async (
    sourceCategories: Category[],
    targetBranch: Category,
    copy: boolean,
  ) => {
    let liveTarget =
      findCategoryInTree(categories, targetBranch.id) ?? targetBranch;
    // Name/structure checks need loaded children — unloaded targets look empty.
    if (!liveTarget.contentCategory && !branchChildrenAreLoaded(liveTarget)) {
      const loaded = await loadBranchChildrenIntoTree(liveTarget.id);
      if (!loaded) return;
      liveTarget = loaded;
    }
    const structureError = getMultiCategoryPasteIntoBranchStructureError(
      sourceCategories,
      liveTarget,
    );
    if (structureError) {
      alert(structureError);
      return;
    }
    if (!copy && !canCutCategoriesLeavingParentValid(sourceCategories)) {
      alert(
        'Cannot cut: the source parent must keep at least two subcategories, unless you cut every subcategory (parent becomes an empty content category).',
      );
      return;
    }
    const nameError = getMultiCategoryPasteIntoBranchNameError(
      sourceCategories,
      liveTarget,
    );
    if (nameError) {
      alert(nameError);
      return;
    }
    if (!copy) {
      const docCategoryId = activeDocument?.categoryId;
      if (
        docCategoryId &&
        !sourceCategories.every((c) => {
          const clipPrefix = getCategoryDocumentPrefix(c.id);
          const docPrefix = getCategoryDocumentPrefix(docCategoryId);
          return !clipPrefix || !docPrefix || clipPrefix === docPrefix;
        })
      ) {
        alert('Cut categories can only be pasted in the same document.');
        return;
      }
    }

    try {
      setLoading(true);
      const movePlans = sourceCategories.map((source) => {
        const parent = findParentCategoryInTree(categories, source.id);
        return {
          source,
          oldParentId: parent?.id ?? null,
          oldIndex: parent?.children?.findIndex((c) => c.id === source.id) ?? -1,
        };
      });
      const sourceParentIdsBefore = new Set(
        movePlans.map((p) => p.oldParentId).filter((id): id is string => id != null),
      );

      if (copy) {
        const copiedSources: Category[] = [];
        const copiedTopIds: string[] = [];
        for (const source of sourceCategories) {
          const snapshot = await fetchCategoryWithChildren(source.id);
          if (!snapshot) {
            throw new Error('Could not fetch category to copy');
          }
          copiedSources.push(snapshot);
        }
        for (const snapshot of copiedSources) {
          const created = await cloneCopiedSubtreeUnderCategory(
            snapshot,
            targetBranch,
            { skipHistory: true },
          );
          copiedTopIds.push(created.id);
        }
        historyService.recordOperation({
          type: 'pasteCategoriesIntoBranch',
          data: {
            targetBranchId: targetBranch.id,
            copy: true,
            copiedTopIds,
            copiedSources,
          },
        });
      } else {
        const moveRecords: {
          categoryId: string;
          oldParentId: string;
          oldIndex: number;
          categoryName: string;
        }[] = [];
        for (const plan of movePlans) {
          const response = await api.put(
            '/categories/move',
            {
              categoryId: plan.source.id,
              newParentId: targetBranch.id,
            },
            {
              headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
              },
            },
          );
          const moved = response.data as Category;
          if (plan.oldParentId != null) {
            moveRecords.push({
              categoryId: moved.id,
              oldParentId: plan.oldParentId,
              oldIndex: plan.oldIndex,
              categoryName: plan.source.name,
            });
          }
        }

        historyService.recordOperation({
          type: 'pasteCategoriesIntoBranch',
          data: {
            targetBranchId: targetBranch.id,
            copy: false,
            moveRecords,
          },
        });

        const emptiedParentIds: string[] = [];
        for (const parentId of Array.from(sourceParentIdsBefore)) {
          const parentInTree = findCategoryInTree(categories, parentId);
          if (!parentInTree?.children) continue;
          const remaining = parentInTree.children.filter(
            (child) => !sourceCategories.some((s) => s.id === child.id),
          );
          if (remaining.length === 0) {
            emptiedParentIds.push(parentId);
          }
        }
        const refreshedEmptiedParents = (
          await Promise.all(
            emptiedParentIds.map((id) => fetchCategoryWithChildren(id)),
          )
        ).filter((c): c is Category => c != null);

        const updatedTarget = await fetchCategoryWithChildren(targetBranch.id);
        if (!updatedTarget) {
          throw new Error('Could not fetch updated target branch');
        }

        setCategories((prev) => {
          let next = [...prev];
          for (const source of sourceCategories) {
            const sourceParent = findParentCategoryInTree(next, source.id);
            if (sourceParent) {
              const remainingChildren = (sourceParent.children || []).filter(
                (child) => child.id !== source.id,
              );
              if (remainingChildren.length === 0) {
                const refreshed = refreshedEmptiedParents.find(
                  (p) => p.id === sourceParent.id,
                );
                if (refreshed) {
                  next = updateCategoryInTree(next, sourceParent.id, () => refreshed);
                } else {
                  next = updateCategoryInTree(next, sourceParent.id, (cat) => ({
                    ...cat,
                    contentCategory: true,
                    children: [],
                  }));
                }
              } else {
                next = updateCategoryInTree(next, sourceParent.id, (cat) => ({
                  ...cat,
                  children: remainingChildren,
                }));
              }
            }
          }
          return updateCategoryInTree(next, targetBranch.id, () => updatedTarget);
        });
        setSelectedCategory(updatedTarget);
        setExpandedNodes((prev) =>
          prev.includes(targetBranch.id) ? prev : [...prev, targetBranch.id],
        );
      }

      if (copy) {
        const updatedTarget = await fetchCategoryWithChildren(targetBranch.id);
        if (updatedTarget) {
          setCategories((prev) =>
            updateCategoryInTree(prev, targetBranch.id, () => updatedTarget),
          );
          setSelectedCategory(updatedTarget);
          setExpandedNodes((prev) =>
            prev.includes(targetBranch.id) ? prev : [...prev, targetBranch.id],
          );
        }
      }

      setSelectedCategories([]);
      await refreshActiveDocumentSubcasesFromServer();
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 401) {
        localStorage.removeItem('token');
        window.location.replace('/login');
        return;
      }
      console.error('Error pasting categories into branch:', error);
      alert(
        'Failed to paste categories: ' +
          (error instanceof Error ? error.message : 'Unknown error'),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDropContents = async (contents: Content[], targetCategory: Category, position: 'before' | 'after' | 'inside', copy: boolean) => {
    if (position === 'inside') {
        let target =
          findCategoryInTree(categories, targetCategory.id) ?? targetCategory;
        const oldParent = findParentCategoryInTree(categories, contents[0].id);
        if (target.contentCategory && !categoryContentsAreLoaded(target)) {
          const loaded = await loadContentRowsIntoTree(target.id);
          if (!loaded) return;
          target = loaded;
        }
        const duplicates = contents.filter(content =>
            hasDuplicateContent(target, content.subcase, content.text, content.id, content.richTextHtml)
        );

        if (duplicates.length > 0) {
            const duplicateNames = duplicates.map(c => formatDuplicateContentLabel(c)).join(', ');
            const message = `The following contents already exist in this category: ${duplicateNames}. Please modify the subcases or choose a different category.`;
            alert(message);
            return;
        }

        let newContents: Content[] = [];
        let response = null;

        const operationData: { content: Content;
                oldContentId: string;
                oldParentId: string;
                oldIndex: number;
               }[] = [];

        let sortedContext: {content: Content, index: number}[] = [];
        for ( const content of contents ) {
             const index = oldParent!.contents!.findIndex(c => c.id === content.id) ?? -1;
             sortedContext.push({content, index});
        }
        sortedContext.sort((a, b) => a.index - b.index);
        setLoading(true);
        for ( const {content, index} of sortedContext ) {
              const oldContentId = content.id;

            if (!copy) {
              response = await api.put(`/content/move`, {
                contentId:  content.id,
                newParentId: targetCategory.id
              },
              {
                headers: {
                  'Content-Type': 'application/x-www-form-urlencoded'
                }
              });
            } else {
              response = await api.post(`/content`, buildContentCopyPayload(content, targetCategory.id));
            }
            const newContent = response.data;
            newContents.push(newContent);
            operationData.push({content: newContent, oldContentId: oldContentId, oldParentId: oldParent!.id, oldIndex: index});
        }

        setLoading(false);
        historyService.recordOperation({
                type: 'dropContent',
                data: operationData,
                isBatch: !copy
              });

       setCategories(prevCategories => {
           let updatedCategories = [...prevCategories];

           // If it's a cut operation, remove the category from its original location
           if (!copy) {
             const movedIds = new Set(contents.map(c => c.id));
             const originalParent = findParentCategoryInTree(updatedCategories, contents[0].id);
             if (originalParent) {
               // Update the original parent to remove the moved rows.
               // Match by id (not object identity): a stale `contents` selection
               // — e.g. references left over after an undo re-created the rows —
               // would otherwise fail to match and leave a phantom row behind,
               // which then duplicates when the next undo re-inserts it.
               const updatedOriginalParent = {
                 ...originalParent,
                 contents: (originalParent.contents || []).filter(child => !movedIds.has(child.id))
               };
               // Update the tree with the modified original parent
               updatedCategories = updateCategoryInTree(updatedCategories, originalParent.id, () => updatedOriginalParent);
             }
           }

           // Update the target parent with the new category
           const liveTarget =
             findCategoryInTree(updatedCategories, target.id) ?? target;
           const updatedTargetParent = {
             ...liveTarget,
             contents: [...(liveTarget.contents || []), ...newContents]
           };

           // Update the tree with the modified target parent
           const newCategories = updateCategoryInTree(updatedCategories, target.id, () => updatedTargetParent);

           const updatedTarget = findCategoryInTree(newCategories, target.id);
          setTimeout(() => {
              if (updatedTarget) {
                setSelectedCategory(updatedTarget);
                selectedCategoryRef.current = updatedTarget;
              }
            }, 0);


           return newCategories;
         });

      if (!copy) {
        // The moved rows no longer live in the source, so drop them from the
        // current selection. This stops the editor from showing a stale row and
        // ensures a subsequent drag operates on fresh content references.
        const movedIds = new Set(contents.map(c => c.id));
        setSelectedContents(prev => prev.filter(c => !movedIds.has(c.id)));
        setCurrentContent(prev => (prev && movedIds.has(prev.id) ? null : prev));
      }

      await refreshActiveDocumentSubcasesFromServer();

    }
  };

  const handleDropCategoriesBetween = async (cats: Category[], targetCategory: Category, position: 'before' | 'after' | 'inside') => {
      if ( !targetCategory || !targetCategory.parentId ) return;
      const parent = findCategoryInTree(categories, targetCategory.parentId);
      if (!parent || !parent.children) return;
      const siblings = parent.children.map(c => c.id);

      const selectedIds = cats.map(c => c.id);

            // Find the indices of the selected categories
      const indices = selectedIds.map(id => siblings.indexOf(id)).sort((a, b) => a - b);

      const firstIndex = indices[0];
      const lastIndex = indices[indices.length - 1]
      const isNotConsecutive = selectedIds.length <= Math.abs(lastIndex - firstIndex);
      if (isNotConsecutive) return false;

      let targetIndex = position === 'after' ?
             siblings.findIndex(id => id === targetCategory.id) :
             siblings.findIndex(id => id === targetCategory.id) - 1;

      if ( targetIndex < firstIndex)
          targetIndex++;

      if ( indices.some(c => c === targetIndex ) )
         return;

      setLoading(true);
      await api.put(`/categories/movechildto`, {
         categoryId: parent.id,
         firstIndex,
         lastIndex,
         moveToIndex: targetIndex
       },
       {
         headers: {
           'Content-Type': 'application/x-www-form-urlencoded'
         }
       });

      setLoading(false);

      historyService.recordOperation({
          type: 'dragCategoryTo',
          data: {
            parentId: parent.id,
            firstIndex: firstIndex,
            lastIndex: lastIndex,
            toIndex: targetIndex
          }
        });
       const updatedCategory = await fetchCategoryWithChildren(parent.id);

       setCategories(prevCategories => {
         const updatedCategories = [...prevCategories];
         return updateCategoryInTree(updatedCategories, parent.id, () => updatedCategory);
       });

       setTimeout(() => {
            setSelectedCategory(updatedCategory);
            //setSelectedCategories([]);
            setExpandedNodes(prev => [...prev, updatedCategory.id]);
          }, 0);
     };

   const handleDropContentsBetween = async (contents: Content[], targetContent: Content, position: 'before' | 'after' | 'inside') => {
        if (isContentPaneSubcaseFilterActive(contentPaneSubcaseFilter)) return;

        const parent = findCategoryInTree(categories, targetContent.parentId);
        if (!parent || !parent.contents) return;
        const siblings = parent.contents.map(c => c.id);

        const selectedIds = contents.map(c => c.id);

              // Find the indices of the selected categories
        const indices = selectedIds.map(id => siblings.indexOf(id)).sort((a, b) => a - b);

        const firstIndex = indices[0];
        const lastIndex = indices[indices.length - 1]
        const isNotConsecutive = selectedIds.length <= Math.abs(lastIndex - firstIndex);
        if (isNotConsecutive) return false;

        let targetIndex = position === 'after' ?
               siblings.findIndex(id => id === targetContent.id) :
               siblings.findIndex(id => id === targetContent.id) - 1;

        if ( targetIndex < firstIndex)
            targetIndex++;

        if ( indices.some(c => c === targetIndex ) )
           return;

        setLoading(true);
        await api.put(`/categories/movechildto`, {
           categoryId: parent.id,
           firstIndex,
           lastIndex,
           moveToIndex: targetIndex
         },
         {
           headers: {
             'Content-Type': 'application/x-www-form-urlencoded'
           }
         });

        setLoading(false);

        historyService.recordOperation({
            type: 'dragContentTo',
            data: {
              parentId: parent.id,
              firstIndex: firstIndex,
              lastIndex: lastIndex,
              toIndex: targetIndex
            }
          });
         const updatedCategory = await fetchCategoryWithChildren(parent.id);

         setCategories(prevCategories => {
           const updatedCategories = [...prevCategories];
           return updateCategoryInTree(updatedCategories, parent.id, () => updatedCategory);
         });

         setTimeout(() => {
              setSelectedCategory(updatedCategory);
              //setSelectedCategories([]);
              setExpandedNodes(prev => [...prev, updatedCategory.id]);
            }, 0);
   };

  // Helper function to check if a category is a descendant of another
  const isDescendant = (parent: Category, child: Category): boolean => {
    if (!parent.children) return false;
    return parent.children.some(cat =>
      cat.id === child.id || isDescendant(cat, child)
    );
  };

  const handleResizeStart = (e: React.MouseEvent) => {
    resizeStartY.current = e.clientY;
    resizeStartHeight.current = contentPanelHeight;
    document.addEventListener('mousemove', handleResizeMove);
    document.addEventListener('mouseup', handleResizeEnd);
  };

  const handleResizeMove = (e: MouseEvent) => {
    const delta = resizeStartY.current - e.clientY;
    const newHeight = Math.max(100, Math.min(window.innerHeight - 200, resizeStartHeight.current + delta));
    setContentPanelHeight(newHeight);
  };

  const handleResizeEnd = () => {
    document.removeEventListener('mousemove', handleResizeMove);
    document.removeEventListener('mouseup', handleResizeEnd);
  };

  const handleRightPaneResizerMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    isRightPaneDraggingRef.current = true;
  };

  const handleContentSave = async (
    subcase: string,
    text: string,
    richTextHtml: string,
    richTextDelta: string,
    stagedMediaIds: string[] = [],
  ) => {
    if (!selectedCategory) {
      console.error('No category selected');
      return;
    }

    try {
      const editingId =
        currentContent?.id?.trim() ||
        (selectedContents.length === 1 ? selectedContents[0]?.id : undefined);
      // History needs the pre-edit snapshot; currentContent is updated live while editing.
      const prevContent = contentRowBeforeSave(editingId);
      setLoading(true);
      const payload: Record<string, unknown> = {
        parentId: selectedCategory.id,
        text,
        richTextHtml,
        richTextDelta,
        subcase,
        id: prevContent?.id,
      };
      if (!prevContent?.id && stagedMediaIds.length > 0) {
        payload.stagedMediaIds = stagedMediaIds;
      }
      const response = await api.post('/content', payload);

      if (response.status === 200) {
        const newContent = response.data;
        if (selectedContents.length === 0) {
          historyService.recordOperation({
            type: 'addContent',
            data: {
              content: newContent,
              categoryId: selectedCategory.id
            }
          });
        } else {
          historyService.recordOperation({
            type: 'updateContent',
            data: {
              content: newContent,
              prevContent: prevContent,
              categoryId: selectedCategory.id
            }
          });
        }

        // Refresh the selected category's contents
        const updatedCategory = await fetchCategoryWithChildren(selectedCategory.id);

        setCategories(prevCategories => {
          const updatedCategories = [...prevCategories];
          return updateCategoryInTree(updatedCategories, selectedCategory.id, () => updatedCategory);
        });
        setLoading(false);

        // Update the selected category to show the new content
        setSelectedCategory(updatedCategory);
        // For edits: keep the saved content visible in the editor to match the current selection.
        // For new content: do not auto-select it after adding.
        if (prevContent?.id) {
          const savedContentId = newContent?.id;
          const updatedSavedContent =
            savedContentId && updatedCategory?.contents
              ? updatedCategory.contents.find((c: any) => c.id === savedContentId) || newContent
              : newContent;
          setSelectedContents(updatedSavedContent ? [updatedSavedContent] : []);
          setCurrentContent(updatedSavedContent || null);
        } else {
          setSelectedContents([]);
          setCurrentContent(null);
          setSelectedSubcase(subcase);
          setContentEditorResetNonce(n => n + 1);
        }
        setSubcases([]);
        setIsEditingContent(false);
        setShowEditorWarning(false); // Clear the warning banner

        await refreshActiveDocumentSubcasesFromServer();
        if (prevContent?.id) {
          setSelectedSubcase(subcase);
        }

        // If a navigation/tab-close action was blocked by unsaved edits, resume it now.
        if (pendingActionRef.current) {
          // Pending action will be resumed by the useEffect once isEditingContent=false is committed.
        }
      }
    } catch (error) {
      setLoading(false);
      console.error('Error saving content:', error);
      alert('Failed to save content');
    }
  };

  const handleContentCancel = () => {
    // If cancelling a brand-new (unsaved) content draft, clear it so "Add" starts fresh next time.
    // If cancelling an edit of an existing content, revert to the selected content (read-only view).
    if (currentContent && !currentContent.id) {
      setCurrentContent(null);
      setSelectedContents([]);
      setContentEditorResetNonce(n => n + 1);
    } else {
      const reverted = selectedContents.length === 1 ? selectedContents[0] : null;
      setCurrentContent(reverted);
      // Force the editor to reset its internal state back to the selected content.
      setContentEditorResetNonce(n => n + 1);
    }
    setIsEditingContent(false);
    setShowEditorWarning(false); // Clear the warning banner

    // If a navigation/tab-close action was blocked by unsaved edits, resume it now.
    if (pendingActionRef.current) {
      // Pending action will be resumed by the useEffect once isEditingContent=false is committed.
    }
  };

  const refreshCurrentContentAttachmentsFromServer = useCallback(async () => {
    const cid = currentContent?.id;
    const catId = selectedCategory?.id;
    if (!cid || !catId) return;
    try {
      const updatedCategory = await fetchCategoryWithChildren(catId);
      setCategories((prevCategories) => {
        const updatedCategories = [...prevCategories];
        return updateCategoryInTree(updatedCategories, catId, () => updatedCategory);
      });
      setSelectedCategory(updatedCategory);
      const findInTree = (cat: Category): Content | null => {
        if (cat.contents?.length) {
          const hit = cat.contents.find((item: Content) => item.id === cid);
          if (hit) return hit;
        }
        if (cat.children?.length) {
          for (const child of cat.children) {
            const nested = findInTree(child);
            if (nested) return nested;
          }
        }
        return null;
      };
      const updated = findInTree(updatedCategory);
      if (updated) {
        setCurrentContent(updated);
        setSelectedContents([updated]);
      }
    } catch (e) {
      console.error('Failed to refresh content after media change', e);
    }
  }, [currentContent?.id, selectedCategory?.id]);

  // Add handlers for unsaved changes confirmation dialog
  const handleUnsavedChangesConfirm = () => {
    if (unsavedChangesDialog.action) {
      unsavedChangesDialog.action();
    }
    setUnsavedChangesDialog({ open: false, action: null, message: '' });
  };

  const handleUnsavedChangesCancel = () => {
    setUnsavedChangesDialog({ open: false, action: null, message: '' });
  };

  const checkForUnsavedChanges = (action: () => void, message: string) => {
    if (effectiveDocumentReadOnly) {
      if (isEditingContent) {
        setIsEditingContent(false);
        setShowEditorWarning(false);
        setCurrentContent(
          selectedContents.length === 1 ? selectedContents[0] : null,
        );
        setContentEditorResetNonce((n) => n + 1);
      }
      action();
      return;
    }
    if (isEditingContent) {
      setShowEditorWarning(true);
      // Remember the user's intended action (close tab, switch tab, etc.) and resume it
      // after they Save or Cancel in the editor.
      _setPendingAction(action);
    } else {
      action();
    }
  };

  const ownedDocumentNames = useMemo(
    () => documents.map((d) => d.name).filter((n): n is string => Boolean(n?.trim())),
    [documents],
  );

  const contentSubcasesForAddEdit = useMemo(() => {
    const docSubcases = activeDocument?.subcases;
    if (!docSubcases?.length) return [];
    if (!isContentPaneSubcaseFilterActive(contentPaneSubcaseFilter)) {
      return [...docSubcases];
    }
    const filterSet = new Set(contentPaneSubcaseFilter!);
    return docSubcases.filter(s => filterSet.has(s));
  }, [activeDocument?.subcases, contentPaneSubcaseFilter]);

  const contentEditorSubcases = useMemo(() => {
    const base = contentSubcasesForAddEdit;
    const selectedOneSubcase =
      selectedContents.length === 1 ? selectedContents[0]?.subcase?.trim() : '';
    const cur = currentContent?.subcase?.trim() || selectedOneSubcase;
    if (cur && !base.includes(cur)) {
      return [...base, cur];
    }
    return base;
  }, [contentSubcasesForAddEdit, currentContent?.subcase, selectedContents]);

  const canAddContent = (): boolean => {
    if (effectiveDocumentReadOnly) return false;
    if ( isEditingContent ) return false;
    if (selectedContents.length > 1)
        return false;
    if (!selectedCategory) return false;
    return selectedCategory.contentCategory;
  };

   const canEditContent = (): boolean => {
      if (effectiveDocumentReadOnly) return false;
      if ( isEditingContent ) return false;
     return selectedContents.length === 1;
    };

  const selectedContentsInPaneOrder = useMemo(() => {
    if (selectedContents.length <= 1) return [];
    const pool = selectedCategory?.contents ?? [];
    const idSet = new Set(selectedContents.map((c) => c.id));
    return pool.filter((c) => idSet.has(c.id));
  }, [selectedCategory?.contents, selectedContents]);

  const selectedContentPreviewIndex = useMemo(() => {
    if (!currentContent?.id || selectedContentsInPaneOrder.length === 0) return -1;
    return selectedContentsInPaneOrder.findIndex((c) => c.id === currentContent.id);
  }, [currentContent?.id, selectedContentsInPaneOrder]);

  const contentPreviewDisabled = useMemo(() => {
    if (isEditingContent) return false;
    const c =
      currentContent ?? (selectedContents.length === 1 ? selectedContents[0] : null);
    if (!c) return true;
    return !rawStringHasPreviewableBody(c.richTextHtml || c.text);
  }, [isEditingContent, selectedContents, currentContent]);

  const openContentPreview = useCallback(() => {
    const fromEditor = contentEditorRef.current?.getRichHtmlForPreview?.();
    const raw =
      typeof fromEditor === 'string' && fromEditor.trim().length > 0
        ? fromEditor
        : (currentContent?.richTextHtml || currentContent?.text || '');
    setContentPreviewHtml(raw);
    setContentPreviewOpen(true);
  }, [currentContent?.richTextHtml, currentContent?.text]);

  const handleAddContent = async () => {
    if (effectiveDocumentReadOnly || !canAddContent()) return;
    setIsEditingContent(true);
    if (selectedContents.length === 1) {
      const selectedContent = selectedContents[0];
      setCurrentContent(selectedContent);
      setSelectedSubcase(selectedContent.subcase ?? '');
    } else {
      let initialText = '';
      let initialHtml: string | undefined = undefined;
      if (autoPasteEnabled) {
        try {
          // Prefer rich HTML when available (e.g. Word/Google Docs), otherwise fall back to plain text.
          const clipboard = navigator.clipboard as any;
          if (clipboard?.read) {
            const items: ClipboardItem[] = await clipboard.read();
            for (const item of items) {
              if (item.types?.includes('text/html')) {
                const blob = await item.getType('text/html');
                initialHtml = await blob.text();
                break;
              }
            }
          }

          if (!initialHtml) {
            const clipboardText = await navigator.clipboard.readText();
            if (clipboardText) {
              initialText = clipboardText;
            }
          }
        } catch (error) {
          console.error('Failed to read clipboard:', error);
        }
      }
      const allowed = contentSubcasesForAddEdit;
      const initialSub =
        selectedSubcase && allowed.includes(selectedSubcase) ? selectedSubcase : allowed[0] ?? '';
      setSelectedSubcase(initialSub);
      setCurrentContent({
        id: '',
        parentId: selectedCategory?.id || '',
        subcase: initialSub,
        text: initialText || (initialHtml ? initialHtml.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim() : ''),
        richTextHtml: initialHtml,
        richTextDelta: undefined,
      });
    }
    setSubcases(contentEditorSubcases);
  };

  const handleDeleteContent = async () => {
    if (!selectedCategory || !selectedContents.length) return;

    try {
      setLoading(true);
      const deletingIds = new Set(selectedContents.map(c => c.id));
      // Record the operation before deleting
      historyService.recordOperation({
        type: 'deleteContent',
        data: selectedContents.map(content => ({
          content,
          categoryId: selectedCategory.id,
          index: selectedCategory.contents!.findIndex(c => c.id === content.id) ?? -1
        })),
        // One undo step per delete action; true only when multiple rows removed together.
        isBatch: selectedContents.length > 1
      });

      for (const content of selectedContents) {
        await api.delete('/content', { params: { contentId: content.id } });
      }

      await refreshActiveDocumentSubcasesFromServer();

      setCategories(prev => {
        const newCategories = [...prev];
        const category = findCategoryInTree(newCategories, selectedCategory.id);
        if (category) {
          category.contents = category.contents?.filter(
            content => !selectedContents.some(selected => selected.id === content.id)
          );
        }
        return newCategories;
      });
      setLoading(false);
      setSelectedContents([]);
      if (currentContent?.id && deletingIds.has(currentContent.id)) {
        setCurrentContent(null);
      }
    } catch (error) {
      setLoading(false);
      console.error('Error deleting content:', error);
      alert('Failed to delete content');
    }
  };

  const handleAddCategoryDialogClose = () => {
    setAddCategoryDialogOpen(false);
    setNewName('');
    setNewDescription('');
    setAddError('');
  };

  const handleAddCategoryDialogConfirm = async () => {
    if (!newName.trim()) {
      setAddError('Category name is required');
      return;
    }
    await handleAddCategory();
    setAddCategoryDialogOpen(false);
  };

  const handleAddCategoryDialogOpen = () => {
    if (!selectedCategory) return;
    setNewName('');
    setNewDescription('');
    setAddError('');
    setAddCategoryDialogOpen(true);
  };

  const handleDeleteSelectedCategory = async () => {
    const categoriesToDelete =
      selectedCategories.length > 0
        ? [...selectedCategories]
        : selectedCategory
          ? [selectedCategory]
          : [];
    if (categoriesToDelete.length === 0 || !canDeleteSelectedCategories()) return;

    try {
      setLoading(true);
      const parentIds = new Set<string>();
      const deletePlans: {
        category: Category;
        parentId: string | null;
        parentName: string | null;
        index: number;
      }[] = [];

      for (const cat of categoriesToDelete) {
        const snapshot = await fetchCategoryWithChildren(cat.id);
        if (!snapshot) {
          throw new Error('Could not fetch category before delete');
        }
        const parent = findParentCategoryInTree(categories, cat.id);
        const parentId = parent?.id ?? null;
        if (parentId) {
          parentIds.add(parentId);
        }
        deletePlans.push({
          category: snapshot,
          parentId,
          parentName: parent?.name ?? null,
          index: parent?.children?.findIndex((c) => c.id === cat.id) ?? -1,
        });
      }

      historyService.recordOperation({
        type: 'deleteCategory',
        data: deletePlans.map((plan) => ({
          category: plan.category,
          parentId: plan.parentId,
          parentName: plan.parentName,
          index: plan.index,
        })),
        isBatch: deletePlans.length > 1,
      });

      for (const plan of deletePlans) {
        await deleteCategory(plan.category.id);
      }

      const refreshedParents = (
        await Promise.all(
          Array.from(parentIds).map((id) => fetchCategoryWithChildren(id)),
        )
      ).filter((c): c is Category => c != null);

      setCategories((prev) => {
        let next = [...prev];
        for (const parent of refreshedParents) {
          next = updateCategoryInTree(next, parent.id, () => parent);
        }
        return next;
      });

      const primaryParentId = deletePlans[0]?.parentId;
      const refreshedPrimaryParent = primaryParentId
        ? refreshedParents.find((p) => p.id === primaryParentId) ?? null
        : null;
      setSelectedCategory(refreshedPrimaryParent);
      setSelectedCategories([]);
      setExpandedNodes((prev) =>
        primaryParentId && !prev.includes(primaryParentId)
          ? [...prev, primaryParentId]
          : prev,
      );
      await refreshActiveDocumentSubcasesFromServer();
    } catch (error) {
      console.error('Error deleting category:', error);
      alert('Failed to delete category');
    } finally {
      setLoading(false);
    }
  };

  // Reset open tabs when the authenticated user changes (e.g. impersonation).
  useEffect(() => {
    setTabs([]);
    setCurrentTab(null);
    setCategories([]);
    setSelectedCategory(null);
    setCurrentDocument(undefined);
    setExpandedNodes([]);
    adminPendingDocumentOpenedRef.current = false;
  }, [user?.id]);

  // Admin console: open a specific document after read-only impersonation.
  useEffect(() => {
    if (!user?.readOnlyImpersonation || adminPendingDocumentOpenedRef.current) {
      return;
    }
    const pendingName = sessionStorage.getItem(ADMIN_PENDING_DOCUMENT_NAME_KEY)?.trim();
    if (!pendingName || documents.length === 0) {
      return;
    }
    const doc = documents.find(
      (d) => d.name === pendingName || d.id === pendingName,
    );
    if (!doc?.categoryId) {
      // Wait for the impersonated user's library to load; do not clear the pending name yet.
      return;
    }
    adminPendingDocumentOpenedRef.current = true;
    sessionStorage.removeItem(ADMIN_PENDING_DOCUMENT_NAME_KEY);
    void handleOpenDocument(doc);
  }, [user?.readOnlyImpersonation, user?.id, documents]);

  const getUserInitials = () => {
    if (!user) return 'U';
    if (user.firstName && user.lastName) {
      return `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`.toUpperCase();
    } else if (user.firstName) {
      return user.firstName.charAt(0).toUpperCase();
    }
    return user.username.charAt(0).toUpperCase();
  };

  useEffect(() => {
    if (!user) return;

    const fetchAvatar = async () => {
      try {
        // Only fetch avatar if user.avatarUrl is a relative path
        if (user.avatarUrl && user.avatarUrl.startsWith('/')) {
          const response = await api.get('/user/avatar', {
            responseType: 'blob',
            headers: {
              'Cache-Control': 'no-cache',
              'Pragma': 'no-cache'
            }
          });

          const blob = new Blob([response.data], { type: response.headers['content-type'] || 'image/png' });
          const dataUrl = URL.createObjectURL(blob);
          setAvatarDataUrl(dataUrl);
        } else if (user.avatarUrl) {
          // If it's a full URL (like Google profile), use it directly
          setAvatarDataUrl(null);
        }
      } catch (error) {
        console.error('Error fetching avatar:', error);
        setAvatarDataUrl(null);
      }
    };

    fetchAvatar();

    return () => {
      if (avatarDataUrl) {
        URL.revokeObjectURL(avatarDataUrl);
      }
    };
  }, [user?.id]);  // Only depend on user ID

  const getAvatarUrl = () => {
    // If user has a full URL (like Google profile), use it directly
    if (user?.avatarUrl && !user.avatarUrl.startsWith('/')) {
      return user.avatarUrl;
    }
    // Otherwise use the blob URL if we have one
    return avatarDataUrl || undefined;
  };

  const handleAvatarChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    // The backend binds this part via @RequestParam("file").
    formData.append('file', file);

    try {
      // Close the menu immediately
      setAvatarMenuAnchorEl(null);

      await api.post('/user/avatar', formData);

      // Fetch the new avatar immediately
      const response = await api.get('/user/avatar', {
        responseType: 'blob',
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });

      const blob = new Blob([response.data], { type: response.headers['content-type'] || 'image/png' });
      const dataUrl = URL.createObjectURL(blob);
      setAvatarDataUrl(dataUrl);

      // Refresh user details in the background
      fetchUserDetails();
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Error uploading avatar:', error.response?.data);
      } else {
        console.error('Error uploading avatar:', error);
      }
    }
  };

  useEffect(() => {
    const fetchPredefinedAvatars = async () => {
      const newDataUrls: {[key: string]: string} = {};
      for (const avatar of PREDEFINED_AVATARS) {
        try {
          const response = await api.get(`/avatars/${avatar.url.split('/').pop()}`, {
            responseType: 'blob'
          });
          const blob = new Blob([response.data], { type: response.headers['content-type'] });
          newDataUrls[avatar.id] = URL.createObjectURL(blob);
        } catch (error) {
          console.error(`Error fetching avatar ${avatar.id}:`, error);
        }
      }
      setPredefinedAvatarDataUrls(newDataUrls);
    };

    fetchPredefinedAvatars();
  }, []);

  const handlePredefinedAvatarSelect = async (avatarUrl: string) => {
    try {
      // Close the menu immediately
      setAvatarMenuAnchorEl(null);

      const filename = avatarUrl.split('/').pop();
      await api.post('/user/avatar/predefined', { avatarUrl: filename });

      // Fetch the new avatar immediately
      const response = await api.get('/user/avatar', {
        responseType: 'blob',
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });

      const blob = new Blob([response.data], { type: response.headers['content-type'] || 'image/png' });
      const dataUrl = URL.createObjectURL(blob);
      setAvatarDataUrl(dataUrl);

      // Refresh user details in the background
      fetchUserDetails();
    } catch (error) {
      console.error('Error setting predefined avatar:', error);
    }
  };

  const handleAutoPasteChange = (enabled: boolean) => {
    setAutoPasteEnabled(enabled);
  };

  const handleToolbarDockMagnifyChange = (enabled: boolean) => {
    setToolbarDockMagnify(enabled);
  };

  const handleWorkspaceChromeChange = (value: WorkspaceChrome) => {
    setWorkspaceChrome(value);
    writeWorkspaceChrome(value);
  };

  const handleShowContentIndexInPaneChange = (enabled: boolean) => {
    setShowContentIndexInPane(enabled);
    localStorage.setItem('showContentIndexInPane', JSON.stringify(enabled));
  };

  const handleClickToRenameCategoryInTreeChange = (enabled: boolean) => {
    setClickToRenameCategoryInTree(enabled);
    localStorage.setItem('clickToRenameCategoryInTree', JSON.stringify(enabled));
  };

  const handleMergeDocument = async (
    documentId: string,
    mergeFromDocumentId: string,
    mergeFromReadAccessGrantId: number | null,
    newName: string,
    resolveMissing: MergeMissingResolve,
    resolveTypeMismatch: MergeTypeMismatchResolve,
  ) => {
    try {
      setLoading(true);
      const mergedDocument = await mergeDocument(
        documentId,
        mergeFromDocumentId,
        mergeFromReadAccessGrantId,
        newName,
        resolveMissing,
        resolveTypeMismatch,
      );

      // Fetch subcases for the merged document before opening it
      if (mergedDocument.categoryId) {
        const response = await api.get('/documents/subcases', { params: { documentId: mergedDocument.categoryId } });
        mergedDocument.subcases = response.data;
      }

      setLoading(false);

      // When merging into a NEW document name, ensure we don't restore a stale/empty expanded-state
      // and instead default-expand the root on first open.
      if (newName && newName.trim()) {
        setDocumentExpandedStates(prev => {
          const next = { ...prev };
          delete next[mergedDocument.id];
          return next;
        });
        setDocumentSelectedCategoryIds(prev => {
          const next = { ...prev };
          delete next[mergedDocument.id];
          return next;
        });
        const nextCategoryTrees = { ...documentCategoryTreesRef.current };
        delete nextCategoryTrees[mergedDocument.id];
        documentCategoryTreesRef.current = nextCategoryTrees;
      }

      // Open the document
      await handleOpenDocument(mergedDocument, { forceExpandRoot: !!newName?.trim() });
    } catch (error) {
      setLoading(false);
      console.error('Error merging document:', error);
      const ax = error as { response?: { data?: unknown } };
      alert(
        formatApiErrorMessage(
          ax?.response?.data,
          'Failed to merge documents.',
        ),
      );
    }
  };

  const loadCompareSessionRoots = async (
    leftCategoryId: string,
    leftDocumentName: string,
    rightSide: DocumentCompareSide,
    rightDocumentName: string,
  ): Promise<{ leftRoot: Category; rightRoot: Category; rightCategoryId: string }> => {
    await openDocument(leftDocumentName);
    const leftRoot = await fetchCategoryWithChildren(leftCategoryId);

    if (rightSide.readAccessGrantId != null) {
      const openRes = await api.post<OpenReadOnlyDocumentResponse>(
        `/documents/read-access/${rightSide.readAccessGrantId}/open`,
      );
      const rightCategoryId = openRes.data.categoryId;
      const rightRoot = await fetchCategoryWithChildren(rightCategoryId);
      return { leftRoot, rightRoot, rightCategoryId };
    }

    await openDocument(rightDocumentName);
    const rightRoot = await fetchCategoryWithChildren(rightSide.categoryId);
    return { leftRoot, rightRoot, rightCategoryId: rightSide.categoryId };
  };

  const refreshCompareSession = async (tabId: string) => {
    const session = compareSessions[tabId];
    if (!session) {
      return;
    }
    try {
      setLoading(true);
      await reloadCompareSessionTrees(tabId, false);
    } catch (error) {
      console.error('Error refreshing document compare:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCompareDocuments = async (
    mergeIntoDocumentId: string,
    mergeFromDocumentId: string,
    mergeFromReadAccessGrantId: number | null,
    leftLabel: string,
    rightLabel: string,
    resolveMissing: MergeMissingResolve = MergeMissingResolve.COPY,
    resolveTypeMismatch: MergeTypeMismatchResolve = MergeTypeMismatchResolve.KEEP,
  ) => {
    if (!documentMergeCompareEnabled) {
      alert('Document merge compare is disabled for this installation.');
      return;
    }
    try {
      setLoading(true);
      const intoDoc = documents.find(
        (d) => d.id === mergeIntoDocumentId || d.name === mergeIntoDocumentId,
      );
      if (!intoDoc?.name || !intoDoc.categoryId) {
        throw new Error('Merge target document not found');
      }

      let rightDocumentName = '';
      let rightSide: DocumentCompareSide;
      if (mergeFromReadAccessGrantId != null) {
        rightSide = {
          label: rightLabel,
          documentName: '',
          categoryId: '',
          readAccessGrantId: mergeFromReadAccessGrantId,
        };
      } else {
        const fromDoc = documents.find(
          (d) => d.id === mergeFromDocumentId || d.name === mergeFromDocumentId,
        );
        if (!fromDoc?.name || !fromDoc.categoryId) {
          throw new Error('Merge source document not found');
        }
        rightDocumentName = fromDoc.name;
        rightSide = {
          label: rightLabel,
          documentName: fromDoc.name,
          categoryId: fromDoc.categoryId,
        };
      }
      const { leftRoot, rightRoot, rightCategoryId } = await loadCompareSessionRoots(
        intoDoc.categoryId,
        intoDoc.name,
        rightSide,
        rightDocumentName,
      );

      const tabId = createDocumentCompareTabId();
      const session: DocumentCompareSession = {
        tabId,
        left: {
          label: leftLabel,
          documentName: intoDoc.name,
          categoryId: intoDoc.categoryId,
        },
        right: {
          ...rightSide,
          label: rightLabel,
          documentName: rightDocumentName,
          categoryId: rightCategoryId,
        },
        leftRoot,
        rightRoot,
        resolveMissing,
        resolveTypeMismatch,
        copyUndoStack: [],
      };

      setCompareSessions((prev) => ({ ...prev, [tabId]: session }));
      const tabLabel = documentCompareTabLabel(leftLabel, rightLabel);
      setTabs((prev) => {
        if (prev.some((t) => t.id === tabId)) {
          return prev;
        }
        return [
          ...prev,
          {
            id: tabId,
            label: tabLabel,
            content: <Typography>Document compare</Typography>,
          },
        ];
      });
      setCurrentTab(tabId);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error('Error opening document compare:', error);
      const ax = error as { response?: { data?: unknown } };
      if (
        shouldRemoveReadAccessGrantAfterOpenError(error) &&
        mergeFromReadAccessGrantId != null
      ) {
        dismissStaleReadAccessGrant(mergeFromReadAccessGrantId);
      }
      alert(
        formatApiErrorMessage(
          ax?.response?.data,
          'Could not open document compare.',
        ),
      );
    }
  };

  const resolveCompareDocumentName = (idOrName: string): string => {
    const doc = documents.find((d) => d.id === idOrName || d.name === idOrName);
    return doc?.name ?? idOrName;
  };

  /** Compare mutations bypass HistoryService — drop stale undo/redo for affected owned documents. */
  const invalidateDocumentUndoAfterCompareMutation = (documentNameOrId: string) => {
    const trimmed = documentNameOrId?.trim();
    if (!trimmed) {
      return;
    }
    const resolvedName = resolveCompareDocumentName(trimmed);
    const doc = documents.find(
      (d) => d.id === resolvedName || d.name === resolvedName,
    );
    if (doc?.id) {
      documentTabsNeedRefreshRef.current.add(doc.id);
      const nextCategoryTrees = { ...documentCategoryTreesRef.current };
      delete nextCategoryTrees[doc.id];
      documentCategoryTreesRef.current = nextCategoryTrees;
    }
    historyService.clearDocumentHistory(doc?.id ?? resolvedName);
    setCanUndo(historyService.canUndo());
    setCanRedo(historyService.canRedo());
  };

  const shouldSaveDocumentViewSnapshotOnTabLeave = (): boolean =>
    !!currentDocument &&
    !!currentTab &&
    !isDocumentCompareTabId(currentTab) &&
    !isSelectiveMergeTabId(currentTab);

  const consumeDocumentTabRefreshFlag = (documentId: string): boolean => {
    if (!documentTabsNeedRefreshRef.current.has(documentId)) {
      return false;
    }
    documentTabsNeedRefreshRef.current.delete(documentId);
    return true;
  };

  const buildCompareCopyUndoEntry = (
    targetDocumentName: string,
    contentResult?: { createdContentIds?: string[] },
    categoryResult?: { createdCategoryIds?: string[] },
    newlyCreatedCategoryId?: string,
  ): CompareCopyUndoEntry | null => {
    const createdContentIds = [...(contentResult?.createdContentIds ?? [])];
    const createdCategoryIds = [...(categoryResult?.createdCategoryIds ?? [])];
    if (
      newlyCreatedCategoryId &&
      !createdCategoryIds.includes(newlyCreatedCategoryId)
    ) {
      createdCategoryIds.push(newlyCreatedCategoryId);
    }
    if (createdContentIds.length === 0 && createdCategoryIds.length === 0) {
      return null;
    }
    return { targetDocumentName, createdContentIds, createdCategoryIds, kind: 'copy' as const };
  };

  const resolveCompareSessionRoots = (session: DocumentCompareSession) => {
    const leftDocumentName = resolveCompareDocumentName(session.left.documentName);
    const leftDoc = documents.find(
      (d) => d.id === session.left.documentName || d.name === leftDocumentName,
    );
    const leftCategoryId = leftDoc?.categoryId ?? session.left.categoryId;

    const rightDocumentName = session.right.readAccessGrantId
      ? ''
      : resolveCompareDocumentName(session.right.documentName);
    const rightDoc = rightDocumentName
      ? documents.find(
          (d) => d.id === session.right.documentName || d.name === rightDocumentName,
        )
      : undefined;
    const rightCategoryId = rightDoc?.categoryId ?? session.right.categoryId;

    return {
      leftDocumentName,
      leftCategoryId,
      rightDocumentName,
      rightSide: {
        ...session.right,
        categoryId: session.right.readAccessGrantId
          ? session.right.categoryId
          : rightCategoryId,
      },
    };
  };

  const reloadCompareSessionTrees = async (
    tabId: string,
    typeConflictMergedSides?:
      | DocumentCompareSession['typeConflictMergedSides']
      | false,
  ) => {
    const session = compareSessions[tabId];
    if (!session) {
      return;
    }
    const { leftDocumentName, leftCategoryId, rightDocumentName, rightSide } =
      resolveCompareSessionRoots(session);
    const { leftRoot, rightRoot, rightCategoryId } = await loadCompareSessionRoots(
      leftCategoryId,
      leftDocumentName,
      rightSide,
      rightDocumentName,
    );
    setCompareSessions((prev) => {
      const session = prev[tabId];
      if (!session) {
        return prev;
      }
      let mergedSides =
        typeConflictMergedSides === false
          ? undefined
          : typeConflictMergedSides ?? session.typeConflictMergedSides;
      mergedSides = pruneStaleTypeConflictMergedSides(leftRoot, rightRoot, mergedSides);
      return {
        ...prev,
        [tabId]: {
          ...session,
          leftRoot,
          rightRoot,
          typeConflictMergedSides: mergedSides,
          left: {
            ...session.left,
            documentName: leftDocumentName,
            categoryId: leftRoot.id,
          },
          right: {
            ...session.right,
            categoryId: rightCategoryId,
            documentName: rightDocumentName || session.right.documentName,
          },
        },
      };
    });
  };

  const handleCompareMergeSettingsChange = (
    tabId: string,
    resolveMissing: MergeMissingResolve,
    resolveTypeMismatch: MergeTypeMismatchResolve,
  ) => {
    setCompareSessions((prev) => {
      const session = prev[tabId];
      if (!session) {
        return prev;
      }
      return {
        ...prev,
        [tabId]: {
          ...session,
          resolveMissing,
          resolveTypeMismatch,
        },
      };
    });
  };

  const reloadSelectiveMergeResultTree = async (tabId: string) => {
    const sm = selectiveMergeSessions[tabId];
    if (!sm) {
      return;
    }
    await openDocument(sm.result.documentName);
    const resultRoot = await fetchCategoryWithChildren(sm.result.categoryId);
    setSelectiveMergeSessions((prev) => ({
      ...prev,
      [tabId]: { ...prev[tabId], resultRoot },
    }));
  };

  /** Reload result + both source trees so edits made in source document tabs appear in compare. */
  const reloadSelectiveMergeSessionTrees = async (tabId: string) => {
    const sm = selectiveMergeSessions[tabId];
    if (!sm) {
      return;
    }

    const loadSideRoot = async (
      side: DocumentCompareSide,
    ): Promise<{ root: Category; categoryId: string }> => {
      if (side.readAccessGrantId != null) {
        const openRes = await api.post<OpenReadOnlyDocumentResponse>(
          `/documents/read-access/${side.readAccessGrantId}/open`,
        );
        const categoryId = openRes.data.categoryId;
        const root = await fetchCategoryWithChildren(categoryId);
        return { root, categoryId };
      }
      await openDocument(side.documentName);
      const root = await fetchCategoryWithChildren(side.categoryId);
      return { root, categoryId: side.categoryId };
    };

    // Sequential: openDocument is session-global, so parallel loads can race.
    const { root: sideARoot, categoryId: sideACategoryId } = await loadSideRoot(sm.sideA);
    const { root: sideBRoot, categoryId: sideBCategoryId } = await loadSideRoot(sm.sideB);
    await openDocument(sm.result.documentName);
    const resultRoot = await fetchCategoryWithChildren(sm.result.categoryId);

    setSelectiveMergeSessions((prev) => {
      const current = prev[tabId];
      if (!current) {
        return prev;
      }
      const sourceRoot =
        selectiveMergeActiveSourceSide(current) === 'a' ? sideARoot : sideBRoot;
      const typeConflictMergedSides = pruneStaleTypeConflictMergedSides(
        resultRoot,
        sourceRoot,
        current.typeConflictMergedSides,
      );
      return {
        ...prev,
        [tabId]: {
          ...current,
          resultRoot,
          sideARoot,
          sideBRoot,
          typeConflictMergedSides,
          sideA: {
            ...current.sideA,
            categoryId: sideACategoryId,
          },
          sideB: {
            ...current.sideB,
            categoryId: sideBCategoryId,
          },
        },
      };
    });
  };

  const refreshSelectiveMergeSession = async (tabId: string) => {
    const sm = selectiveMergeSessions[tabId];
    if (!sm) {
      return;
    }
    try {
      setLoading(true);
      await reloadSelectiveMergeSessionTrees(tabId);
    } catch (error) {
      console.error('Error refreshing selective merge compare:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStartSelectiveMerge = async (
    mergeIntoDocumentId: string,
    mergeFromDocumentId: string,
    mergeFromReadAccessGrantId: number | null,
    leftLabel: string,
    rightLabel: string,
    newName: string,
    baseline: SelectiveMergeBaseline,
    resolveMissing: MergeMissingResolve = MergeMissingResolve.COPY,
    resolveTypeMismatch: MergeTypeMismatchResolve = MergeTypeMismatchResolve.KEEP,
  ) => {
    if (!documentMergeCompareEnabled) {
      alert('Document merge compare is disabled for this installation.');
      return;
    }
    const trimmedName = newName.trim();
    if (!trimmedName) {
      throw new Error('Document name is required');
    }
    const intoDoc = documents.find(
      (d) => d.id === mergeIntoDocumentId || d.name === mergeIntoDocumentId,
    );
    if (!intoDoc?.name || !intoDoc.categoryId) {
      throw new Error('Left document not found');
    }

    let rightDocumentName = '';
    let rightSide: DocumentCompareSide;
    if (mergeFromReadAccessGrantId != null) {
      rightSide = {
        label: rightLabel,
        documentName: '',
        categoryId: '',
        readAccessGrantId: mergeFromReadAccessGrantId,
      };
    } else {
      const fromDoc = documents.find(
        (d) => d.id === mergeFromDocumentId || d.name === mergeFromDocumentId,
      );
      if (!fromDoc?.name || !fromDoc.categoryId) {
        throw new Error('Right document not found');
      }
      rightDocumentName = fromDoc.name;
      rightSide = {
        label: rightLabel,
        documentName: fromDoc.name,
        categoryId: fromDoc.categoryId,
      };
    }

    if (baseline === 'right' && mergeFromReadAccessGrantId == null && !rightDocumentName) {
      throw new Error('Compare with document not found');
    }

    try {
      setLoading(true);
      const leftDocumentName = intoDoc.name;
      const { leftRoot, rightRoot, rightCategoryId } = await loadCompareSessionRoots(
        intoDoc.categoryId,
        intoDoc.name,
        rightSide,
        rightDocumentName,
      );

      const created = await createSelectiveMergeDocument(
        leftDocumentName,
        rightDocumentName,
        mergeFromReadAccessGrantId,
        trimmedName,
        baseline,
      );

      if (created.categoryId) {
        const response = await api.get('/documents/subcases', {
          params: { documentId: created.categoryId },
        });
        created.subcases = response.data;
      }

      await openDocument(created.name);
      const resultRoot = await fetchCategoryWithChildren(created.categoryId!);

      const activeSourceSide: SelectiveMergeSourceSide =
        baseline === 'left' ? 'b' : baseline === 'right' ? 'a' : 'b';

      const tabId = createSelectiveMergeTabId();
      const session: SelectiveMergeSession = {
        tabId,
        baseline,
        result: {
          label: trimmedName,
          documentName: created.name,
          categoryId: created.categoryId!,
        },
        resultRoot,
        sideA: {
          label: leftLabel,
          documentName: intoDoc.name,
          categoryId: intoDoc.categoryId,
        },
        sideB: {
          ...rightSide,
          label: rightLabel,
          documentName: rightDocumentName,
          categoryId: rightCategoryId,
        },
        sideARoot: leftRoot,
        sideBRoot: rightRoot,
        activeSourceSide,
        resolveMissing,
        resolveTypeMismatch,
        copyUndoStack: [],
      };

      setSelectiveMergeSessions((prev) => ({ ...prev, [tabId]: session }));
      setTabs((prev) => {
        if (prev.some((t) => t.id === tabId)) {
          return prev;
        }
        return [
          ...prev,
          {
            id: tabId,
            label: selectiveMergeTabLabel(trimmedName, leftLabel, rightLabel, baseline),
            content: <Typography>Building new document</Typography>,
          },
        ];
      });
      setCurrentTab(tabId);
      invalidateDocumentUndoAfterCompareMutation(created.id);
      await refreshDocuments();
    } catch (error) {
      console.error('Error starting selective merge:', error);
      const ax = error as { response?: { data?: unknown } };
      alert(
        mergeCompareActionErrorMessage(ax?.response?.data, 'Failed to start selective merge.'),
      );
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleSelectiveMergeActiveSourceChange = (
    tabId: string,
    side: SelectiveMergeSourceSide,
  ) => {
    setSelectiveMergeSessions((prev) => {
      const sm = prev[tabId];
      if (!sm || sm.baseline !== 'intersection') {
        return prev;
      }
      return { ...prev, [tabId]: { ...sm, activeSourceSide: side } };
    });
  };

  const handleSelectiveMergeSelectedContent = async (
    tabId: string,
    selection: MergeCompareContentSelection,
  ) => {
    const sm = selectiveMergeSessions[tabId];
    if (!sm) {
      return;
    }
    await handleMergeCompareSelectedContent(tabId, selection, {
      session: buildSelectiveMergeDisplaySession(sm),
      selectiveMergeTabId: tabId,
    });
  };

  const handleMergeCompareSelectedContent = async (
    tabId: string,
    selection: MergeCompareContentSelection,
    selectiveMergeContext?: {
      session: DocumentCompareSession;
      selectiveMergeTabId: string;
    },
  ) => {
    const session = selectiveMergeContext?.session ?? compareSessions[tabId];
    if (!session) {
      return;
    }
    const applyingTabId = selectiveMergeContext?.selectiveMergeTabId ?? tabId;
    const isSelectiveMergeApply = selectiveMergeContext != null;

    const refreshLeftTreeAfterMutation = async (): Promise<Category> => {
      const leftRoot = await fetchCategoryWithChildren(session.left.categoryId);
      if (isSelectiveMergeApply) {
        setSelectiveMergeSessions((prev) => ({
          ...prev,
          [selectiveMergeContext.selectiveMergeTabId]: {
            ...prev[selectiveMergeContext.selectiveMergeTabId],
            resultRoot: leftRoot,
          },
        }));
      } else {
        try {
          await reloadCompareSessionTrees(tabId);
        } catch (refreshError) {
          console.error('Compare trees could not be reloaded after merge:', refreshError);
          setCompareSessions((prev) => ({
            ...prev,
            [tabId]: { ...prev[tabId], leftRoot },
          }));
        }
      }
      return leftRoot;
    };

    const refreshRightTreeAfterMutation = async (): Promise<Category> => {
      const rightRoot = await fetchCategoryWithChildren(session.right.categoryId);
      if (isSelectiveMergeApply) {
        return rightRoot;
      }
      try {
        await reloadCompareSessionTrees(tabId);
      } catch (refreshError) {
        console.error('Compare trees could not be reloaded after merge:', refreshError);
        setCompareSessions((prev) => ({
          ...prev,
          [tabId]: { ...prev[tabId], rightRoot },
        }));
      }
      return rightRoot;
    };

    const leftDocumentName = resolveCompareDocumentName(session.left.documentName);
    const rightDocumentName = session.right.readAccessGrantId
      ? ''
      : resolveCompareDocumentName(session.right.documentName);

    const persistCopyMutation = (
      leftRoot: Category,
      rightRoot: Category,
      leftCopyUndo: CompareCopyUndoEntry | null,
      rightCopyUndo: CompareCopyUndoEntry | null,
    ) => {
      const stack = [...(session.copyUndoStack ?? [])];
      if (leftCopyUndo) {
        stack.push(leftCopyUndo);
      }
      if (rightCopyUndo) {
        stack.push(rightCopyUndo);
      }
      if (isSelectiveMergeApply) {
        setSelectiveMergeSessions((prev) => ({
          ...prev,
          [selectiveMergeContext.selectiveMergeTabId]: {
            ...prev[selectiveMergeContext.selectiveMergeTabId],
            resultRoot: leftRoot,
            copyUndoStack: stack,
          },
        }));
        return;
      }
      setCompareSessions((prev) => ({
        ...prev,
        [tabId]: {
          ...prev[tabId],
          leftRoot,
          rightRoot,
          left: { ...prev[tabId].left, documentName: leftDocumentName },
          right: {
            ...prev[tabId].right,
            documentName: rightDocumentName || prev[tabId].right.documentName,
          },
          copyUndoStack: stack,
        },
      }));
    };

    try {
      setMergeCompareApplyingTabId(applyingTabId);
      let leftRoot = session.leftRoot;
      let rightRoot = session.rightRoot;
      const messages: string[] = [];
      let leftCopyUndo: CompareCopyUndoEntry | null = null;
      let rightCopyUndo: CompareCopyUndoEntry | null = null;

      if (selection.mergeCategorySubtreeToLeft) {
        const target = selection.mergeCategorySubtreeToLeft;
        const subtreeResolution =
          target.treatIntoAsParent || target.explicitCopy
            ? {
                resolveMissing: MergeMissingResolve.COPY,
                resolveTypeMismatch: MergeTypeMismatchResolve.KEEP,
              }
            : {
                resolveMissing: session.resolveMissing,
                resolveTypeMismatch: session.resolveTypeMismatch,
              };
        await openDocument(leftDocumentName);
        if (session.right.readAccessGrantId == null && rightDocumentName) {
          await openDocument(rightDocumentName);
        }
        const result = await mergeCompareCategorySubtree({
          mergeIntoDocumentId: leftDocumentName,
          mergeIntoCategoryId: target.mergeIntoCategoryId,
          mergeFromCategoryId: target.sourceCategoryId,
          mergeFromDocumentId: rightDocumentName,
          mergeFromReadAccessGrantId: session.right.readAccessGrantId,
          treatIntoAsParent: target.treatIntoAsParent,
          resolveMissing: subtreeResolution.resolveMissing,
          resolveTypeMismatch: subtreeResolution.resolveTypeMismatch,
        });
        if (result.skipped) {
          messages.push('Merge to left: skipped (missing subcategory resolution).');
        } else if (result.applied) {
          invalidateDocumentUndoAfterCompareMutation(leftDocumentName);
        }
        leftCopyUndo = buildCompareCopyUndoEntry(
          leftDocumentName,
          { createdContentIds: result.createdContentIds },
          { createdCategoryIds: result.createdCategoryIds },
        );
        leftRoot = await refreshLeftTreeAfterMutation();
        if (leftCopyUndo) {
          persistCopyMutation(leftRoot, rightRoot, leftCopyUndo, null);
        }
        if (messages.length > 0) {
          alert(messages.join('\n'));
        }
        return;
      }

      if (selection.mergeCategorySubtreeToRight) {
        if (session.right.readAccessGrantId != null) {
          throw new Error('Cannot merge into a read-only shared document.');
        }
        if (!rightDocumentName) {
          throw new Error('Merge source document not found');
        }
        const target = selection.mergeCategorySubtreeToRight;
        const subtreeResolution =
          target.treatIntoAsParent || target.explicitCopy
            ? {
                resolveMissing: MergeMissingResolve.COPY,
                resolveTypeMismatch: MergeTypeMismatchResolve.KEEP,
              }
            : {
                resolveMissing: session.resolveMissing,
                resolveTypeMismatch: session.resolveTypeMismatch,
              };
        await openDocument(leftDocumentName);
        await openDocument(rightDocumentName);
        const result = await mergeCompareCategorySubtree({
          mergeIntoDocumentId: rightDocumentName,
          mergeIntoCategoryId: target.mergeIntoCategoryId,
          mergeFromCategoryId: target.sourceCategoryId,
          mergeFromDocumentId: leftDocumentName,
          treatIntoAsParent: target.treatIntoAsParent,
          resolveMissing: subtreeResolution.resolveMissing,
          resolveTypeMismatch: subtreeResolution.resolveTypeMismatch,
        });
        if (result.skipped) {
          messages.push('Merge to right: skipped (missing subcategory resolution).');
        } else if (result.applied) {
          invalidateDocumentUndoAfterCompareMutation(rightDocumentName);
        }
        rightCopyUndo = buildCompareCopyUndoEntry(
          rightDocumentName,
          { createdContentIds: result.createdContentIds },
          { createdCategoryIds: result.createdCategoryIds },
        );
        rightRoot = await refreshRightTreeAfterMutation();
        if (rightCopyUndo) {
          persistCopyMutation(leftRoot, rightRoot, null, rightCopyUndo);
        }
        if (messages.length > 0) {
          alert(messages.join('\n'));
        }
        return;
      }

      if (selection.typeConflict) {
        const { direction, rows } = selection.typeConflict;
        if (!rows.length) {
          return;
        }
        await openDocument(leftDocumentName);
        if (session.right.readAccessGrantId == null && rightDocumentName) {
          await openDocument(rightDocumentName);
        }
        if (direction === 'right' && session.right.readAccessGrantId != null) {
          throw new Error('Cannot merge into a read-only shared document.');
        }
        if (direction === 'right') {
          if (!rightDocumentName) {
            throw new Error('Merge source document not found');
          }
          await openDocument(rightDocumentName);
        }
        for (const row of rows) {
          await mergeCompareTypeConflict({
            direction,
            leftDocumentId: leftDocumentName,
            leftCategoryId: row.leftCategoryId,
            rightDocumentId: rightDocumentName,
            rightCategoryId: row.rightCategoryId,
            mergeFromReadAccessGrantId: session.right.readAccessGrantId,
          });
        }
        let mergedSides = session.typeConflictMergedSides;
        for (const row of rows) {
          mergedSides = applyTypeConflictMergeRecord(mergedSides, row.rowKey, direction);
        }
        if (isSelectiveMergeApply) {
          if (direction === 'left') {
            await refreshLeftTreeAfterMutation();
          }
          setSelectiveMergeSessions((prev) => ({
            ...prev,
            [selectiveMergeContext.selectiveMergeTabId]: {
              ...prev[selectiveMergeContext.selectiveMergeTabId],
              typeConflictMergedSides: mergedSides,
            },
          }));
        } else {
          try {
            await reloadCompareSessionTrees(tabId, mergedSides);
          } catch (refreshError) {
            console.error('Compare trees could not be reloaded after type-conflict merge:', refreshError);
            setCompareSessions((prev) => ({
              ...prev,
              [tabId]: {
                ...prev[tabId],
                typeConflictMergedSides: mergedSides,
              },
            }));
          }
        }
        invalidateDocumentUndoAfterCompareMutation(
          direction === 'left' ? leftDocumentName : rightDocumentName,
        );
        return;
      }

      const copyLeftContentIds = selection.copyToLeft?.sourceContentIds ?? [];
      const copyLeftCategoryIds = selection.copyToLeft?.sourceCategoryIds ?? [];
      const shouldProcessCopyLeft =
        copyLeftContentIds.length > 0 ||
        copyLeftCategoryIds.length > 0 ||
        selection.copyToLeft?.targetCategoryId != null ||
        selection.copyToLeft?.targetParentCategoryId != null ||
        selection.copyToLeft?.createBranchShell != null;
      if (shouldProcessCopyLeft) {
        await openDocument(leftDocumentName);
        if (session.right.readAccessGrantId == null && rightDocumentName) {
          await openDocument(rightDocumentName);
        }
        let contentResult;
        let categoryResult;
        if (copyLeftContentIds.length > 0) {
          const targetContentCategoryId =
            selection.copyToLeft?.targetCategoryId ?? '';
          contentResult = await mergeCompareContent({
            mergeIntoDocumentId: leftDocumentName,
            targetContentCategoryId,
            sourceContentIds: copyLeftContentIds,
            mergeFromDocumentId: rightDocumentName,
            mergeFromReadAccessGrantId: session.right.readAccessGrantId,
          });
          appendMergeCompareCopyMessage(messages, 'left', contentResult, 'rows');
        }
        if (copyLeftCategoryIds.length > 0) {
          categoryResult = await mergeCompareCategories({
            mergeIntoDocumentId: leftDocumentName,
            targetParentCategoryId: selection.copyToLeft?.targetParentCategoryId,
            sourceCategoryIds: copyLeftCategoryIds,
            mergeFromDocumentId: rightDocumentName,
            mergeFromReadAccessGrantId: session.right.readAccessGrantId,
            createBranchShell: selection.copyToLeft?.createBranchShell,
          });
          appendMergeCompareCopyMessage(messages, 'left', categoryResult, 'categories');
        }
        leftCopyUndo = buildCompareCopyUndoEntry(
          leftDocumentName,
          contentResult,
          categoryResult,
          selection.copyToLeft?.newlyCreatedCategoryId,
        );
        if (
          copyLeftContentIds.length > 0 ||
          copyLeftCategoryIds.length > 0 ||
          selection.copyToLeft?.newlyCreatedCategoryId
        ) {
          leftRoot = await fetchCategoryWithChildren(session.left.categoryId);
        }
        invalidateDocumentUndoAfterCompareMutation(leftDocumentName);
      }

      const copyRightContentIds = selection.copyToRight?.sourceContentIds ?? [];
      const copyRightCategoryIds = selection.copyToRight?.sourceCategoryIds ?? [];
      const shouldProcessCopyRight =
        copyRightContentIds.length > 0 ||
        copyRightCategoryIds.length > 0 ||
        selection.copyToRight?.targetCategoryId != null ||
        selection.copyToRight?.targetParentCategoryId != null ||
        selection.copyToRight?.createBranchShell != null;
      if (shouldProcessCopyRight) {
        if (session.right.readAccessGrantId != null) {
          throw new Error('Cannot copy into a read-only shared document.');
        }
        if (!rightDocumentName) {
          throw new Error('Merge source document not found');
        }
        await openDocument(leftDocumentName);
        await openDocument(rightDocumentName);
        let contentResult;
        let categoryResult;
        if (copyRightContentIds.length > 0) {
          const targetContentCategoryId =
            selection.copyToRight?.targetCategoryId ?? '';
          contentResult = await mergeCompareContent({
            mergeIntoDocumentId: rightDocumentName,
            targetContentCategoryId,
            sourceContentIds: copyRightContentIds,
            mergeFromDocumentId: leftDocumentName,
          });
          appendMergeCompareCopyMessage(messages, 'right', contentResult, 'rows');
        }
        if (copyRightCategoryIds.length > 0) {
          categoryResult = await mergeCompareCategories({
            mergeIntoDocumentId: rightDocumentName,
            targetParentCategoryId: selection.copyToRight?.targetParentCategoryId,
            sourceCategoryIds: copyRightCategoryIds,
            mergeFromDocumentId: leftDocumentName,
            createBranchShell: selection.copyToRight?.createBranchShell,
          });
          appendMergeCompareCopyMessage(messages, 'right', categoryResult, 'categories');
        }
        rightCopyUndo = buildCompareCopyUndoEntry(
          rightDocumentName,
          contentResult,
          categoryResult,
          selection.copyToRight?.newlyCreatedCategoryId,
        );
        if (
          copyRightContentIds.length > 0 ||
          copyRightCategoryIds.length > 0 ||
          selection.copyToRight?.newlyCreatedCategoryId
        ) {
          rightRoot = await fetchCategoryWithChildren(session.right.categoryId);
        }
        invalidateDocumentUndoAfterCompareMutation(rightDocumentName);
      }

      persistCopyMutation(leftRoot, rightRoot, leftCopyUndo, rightCopyUndo);
      if (messages.length > 0) {
        alert(messages.join('\n'));
      }
    } catch (error) {
      console.error('Error copying merge compare content:', error);
      const ax = error as { response?: { data?: unknown } };
      alert(
        mergeCompareActionErrorMessage(
          ax?.response?.data,
          'Failed to apply merge compare action.',
        ),
      );
      throw error;
    } finally {
      setMergeCompareApplyingTabId(null);
    }
  };

  const pushCompareUndoEntry = (tabId: string, entry: CompareUndoEntry, selectiveMergeTab: boolean) => {
    if (selectiveMergeTab) {
      setSelectiveMergeSessions((prev) => {
        const current = prev[tabId];
        if (!current) {
          return prev;
        }
        return {
          ...prev,
          [tabId]: {
            ...current,
            copyUndoStack: [...(current.copyUndoStack ?? []), entry],
          },
        };
      });
      return;
    }
    setCompareSessions((prev) => {
      const current = prev[tabId];
      if (!current) {
        return prev;
      }
      return {
        ...prev,
        [tabId]: {
          ...current,
          copyUndoStack: [...(current.copyUndoStack ?? []), entry],
        },
      };
    });
  };

  const popCompareUndoEntry = (tabId: string, selectiveMergeTab: boolean) => {
    if (selectiveMergeTab) {
      setSelectiveMergeSessions((prev) => {
        const current = prev[tabId];
        if (!current?.copyUndoStack?.length) {
          return prev;
        }
        return {
          ...prev,
          [tabId]: {
            ...current,
            copyUndoStack: current.copyUndoStack.slice(0, -1),
          },
        };
      });
      return;
    }
    setCompareSessions((prev) => {
      const current = prev[tabId];
      if (!current?.copyUndoStack?.length) {
        return prev;
      }
      return {
        ...prev,
        [tabId]: {
          ...current,
          copyUndoStack: current.copyUndoStack.slice(0, -1),
        },
      };
    });
  };

  const restoreCompareDeletedContent = async (entry: CompareDeleteContentUndoEntry) => {
    const { content, categoryId, index } = entry;
    const response = await api.post('/content', {
      parentId: categoryId,
      text: content.text,
      richTextHtml: content.richTextHtml,
      richTextDelta: content.richTextDelta,
      subcase: content.subcase,
      id: content.id,
      ...(typeof content.attachmentsJson === 'string'
        ? { attachmentsJson: content.attachmentsJson }
        : {}),
    });
    if (!response.data) {
      throw new Error('Failed to restore content');
    }
    const newContent = response.data as Content;
    if (index >= 0) {
      await api.put(
        '/content/moveto',
        {
          contentId: newContent.id,
          moveTo: index,
        },
        {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
        },
      );
    }
  };

  const applyCompareUndoEntry = async (entry: CompareUndoEntry) => {
    await openDocument(entry.targetDocumentName);
    if (isCompareCopyUndoEntry(entry)) {
      for (const contentId of entry.createdContentIds) {
        await deleteContent(contentId);
      }
      for (const categoryId of [...entry.createdCategoryIds].reverse()) {
        await deleteCategory(categoryId);
      }
    } else if (entry.kind === 'delete-content') {
      await restoreCompareDeletedContent(entry);
    } else if (entry.kind === 'delete-category') {
      if (!entry.parentId) {
        throw new Error('Cannot restore deleted category without a parent');
      }
      await runRestoreCategorySubtreeFromSnapshot(entry.parentId, entry.index, entry.category);
    }
    invalidateDocumentUndoAfterCompareMutation(entry.targetDocumentName);
  };

  const resolveCompareDeleteDocumentName = (
    session: DocumentCompareSession,
    side: 'left' | 'right',
  ): string | null => {
    if (side === 'left') {
      return resolveCompareDocumentName(session.left.documentName);
    }
    if (session.right.readAccessGrantId != null) {
      return null;
    }
    return resolveCompareDocumentName(session.right.documentName);
  };

  const canDeleteOnCompareSide = (
    session: DocumentCompareSession,
    side: 'left' | 'right',
    selectiveMergeTab: boolean,
  ): boolean => {
    if (selectiveMergeTab) {
      return side === 'left';
    }
    if (side === 'left') {
      return true;
    }
    return session.right.readAccessGrantId == null;
  };

  const handleCompareDeleteContentRequest = (
    tabId: string,
    side: 'left' | 'right',
    content: Content,
    categoryId: string,
    parentName: string,
  ) => {
    const selectiveMergeTab = isSelectiveMergeTabId(tabId);
    const session = selectiveMergeTab
      ? buildSelectiveMergeDisplaySession(selectiveMergeSessions[tabId])
      : compareSessions[tabId];
    if (!session || !canDeleteOnCompareSide(session, side, selectiveMergeTab)) {
      return;
    }
    const targetDocumentName = resolveCompareDeleteDocumentName(session, side);
    if (!targetDocumentName) {
      return;
    }
    setCompareDeleteSource({
      tabId,
      kind: 'content',
      side,
      targetDocumentName,
      parentName,
      content,
      categoryId,
    });
    setCompareDeleteBusy(false);
  };

  const handleCompareDeleteCategoryRequest = (
    tabId: string,
    side: 'left' | 'right',
    category: Category,
    parentName: string,
    parentId: string | null,
    index: number,
  ) => {
    const selectiveMergeTab = isSelectiveMergeTabId(tabId);
    const session = selectiveMergeTab
      ? buildSelectiveMergeDisplaySession(selectiveMergeSessions[tabId])
      : compareSessions[tabId];
    if (!session || !canDeleteOnCompareSide(session, side, selectiveMergeTab)) {
      return;
    }
    const sideRoot = side === 'left' ? session.leftRoot : session.rightRoot;
    const parent = parentId ? findCategoryInTreeFromRoots([sideRoot], parentId) : null;
    if (!category.parentId || !canDeleteCategoryUnderParent(parent, category.id)) {
      return;
    }
    const targetDocumentName = resolveCompareDeleteDocumentName(session, side);
    if (!targetDocumentName) {
      return;
    }
    setCompareDeleteSource({
      tabId,
      kind: 'category',
      side,
      targetDocumentName,
      parentName,
      category,
      parentId,
      index,
    });
    setCompareDeleteBusy(false);
  };

  const performCompareDelete = async () => {
    if (!compareDeleteSource) {
      return;
    }
    const {
      tabId,
      kind,
      targetDocumentName,
      content,
      categoryId,
      category,
      parentId,
      index,
      parentName,
    } = compareDeleteSource;
    const selectiveMergeTab = isSelectiveMergeTabId(tabId);

    setCompareDeleteBusy(true);
    try {
      await openDocument(targetDocumentName);

      if (kind === 'content' && content && categoryId) {
        const session = selectiveMergeTab
          ? buildSelectiveMergeDisplaySession(selectiveMergeSessions[tabId])
          : compareSessions[tabId];
        const root = compareDeleteSource.side === 'left' ? session?.leftRoot : session?.rightRoot;
        const parent =
          (root ? findParentCategoryInTree([root], content.id) : null) ??
          findParentCategoryInTree(categories, content.id);
        let oldIndex = parent?.contents?.findIndex((c) => c.id === content.id) ?? index ?? -1;
        if (oldIndex < 0) {
          try {
            const listResp = await api.get<Content[]>('/content/list', {
              params: { categoryId },
            });
            const list = listResp.data ?? [];
            oldIndex = list.findIndex((c) => c.id === content.id);
          } catch {
            oldIndex = -1;
          }
        }

        await api.delete('/content', { params: { contentId: content.id } });
        invalidateDocumentUndoAfterCompareMutation(targetDocumentName);

        pushCompareUndoEntry(
          tabId,
          {
            kind: 'delete-content',
            targetDocumentName,
            content,
            categoryId,
            index: oldIndex,
          },
          selectiveMergeTab,
        );
      } else if (kind === 'category' && category && parentId) {
        const snapshot = await fetchCategoryWithChildren(category.id);
        if (!snapshot) {
          throw new Error('Could not fetch category before delete');
        }
        await deleteCategory(category.id);
        invalidateDocumentUndoAfterCompareMutation(targetDocumentName);

        pushCompareUndoEntry(
          tabId,
          {
            kind: 'delete-category',
            targetDocumentName,
            category: snapshot,
            parentId,
            parentName,
            index: index ?? -1,
          },
          selectiveMergeTab,
        );
      }

      if (selectiveMergeTab) {
        await reloadSelectiveMergeSessionTrees(tabId);
      } else {
        await reloadCompareSessionTrees(tabId);
      }
      setCompareDeleteSource(null);
    } catch (error) {
      console.error('Error deleting from compare:', error);
      alert(kind === 'category' ? 'Failed to delete category' : 'Failed to delete content');
    } finally {
      setCompareDeleteBusy(false);
    }
  };

  const handleCompareUndoLastCopy = async (tabId: string) => {
    const selectiveMergeTab = isSelectiveMergeTabId(tabId);
    const stack = selectiveMergeTab
      ? selectiveMergeSessions[tabId]?.copyUndoStack
      : compareSessions[tabId]?.copyUndoStack;
    if (!stack?.length) {
      return;
    }
    const entry = stack[stack.length - 1];
    try {
      setMergeCompareApplyingTabId(tabId);
      await applyCompareUndoEntry(entry);
      if (selectiveMergeTab) {
        await reloadSelectiveMergeSessionTrees(tabId);
      } else {
        await reloadCompareSessionTrees(tabId);
      }
      popCompareUndoEntry(tabId, selectiveMergeTab);
    } catch (error) {
      console.error('Error undoing compare action:', error);
      const ax = error as { response?: { data?: unknown } };
      alert(
        mergeCompareActionErrorMessage(
          ax?.response?.data,
          'Failed to undo the last action.',
        ),
      );
      throw error;
    } finally {
      setMergeCompareApplyingTabId(null);
    }
  };

  const handleRenameSubcase = () => {
    setOpenRenameSubcaseDialog(true);
  };

  const handleRenameSubcaseClose = () => {
    setOpenRenameSubcaseDialog(false);
  };

  const handleRenameSubcaseSubmit = async (oldSubcase: string, newSubcase: string) => {
    const docForReload = activeDocument;
    const categoryId = docForReload?.categoryId;
    try {
      setLoading(true);
      const response = await api.put('/documents/subcases/rename', {
        oldSubcase,
        newSubcase
      }, { params: { documentId: docForReload?.id } });

      historyService.recordOperation({
          type: 'renameSubcase',
          data: {
            oldName: oldSubcase,
            newName: newSubcase
          }
        });

      if (response.status === 200 && docForReload && categoryId) {
        await reloadOpenDocumentCategoryTree(docForReload, true);
        await refreshActiveDocumentSubcasesFromServer(categoryId);
        handleRenameSubcaseClose();
      }
    } catch (error) {
      console.error('Error renaming subcase:', error);
      alert('Failed to rename subcase');
    } finally {
      setLoading(false);
    }
  };

  /** Panel dropdown ↔ editor subcase field ↔ currentContent.subcase while editing. */
  const syncSubcasePanelAndContent = useCallback((newSubcase: string) => {
    setSelectedSubcase(newSubcase);
    setCurrentContent(prev => (prev ? { ...prev, subcase: newSubcase } : prev));
  }, []);

  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearch(subTreeSearch);
    }, 300);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [subTreeSearch]);

  /** Scroll the content pane so the row for {@code contentId} is visible (retries until rendered). */
  const scrollToContent = useCallback((contentId: string, attempt = 0) => {
    if (!contentId) return;
    const targetRow = document.querySelector(
      `[data-content-id="${CSS.escape(contentId)}"]`,
    );
    if (targetRow) {
      targetRow.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    // Row may not be mounted yet (category switch / lazy contents load).
    if (attempt < 20) {
      window.setTimeout(() => scrollToContent(contentId, attempt + 1), 50);
    }
  }, []);

  const stepSelectedContentPreview = useCallback(
    (direction: -1 | 1) => {
      if (selectedContentsInPaneOrder.length <= 1) return;
      let idx = selectedContentPreviewIndex;
      if (idx < 0) {
        idx = direction === 1 ? -1 : selectedContentsInPaneOrder.length;
      }
      const nextIdx =
        (idx + direction + selectedContentsInPaneOrder.length) %
        selectedContentsInPaneOrder.length;
      const next = selectedContentsInPaneOrder[nextIdx];
      previewContentRow(next);
      setTimeout(() => scrollToContent(next.id), 50);
    },
    [selectedContentsInPaneOrder, selectedContentPreviewIndex, scrollToContent],
  );

  useEffect(() => {
    const handlePaneArrowKeyDown = (e: KeyboardEvent) => {
      if (tabs.length === 0 || currentTab == null) return;
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      // A modal dialog (e.g. step-by-step review, which uses ←/→) traps focus; don't also
      // navigate the tree/pane underneath it.
      if (document.querySelector('.MuiDialog-container')) return;

      const target = e.target as HTMLElement | null;
      const activeEl = document.activeElement as HTMLElement | null;
      const isTextInput =
        (e.target instanceof HTMLInputElement && e.target.type !== 'checkbox') ||
        e.target instanceof HTMLTextAreaElement;
      const isContentEditable =
        (!!target && (target.isContentEditable || !!target.closest('[contenteditable="true"]'))) ||
        (!!activeEl && (activeEl.isContentEditable || !!activeEl.closest('[contenteditable="true"]')));
      const isQuillEditor =
        (!!target && !!target.closest('.ql-editor')) ||
        (!!activeEl && !!activeEl.closest('.ql-editor'));
      if (isTextInput || isContentEditable || isQuillEditor) return;

      const treeLike =
        !!activeEl?.closest('[role="tree"]') ||
        !!activeEl?.closest('.MuiTreeView-root');
      if (treeLike) return;
      if (e.ctrlKey || e.metaKey || e.altKey || e.shiftKey) return;

      if (selectedContents.length > 1) {
        e.preventDefault();
        stepSelectedContentPreview(e.key === 'ArrowLeft' ? -1 : 1);
        return;
      }

      if (e.key === 'ArrowLeft') {
        if (!selectedCategory?.parentId) return;
        e.preventDefault();
        handleNavigateToParent();
        return;
      }

      if (selectedCategories.length !== 1 || !selectedCategory) return;
      const drillTarget = selectedCategories[0];
      const canDrillDown = selectedCategory.children?.some(
        (child) => child.id === drillTarget.id,
      );
      if (!canDrillDown) return;
      e.preventDefault();
      handleCategoryNavigate(drillTarget);
    };

    window.addEventListener('keydown', handlePaneArrowKeyDown);
    return () => window.removeEventListener('keydown', handlePaneArrowKeyDown);
  }, [
    tabs.length,
    currentTab,
    selectedContents.length,
    selectedCategories,
    selectedCategory,
    stepSelectedContentPreview,
    handleCategoryNavigate,
    handleNavigateToParent,
  ]);

  const renderMainContent = () => {
    const isDocumentReadOnly = effectiveDocumentReadOnly;
    const renderRightPane = () => {
      const mainContent = (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', flex: 1 }}>
          <SplitPane
            leftPane={
              <CategoryContent
                ref={categoryContentRef}
                categories={categories}
                selectedCategory={selectedCategory}
                onCategoryMultiSelect={handleCategoryMultiSelect}
                onCategoryNavigate={handleCategoryNavigate}
                onNavigateToParent={handleNavigateToParent}
                onCategoryCheckboxToggle={handleCategoryCheckboxToggle}
                onContentMultiSelect={handleContentMultiSelect}
                onContentCheckboxToggle={handleContentCheckboxToggle}
                viewedContentId={currentContent?.id ?? null}
                selectedCategories={selectedCategories}
                selectedContents={selectedContents}
                onSelectAllCategories={handleSelectAllCategories}
                onSelectAllContents={handleSelectAllContents}
                onInvertSelectionCategories={
                  isDocumentReadOnly ? undefined : handleInvertSelectionCategories
                }
                onInvertSelectionContents={
                  isDocumentReadOnly ? undefined : handleInvertSelectionContents
                }
                onEditContent={isDocumentReadOnly ? () => {} : handleAddContent}
                onAdjustTextContent={isDocumentReadOnly ? undefined : handleAdjustTextContent}
                canAdjustTextContent={canAdjustTextContent()}
                onGroupCategories={isDocumentReadOnly ? () => {} : handleGroupCategoriesDialogOpen}
                onOrganizeCategories={
                  isDocumentReadOnly ? () => {} : () => handleOrganizeCategoriesOpen()
                }
                canOrganizeCategories={() =>
                  !isDocumentReadOnly && canOrganizeCategories()
                }
                onCollectBranchContent={
                  isDocumentReadOnly ? () => {} : handleCollectBranchContent
                }
                canCollectBranchContent={() =>
                  !isDocumentReadOnly && canCollectBranchContent()
                }
                onMergeCategories={isDocumentReadOnly ? () => {} : handleMergeCategoriesDialogOpen}
                onDeleteSelected={isDocumentReadOnly ? () => {} : handleDelete}
                onCategoryContextMenu={(category) => {
                  setSelectedContents([]);
                  setCurrentContent(null);
                  setSelectedCategories([category]);
                }}
                onContentContextMenu={(content) => {
                  setSelectedCategories([]);
                  setSelectedContents([content]);
                  setCurrentContent(content);
                }}
                canDeletePaneCategories={() =>
                  !isDocumentReadOnly &&
                  selectedContents.length === 0 &&
                  canDeleteSelectedCategories()
                }
                onRenameCategory={
                  isDocumentReadOnly ? () => {} : () => handleRenameCategory()
                }
                canRenamePaneCategory={() =>
                  !isDocumentReadOnly &&
                  selectedContents.length === 0 &&
                  selectedCategories.length === 1 &&
                  selectedCategories[0].contentCategory === true &&
                  canRenameCategory(selectedCategories[0])
                }
                onMoveSelected={isDocumentReadOnly ? () => {} : handleMoveCategory}
                onCutSelected={isDocumentReadOnly ? () => {} : handleCutCategory}
                onCopySelected={handleCopyCategory}
                canEditContent={!isDocumentReadOnly && canEditContent()}
                canGroupCategories={!isDocumentReadOnly && canGroupCategories()}
                canMergeCategories={!isDocumentReadOnly && canMergeCategories()}
                canDeleteSelected={!isDocumentReadOnly && canDeleteSelectedCategories()}
                canMoveSelected={(direction) =>
                  !isDocumentReadOnly && canMoveSelectedCategories(direction)
                }
                canCutSelected={!isDocumentReadOnly && canCutSelectedCategories()}
                canCopySelected={canCopySelectedCategories()}
                onDropCategories={
                  isDocumentReadOnly ? () => {} : handleDropCategoriesBetween
                }
                onDropCategoriesIntoContent={
                  isDocumentReadOnly ? () => {} : handleDropCategoriesIntoContent
                }
                onDropCategoriesIntoBranch={
                  isDocumentReadOnly ? () => {} : handleDropCategoriesIntoBranch
                }
                onDropContents={isDocumentReadOnly ? () => {} : handleDropContentsBetween}
                matchingContentIds={matchingContentIds}
                visibleSubcaseFilter={contentPaneSubcaseFilter}
                contentReorderDisabled={isContentPaneSubcaseFilterActive(contentPaneSubcaseFilter)}
                showContentIndex={showContentIndexInPane}
                paneLoading={categoryPaneLoading}
                cutContentIds={cutContentIds}
                cutCategoryIds={cutCategoryIds}
              />
            }
            rightPane={
              <ContentPanel
                ref={contentPanelRef}
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  opacity: isEditingContent ? 1 : 0.85,
                  '& .ql-editor': {
                    color: isEditingContent ? 'inherit' : theme.palette.text.primary,
                    backgroundColor: isEditingContent
                      ? 'inherit'
                      : theme.palette.mode === 'dark'
                        ? alpha(theme.palette.common.white, 0.04)
                        : alpha(theme.palette.common.black, 0.02),
                    '& p': {
                      margin: 0,
                      marginBottom: '0.35em',
                    },
                    '& p:last-child': {
                      marginBottom: 0,
                    },
                  },
                  '& .ql-container': {
                    borderColor: theme.palette.divider,
                  },
                  // Gray out controls when not in editing mode (except Add/Edit + panel subcase select)
                  '& .MuiButton-root, & .MuiIconButton-root': {
                    opacity: isEditingContent ? 1 : 0.5,
                  },
                  '& .MuiButton-root': {
                    opacity: isEditingContent ? 1 : 0.5,
                  },
                  '& .MuiIconButton-root': {
                    opacity: isEditingContent ? 1 : 0.5,
                  },
                  '& .panel-subcase-select': {
                    opacity: '1 !important',
                  },
                  '& .panel-subcase-select .MuiInputBase-root': {
                    opacity: '1 !important',
                  },
                  '& .content-preview-header-button': {
                    opacity: '1 !important',
                  },
                  '& .content-media-play-button': {
                    opacity: '1 !important',
                  },
                }}
              >
                <ContentPanelHeader>
                  <ContentPanelTitle>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        flexWrap: 'wrap',
                        width: '100%',
                      }}
                    >
                      <Tooltip
                        title={
                          isEditingContent
                            ? getToolbarTooltipTextForState('content-panel-editing-active', true)
                            : selectedContents.length !== 1
                              ? getToolbarTooltipTextForState('add-content', !!canAddContent())
                              : getToolbarTooltipTextForState('edit-content', !!canEditContent())
                        }
                        placement="bottom"
                      >
                        <span>
                          <Button
                            variant="contained"
                            size="small"
                            color="primary"
                            onClick={handleAddContent}
                            disabled={
                              isDocumentReadOnly || !(canAddContent() || canEditContent())
                            }
                            sx={{
                              opacity: '1 !important',
                              backgroundColor: (theme) => `${theme.palette.primary.dark} !important`,
                              '&:hover': {
                                backgroundColor: (theme) => `${theme.palette.primary.main} !important`,
                              },
                              '&.Mui-disabled': {
                                opacity: '1 !important',
                                backgroundColor: (theme) => `${theme.palette.grey[300]} !important`,
                                color: (theme) => `${theme.palette.grey[700]} !important`,
                              },
                            }}
                          >
                            {isEditingContent ? (
                              <Box
                                component="span"
                                sx={{
                                  '@keyframes contentHeaderEditingFlash': {
                                    '0%, 100%': { opacity: 1 },
                                    '50%': { opacity: 0.35 },
                                  },
                                  animation: 'contentHeaderEditingFlash 1s ease-in-out infinite',
                                  fontWeight: 600,
                                  letterSpacing: '0.02em',
                                  '@media (prefers-reduced-motion: reduce)': {
                                    animation: 'none',
                                    opacity: 1,
                                  },
                                }}
                              >
                                Editing…
                              </Box>
                            ) : canEditContent() ? (
                              'Edit Content'
                            ) : (
                              'Add Content'
                            )}
                          </Button>
                        </span>
                      </Tooltip>
                      <Tooltip
                        title={getToolbarTooltipTextForState(
                          'adjust-text-content',
                          !!canAdjustTextContent(),
                        )}
                        placement="bottom"
                      >
                        <span>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={handleAdjustTextContent}
                            disabled={isDocumentReadOnly || !canAdjustTextContent()}
                            startIcon={<NotesIcon />}
                            sx={{
                              opacity: '1 !important',
                              '&.Mui-disabled': {
                                opacity: '1 !important',
                              },
                            }}
                          >
                            Adjust text
                          </Button>
                        </span>
                      </Tooltip>
                      {selectedContents.length > 1 && (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.25,
                            border: 1,
                            borderColor: 'divider',
                            borderRadius: 1,
                            px: 0.25,
                          }}
                        >
                          <Tooltip title="Previous selected row (←)" placement="bottom">
                            <span>
                              <IconButton
                                size="small"
                                aria-label="Previous selected row"
                                onClick={() => stepSelectedContentPreview(-1)}
                                disabled={selectedContentsInPaneOrder.length <= 1}
                              >
                                <ChevronLeftIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Typography
                            variant="body2"
                            sx={{
                              minWidth: 52,
                              textAlign: 'center',
                              fontVariantNumeric: 'tabular-nums',
                              color: 'text.secondary',
                              userSelect: 'none',
                            }}
                          >
                            {selectedContentPreviewIndex >= 0
                              ? `${selectedContentPreviewIndex + 1} / ${selectedContentsInPaneOrder.length}`
                              : `— / ${selectedContentsInPaneOrder.length}`}
                          </Typography>
                          <Tooltip title="Next selected row (→)" placement="bottom">
                            <span>
                              <IconButton
                                size="small"
                                aria-label="Next selected row"
                                onClick={() => stepSelectedContentPreview(1)}
                                disabled={selectedContentsInPaneOrder.length <= 1}
                              >
                                <ChevronRightIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      )}
                      <Tooltip
                        title={getToolbarTooltipTextForState('content-large-preview', true)}
                        placement="bottom"
                      >
                        <span>
                          <Button
                            variant="outlined"
                            size="small"
                            color="primary"
                            className="content-preview-header-button"
                            onClick={openContentPreview}
                            disabled={contentPreviewDisabled}
                            sx={{
                              opacity: '1 !important',
                              borderColor: (theme) => `${theme.palette.primary.dark} !important`,
                              color: (theme) => `${theme.palette.primary.dark} !important`,
                              '&:hover': {
                                borderColor: (theme) => `${theme.palette.primary.main} !important`,
                                backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.06),
                              },
                              '&.Mui-disabled': {
                                opacity: '0.5 !important',
                                borderColor: (theme) => `${theme.palette.grey[400]} !important`,
                              },
                            }}
                          >
                            Content preview
                          </Button>
                        </span>
                      </Tooltip>
                      {contentEditorSubcases.length > 0 && (
                        <Box sx={{ minWidth: 180, ml: { xs: 0, sm: 'auto' } }}>
                          <DropdownFieldTooltip
                            title={getToolbarTooltipTextForState('select-subcase', true)}
                          >
                            <FormControl size="small" className="panel-subcase-select" sx={{ minWidth: 180 }}>
                              <PanelSubcaseSelect
                                variant="outlined"
                                value={selectedSubcase}
                                onChange={handleSubcaseChange}
                                displayEmpty
                              >
                                <MenuItem value="">
                                  <em>Select Subcase</em>
                                </MenuItem>
                                {contentEditorSubcases.map((subcase: string) => (
                                  <MenuItem key={subcase} value={subcase}>
                                    {subcase}
                                  </MenuItem>
                                ))}
                              </PanelSubcaseSelect>
                            </FormControl>
                          </DropdownFieldTooltip>
                        </Box>
                      )}
                    </Box>
                  </ContentPanelTitle>
                </ContentPanelHeader>
                <ContentPanelContent
                  sx={{ flex: 1, overflow: 'auto' }}
                >
                  <ContentEditor
                    ref={contentEditorRef}
                    key={`content-editor-${activeDocument?.id || 'none'}-${contentEditorResetNonce}`}
                    subcases={contentEditorSubcases}
                    subcaseSelectOnly={isContentPaneSubcaseFilterActive(contentPaneSubcaseFilter)}
                    initialSubcase={
                      currentContent?.id
                        ? currentContent.subcase
                        : isEditingContent && currentContent != null
                          ? selectedSubcase || currentContent.subcase || ''
                          : ''
                    }
                    initialText={(() => {
                      if (!currentContent) return '';
                      const row = resolveContentRowForDisplay(currentContent);
                      return row.richTextHtml?.trim() || row.richTextDelta?.trim() || row.text || '';
                    })()}
                    readOnly={isDocumentReadOnly || !isEditingContent}
                    onSave={handleContentSave}
                    onCancel={handleContentCancel}
                    onSubcaseChange={syncSubcasePanelAndContent}
                    highlightedText={highlightedText}
                    searchTerms={searchTerms}
                    searchMode={searchMode}
                    wholeWord={wholeWord}
                    caseSensitive={caseSensitive}
                    showWarning={showEditorWarning}
                    category={selectedCategory}
                    contentId={
                      currentContent?.id?.trim()
                        ? currentContent.id
                        : selectedContents.length === 1
                          ? selectedContents[0].id
                          : undefined
                    }
                    attachmentsJson={currentContent?.attachmentsJson}
                    onMediaAttachmentsChanged={refreshCurrentContentAttachmentsFromServer}
                  />
                </ContentPanelContent>
              </ContentPanel>
            }
            initialSplitPosition={rightPaneSplitPosition}
            minSplitPosition={30}
            maxSplitPosition={90}
            isHorizontal={true}
            hideResizer={!isContentPanelOpen}
          />
        </Box>
      );

      if (isSearchOpen && isFeatureEnabled(FEATURE_FLAG_KEYS.ASK_AI)) {
        return (
          <SplitPane
            leftPane={mainContent}
            rightPane={
              <Box sx={{ height: '100%', width: '100%' }}>
                <SearchSettingsProvider>
                  <SemanticSearchWindow
                    open={isSearchOpen && isFeatureEnabled(FEATURE_FLAG_KEYS.ASK_AI)}
                    onClose={() => setIsSearchOpen(false)}
                    documentId={activeDocument?.id || ''}
                  />
                </SearchSettingsProvider>
              </Box>
            }
            initialSplitPosition={70}
            minSplitPosition={50}
            maxSplitPosition={90}
          />
        );
      }

      return mainContent;
    };

    return (
      <>
        {isDocumentReadOnly && currentDocument?.ownerUsername && (
          <Alert severity="info" sx={{ mb: 1, flexShrink: 0 }}>
            View-only: <strong>{currentDocument.name}</strong> shared by{' '}
            <strong>{currentDocument.ownerUsername}</strong>. Changes are not saved.
            {ownedDocumentNames.includes(currentDocument.name) ? (
              <>
                {' '}
                You also have your own document named &quot;{currentDocument.name}&quot; in your
                library — use the tab title to tell them apart.
              </>
            ) : null}
          </Alert>
        )}
        <Box
          sx={{
            width: '100%',
            minWidth: 0,
            maxWidth: '100%',
            flexShrink: 0,
            overflow: 'hidden',
            boxSizing: 'border-box',
          }}
        >
          <StyledTabs
            value={currentTab || false}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
          >
            {tabs.map((tab) => (
              <Tab
                key={tab.id}
                value={tab.id}
                aria-label={`Document: ${tab.label}`}
                label={
                  <TabLabel>
                    <DocumentTabTitle label={tab.label} />
                    <IconButton
                      component="span"
                      size="small"
                      aria-label={`Close ${tab.label}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCloseTab(e, tab.id);
                      }}
                      onMouseDown={(e) => e.stopPropagation()}
                      sx={(t) => ({
                        flexShrink: 0,
                        ml: 0.125,
                        p: 0,
                        width: 22,
                        height: 22,
                        minWidth: 22,
                        borderRadius: '50%',
                        color: alpha(t.palette.text.secondary, 0.52),
                        transition: t.transitions.create(['color', 'background-color'], {
                          duration: t.transitions.duration.shorter,
                        }),
                        '& .MuiSvgIcon-root': { fontSize: 15 },
                        '&:hover': {
                          color: alpha(t.palette.text.primary, 0.88),
                          bgcolor: alpha(t.palette.common.black, 0.07),
                        },
                        '&:active': {
                          bgcolor: alpha(t.palette.common.black, 0.1),
                        },
                        '.MuiTab-root.Mui-selected &': {
                          color: alpha(t.palette.text.secondary, 0.62),
                        },
                        '.MuiTab-root.Mui-selected &:hover': {
                          color: alpha(t.palette.text.primary, 0.9),
                          bgcolor: alpha(t.palette.common.black, 0.08),
                        },
                      })}
                    >
                      <CloseRoundedIcon aria-hidden />
                    </IconButton>
                  </TabLabel>
                }
              />
            ))}
          </StyledTabs>
        </Box>
        <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          {/* One HTML5 backend for the whole workspace; mount only the active tab to avoid duplicate backends */}
          <DndProvider backend={HTML5Backend}>
            <CustomDragLayer />
            {tabs.map((tab) =>
              currentTab === tab.id ? (
                isDocumentCompareTabId(tab.id) && compareSessions[tab.id] ? (
                  <Box
                    key={tab.id}
                    role="tabpanel"
                    sx={(t) => ({
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      flex: 1,
                      backgroundColor: t.palette.background.paper,
                    })}
                  >
                    {documentMergeCompareEnabled ? (
                      <DocumentCompareView
                        session={compareSessions[tab.id]}
                        onMergeSelectedContent={(selection) =>
                          handleMergeCompareSelectedContent(tab.id, selection)
                        }
                        onUndoLastCopy={() => handleCompareUndoLastCopy(tab.id)}
                        canUndoLastCopy={
                          (compareSessions[tab.id]?.copyUndoStack?.length ?? 0) > 0
                        }
                        onMergeSettingsChange={(resolveMissing, resolveTypeMismatch) =>
                          handleCompareMergeSettingsChange(
                            tab.id,
                            resolveMissing,
                            resolveTypeMismatch,
                          )
                        }
                        mergeApplying={mergeCompareApplyingTabId === tab.id}
                        canDeleteOnSide={(side) =>
                          canDeleteOnCompareSide(compareSessions[tab.id], side, false)
                        }
                        onDeleteContent={(side, content, categoryId, parentName) =>
                          handleCompareDeleteContentRequest(
                            tab.id,
                            side,
                            content,
                            categoryId,
                            parentName,
                          )
                        }
                        onDeleteCategory={(side, category, parentName, parentId, index) =>
                          handleCompareDeleteCategoryRequest(
                            tab.id,
                            side,
                            category,
                            parentName,
                            parentId,
                            index,
                          )
                        }
                      />
                    ) : (
                      <Alert severity="info" sx={{ m: 2 }}>
                        Document merge compare is disabled for this installation. Enable the
                        Document merge compare feature flag in the admin console, or close this
                        tab.
                      </Alert>
                    )}
                  </Box>
                ) : isSelectiveMergeTabId(tab.id) && selectiveMergeSessions[tab.id] ? (
                  <Box
                    key={tab.id}
                    role="tabpanel"
                    sx={(t) => ({
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      flex: 1,
                      backgroundColor: t.palette.background.paper,
                    })}
                  >
                    {documentMergeCompareEnabled ? (
                      <DocumentCompareView
                        session={buildSelectiveMergeDisplaySession(
                          selectiveMergeSessions[tab.id],
                        )}
                        selectiveMerge={{
                          baseline: selectiveMergeSessions[tab.id].baseline,
                          sideALabel: selectiveMergeSessions[tab.id].sideA.label,
                          sideBLabel: selectiveMergeSessions[tab.id].sideB.label,
                          activeSourceSide: selectiveMergeActiveSourceSide(
                            selectiveMergeSessions[tab.id],
                          ),
                          onActiveSourceSideChange:
                            selectiveMergeSessions[tab.id].baseline === 'intersection'
                              ? (side) =>
                                  handleSelectiveMergeActiveSourceChange(tab.id, side)
                              : undefined,
                        }}
                        onMergeSelectedContent={(selection) =>
                          handleSelectiveMergeSelectedContent(tab.id, selection)
                        }
                        onUndoLastCopy={() => handleCompareUndoLastCopy(tab.id)}
                        canUndoLastCopy={
                          (selectiveMergeSessions[tab.id]?.copyUndoStack?.length ?? 0) > 0
                        }
                        onMergeSettingsChange={(resolveMissing, resolveTypeMismatch) =>
                          setSelectiveMergeSessions((prev) => ({
                            ...prev,
                            [tab.id]: {
                              ...prev[tab.id],
                              resolveMissing,
                              resolveTypeMismatch,
                            },
                          }))
                        }
                        mergeApplying={mergeCompareApplyingTabId === tab.id}
                        canDeleteOnSide={(side) =>
                          canDeleteOnCompareSide(
                            buildSelectiveMergeDisplaySession(selectiveMergeSessions[tab.id]),
                            side,
                            true,
                          )
                        }
                        onDeleteContent={(side, content, categoryId, parentName) =>
                          handleCompareDeleteContentRequest(
                            tab.id,
                            side,
                            content,
                            categoryId,
                            parentName,
                          )
                        }
                        onDeleteCategory={(side, category, parentName, parentId, index) =>
                          handleCompareDeleteCategoryRequest(
                            tab.id,
                            side,
                            category,
                            parentName,
                            parentId,
                            index,
                          )
                        }
                      />
                    ) : (
                      <Alert severity="info" sx={{ m: 2 }}>
                        Document merge compare is disabled for this installation.
                      </Alert>
                    )}
                  </Box>
                ) : (
                <Box
                  key={tab.id}
                  role="tabpanel"
                  sx={(t) => ({
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                    backgroundColor: t.palette.background.paper,
                  })}
                >
                  <SplitPane
                    leftPane={
                      <CategoryTree
                        categories={categories}
                        selectedCategoryId={selectedCategory?.id || null}
                        onCategorySelect={handleCategorySelect}
                        onCategoriesChange={handleCategoriesChange}
                        onSplitCategory={
                          isDocumentReadOnly ? () => {} : handleSplitDialogOpen
                        }
                        onAddCategory={
                          isDocumentReadOnly ? () => {} : handleAddCategoryDialogOpen
                        }
                        onAddContent={isDocumentReadOnly ? () => {} : handleAddContent}
                        onDeleteCategory={isDocumentReadOnly ? () => {} : handleDelete}
                        onFlattenCategory={
                          isDocumentReadOnly ? () => {} : handleFlattenCategory
                        }
                        onCollapseSubtreeToContentCategory={
                          isDocumentReadOnly
                            ? () => {}
                            : handleCollapseSubtreeToContentCategory
                        }
                        canCollapseSubtreeToContentCategory={(cat) =>
                          !isDocumentReadOnly && canCollapseSubtreeToContentCategory(cat)
                        }
                        onAddMatchingContent={
                          isFeatureEnabled(FEATURE_FLAG_KEYS.SEMANTIC_FIND)
                            ? handleAddMatchingContentDialogOpen
                            : undefined
                        }
                        canAddMatchingContent={(cat) =>
                          !isDocumentReadOnly && canAddMatchingContentFor(cat)
                        }
                        onCompareContent={
                          isFeatureEnabled(FEATURE_FLAG_KEYS.COMPARE_CONTENT_SELECTION)
                            ? handleCompareContentDialogOpen
                            : undefined
                        }
                        canCompareContent={(cat) =>
                          !isDocumentReadOnly && canCompareContentFor(cat)
                        }
                        onRenameCategory={
                          isDocumentReadOnly ? () => {} : handleRenameCategory
                        }
                        onMoveCategory={isDocumentReadOnly ? () => {} : handleMoveCategory}
                        onCutCategory={isDocumentReadOnly ? () => {} : handleCutCategory}
                        onCopyCategory={handleCopyCategory}
                        onPasteCategory={isDocumentReadOnly ? () => {} : handlePasteCategory}
                        onImportSubtreeFromDocument={() =>
                          setImportSubtreeDialogOpen(true)
                        }
                        onDuplicateAsDocument={
                          isDocumentReadOnly
                            ? undefined
                            : () => setDuplicateCategoryDialogOpen(true)
                        }
                        canAddCategory={(cat) => !isDocumentReadOnly && canAddCategory(cat)}
                        canDeleteCategory={(cat) => !isDocumentReadOnly && canDeleteCategory(cat)}
                        canFlattenCategory={() => !isDocumentReadOnly && canFlattenCategory()}
                        canMoveCategory={(cat, dir) =>
                          !isDocumentReadOnly && canMoveCategory(cat, dir)
                        }
                        canCutCategory={(cat) => !isDocumentReadOnly && canCutCategory(cat)}
                        canCopyCategory={canCopyCategory}
                        canPasteCategory={(cat) => !isDocumentReadOnly && canPaste(cat)}
                        canImportSubtreeFromDocument={(cat) =>
                          !isDocumentReadOnly && canImportSubtreeFromDocument(cat)
                        }
                        canSplitCategory={!isDocumentReadOnly && !!selectedCategory}
                        canRenameCategory={(cat) => !isDocumentReadOnly && canRenameCategory(cat)}
                        validateCategoryRenameName={getCategoryRenameValidationError}
                        onCategoryInlineRenameCommit={handleCategoryInlineRenameCommit}
                        clickToRenameInTreeEnabled={clickToRenameCategoryInTree}
                        renameDialogOpen={renameDialogOpen}
                        clipboardCategory={clipboardCategory}
                        clipboardContents={clipboardContents}
                        cutCategoryIds={cutCategoryIds}
                        expandedNodes={expandedNodes}
                        onNodeToggle={handleNodeToggle}
                        onDragStart={isDocumentReadOnly ? () => {} : handleDragStart}
                        onDragOver={isDocumentReadOnly ? () => {} : handleDragOver}
                        onDropCategory={isDocumentReadOnly ? () => {} : handleDropCategory}
                        onDropCategoriesIntoContent={
                          isDocumentReadOnly ? () => {} : handleDropCategoriesIntoContent
                        }
                        onDropCategoriesIntoBranch={
                          isDocumentReadOnly ? () => {} : handleDropCategoriesIntoBranch
                        }
                        canDropCategoriesIntoBranch={
                          isDocumentReadOnly
                            ? () => false
                            : canDropCategoriesIntoBranch
                        }
                        onDropContents={isDocumentReadOnly ? () => {} : handleDropContents}
                        category={selectedCategory || categories[0]}
                        onCategoryMultiSelect={handleCategoryMultiSelect}
                        selectedCategories={selectedCategories}
                        onOrganizeCategories={handleOrganizeCategoriesFromTree}
                        canOrganizeCategories={(cat) =>
                          !isDocumentReadOnly &&
                          (cat?.id === selectedCategory?.id
                            ? canOrganizeCategories()
                            : canGenerateSubtree(cat) || canOrganizeBranch(cat))
                        }
                        showOrganizeCategories={
                          !isDocumentReadOnly &&
                          isFeatureEnabled(FEATURE_FLAG_KEYS.GENERATE_SUBTREE_FROM_CONTENT)
                        }
                        onCategoryAiReport={handleCategoryAiReportFromTree}
                        canCategoryAiReport={canCategoryAiReport}
                        showCategoryAiReport={
                          isFeatureEnabled(FEATURE_FLAG_KEYS.CATEGORY_AI_REPORT)
                        }
                        onShowSubtree={openSubTreeOverlay}
                        visibleSubcaseFilter={contentPaneSubcaseFilter}
                      />
                    }
                    rightPane={
                      <Box sx={{ position: 'relative', height: '100%', width: '100%' }}>
                        {renderRightPane()}
                        {subTreeOverlayOpen && (
                          <Box
                            sx={(t) => ({
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              width: '100%',
                              height: '100%',
                              backgroundColor: t.palette.background.paper,
                              color: t.palette.text.primary,
                              zIndex: 1000,
                              px: 2,
                              pt: 1,
                              pb: 2,
                              display: 'flex',
                              flexDirection: 'column',
                              minHeight: 0,
                            })}
                          >
                            <IconButton
                              onClick={() => setSubTreeOverlayOpen(false)}
                              aria-label="Close Category Structure Overview"
                              sx={{ position: 'absolute', top: 8, right: 8, zIndex: 2, color: 'text.primary' }}
                            >
                              <CloseIcon />
                            </IconButton>
                            {subTreeOverlayOpening ? (
                              <Box
                                sx={{
                                  flex: 1,
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  gap: 2,
                                  minHeight: 0,
                                }}
                              >
                                <CircularProgress size={36} aria-label="Loading Category Structure Overview" />
                                <Typography variant="body2" color="text.secondary">
                                  Loading Category Structure Overview…
                                </Typography>
                              </Box>
                            ) : subTreeOverlayData.category ? (
                            <>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 1,
                                flexWrap: 'wrap',
                                flexShrink: 0,
                                mb: subTreeOverlayFilterExpanded ? 0.5 : 0,
                                pr: 6,
                                width: '100%',
                              }}
                            >
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                  flexWrap: 'wrap',
                                  minWidth: 0,
                                }}
                              >
                                <Tooltip
                                  title={
                                    subTreeOverlayFilterExpanded
                                      ? 'Hide subcase filter (maximize diagram)'
                                      : 'Show subcase and group filters'
                                  }
                                >
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    color="inherit"
                                    startIcon={
                                      subTreeOverlayFilterExpanded ? (
                                        <ExpandLessIcon fontSize="small" />
                                      ) : (
                                        <ExpandMoreIcon fontSize="small" />
                                      )
                                    }
                                    onClick={() => setSubTreeOverlayFilterExpanded(e => !e)}
                                    sx={{ borderColor: 'divider' }}
                                  >
                                    {subTreeOverlayFilterExpanded ? 'Hide filters' : 'Filters'}
                                  </Button>
                                </Tooltip>
                                {!subTreeOverlayFilterExpanded && (
                                  <Typography variant="caption" color="text.secondary" component="span">
                                    {(() => {
                                      const total = activeDocument?.subcases?.length ?? 0;
                                      const n = subTreeOverlayFilterSubcases.length;
                                      if (total === 0) {
                                        return 'No document subcases';
                                      }
                                      if (n === 0) {
                                        return 'No subcases selected (diagram empty until you select some in Filters)';
                                      }
                                      if (
                                        n === total &&
                                        subTreeOverlayFilterGroupNames.length === 0
                                      ) {
                                        return 'Showing all subcases';
                                      }
                                      const bits = [`${n}/${total} subcases`];
                                      if (subTreeOverlayFilterGroupNames.length > 0) {
                                        bits.push(
                                          `${subTreeOverlayFilterGroupNames.length} group(s)`,
                                        );
                                      }
                                      return bits.join(' · ');
                                    })()}
                                  </Typography>
                                )}
                              </Box>
                              <Box
                                sx={{
                                  display: 'inline-flex',
                                  alignItems: 'stretch',
                                  '& .MuiToggleButton-root:first-of-type': {
                                    borderTopRightRadius: 0,
                                    borderBottomRightRadius: 0,
                                  },
                                  '& .MuiToggleButton-root:last-of-type': {
                                    borderTopLeftRadius: 0,
                                    borderBottomLeftRadius: 0,
                                    marginLeft: '-1px',
                                  },
                                }}
                              >
                                <Tooltip
                                  title={
                                    getToolbarTooltipTextForState('subtree-layout-vertical', true) ||
                                    helpContent['subtree-layout-vertical'].simple
                                  }
                                  placement="bottom"
                                >
                                  <span>
                                    <ToggleButton
                                      size="small"
                                      value="vertical"
                                      selected={subTreeOverlayLayoutMode === 'vertical'}
                                      onClick={() => persistSubTreeOverlayLayoutMode('vertical')}
                                    >
                                      Textual
                                    </ToggleButton>
                                  </span>
                                </Tooltip>
                                <Tooltip
                                  title={
                                    getToolbarTooltipTextForState('subtree-layout-horizontal', true) ||
                                    helpContent['subtree-layout-horizontal'].simple
                                  }
                                  placement="bottom"
                                >
                                  <span>
                                    <ToggleButton
                                      size="small"
                                      value="horizontal"
                                      selected={subTreeOverlayLayoutMode === 'horizontal'}
                                      onClick={() => persistSubTreeOverlayLayoutMode('horizontal')}
                                    >
                                      Graphical
                                    </ToggleButton>
                                  </span>
                                </Tooltip>
                              </Box>
                            </Box>
                            {subTreeOverlayFilterExpanded && (
                            <Paper variant="outlined" sx={{ p: 1.5, mb: 0.5, flexShrink: 0, pr: 6 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                                <Typography variant="subtitle2">
                                  Subcases &amp; groups
                                </Typography>
                                <Button size="small" onClick={() => setSubTreeOverlayFilterExpanded(false)}>
                                  Done
                                </Button>
                              </Box>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                Narrow the view with groups or individual subcases. Use Hide filters when finished for a larger tree.
                              </Typography>
                              {loadingSubcaseGroups ? (
                                <Box sx={{ my: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <CircularProgress size={18} />
                                  <Typography variant="body2">Loading groups…</Typography>
                                </Box>
                              ) : (
                                subcaseGroups.length > 0 && (
                                  <Box sx={{ mb: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                    {subcaseGroups.map(group => (
                                      <Chip
                                        key={group.name}
                                        label={group.name}
                                        color={
                                          subTreeOverlayFilterGroupNames.includes(group.name) ? 'primary' : 'default'
                                        }
                                        onClick={() => handleSubTreeOverlayGroupSelect(group.name)}
                                        sx={{ cursor: 'pointer' }}
                                      />
                                    ))}
                                  </Box>
                                )
                              )}
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', mb: 0.5 }}>
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={
                                        subTreeOverlayFilterSubcases.length ===
                                          (activeDocument?.subcases?.length || 0) &&
                                        (activeDocument?.subcases?.length || 0) > 0
                                      }
                                      indeterminate={
                                        subTreeOverlayFilterSubcases.length > 0 &&
                                        subTreeOverlayFilterSubcases.length <
                                          (activeDocument?.subcases?.length || 0)
                                      }
                                      onChange={e => {
                                        subTreeOverlaySubcasesBaselineRef.current = null;
                                        if (e.target.checked) {
                                          setSubTreeOverlayFilterSubcases([...(activeDocument?.subcases || [])]);
                                          setSubTreeOverlayFilterGroupNames([]);
                                        } else {
                                          setSubTreeOverlayFilterSubcases([]);
                                          setSubTreeOverlayFilterGroupNames([]);
                                        }
                                      }}
                                    />
                                  }
                                  label="Select all subcases"
                                />
                                <InvertSelectionButton
                                  disabled={(activeDocument?.subcases?.length || 0) === 0}
                                  onClick={() => {
                                    const pool = activeDocument?.subcases || [];
                                    subTreeOverlaySubcasesBaselineRef.current = null;
                                    setSubTreeOverlayFilterGroupNames([]);
                                    setSubTreeOverlayFilterSubcases(prev =>
                                      invertSubcaseSelection(pool, prev),
                                    );
                                  }}
                                />
                              </Box>
                              <List dense sx={{ maxHeight: 200, overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 1 }}>
                                {(activeDocument?.subcases || []).map((subcase: string) => (
                                  <ListItem key={subcase} disablePadding>
                                    <ListItemButton
                                      onClick={() => {
                                        setSubTreeOverlayFilterSubcases(prev =>
                                          prev.includes(subcase)
                                            ? prev.filter(s => s !== subcase)
                                            : [...prev, subcase],
                                        );
                                      }}
                                    >
                                      <Checkbox
                                        edge="start"
                                        checked={subTreeOverlayFilterSubcases.includes(subcase)}
                                        tabIndex={-1}
                                        disableRipple
                                        size="small"
                                      />
                                      <ListItemText primary={subcase} />
                                    </ListItemButton>
                                  </ListItem>
                                ))}
                              </List>
                            </Paper>
                            )}
                            <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden', position: 'relative' }}>
                              <SubTreeView
                                rootCategory={subTreeOverlayData.category}
                                allCategories={categories}
                                subcases={subTreeOverlayFilterSubcases}
                                availableSubcases={activeDocument?.subcases ?? []}
                                subcaseGroups={subcaseGroups}
                                subcaseGroupsLoading={loadingSubcaseGroups}
                                includeDescriptions={includeDescriptions}
                                layoutMode={subTreeOverlayLayoutMode}
                                onLayoutModeChange={persistSubTreeOverlayLayoutMode}
                                onCopyContent={isDocumentReadOnly ? undefined : handleOverviewCopyRequest}
                                onDeleteContent={isDocumentReadOnly ? undefined : handleOverviewDeleteRequest}
                                onAdjustContent={isDocumentReadOnly ? undefined : handleOverviewAdjustRequest}
                                canAdjustContent={isDocumentReadOnly ? undefined : canAdjustOverviewContent}
                              />
                              {subTreeOverlayScopeLoading && !subTreeOverlayOpening && (
                                <Box
                                  sx={{
                                    position: 'absolute',
                                    inset: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    bgcolor: (t) => alpha(t.palette.background.paper, 0.65),
                                    zIndex: 1,
                                  }}
                                >
                                  <CircularProgress size={28} aria-label="Loading overview scope" />
                                </Box>
                              )}
                            </Box>
                            </>
                            ) : null}
                          </Box>
                        )}
                      </Box>
                    }
                    initialSplitPosition={30}
                    minSplitPosition={20}
                    maxSplitPosition={80}
                  />
                </Box>
                )
              ) : null
            )}
          </DndProvider>
        </Box>
      </>
    );
  };

  useEffect(() => {
    if ((subTreeOverlayOpen || contentPaneFilterDialogOpen) && activeDocument?.id) {
      setLoadingSubcaseGroups(true);
      api.get('/documents/subcase-groups', { params: { documentId: activeDocument?.categoryId } })
        .then(res => {
          const groupsMap = res.data;
          const groupsArray = Object.entries(groupsMap).map(([name, subcases]) => ({
            name,
            subcases: subcases as string[],
          }));
          setSubcaseGroups(groupsArray);
        })
        .catch(() => setSubcaseGroups([]))
        .finally(() => setLoadingSubcaseGroups(false));
    }
  }, [subTreeOverlayOpen, contentPaneFilterDialogOpen, activeDocument?.id]);

  useEffect(() => {
    if (!isFeatureEnabled(FEATURE_FLAG_KEYS.FILTER_CONTENT_PANE_BY_SUBCASE)) {
      setContentPaneFilterDialogOpen(false);
      setContentPaneSubcaseFilter(null);
    }
  }, [isFeatureEnabled]);

  useEffect(() => {
    if (!subTreeOverlayOpen || !selectedCategory) return;
    const overlayCategoryId = subTreeOverlayData.category?.id;
    if (overlayCategoryId && selectedCategory.id === overlayCategoryId) return;

    let cancelled = false;
    void (async () => {
      const scope = await ensureOverviewScopeLoaded(selectedCategory);
      if (cancelled || !scope) return;
      setSubTreeOverlayData((prev) => ({
        ...prev,
        category: scope,
      }));
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedCategory?.id, subTreeOverlayOpen, ensureOverviewScopeLoaded]);

  useEffect(() => {
    if (!subTreeOverlayOpen) {
      overviewScopeLoadSeqRef.current += 1;
      setSubTreeOverlayScopeLoading(false);
      setSubTreeOverlayOpening(false);
      setSubTreeOverlayFilterExpanded(false);
    }
  }, [subTreeOverlayOpen]);

  // Load subcase groups when find dialog opens
  useEffect(() => {
    if (findDialogOpen && activeDocument?.id) {
      setLoadingSubcaseGroups(true);
      api.get('/documents/subcase-groups', { params: { documentId: activeDocument?.categoryId } })
        .then(res => {
          const groupsMap = res.data;
          const groupsArray = Object.entries(groupsMap).map(([name, subcases]) => ({
            name,
            subcases: subcases as string[],
          }));
          setSubcaseGroups(groupsArray);
        })
        .catch(() => setSubcaseGroups([]))
        .finally(() => setLoadingSubcaseGroups(false));
    }
  }, [findDialogOpen, activeDocument?.id]);

  // Load subcase groups when duplicate category dialog opens
  useEffect(() => {
    if (duplicateCategoryDialogOpen && activeDocument?.id) {
      setLoadingSubcaseGroups(true);
      api.get('/documents/subcase-groups', { params: { documentId: activeDocument?.categoryId } })
        .then(res => {
          const groupsMap = res.data;
          const groupsArray = Object.entries(groupsMap).map(([name, subcases]) => ({
            name,
            subcases: subcases as string[],
          }));
          setSubcaseGroups(groupsArray);
        })
        .catch(() => setSubcaseGroups([]))
        .finally(() => setLoadingSubcaseGroups(false));
    }
  }, [duplicateCategoryDialogOpen, activeDocument?.id]);

  const subcasesUnionForSubcaseGroupNames = (names: string[]) => {
    const acc = new Set<string>();
    names.forEach(n => {
      const g = subcaseGroups.find(x => x.name === n);
      g?.subcases.forEach(s => acc.add(s));
    });
    return acc;
  };

  const handleSubTreeOverlayGroupSelect = (groupName: string) => {
    const group = subcaseGroups.find(g => g.name === groupName);
    if (!group) return;

    const docSubcases = activeDocument?.subcases || [];
    const allDocSubcasesSelected = (selected: string[]) =>
      docSubcases.length > 0 &&
      selected.length === docSubcases.length &&
      docSubcases.every(s => selected.includes(s));

    setSubTreeOverlayFilterGroupNames(prevNames => {
      const turningOff = prevNames.includes(groupName);
      const nextNames = turningOff
        ? prevNames.filter(n => n !== groupName)
        : [...prevNames, groupName];

      setSubTreeOverlayFilterSubcases(prevSub => {
        if (!turningOff) {
          if (prevNames.length === 0) {
            subTreeOverlaySubcasesBaselineRef.current = [...prevSub];
          }
          if (allDocSubcasesSelected(prevSub) && prevNames.length === 0) {
            return [...group.subcases];
          }
          const merged = [...prevSub];
          for (const s of group.subcases) {
            if (merged.indexOf(s) === -1) merged.push(s);
          }
          return merged;
        }
        const cover = subcasesUnionForSubcaseGroupNames(nextNames);
        const filtered = prevSub.filter(s => !group.subcases.includes(s) || cover.has(s));
        if (nextNames.length === 0) {
          if (subTreeOverlaySubcasesBaselineRef.current !== null) {
            const restored = [...subTreeOverlaySubcasesBaselineRef.current];
            subTreeOverlaySubcasesBaselineRef.current = null;
            return restored;
          }
          return filtered.length === 0 ? [...docSubcases] : filtered;
        }
        return filtered;
      });

      return nextNames;
    });
  };

  const persistSubTreeOverlayLayoutMode = useCallback((v: 'vertical' | 'horizontal') => {
    setSubTreeOverlayLayoutMode(v);
    try {
      localStorage.setItem('subtree_layout_mode', v);
    } catch {
      /* ignore */
    }
  }, []);

  /** Opened from a content row in the Category Structure Overview. Opens the "Copy to…" picker without leaving the overview. */
  const handleOverviewCopyRequest = useCallback((payload: {
    contentId: string;
    parentId: string;
    parentName: string;
    content: Content;
  }) => {
    if (effectiveDocumentReadOnly) return;
    setOverviewCopySource({
      content: payload.content,
      parentId: payload.parentId,
      parentName: payload.parentName,
    });
    setOverviewCopyTargetId('');
    setOverviewCopyError(null);
    setOverviewCopyBusy(false);
  }, [effectiveDocumentReadOnly]);

  /** Opened from a content row in the Category Structure Overview. Confirms deletion without leaving the overview. */
  const handleOverviewDeleteRequest = useCallback((contentId: string) => {
    if (effectiveDocumentReadOnly) return;
    const roots: Category[] = subTreeOverlayData.category
      ? [subTreeOverlayData.category]
      : categories;
    const parent =
      findParentCategoryInTree(roots, contentId) ??
      findParentCategoryInTree(categories, contentId);
    const content = parent?.contents?.find(c => c.id === contentId) ?? null;
    if (!parent || !content) return;
    setOverviewDeleteSource({ content, categoryId: parent.id, parentName: parent.name });
    setOverviewDeleteBusy(false);
  }, [categories, effectiveDocumentReadOnly, subTreeOverlayData.category]);

  /** Opened from a content row in the Category Structure Overview. Opens Adjust text without leaving the overview. */
  const handleOverviewAdjustRequest = useCallback((contentId: string) => {
    if (effectiveDocumentReadOnly) return;
    const roots: Category[] = subTreeOverlayData.category
      ? [subTreeOverlayData.category]
      : categories;
    const parent =
      findParentCategoryInTree(roots, contentId) ??
      findParentCategoryInTree(categories, contentId);
    const content = parent?.contents?.find(c => c.id === contentId) ?? null;
    if (!parent || !content || !canAdjustContentRowInCategory(content, parent)) return;
    setAdjustTextCategoryId(parent.id);
    setAdjustTextRow(content);
  }, [categories, effectiveDocumentReadOnly, subTreeOverlayData.category]);

  const canAdjustOverviewContent = useCallback((contentId: string): boolean => {
    const roots: Category[] = subTreeOverlayData.category
      ? [subTreeOverlayData.category]
      : categories;
    const parent =
      findParentCategoryInTree(roots, contentId) ??
      findParentCategoryInTree(categories, contentId);
    const content = parent?.contents?.find(c => c.id === contentId) ?? null;
    return !!(parent && content && canAdjustContentRowInCategory(content, parent));
  }, [categories, subTreeOverlayData.category]);

  const performOverviewDelete = async () => {
    if (!overviewDeleteSource) return;
    const { content, categoryId } = overviewDeleteSource;
    const parent = findCategoryInTree(categories, categoryId);
    const oldIndex = parent?.contents?.findIndex(c => c.id === content.id) ?? -1;

    setOverviewDeleteBusy(true);
    try {
      historyService.recordOperation({
        type: 'deleteContent',
        data: [{ content, categoryId, index: oldIndex }],
        isBatch: false,
      });
      await api.delete('/content', { params: { contentId: content.id } });
      await refreshActiveDocumentSubcasesFromServer();

      setCategories(prev => {
        const newCategories = [...prev];
        const category = findCategoryInTree(newCategories, categoryId);
        if (category) {
          category.contents = category.contents?.filter(c => c.id !== content.id);
        }
        return newCategories;
      });

      setSelectedContents(prev => prev.filter(c => c.id !== content.id));
      if (currentContent?.id === content.id) {
        setCurrentContent(null);
      }
      setOverviewDeleteSource(null);
    } catch (error) {
      console.error('Error deleting content from overview:', error);
      alert('Failed to delete content');
    } finally {
      setOverviewDeleteBusy(false);
    }
  };

  const performOverviewCopy = async () => {
    if (!overviewCopySource || !overviewCopyTargetId) return;
    const { content, parentId } = overviewCopySource;
    const isMove = overviewCopyMode === 'move';
    if (overviewCopyTargetId === parentId) {
      setOverviewCopyError('This row already lives in that category. Pick a different one.');
      return;
    }
    const overlayRoots = subTreeOverlayData.category ? [subTreeOverlayData.category] : [];
    let targetCategory =
      findCategoryInTree(categories, overviewCopyTargetId) ??
      findCategoryInTree(overlayRoots, overviewCopyTargetId);
    if (!targetCategory || !targetCategory.contentCategory) {
      setOverviewCopyError('Pick a content category as the destination.');
      return;
    }
    if (!categoryContentsAreLoaded(targetCategory)) {
      const loaded = await loadContentRowsIntoTree(targetCategory.id);
      if (!loaded) {
        setOverviewCopyError('Could not load the destination category.');
        return;
      }
      targetCategory = loaded;
    }
    if (hasDuplicateContent(targetCategory, content.subcase, content.text, undefined, content.richTextHtml)) {
      setOverviewCopyError(`An identical row already exists in "${targetCategory.name}".`);
      return;
    }

    const targetId = targetCategory.id;
    const targetName = targetCategory.name;

    setOverviewCopyBusy(true);
    setOverviewCopyError(null);
    try {
      const oldIndex = findParentCategoryInTree(categories, content.id)?.contents?.findIndex(c => c.id === content.id) ?? -1;
      let newContent: Content;
      if (isMove) {
        const response = await api.put(
          '/content/move',
          { contentId: content.id, newParentId: targetId },
          { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } },
        );
        newContent = response.data;
      } else {
        const response = await api.post('/content', buildContentCopyPayload(content, targetId));
        newContent = response.data;
      }

      historyService.recordOperation({
        type: 'dropContent',
        data: [{ content: newContent, oldContentId: content.id, oldParentId: parentId, oldIndex }],
        isBatch: isMove,
        label: isMove ? 'Move content to category' : 'Copy content to category',
      });

      setCategories(prev => {
        let next = prev;
        if (isMove) {
          next = updateCategoryInTree(next, parentId, (cat) => ({
            ...cat,
            contents: (cat.contents || []).filter(c => c.id !== content.id),
          }));
        }
        next = updateCategoryInTree(next, targetId, (cat) => ({
          ...cat,
          contents: [...(cat.contents || []), newContent],
        }));
        const overlayRootId = subTreeOverlayData.category?.id;
        if (overlayRootId) {
          const refreshedRoot = findCategoryInTree(next, overlayRootId);
          if (refreshedRoot) {
            setTimeout(() => setSubTreeOverlayData(prevData => ({ ...prevData, category: refreshedRoot })), 0);
          }
        }
        return next;
      });

      await refreshActiveDocumentSubcasesFromServer();

      setOverviewCopySource(null);
      setOverviewCopyTargetId('');
      setOverviewCopySnackbarMessage(`${isMove ? 'Moved' : 'Copied'} to "${targetName}".`);
      setOverviewCopySnackbarOpen(true);
    } catch {
      setOverviewCopyError(`Failed to ${isMove ? 'move' : 'copy'} this row. Please try again.`);
    } finally {
      setOverviewCopyBusy(false);
    }
  };

  const overviewCopyTargetIds = useMemo(() => {
    const ids = new Set<string>();
    if (!overviewCopySource) return ids;
    const root =
      (activeDocument?.categoryId
        ? findCategoryInTree(categories, activeDocument.categoryId)
        : null) ??
      subTreeOverlayData.category ??
      null;
    if (!root) return ids;
    const walk = (cat: Category) => {
      if (cat.contentCategory && cat.id !== overviewCopySource.parentId) {
        ids.add(cat.id);
      }
      (cat.children ?? []).forEach(walk);
    };
    walk(root);
    return ids;
  }, [overviewCopySource, activeDocument?.categoryId, categories, subTreeOverlayData.category]);

  const openSubTreeOverlay = useCallback(async () => {
    if (!selectedCategory) return;
    if (subTreeOverlayOpen) {
      setSubTreeOverlayOpen(false);
      return;
    }
    const all = [...(activeDocument?.subcases ?? [])];
    setSubTreeOverlayFilterSubcases(all);
    setSubTreeOverlayFilterGroupNames([]);
    subTreeOverlaySubcasesBaselineRef.current = null;
    setSubTreeOverlayFilterExpanded(false);
    try {
      const saved = localStorage.getItem('subtree_layout_mode');
      setSubTreeOverlayLayoutMode(saved === 'horizontal' ? 'horizontal' : 'vertical');
    } catch {
      setSubTreeOverlayLayoutMode('vertical');
    }
    const scopeInTree = findCategoryInTree(categories, selectedCategory.id) ?? selectedCategory;
    const needsLoad = !subtreeFullyLoaded(scopeInTree);
    setSubTreeOverlayOpening(needsLoad);
    setSubTreeOverlayData({
      category: scopeInTree,
      includeDescriptions,
    });
    setSubTreeOverlayOpen(true);
    const scope = await ensureOverviewScopeLoaded(selectedCategory);
    setSubTreeOverlayOpening(false);
    if (!scope) {
      setSubTreeOverlayOpen(false);
      return;
    }
    setSubTreeOverlayData({
      category: scope,
      includeDescriptions,
    });
  }, [
    selectedCategory,
    categories,
    activeDocument?.subcases,
    includeDescriptions,
    subTreeOverlayOpen,
    ensureOverviewScopeLoaded,
  ]);

  const handleToggleAskAi = () => {
    if (tabs.length === 0) return;
    if (isSearchOpen) {
      setIsSearchOpen(false);
      return;
    }
    setTreeAssistantOpen(false);
    setIsSearchOpen(true);
  };

  const handleToggleTreeAssistant = () => {
    if (treeAssistantOpen) {
      setTreeAssistantOpen(false);
      return;
    }
    setIsSearchOpen(false);
    setTreeAssistantOpen(true);
  };

  const handleContentPaneFilterGroupSelect = (groupName: string) => {
    const group = subcaseGroups.find(g => g.name === groupName);
    if (!group) return;

    const docSubcases = activeDocument?.subcases || [];
    const allDocSubcasesSelected = (selected: string[]) =>
      docSubcases.length > 0 &&
      selected.length === docSubcases.length &&
      docSubcases.every(s => selected.includes(s));

    setContentPaneFilterDraftGroupNames(prevNames => {
      const turningOff = prevNames.includes(groupName);
      const nextNames = turningOff
        ? prevNames.filter(n => n !== groupName)
        : [...prevNames, groupName];

      setContentPaneFilterDraftSubcases(prevSub => {
        if (!turningOff) {
          if (prevNames.length === 0) {
            contentPaneFilterSubcasesBaselineRef.current = [...prevSub];
          }
          if (allDocSubcasesSelected(prevSub) && prevNames.length === 0) {
            return [...group.subcases];
          }
          const merged = [...prevSub];
          for (const s of group.subcases) {
            if (merged.indexOf(s) === -1) merged.push(s);
          }
          return merged;
        }
        const cover = subcasesUnionForSubcaseGroupNames(nextNames);
        const filtered = prevSub.filter(s => !group.subcases.includes(s) || cover.has(s));
        if (nextNames.length === 0) {
          if (contentPaneFilterSubcasesBaselineRef.current !== null) {
            const restored = [...contentPaneFilterSubcasesBaselineRef.current];
            contentPaneFilterSubcasesBaselineRef.current = null;
            return restored;
          }
          return filtered.length === 0 ? [...docSubcases] : filtered;
        }
        return filtered;
      });

      return nextNames;
    });
  };

  const openContentPaneFilterDialog = () => {
    if (!isFeatureEnabled(FEATURE_FLAG_KEYS.FILTER_CONTENT_PANE_BY_SUBCASE)) return;
    if (!activeDocument?.subcases?.length) return;
    contentPaneFilterSnapshotRef.current =
      contentPaneSubcaseFilter === null ? null : [...contentPaneSubcaseFilter];
    contentPaneFilterSubcasesBaselineRef.current = null;
    contentPaneFilterGroupsInferredRef.current = false;
    setContentPaneFilterDraftGroupNames([]);
    setContentPaneFilterDraftSubcases(
      contentPaneSubcaseFilter != null
        ? [...contentPaneSubcaseFilter]
        : [...activeDocument.subcases]
    );
    setContentPaneFilterDialogOpen(true);
  };

  const cancelContentPaneFilterDialog = () => {
    const snap = contentPaneFilterSnapshotRef.current;
    setContentPaneSubcaseFilter(snap === null ? null : [...snap]);
    setContentPaneFilterDialogOpen(false);
  };

  useEffect(() => {
    if (!contentPaneFilterDialogOpen || !activeDocument?.subcases?.length) return;
    const doc = activeDocument.subcases;
    const docSet = new Set(doc);
    const normalized = Array.from(
      new Set(contentPaneFilterDraftSubcases.filter(s => docSet.has(s)))
    );
    const draftSet = new Set(normalized);
    const allSelected =
      doc.length > 0 &&
      doc.every(s => draftSet.has(s)) &&
      draftSet.size === doc.length;
    setContentPaneSubcaseFilter(allSelected ? null : normalized);
  }, [contentPaneFilterDialogOpen, contentPaneFilterDraftSubcases, activeDocument?.subcases]);

  useEffect(() => {
    if (!contentPaneFilterDialogOpen) {
      contentPaneFilterGroupsInferredRef.current = false;
    }
  }, [contentPaneFilterDialogOpen]);

  /** Infer primary group chips from draft (Show sub tree parity) once groups load and filter was partial at open. */
  useEffect(() => {
    if (!contentPaneFilterDialogOpen || contentPaneFilterGroupsInferredRef.current) return;
    if (contentPaneFilterSnapshotRef.current === null) {
      contentPaneFilterGroupsInferredRef.current = true;
      return;
    }
    if (loadingSubcaseGroups) return;

    const docSet = new Set(activeDocument?.subcases || []);
    const draftSet = new Set(
      contentPaneFilterDraftSubcases.filter(s => docSet.has(s))
    );
    const inferred = subcaseGroups
      .filter(g => {
        const membersInDoc = g.subcases.filter(s => docSet.has(s));
        return (
          membersInDoc.length > 0 &&
          membersInDoc.every(s => draftSet.has(s))
        );
      })
      .map(g => g.name);
    setContentPaneFilterDraftGroupNames(inferred);
    contentPaneFilterGroupsInferredRef.current = true;
  }, [
    contentPaneFilterDialogOpen,
    loadingSubcaseGroups,
    subcaseGroups,
    contentPaneFilterDraftSubcases,
    activeDocument?.subcases,
  ]);

  useEffect(() => {
    setSubTreeOverlayOpen(false);
  }, [currentTab]);

  useEffect(() => {
    setContentPaneSubcaseFilter(null);
  }, [currentTab]);

  /** Cut/move is same-document only; copy clipboard may span documents. */
  useEffect(() => {
    setIsCutOperation(prevCut => {
      if (prevCut) {
        setClipboardCategory(null);
        setClipboardCategories([]);
        setClipboardCContents([]);
        return false;
      }
      return prevCut;
    });
  }, [currentTab]);

  const handleSubcaseGroupsClick = () => {
    setSubcaseGroupsDialogOpen(true);
  };

  const handleSubcaseChange = (event: any) => {
    syncSubcasePanelAndContent(event.target.value);
  };

  const handleNodeToggle = async (nodeIds: string[]) => {
    const newlyExpanded = nodeIds.filter((id) => !expandedNodes.includes(id));
    // Expand optimistically so double-click feels instant; load children in parallel.
    setExpandedNodes(nodeIds);
    if (currentDocument) {
      setDocumentExpandedStates((prev) => ({
        ...prev,
        [currentDocument.id]: nodeIds,
      }));
    }
    let treeSnapshot = categories;
    for (const id of newlyExpanded) {
      const cat = findCategoryInTree(treeSnapshot, id);
      if (cat && !cat.contentCategory && !branchChildrenAreLoaded(cat)) {
        const loaded = await loadBranchChildrenIntoTree(id);
        if (loaded) {
          treeSnapshot = mergeBranchFetchIntoTree(treeSnapshot, id, loaded);
        }
      }
    }
  };

  const handleFontSizeChange = (newSize: number) => {
    setFontSize(newSize);
    localStorage.setItem('fontSize', newSize.toString());
    // Update global CSS variable
    document.documentElement.style.setProperty('--app-font-size', `${newSize}px`);
  };

  // Category find functionality
  const performBreadthFirstSearch = (startCategory: Category, searchString: string): Category[] => {
    const results: Category[] = [];
    const queue: Category[] = [startCategory];

    while (queue.length > 0) {
      const currentCategory = queue.shift()!;

      // Check if current category name contains the search string
      if (currentCategory.name.toLowerCase().includes(searchString.toLowerCase())) {
        results.push(currentCategory);
      }

      // Add children to queue for breadth-first search
      if (currentCategory.children) {
        queue.push(...currentCategory.children);
      }
    }

    return results;
  };

  const handleFindCategory = () => {
    if (findDialogOpen) {
      handleFindDialogClose();
      return;
    }
    setFindDialogOpen(true);
  };

  const canGenerateSubtree = (category: Category | null): boolean => {
    if (!isFeatureEnabled(FEATURE_FLAG_KEYS.GENERATE_SUBTREE_FROM_CONTENT)) return false;
    if (!category?.contentCategory) return false;
    const live = findCategoryInTree(categories, category.id) ?? category;
    if (!categoryContentsAreLoaded(live)) {
      // Need at least two rows; allow when the server says there is content (user loads on open).
      return live.hasContents === true;
    }
    return (live.contents?.length ?? 0) > 1;
  };

  const canGenerateInitialCategories = (category: Category | null): boolean => {
    if (!canGenerateSubtree(category)) return false;
    const live = category ? findCategoryInTree(categories, category.id) ?? category : null;
    if (!live) return false;
    if (branchChildrenAreLoaded(live)) {
      return (live.children?.length ?? 0) === 0;
    }
    return live.hasChildCategories !== true;
  };


  const handleGenerateSubtreeSuccess = async (updatedCategoryId?: string) => {
    const categoryIdToFetch = updatedCategoryId || selectedCategory?.id;
    if (categoryIdToFetch) {
      try {
        setLoading(true);
        const updatedCategory = await fetchCategoryWithChildren(categoryIdToFetch);
        setLoading(false);
        if (updatedCategory) {
          setSelectedCategory(updatedCategory);
          setCategories(prev => {
            // Helper to recursively replace the category in the tree
            const replaceCategoryInTree = (cats: Category[]): Category[] => {
              return cats.map(cat => {
                if (cat.id === (selectedCategory?.id || updatedCategory.id)) {
                  // Replace the old category with the updated one
                  return updatedCategory;
                }
                if (cat.children) {
                  return {
                    ...cat,
                    children: replaceCategoryInTree(cat.children)
                  };
                }
                return cat;
              });
            };

            return replaceCategoryInTree(prev);
          });
          setExpandedNodes(prev => [...prev, updatedCategory.id]);
        }
      } catch (error) {
        setLoading(false);
        console.error('Error refreshing category after generating subtree:', error);
      }
    }
  };

  const handleFindDialogClose = () => {
    setFindDialogOpen(false);
    setFindSearchString('');
    setFindResults([]);
    setCurrentFindIndex(0);
    setFindStarted(false);
    setShowEndWarning(false);
    setShowStartWarning(false);
    setHasWrappedAround(false);
    setHighlightedText('');
    setMatchingContentIds([]);
    // Reset position to initial top-right position (calculate dynamically)
    setDialogPosition({ x: Math.max(0, window.innerWidth - 350), y: 80 });
  };

  const handleFindSearch = () => {
    if (!findSearchString.trim()) return;

    void (async () => {
      const docRoot = categories[0];
      if (!docRoot) return;

      let searchRoot = docRoot;
      if (collectUnloadedNodesInSubtree(docRoot).length > 0) {
        try {
          setLoading(true);
          const full = await fetchCategoryWithChildren(docRoot.id);
          assertSubtreeFullyLoadedForDisplay(full, 'handleFindSearch');
          setCategories((prev) => updateCategoryInTree(prev, docRoot.id, () => full));
          searchRoot = full;
        } catch (err) {
          reportCategoryLoadFault(err, 'Failed to load categories for find');
          return;
        } finally {
          setLoading(false);
        }
      }

      const startCategory = selectedCategory
        ? findCategoryInTree([searchRoot], selectedCategory.id) ?? searchRoot
        : searchRoot;

      const results = performBreadthFirstSearch(startCategory, findSearchString);
      setFindResults(results);
      setCurrentFindIndex(0);
      setFindStarted(true);
      setShowEndWarning(false);
      setShowStartWarning(false);

      if (results.length > 0) {
        const targetCategory = results[0];
        setSelectedCategory(targetCategory);
        void expandParentsToMakeVisible(targetCategory);
      } else {
        setShowEndWarning(true);
        setTimeout(() => setShowEndWarning(false), 3000);
      }
    })();
  };

  const handleFindNext = () => {
    if (findResults.length === 0) return;

    const nextIndex = currentFindIndex + 1;

    if (nextIndex >= findResults.length) {
      // Reached the end, automatically continue from the top
      setShowEndWarning(true);
      setHasWrappedAround(true);
      setTimeout(() => setShowEndWarning(false), 3000); // Hide after 3 seconds
      setCurrentFindIndex(0);
      const targetCategory = findResults[0];
      setSelectedCategory(targetCategory);
      void expandParentsToMakeVisible(targetCategory);
    } else {
      setShowEndWarning(false);
      setShowStartWarning(false);
      setHasWrappedAround(false);
      setCurrentFindIndex(nextIndex);
      const targetCategory = findResults[nextIndex];
      setSelectedCategory(targetCategory);
      void expandParentsToMakeVisible(targetCategory);
    }
  };

  const handleFindPrevious = () => {
    if (findResults.length === 0) return;

    const prevIndex = currentFindIndex - 1;

    if (prevIndex < 0) {
      // Reached the beginning, automatically continue from the end
      setShowStartWarning(true);
      setHasWrappedAround(true);
      setTimeout(() => setShowStartWarning(false), 3000); // Hide after 3 seconds
      setCurrentFindIndex(findResults.length - 1);
      const targetCategory = findResults[findResults.length - 1];
      setSelectedCategory(targetCategory);
      void expandParentsToMakeVisible(targetCategory);
    } else {
      setShowEndWarning(false);
      setShowStartWarning(false);
      setHasWrappedAround(false);
      setCurrentFindIndex(prevIndex);
      const targetCategory = findResults[prevIndex];
      setSelectedCategory(targetCategory);
      void expandParentsToMakeVisible(targetCategory);
    }
  };

  const scrollToCategory = (categoryId: string) => {

    // Find the category object to get its name
    const targetCategory = findCategoryInTree(categories, categoryId);
    if (!targetCategory) {
      return;
    }

    // Find the tree view container
    const treeView = document.querySelector('.MuiTreeView-root');
    if (!treeView) {
      return;
    }

    // Use the same approach as the working test script
    const treeItems = document.querySelectorAll('.MuiTreeItem-root');

    // Find the tree item that contains the category name
    let targetTreeItem = null;
    for (const treeItem of Array.from(treeItems)) {
      const textContent = treeItem.textContent;
      if (textContent && textContent.includes(targetCategory.name)) {
        targetTreeItem = treeItem;
        break;
      }
    }

    if (targetTreeItem) {
      targetTreeItem.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } else {
      // Fallback: scroll to the last tree item to show that scrolling works
      if (treeItems.length > 0) {
        const lastTreeItem = treeItems[treeItems.length - 1];
        lastTreeItem.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  };

  // Dialog drag handlers
  const handleDialogMouseDown = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;

    // Check if the click is on the header area (the gray background area)
    const isHeader = target.closest('[style*="background-color: rgb(240, 240, 240)"]') ||
                    target.closest('[style*="background-color: #f0f0f0"]') ||
                    target.style.backgroundColor === 'rgb(240, 240, 240)' ||
                    target.style.backgroundColor === '#f0f0f0';

    if (isHeader) {
      e.preventDefault();
      setIsDragging(true);
      const rect = e.currentTarget.getBoundingClientRect();
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      });
    }
  };

  const handleDialogMouseMove = (e: MouseEvent) => {
    if (isDragging) {
      const newX = e.clientX - dragOffset.x;
      const newY = e.clientY - dragOffset.y;

      // Keep dialog within viewport bounds
      const maxX = window.innerWidth - 400; // dialog width
      const maxY = window.innerHeight - 300; // dialog height

      setDialogPosition({
        x: Math.max(0, Math.min(newX, maxX)),
        y: Math.max(0, Math.min(newY, maxY))
      });
    }
  };

  const handleDialogMouseUp = () => {
    setIsDragging(false);
  };

  // Add global mouse event listeners for dragging
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleDialogMouseMove);
      document.addEventListener('mouseup', handleDialogMouseUp);

      return () => {
        document.removeEventListener('mousemove', handleDialogMouseMove);
        document.removeEventListener('mouseup', handleDialogMouseUp);
      };
    }
  }, [isDragging, dragOffset]);

  // Global keyboard shortcuts for find dialog
  useEffect(() => {
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      // Prevent shortcuts when typing in input fields
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
        return;
      }

      // Ctrl+F or Cmd+F: Open find dialog
      if ((e.ctrlKey || e.metaKey) && e.key === 'f') {
        e.preventDefault();
        if (!findDialogOpen) {
          handleFindCategory();
        }
      }
    };

    document.addEventListener('keydown', handleGlobalKeyDown);
    return () => {
      document.removeEventListener('keydown', handleGlobalKeyDown);
    };
  }, [findDialogOpen]);

  const workspaceMenuModel = useMemo(() => {
    const cutHelpKey =
      selectedContents.length > 0 ? ('cut-content' as const) : ('cut-category' as const);
    const copyHelpKey =
      selectedContents.length > 0 ? ('copy-content' as const) : ('copy-category' as const);
    const pasteHelpKey =
      selectedContents.length > 0 || clipboardContents.length > 0
        ? ('paste-content' as const)
        : clipboardCategory || clipboardCategories.length > 0
          ? ('paste-category' as const)
          : ('paste-empty' as const);

    const ctx = {
      tooltipFor: getToolbarTooltipTextForState,
      isFeatureEnabled,
      effectiveDocumentReadOnly,
      tabsLength: tabs.length,
      selectedContentsLength: selectedContents.length,
      canUndo,
      canRedo,
      undoOperationName: historyService.getUndoOperationName(),
      redoOperationName: historyService.getRedoOperationName(),
      canCut: () => canCutSelectedCategories(),
      canCopy: () => canCopySelectedCategories(),
      canPaste: () => canPaste(selectedCategory),
      cutHelpKey,
      copyHelpKey,
      pasteHelpKey,
      invertSelectionHelpTooltip: 'Invert row selection in the content pane',
      contentFilterActiveTooltip: '',
      canAddCategory: () => canAddCategory(selectedCategory),
      canRenameCategory: () => canRenameSelectedCategory(),
      canDeleteCategory: () =>
        selectedContents.length === 0 && canDeleteSelectedCategories(),
      canSplitCategory: () => !!(selectedCategory && selectedCategory.contentCategory),
      canGroupCategories: () => canGroupCategories(),
      canMergeCategories: () => canMergeCategories(),
      canFlattenCategory: () => canFlattenCategory(),
      canCollectBranchContent: () => canCollectBranchContent(),
      canImportSubtree: () => canImportSubtreeFromDocument(selectedCategory),
      canMoveCategoryUp: () =>
        selectedContents.length === 0 && canMoveSelectedCategories('up'),
      canMoveCategoryDown: () =>
        selectedContents.length === 0 && canMoveSelectedCategories('down'),
      hasSelectedCategory: !!selectedCategory,
      canDuplicateAsDocument: () => !!selectedCategory,
      canAddContent: () => canAddContent(),
      canEditContent: () => canEditContent(),
      canAdjustTextContent: () => canAdjustTextContent(),
      canDeleteContent: () => selectedContents.length > 0 && canDelete(),
      canMoveContentUp: () =>
        selectedContents.length > 0 && canMoveSelectedCategories('up'),
      canMoveContentDown: () =>
        selectedContents.length > 0 && canMoveSelectedCategories('down'),
      canInvertContentSelection: () =>
        !!(
          selectedCategory?.contentCategory &&
          (selectedCategory.contents?.length ?? 0) > 0
        ),
      canRenameSubcase: () => tabs.length > 0,
      canManageSubcaseGroups: () => !!activeDocument,
      canFilterContentPane: () => canFilterContentPaneBySubcase,
      canFindInDocument: () => tabs.length > 0,
      canShowSubTree: () => tabs.length > 0 && !!selectedCategory,
      canOrganizeCategories: () => canOrganizeCategories(),
      canAddMatchingContent: () => canAddMatchingContent(),
      canCompareContent: () => canCompareContent(),
      canCategoryAiReport: () => canCategoryAiReport(selectedCategory),
      canAskAi: () => tabs.length > 0,
      canTreeAssistant: () => true,
      onUndo: handleUndo,
      onRedo: handleRedo,
      onCut: handleCutCategory,
      onCopy: handleCopyCategory,
      onPaste: () => void handlePasteCategory(),
      onAddCategory: handleAddCategoryDialogOpen,
      onRenameCategory: handleRenameCategory,
      onDeleteCategory: handleDelete,
      onSplitCategory: () => openSplitDialog(),
      onGroupCategories: handleGroupCategoriesDialogOpen,
      onMergeCategories: handleMergeCategoriesDialogOpen,
      onFlattenCategory: () => void handleFlattenCategory(),
      onCollectBranchContent: () => void handleCollectBranchContent(),
      onImportSubtree: () => setImportSubtreeDialogOpen(true),
      onMoveCategoryUp: () => void handleMoveCategory('up'),
      onMoveCategoryDown: () => void handleMoveCategory('down'),
      onDuplicateAsDocument: () => setDuplicateCategoryDialogOpen(true),
      onAddContent: handleAddContent,
      onEditContent: handleAddContent,
      onAdjustTextContent: handleAdjustTextContent,
      onDeleteContent: handleDelete,
      onMoveContentUp: () => void handleMoveCategory('up'),
      onMoveContentDown: () => void handleMoveCategory('down'),
      onInvertContentSelection: () =>
        handleInvertSelectionContents(selectedCategory?.contents),
      onRenameSubcase: handleRenameSubcase,
      onManageSubcaseGroups: handleSubcaseGroupsClick,
      onFilterContentPane: openContentPaneFilterDialog,
      onFindInDocument: handleFindCategory,
      onShowSubTree: openSubTreeOverlay,
      onOrganizeCategories: handleOrganizeCategoriesOpen,
      onAddMatchingContent: handleAddMatchingContentDialogOpen,
      onCompareContent: handleCompareContentDialogOpen,
      onCategoryAiReport: handleCategoryAiReportFromTree,
      onAskAi: handleToggleAskAi,
      onTreeAssistant: handleToggleTreeAssistant,
      quickActionIcons: {
        add: <AddIcon />,
        edit: <EditIcon />,
        delete: <DeleteIcon />,
        split: <CallSplitIcon />,
        rename: <DriveFileRenameOutlineIcon />,
        moveUp: <ArrowUpwardIcon />,
        moveDown: <ArrowDownwardIcon />,
        cut: <ContentCutIcon />,
        copy: <ContentCopyIcon />,
        paste: <ContentPasteIcon />,
        undo: <UndoIcon />,
        redo: <RedoIcon />,
      },
      canQuickEdit: () => canEditContent(),
      canQuickDelete: () => canDelete(),
      canQuickMoveUp: () => canMoveSelectedCategories('up'),
      canQuickMoveDown: () => canMoveSelectedCategories('down'),
      onQuickEdit: handleAddContent,
      onQuickDelete: handleDelete,
      onQuickRename: () => handleRenameCategory(),
      onQuickMoveUp: () => void handleMoveCategory('up'),
      onQuickMoveDown: () => void handleMoveCategory('down'),
    };

    return {
      sections: buildWorkspaceMenuSections(ctx),
      quickActions: buildWorkspaceQuickActions(ctx),
    };
  }, [
    activeDocument,
    canRedo,
    canUndo,
    clipboardCategories.length,
    clipboardCategory,
    clipboardContents.length,
    effectiveDocumentReadOnly,
    getToolbarTooltipTextForState,
    isFeatureEnabled,
    selectedCategory,
    selectedContents.length,
    tabs.length,
    canFilterContentPaneBySubcase,
  ]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        bgcolor: 'background.default',
      }}
    >
    {loading && (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        backgroundColor: (theme) => alpha(theme.palette.common.black, 0.14),
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
    justifyContent: 'center',
      }}
    >
      <Box
        role="status"
        aria-live="polite"
        aria-label="Loading document"
        sx={(theme) => ({
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1.5,
          px: 3,
          py: 2.5,
          borderRadius: 2,
          bgcolor: 'background.paper',
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: theme.shadows[8],
        })}
      >
        <Box
          sx={{
            position: 'relative',
            width: 52,
            height: 52,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CircularProgress
            size={52}
            thickness={4.2}
            color="primary"
            disableShrink
            sx={{
              position: 'absolute',
              left: 0,
              top: 0,
              '& .MuiCircularProgress-circle': {
                strokeLinecap: 'round',
              },
            }}
          />
          <Box
            component="img"
            src="/images/logo.png"
            alt=""
            aria-hidden
            sx={{
              position: 'relative',
              zIndex: 1,
              width: 24,
              height: 24,
              objectFit: 'contain',
              pointerEvents: 'none',
              userSelect: 'none',
            }}
          />
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, textAlign: 'center', letterSpacing: '-0.01em' }}>
          Loading document…
        </Typography>
      </Box>
    </Box>
  )}
      <CssBaseline />
      {isImpersonating && user && (
        <Alert
          severity="warning"
          sx={{
            position: 'fixed',
            bottom: 16,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: (t) => t.zIndex.snackbar + 1,
            maxWidth: 'min(640px, calc(100vw - 32px))',
            boxShadow: 4,
          }}
          action={
            <Button color="inherit" size="small" onClick={() => void endImpersonation()}>
              Exit view
            </Button>
          }
        >
          Viewing as <strong>{user.username}</strong> (read-only)
          {impersonatorUsername ? ` — admin: ${impersonatorUsername}` : ''}
        </Alert>
      )}
      <StyledAppBar
        position="fixed"
        sx={{
          '& .MuiToolbar-root': {
            minHeight: effectiveAppBarHeight,
          },
        }}
      >
        <Toolbar
          ref={toolbarMagnifyRootRef}
          disableGutters
          onMouseEnter={handleToolbarMouseEnter}
          onMouseMove={handleToolbarMagnifyMove}
          onMouseLeave={handleToolbarMagnifyLeave}
          sx={(theme) => {
            const toolbarIconSx = {
              '& .MuiSvgIcon-root': {
                fontSize: 'var(--app-toolbar-icon-size, 24px)',
              },
            };
            const dockMagnifySx = toolbarDockMagnify
              ? {
                  overflow: 'visible',
                  '& .MuiIconButton-root': {
                    transition: 'transform 0.09s ease-out',
                  },
                }
              : {};
            const menuBarChromeSx =
              workspaceChrome === 'menuBar' && !isAdminRoute
                ? { alignItems: 'center', py: 0.25 }
                : {};
            if (isAdminRoute) {
              return {
                ...toolbarIconSx,
                ...dockMagnifySx,
                ...menuBarChromeSx,
                display: 'flex',
                justifyContent: 'space-between',
                width: '100%',
                paddingLeft: theme.spacing(2),
                paddingRight: theme.spacing(2),
              };
            }
            if (useMainToolbarGrid) {
              return {
                ...toolbarIconSx,
                ...dockMagnifySx,
                ...menuBarChromeSx,
                display: 'grid',
                gridTemplateColumns: `${MAIN_PANE_LEFT_OFFSET_PX}px minmax(0, 1fr)`,
                alignItems: 'center',
                columnGap: 0,
                paddingLeft: 0,
                paddingRight: theme.spacing(1.5),
                boxSizing: 'border-box',
                overflow: 'visible',
              };
            }
            return {
              ...toolbarIconSx,
              ...dockMagnifySx,
              ...menuBarChromeSx,
              display: 'flex',
              paddingLeft: `${MAIN_PANE_GAP_PX}px`,
              paddingRight: theme.spacing(1.5),
              boxSizing: 'border-box',
              ...(toolbarDockMagnify ? { overflow: 'visible' } : {}),
            };
          }}
        >
          <Box
            sx={(theme) =>
              useMainToolbarGrid
                ? {
                    display: 'flex',
                    alignItems: 'center',
                    pl: theme.spacing(2),
                    minWidth: 0,
                    overflow: toolbarDockMagnify ? 'visible' : 'hidden',
                  }
                : { display: 'contents' }
            }
          >
            <IconButton
              color="inherit"
              aria-label="open drawer"
              onClick={() => setIsDocumentPanelVisible(!isDocumentPanelVisible)}
              sx={{
                mr: 1,
                borderRadius: 1.5,
                bgcolor: (theme) => alpha(theme.palette.common.white, 0.06),
                '&:hover': { bgcolor: (theme) => alpha(theme.palette.common.white, 0.12) },
              }}
              disabled={location.pathname.startsWith('/admin')}
            >
              <Badge
                badgeContent={documentSharingEnabled ? incomingShareCount : 0}
                color="secondary"
                overlap="circular"
                invisible={!documentSharingEnabled || incomingShareCount <= 0}
              >
                <MenuIcon />
              </Badge>
            </IconButton>
            <LogoImage
              src="/images/logo.png"
              alt="Narralyze Logo"
              sx={{ height: compactToolbar ? 40 : 48, mr: compactToolbar ? 1.5 : 2 }}
            />
          </Box>
          <Box
            ref={setToolbarRowRef}
            sx={
              useMainToolbarGrid
                ? {
                    display: 'flex',
                    alignItems: 'center',
                    minWidth: 0,
                    flex: 1,
                    width: '100%',
                    overflow: 'visible',
                  }
                : {
                    display: 'flex',
                    alignItems: 'center',
                    minWidth: 0,
                    flex: 1,
                    width: '100%',
                    ...(isAdminRoute ? { justifyContent: 'flex-end' } : {}),
                  }
            }
          >
        {!location.pathname.startsWith('/admin') && workspaceChrome === 'menuBar' ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              flex: 1,
              minWidth: 0,
              gap: 0,
              ml: useMainToolbarGrid ? -0.75 : compactToolbar ? 0.5 : 1,
              py: 0,
            }}
          >
            <WorkspaceMenuBar
              sections={workspaceMenuModel.sections}
              compact={compactToolbar}
            />
            <WorkspaceQuickToolbar actions={workspaceMenuModel.quickActions} />
          </Box>
        ) : !location.pathname.startsWith('/admin') ? (
          <Box
            ref={setToolbarRef}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: compactToolbar ? 0.125 : 0.25,
              flex: 1,
              minWidth: 0,
              ml: useMainToolbarGrid ? -0.75 : compactToolbar ? 0.5 : 1,
              flexWrap: 'nowrap',
              minHeight: compactToolbar ? 44 : 52,
              py: compactToolbar ? 0.25 : 0.5,
              overflow: 'hidden',
            }}
          >
            {/* Dynamic Toolbar Buttons — hidden in read-only shared document sessions */}
            {!effectiveDocumentReadOnly && visibleButtons.has('add') && (
              <Tooltip title={getToolbarTooltipTextForState('add-category', !!canAddCategory(selectedCategory))}>
                <span>
                  <StyledIconButton
                    onClick={handleAddCategoryDialogOpen}
                    disabled={!canAddCategory(selectedCategory)}
                  >
                    <AddIcon />
                  </StyledIconButton>
                </span>
              </Tooltip>
            )}

            {!effectiveDocumentReadOnly && visibleButtons.has('edit') && (
              <Tooltip title={(selectedContents.length !== 1) ?
                    getToolbarTooltipTextForState('add-content', !!canAddContent()) :
                    getToolbarTooltipTextForState('edit-content', !!canEditContent())}>
                <span>
                  <StyledIconButton
                    onClick={handleAddContent}
                    disabled={!(canAddContent() || canEditContent())}
                  >
                    <EditIcon />
                  </StyledIconButton>
                </span>
              </Tooltip>
            )}

            {!effectiveDocumentReadOnly && visibleButtons.has('adjustText') && (
              <Tooltip
                title={getToolbarTooltipTextForState(
                  'adjust-text-content',
                  !!canAdjustTextContent(),
                )}
              >
                <span>
                  <StyledIconButton
                    onClick={handleAdjustTextContent}
                    disabled={!canAdjustTextContent()}
                    aria-label="Adjust text"
                  >
                    <NotesIcon />
                  </StyledIconButton>
                </span>
              </Tooltip>
            )}

            {!effectiveDocumentReadOnly && visibleButtons.has('delete') && (
              <Tooltip title={
                selectedContents.length > 0
                  ? getToolbarTooltipTextForState('delete-content', !!canDelete())
                  : getToolbarTooltipTextForState('delete-category', !!canDeleteSelectedCategories())
              }>
                <span>
                  <StyledIconButton
                    onClick={handleDelete}
                    disabled={!canDelete()}
                  >
                    <DeleteIcon />
                  </StyledIconButton>
                </span>
              </Tooltip>
            )}

            {!effectiveDocumentReadOnly && visibleButtons.has('split') && (
              <Tooltip title={getToolbarTooltipTextForState('split-category', !!(selectedCategory && selectedCategory.contentCategory))}>
                <span>
                  <StyledIconButton
                    onClick={handleSplitDialogOpen}
                    disabled={!selectedCategory || !selectedCategory.contentCategory}
                  >
                    <CallSplitIcon />
                  </StyledIconButton>
                </span>
              </Tooltip>
            )}

            {!effectiveDocumentReadOnly && visibleButtons.has('addMatchingContent') && isFeatureEnabled(FEATURE_FLAG_KEYS.SEMANTIC_FIND) && (
              <Tooltip title={getToolbarTooltipTextForState('add-matching-content', canAddMatchingContent())}>
                <span>
                  <StyledIconButton
                    onClick={handleAddMatchingContentDialogOpen}
                    disabled={!canAddMatchingContent()}
                  >
                    <PlaylistAddOutlinedIcon />
                  </StyledIconButton>
                </span>
              </Tooltip>
            )}

            {!effectiveDocumentReadOnly &&
              visibleButtons.has('compareContent') &&
              isFeatureEnabled(FEATURE_FLAG_KEYS.COMPARE_CONTENT_SELECTION) && (
              <Tooltip title={getToolbarTooltipTextForState('compare-content', canCompareContent())}>
                <span>
                  <StyledIconButton
                    onClick={handleCompareContentDialogOpen}
                    disabled={!canCompareContent()}
                  >
                    <CompareArrowsIcon />
                  </StyledIconButton>
                </span>
              </Tooltip>
            )}

            {!effectiveDocumentReadOnly && visibleButtons.has('group') && (
              <Tooltip title={getToolbarTooltipTextForState('group-categories', !!canGroupCategories())}>
                <span>
                  <StyledIconButton
                    onClick={handleGroupCategoriesDialogOpen}
                    disabled={!canGroupCategories()}
                  >
                    <CallMergeIcon />
                  </StyledIconButton>
                </span>
              </Tooltip>
            )}

            {!effectiveDocumentReadOnly && visibleButtons.has('flatten') && (
              <Tooltip title={getToolbarTooltipTextForState('flatten-category', !!canFlattenCategory())}>
                <span>
                  <StyledIconButton
                    onClick={handleFlattenCategory}
                    disabled={!canFlattenCategory()}
                  >
                    <KeyboardDoubleArrowUpIcon />
                  </StyledIconButton>
                </span>
              </Tooltip>
            )}

            {!effectiveDocumentReadOnly && visibleButtons.has('collapseSubtree') && (
              <Tooltip title={getToolbarTooltipTextForState(
                'collapse-subtree-to-content-category',
                canCollectBranchContent(),
              )}>
                <span>
                  <StyledIconButton
                    onClick={handleCollectBranchContent}
                    disabled={!canCollectBranchContent()}
                  >
                    <ViewListOutlinedIcon />
                  </StyledIconButton>
                </span>
              </Tooltip>
            )}

            {!effectiveDocumentReadOnly && visibleButtons.has('merge') && (
              <Tooltip title={getToolbarTooltipTextForState('merge-categories', !!canMergeCategories())}>
                <span>
                  <StyledIconButton
                    onClick={handleMergeCategoriesDialogOpen}
                    disabled={!canMergeCategories()}
                  >
                    <JoinFullIcon />
                  </StyledIconButton>
                </span>
              </Tooltip>
            )}

            {!effectiveDocumentReadOnly &&
              isFeatureEnabled(FEATURE_FLAG_KEYS.GENERATE_SUBTREE_FROM_CONTENT) &&
              visibleButtons.has('generate') && (
              <Tooltip title={getToolbarTooltipTextForState('structure-categories', canOrganizeCategories())}>
                <span>
                  <StyledIconButton
                    onClick={() => handleOrganizeCategoriesOpen()}
                    disabled={!canOrganizeCategories()}
                  >
                    <AutoFixHighDirectionIcon direction="north" />
                  </StyledIconButton>
                </span>
              </Tooltip>
            )}

            {!effectiveDocumentReadOnly && visibleButtons.has('rename') && (
              <Tooltip title={getToolbarTooltipTextForState('rename-category', !!canRenameSelectedCategory())}>
                <span>
                  <StyledIconButton
                    onClick={() => handleRenameCategory()}
                    disabled={!canRenameSelectedCategory()}
                  >
                    <DriveFileRenameOutlineIcon />
                  </StyledIconButton>
                </span>
              </Tooltip>
            )}

            {!effectiveDocumentReadOnly && visibleButtons.has('moveUp') && (
              <Tooltip title={getToolbarTooltipTextForState(selectedContents.length > 0 ? 'move-content-up' : 'move-category-up', !!canMoveSelectedCategories('up'))}>
                <span>
                  <StyledIconButton
                    onClick={() => handleMoveCategory('up')}
                    disabled={!canMoveSelectedCategories('up')}
                  >
                    <ArrowUpwardIcon />
                  </StyledIconButton>
                </span>
              </Tooltip>
            )}

            {!effectiveDocumentReadOnly && visibleButtons.has('moveDown') && (
              <Tooltip title={getToolbarTooltipTextForState(selectedContents.length > 0 ? 'move-content-down' :'move-category-down', !!canMoveSelectedCategories('down'))}>
                <span>
                  <StyledIconButton
                    onClick={() => handleMoveCategory('down')}
                    disabled={!canMoveSelectedCategories('down')}
                  >
                    <ArrowDownwardIcon />
                  </StyledIconButton>
                </span>
              </Tooltip>
            )}

            {!effectiveDocumentReadOnly && visibleButtons.has('cut') && (
              <Tooltip title={getToolbarTooltipTextForState(selectedContents.length > 0 ? 'cut-content' : 'cut-category', !!canCutSelectedCategories())}>
                <span>
                  <StyledIconButton
                    onClick={handleCutCategory}
                    disabled={!canCutSelectedCategories()}
                  >
                    <ContentCutIcon />
                  </StyledIconButton>
                </span>
              </Tooltip>
            )}

            {!effectiveDocumentReadOnly && visibleButtons.has('copy') && (
              <Tooltip title={getToolbarTooltipTextForState(selectedContents.length > 0 ? 'copy-content' : 'copy-category', !!canCopySelectedCategories())}>
                <span>
                  <StyledIconButton
                    onClick={handleCopyCategory}
                    disabled={!canCopySelectedCategories()}
                  >
                    <ContentCopyIcon />
                  </StyledIconButton>
                </span>
              </Tooltip>
            )}

            {!effectiveDocumentReadOnly && visibleButtons.has('paste') && (
              <Tooltip title={getToolbarTooltipTextForState(
                clipboardContents.length > 0
                  ? 'paste-content'
                  : clipboardCategories.length >= 2 || clipboardCategory
                    ? 'paste-category'
                    : 'paste-empty',
                !!canPaste(selectedCategory),
              )}>
                <span>
                  <StyledIconButton
                    onClick={() => {
                      void handlePasteCategory();
                    }}
                    disabled={!canPaste(selectedCategory)}
                  >
                    <ContentPasteIcon />
                  </StyledIconButton>
                </span>
              </Tooltip>
            )}

            {!effectiveDocumentReadOnly && visibleButtons.has('importSubtree') && (
              <Tooltip
                title={getToolbarTooltipTextForState(
                  'import-subtree-from-document',
                  !!canImportSubtreeFromDocument(selectedCategory),
                )}
              >
                <span>
                  <StyledIconButton
                    onClick={() => setImportSubtreeDialogOpen(true)}
                    disabled={!canImportSubtreeFromDocument(selectedCategory)}
                  >
                    <DriveFolderUploadIcon />
                  </StyledIconButton>
                </span>
              </Tooltip>
            )}

            {!effectiveDocumentReadOnly && visibleButtons.has('undo') && (
              <Tooltip title={canUndo ? `Undo ${historyService.getUndoOperationName()}` : getToolbarTooltipTextForState('undo-disabled', false)}>
                <span>
                  <StyledIconButton
                    onClick={handleUndo}
                    disabled={!canUndo}
                  >
                    <UndoIcon />
                  </StyledIconButton>
                </span>
              </Tooltip>
            )}

            {!effectiveDocumentReadOnly && visibleButtons.has('redo') && (
              <Tooltip title={canRedo ? `Redo ${historyService.getRedoOperationName()}` : getToolbarTooltipTextForState('redo-disabled', false)}>
                <span>
                  <StyledIconButton
                    onClick={handleRedo}
                    disabled={!canRedo}
                  >
                    <RedoIcon />
                  </StyledIconButton>
                </span>
              </Tooltip>
            )}

            {!effectiveDocumentReadOnly && visibleButtons.has('duplicate') && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 4 }}>
                  <Tooltip title={getToolbarTooltipTextForState('duplicate-as-document', !!selectedCategory)}>
                    <span>
                      <StyledIconButton
                        onClick={() => setDuplicateCategoryDialogOpen(true)}
                        disabled={!selectedCategory}
                      >
                        <FileCopyIcon />
                      </StyledIconButton>
                    </span>
                  </Tooltip>
              </Box>
            )}

            {!effectiveDocumentReadOnly && visibleButtons.has('renameSubcase') && (
              <Tooltip title={getToolbarTooltipTextForState('rename-subcase', tabs.length > 0)}>
                <span>
                  <StyledIconButton
                    onClick={handleRenameSubcase}
                    disabled={tabs.length === 0}
                  >
                    <EditNoteIcon />
                  </StyledIconButton>
                </span>
              </Tooltip>
            )}

            {!effectiveDocumentReadOnly && visibleButtons.has('subcaseGroups') && (
              <Tooltip title={getToolbarTooltipTextForState('manage-subcase-groups', !!activeDocument)}>
                <span>
                  <StyledIconButton
                    onClick={handleSubcaseGroupsClick}
                    disabled={!activeDocument}
                  >
                    <GroupIcon />
                  </StyledIconButton>
                </span>
              </Tooltip>
            )}

            {visibleButtons.has('contentFilter') &&
              isFeatureEnabled(FEATURE_FLAG_KEYS.FILTER_CONTENT_PANE_BY_SUBCASE) && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  flexWrap: 'wrap',
                }}
              >
                <Tooltip
                  title={
                    !canFilterContentPaneBySubcase
                      ? getToolbarTooltipTextForState(
                          'content-pane-subcase-filter',
                          false,
                        )
                      : isContentPaneSubcaseFilterActive(
                            contentPaneSubcaseFilter,
                          )
                        ? (() => {
                            const base = getToolbarTooltipText(
                              'content-pane-subcase-filter-active',
                            );
                            const subcaseCount = activeDocument?.subcases?.length ?? 0;
                            const suffix = ` (${contentPaneSubcaseFilter!.length}/${subcaseCount} subcases shown)`;
                            return base.trim() !== ''
                              ? `${base}${suffix}`
                              : `Subcase filter is on${suffix}. Hidden rows are not deleted — click to change or clear.`;
                          })()
                        : getToolbarTooltipTextForState(
                            'content-pane-subcase-filter',
                            true,
                          )
                  }
                  placement="bottom"
                >
                  <span>
                    <Badge
                      color="warning"
                      invisible={!isContentPaneSubcaseFilterActive(contentPaneSubcaseFilter)}
                      badgeContent={
                        isContentPaneSubcaseFilterActive(contentPaneSubcaseFilter)
                          ? contentPaneSubcaseFilter!.length
                          : undefined
                      }
                      sx={{
                        '& .MuiBadge-badge': {
                          fontWeight: 800,
                          fontSize: '0.65rem',
                          lineHeight: 1,
                          minWidth: 18,
                          height: 18,
                          padding: '0 4px',
                          border: '2px solid #2c3e50',
                          top: 10,
                          right: 8,
                          transformOrigin: '100% 0%',
                        },
                        /* MUI puts .MuiBadge-invisible on the badge node, not the root — avoid overriding scale(0) */
                        '& .MuiBadge-badge:not(.MuiBadge-invisible)': {
                          transform: 'scale(1) translate(50%, -50%)',
                        },
                      }}
                    >
                      <StyledIconButton
                        onClick={openContentPaneFilterDialog}
                        disabled={!canFilterContentPaneBySubcase}
                        sx={{
                          ...(isContentPaneSubcaseFilterActive(contentPaneSubcaseFilter)
                            ? {
                                backgroundColor: 'rgba(237, 108, 2, 0.35)',
                                outline: '2px solid rgba(255,255,255,0.9)',
                                outlineOffset: 1,
                                '&:hover': { backgroundColor: 'rgba(237, 108, 2, 0.55)' },
                                '@keyframes contentPaneFilterToolbarPulse': {
                                  '0%, 100%': {
                                    opacity: 1,
                                    color: '#fff',
                                  },
                                  '50%': {
                                    opacity: 0.45,
                                    color: '#ffe0b2',
                                  },
                                },
                                '& .MuiSvgIcon-root': {
                                  fontSize: 'var(--app-toolbar-icon-size, 24px)',
                                  animation:
                                    'contentPaneFilterToolbarPulse 1.5s ease-in-out infinite',
                                },
                                '@media (prefers-reduced-motion: reduce)': {
                                  '& .MuiSvgIcon-root': {
                                    fontSize: 'var(--app-toolbar-icon-size, 24px)',
                                    animation: 'none',
                                    opacity: 1,
                                    color: '#fff',
                                  },
                                },
                              }
                            : {}),
                        }}
                      >
                        <FilterListIcon />
                      </StyledIconButton>
                    </Badge>
                  </span>
                </Tooltip>
              </Box>
            )}

            {visibleButtons.has('find') && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  ml: visibleButtons.has('contentFilter') ? 4 : 0,
                }}
              >
                <Tooltip title={getToolbarTooltipTextForState('find-in-document', tabs.length > 0)}>
                  <span>
                    <StyledIconButton
                      onClick={handleFindCategory}
                      disabled={tabs.length === 0}
                    >
                      <FindInPageIcon />
                    </StyledIconButton>
                  </span>
                </Tooltip>
              </Box>
            )}
            {visibleButtons.has('showSubTree') && (
              <Tooltip title={getToolbarTooltipTextForState('show-sub-tree', tabs.length > 0 && !!selectedCategory)}>
                <span>
                  <StyledIconButton
                    onClick={openSubTreeOverlay}
                    disabled={tabs.length === 0 || !selectedCategory}
                  >
                    <VisibilityIcon />
                  </StyledIconButton>
                </span>
              </Tooltip>
            )}
            {visibleButtons.has('categoryAiReport') &&
              isFeatureEnabled(FEATURE_FLAG_KEYS.CATEGORY_AI_REPORT) && (
              <Tooltip
                title={getToolbarTooltipTextForState(
                  'category-ai-report',
                  canCategoryAiReport(selectedCategory),
                )}
              >
                <span>
                  <StyledIconButton
                    onClick={handleCategoryAiReportFromTree}
                    disabled={!canCategoryAiReport(selectedCategory)}
                  >
                    <SummarizeIcon />
                  </StyledIconButton>
                </span>
              </Tooltip>
            )}
            {visibleButtons.has('askAi') && isFeatureEnabled(FEATURE_FLAG_KEYS.ASK_AI) && (
              <Tooltip title={getToolbarTooltipTextForState('ask-ai', tabs.length > 0)}>
                <span>
                  <StyledIconButton
                    onClick={handleToggleAskAi}
                    disabled={tabs.length === 0}
                  >
                    <AiSparklesIcon />
                  </StyledIconButton>
                </span>
              </Tooltip>
            )}
            {visibleButtons.has('treeAssistant') &&
              !effectiveDocumentReadOnly &&
              isFeatureEnabled(FEATURE_FLAG_KEYS.TREE_ASSISTANT) && (
              <Tooltip title={getToolbarTooltipTextForState('tree-assistant', true)}>
                <span aria-label="Structure builder">
                  <StyledIconButton
                    onClick={handleToggleTreeAssistant}
                    aria-label="Structure builder"
                  >
                    <TreeAssistantIcon />
                  </StyledIconButton>
                </span>
              </Tooltip>
            )}

          </Box>
        ) : null}
        {!location.pathname.startsWith('/admin') && workspaceChrome === 'iconBar' && showToolbarMore && (
          <Tooltip title="More Actions">
            <span>
              <StyledIconButton
                onClick={handleToolbarMenuOpen}
                sx={{ flexShrink: 0, ml: 0.25 }}
              >
                <MoreVertIcon />
              </StyledIconButton>
            </span>
          </Tooltip>
        )}
        {!location.pathname.startsWith('/admin') && workspaceChrome === 'iconBar' && (
            <Menu
              anchorEl={toolbarMenuAnchorEl}
              open={Boolean(toolbarMenuAnchorEl)}
              onClose={handleToolbarMenuClose}
              PaperProps={{
                sx: {
                  maxHeight: 400,
                  width: 280,
                  p: 1
                }
              }}
            >
              {/* Category Operations */}
              {!visibleButtons.has('adjustText') && (
                <ToolbarOverflowMenuItem
                  showTooltip={showToolbarOverflowTooltip}
                  title={getToolbarTooltipTextForState(
                    'adjust-text-content',
                    !!canAdjustTextContent(),
                  )}
                  disabled={!canAdjustTextContent()}
                  onClick={() => {
                    handleAdjustTextContent();
                    handleToolbarMenuClose();
                  }}
                  icon={<NotesIcon fontSize="small" />}
                  label="Adjust text"
                />
              )}
              {!visibleButtons.has('split') && (
                <ToolbarOverflowMenuItem
                  showTooltip={showToolbarOverflowTooltip}
                  title={getToolbarTooltipTextForState('split-category', !!(selectedCategory && selectedCategory.contentCategory))}
                  disabled={!selectedCategory || !selectedCategory.contentCategory}
                  onClick={() => { openSplitDialog(); handleToolbarMenuClose(); }}
                  icon={<CallSplitIcon fontSize="small" />}
                  label="Split Category"
                />
              )}
              {!visibleButtons.has('addMatchingContent') && isFeatureEnabled(FEATURE_FLAG_KEYS.SEMANTIC_FIND) && (
                <ToolbarOverflowMenuItem
                  showTooltip={showToolbarOverflowTooltip}
                  title={getToolbarTooltipTextForState('add-matching-content', canAddMatchingContent())}
                  disabled={!canAddMatchingContent()}
                  onClick={() => {
                    handleAddMatchingContentDialogOpen();
                    handleToolbarMenuClose();
                  }}
                  icon={<PlaylistAddOutlinedIcon fontSize="small" />}
                  label="Add matching content (AI)"
                />
              )}
              {!visibleButtons.has('compareContent') &&
                isFeatureEnabled(FEATURE_FLAG_KEYS.COMPARE_CONTENT_SELECTION) && (
                <ToolbarOverflowMenuItem
                  showTooltip={showToolbarOverflowTooltip}
                  title={getToolbarTooltipTextForState('compare-content', canCompareContent())}
                  disabled={!canCompareContent()}
                  onClick={() => {
                    handleCompareContentDialogOpen();
                    handleToolbarMenuClose();
                  }}
                  icon={<CompareArrowsIcon fontSize="small" />}
                  label="Compare content and select"
                />
              )}
              {!visibleButtons.has('group') && (
                <ToolbarOverflowMenuItem
                  showTooltip={showToolbarOverflowTooltip}
                  title={getToolbarTooltipTextForState('group-categories', !!canGroupCategories())}
                  disabled={!canGroupCategories()}
                  onClick={() => { handleGroupCategoriesDialogOpen(); handleToolbarMenuClose(); }}
                  icon={<CallMergeIcon fontSize="small" />}
                  label="Group categories"
                />
              )}
              {!visibleButtons.has('flatten') && (
                <ToolbarOverflowMenuItem
                  showTooltip={showToolbarOverflowTooltip}
                  title={getToolbarTooltipTextForState('flatten-category', !!canFlattenCategory())}
                  disabled={!canFlattenCategory()}
                  onClick={async () => { await handleFlattenCategory(); handleToolbarMenuClose(); }}
                  icon={<KeyboardDoubleArrowUpIcon fontSize="small" />}
                  label="Flatten category"
                />
              )}
              {!visibleButtons.has('collapseSubtree') && (
                <ToolbarOverflowMenuItem
                  showTooltip={showToolbarOverflowTooltip}
                  title={getToolbarTooltipTextForState(
                    'collapse-subtree-to-content-category',
                    canCollectBranchContent(),
                  )}
                  disabled={!canCollectBranchContent()}
                  onClick={async () => {
                    await handleCollectBranchContent();
                    handleToolbarMenuClose();
                  }}
                  icon={<ViewListOutlinedIcon fontSize="small" />}
                  label="Collect branch content"
                />
              )}
              {!visibleButtons.has('merge') && (
                <ToolbarOverflowMenuItem
                  showTooltip={showToolbarOverflowTooltip}
                  title={getToolbarTooltipTextForState('merge-categories', !!canMergeCategories())}
                  disabled={!canMergeCategories()}
                  onClick={() => { handleMergeCategoriesDialogOpen(); handleToolbarMenuClose(); }}
                  icon={<JoinFullIcon fontSize="small" />}
                  label="Merge categories"
                />
              )}
              {!effectiveDocumentReadOnly &&
                !visibleButtons.has('generate') &&
                isFeatureEnabled(FEATURE_FLAG_KEYS.GENERATE_SUBTREE_FROM_CONTENT) && (
                <ToolbarOverflowMenuItem
                  showTooltip={showToolbarOverflowTooltip}
                  title={getToolbarTooltipTextForState('structure-categories', canOrganizeCategories())}
                  disabled={!canOrganizeCategories()}
                  onClick={() => { handleOrganizeCategoriesOpen(); handleToolbarMenuClose(); }}
                  icon={<AutoFixHighDirectionIcon direction="north" variant="menu" />}
                  label="Structure categories (AI)"
                />
              )}
              {!visibleButtons.has('rename') && (
                <ToolbarOverflowMenuItem
                  showTooltip={showToolbarOverflowTooltip}
                  title={getToolbarTooltipTextForState('rename-category', !!canRenameSelectedCategory())}
                  disabled={!canRenameSelectedCategory()}
                  onClick={() => { handleRenameCategory(); handleToolbarMenuClose(); }}
                  icon={<DriveFileRenameOutlineIcon fontSize="small" />}
                  label="Rename Category"
                />
              )}

              {/* Movement Operations */}
              {(!visibleButtons.has('moveUp') || !visibleButtons.has('moveDown')) && <Divider sx={{ my: 1, borderColor: 'rgba(255, 255, 255, 0.3)' }} />}
              {!visibleButtons.has('moveUp') && (
                <ToolbarOverflowMenuItem
                  showTooltip={showToolbarOverflowTooltip}
                  title={getToolbarTooltipTextForState(selectedContents.length > 0 ? 'move-content-up' : 'move-category-up', !!canMoveSelectedCategories('up'))}
                  disabled={!canMoveSelectedCategories('up')}
                  onClick={async () => { await handleMoveCategory('up'); handleToolbarMenuClose(); }}
                  icon={<ArrowUpwardIcon fontSize="small" />}
                  label="Move Up"
                />
              )}
              {!visibleButtons.has('moveDown') && (
                <ToolbarOverflowMenuItem
                  showTooltip={showToolbarOverflowTooltip}
                  title={getToolbarTooltipTextForState(selectedContents.length > 0 ? 'move-content-down' : 'move-category-down', !!canMoveSelectedCategories('down'))}
                  disabled={!canMoveSelectedCategories('down')}
                  onClick={async () => { await handleMoveCategory('down'); handleToolbarMenuClose(); }}
                  icon={<ArrowDownwardIcon fontSize="small" />}
                  label="Move Down"
                />
              )}

              {/* Clipboard Operations */}
              {(!visibleButtons.has('cut') ||
                !visibleButtons.has('copy') ||
                !visibleButtons.has('paste') ||
                !visibleButtons.has('importSubtree')) && (
                <Divider sx={{ my: 1, borderColor: 'rgba(255, 255, 255, 0.3)' }} />
              )}
              {!visibleButtons.has('cut') && (
                <ToolbarOverflowMenuItem
                  showTooltip={showToolbarOverflowTooltip}
                  title={getToolbarTooltipTextForState(selectedContents.length > 0 ? 'cut-content' : 'cut-category', !!canCutSelectedCategories())}
                  disabled={!canCutSelectedCategories()}
                  onClick={() => { handleCutCategory(); handleToolbarMenuClose(); }}
                  icon={<ContentCutIcon fontSize="small" />}
                  label="Cut"
                />
              )}
              {!visibleButtons.has('copy') && (
                <ToolbarOverflowMenuItem
                  showTooltip={showToolbarOverflowTooltip}
                  title={getToolbarTooltipTextForState(selectedContents.length > 0 ? 'copy-content' : 'copy-category', !!canCopySelectedCategories())}
                  disabled={!canCopySelectedCategories()}
                  onClick={() => { handleCopyCategory(); handleToolbarMenuClose(); }}
                  icon={<ContentCopyIcon fontSize="small" />}
                  label="Copy"
                />
              )}
              {!visibleButtons.has('paste') && (
                <ToolbarOverflowMenuItem
                  showTooltip={showToolbarOverflowTooltip}
                  title={getToolbarTooltipTextForState(
                    clipboardContents.length > 0
                      ? 'paste-content'
                      : clipboardCategories.length >= 2 || clipboardCategory
                        ? 'paste-category'
                        : 'paste-empty',
                    !!canPaste(selectedCategory),
                  )}
                  disabled={!canPaste(selectedCategory)}
                  onClick={async () => { await handlePasteCategory(); handleToolbarMenuClose(); }}
                  icon={<ContentPasteIcon fontSize="small" />}
                  label="Paste"
                />
              )}
              {!visibleButtons.has('importSubtree') && (
                <ToolbarOverflowMenuItem
                  showTooltip={showToolbarOverflowTooltip}
                  title={getToolbarTooltipTextForState(
                    'import-subtree-from-document',
                    !!canImportSubtreeFromDocument(selectedCategory),
                  )}
                  disabled={!canImportSubtreeFromDocument(selectedCategory)}
                  onClick={() => {
                    setImportSubtreeDialogOpen(true);
                    handleToolbarMenuClose();
                  }}
                  icon={<DriveFolderUploadIcon fontSize="small" />}
                  label="Import branch from document…"
                />
              )}

              {/* History Operations */}
              {(!visibleButtons.has('undo') || !visibleButtons.has('redo')) && <Divider sx={{ my: 1, borderColor: 'rgba(255, 255, 255, 0.3)' }} />}
              {!visibleButtons.has('undo') && (
                <ToolbarOverflowMenuItem
                  showTooltip={showToolbarOverflowTooltip}
                  title={canUndo ? `Undo ${historyService.getUndoOperationName()}` : getToolbarTooltipTextForState('undo-disabled', false)}
                  disabled={!canUndo}
                  onClick={async () => { await handleUndo(); handleToolbarMenuClose(); }}
                  icon={<UndoIcon fontSize="small" />}
                  label={`Undo ${historyService.getUndoOperationName()}`}
                />
              )}
              {!visibleButtons.has('redo') && (
                <ToolbarOverflowMenuItem
                  showTooltip={showToolbarOverflowTooltip}
                  title={canRedo ? `Redo ${historyService.getRedoOperationName()}` : getToolbarTooltipTextForState('redo-disabled', false)}
                  disabled={!canRedo}
                  onClick={async () => { await handleRedo(); handleToolbarMenuClose(); }}
                  icon={<RedoIcon fontSize="small" />}
                  label={`Redo ${historyService.getRedoOperationName()}`}
                />
              )}

              {/* Spacer between History and Document operations */}
              <Divider sx={{ my: 2, borderColor: 'rgba(255, 255, 255, 0.5)', borderWidth: 2 }} />
              <Box sx={{ height: 20 }} />
              {!visibleButtons.has('duplicate') && (
                <ToolbarOverflowMenuItem
                  showTooltip={showToolbarOverflowTooltip}
                  title={getToolbarTooltipTextForState('duplicate-as-document', !!selectedCategory)}
                  disabled={!selectedCategory}
                  onClick={() => { setDuplicateCategoryDialogOpen(true); handleToolbarMenuClose(); }}
                  icon={<FileCopyIcon fontSize="small" />}
                  label="Duplicate as document"
                />
              )}
              {!visibleButtons.has('renameSubcase') && (
                <ToolbarOverflowMenuItem
                  showTooltip={showToolbarOverflowTooltip}
                  title={getToolbarTooltipTextForState('rename-subcase', tabs.length > 0)}
                  disabled={tabs.length === 0}
                  onClick={() => { handleRenameSubcase(); handleToolbarMenuClose(); }}
                  icon={<EditNoteIcon fontSize="small" />}
                  label="Rename Subcase"
                />
              )}
              {!visibleButtons.has('subcaseGroups') && (
                <ToolbarOverflowMenuItem
                  showTooltip={showToolbarOverflowTooltip}
                  title={getToolbarTooltipTextForState('manage-subcase-groups', !!activeDocument)}
                  disabled={!activeDocument}
                  onClick={() => { handleSubcaseGroupsClick(); handleToolbarMenuClose(); }}
                  icon={<GroupIcon fontSize="small" />}
                  label="Manage Subcase Groups"
                />
              )}

              {(TOOLBAR_UTILITY_BUTTON_PRIORITY.some((id) => !visibleButtons.has(id))) && (
                <Divider sx={{ my: 1, borderColor: 'rgba(255, 255, 255, 0.3)' }} />
              )}
              {!visibleButtons.has('contentFilter') &&
                isFeatureEnabled(FEATURE_FLAG_KEYS.FILTER_CONTENT_PANE_BY_SUBCASE) && (
                <ToolbarOverflowMenuItem
                  showTooltip={showToolbarOverflowTooltip}
                  title={
                    !canFilterContentPaneBySubcase
                      ? getToolbarTooltipTextForState('content-pane-subcase-filter', false)
                      : isContentPaneSubcaseFilterActive(contentPaneSubcaseFilter)
                        ? (() => {
                            const base = getToolbarTooltipText('content-pane-subcase-filter-active');
                            const subcaseCount = activeDocument?.subcases?.length ?? 0;
                            const suffix = ` (${contentPaneSubcaseFilter!.length}/${subcaseCount} subcases shown)`;
                            return base.trim() !== ''
                              ? `${base}${suffix}`
                              : `Subcase filter is on${suffix}. Hidden rows are not deleted — click to change or clear.`;
                          })()
                        : getToolbarTooltipTextForState('content-pane-subcase-filter', true)
                  }
                  disabled={!canFilterContentPaneBySubcase}
                  onClick={() => { openContentPaneFilterDialog(); handleToolbarMenuClose(); }}
                  icon={<FilterListIcon fontSize="small" />}
                  label="Filter content pane by subcase"
                />
              )}
              {!visibleButtons.has('find') && (
                <ToolbarOverflowMenuItem
                  showTooltip={showToolbarOverflowTooltip}
                  title={getToolbarTooltipTextForState('find-in-document', tabs.length > 0)}
                  disabled={tabs.length === 0}
                  onClick={() => { handleFindCategory(); handleToolbarMenuClose(); }}
                  icon={<FindInPageIcon fontSize="small" />}
                  label="Find in document"
                />
              )}
              {!visibleButtons.has('showSubTree') && (
                <ToolbarOverflowMenuItem
                  showTooltip={showToolbarOverflowTooltip}
                  title={getToolbarTooltipTextForState('show-sub-tree', tabs.length > 0 && !!selectedCategory)}
                  disabled={tabs.length === 0 || !selectedCategory}
                  onClick={() => { openSubTreeOverlay(); handleToolbarMenuClose(); }}
                  icon={<VisibilityIcon fontSize="small" />}
                  label="Category Structure Overview"
                />
              )}
              {!visibleButtons.has('categoryAiReport') &&
                isFeatureEnabled(FEATURE_FLAG_KEYS.CATEGORY_AI_REPORT) && (
                <ToolbarOverflowMenuItem
                  showTooltip={showToolbarOverflowTooltip}
                  title={getToolbarTooltipTextForState(
                    'category-ai-report',
                    canCategoryAiReport(selectedCategory),
                  )}
                  disabled={!canCategoryAiReport(selectedCategory)}
                  onClick={() => {
                    handleCategoryAiReportFromTree();
                    handleToolbarMenuClose();
                  }}
                  icon={<SummarizeIcon fontSize="small" />}
                  label="Generate report (AI)"
                />
              )}
              {!visibleButtons.has('askAi') && isFeatureEnabled(FEATURE_FLAG_KEYS.ASK_AI) && (
                <ToolbarOverflowMenuItem
                  showTooltip={showToolbarOverflowTooltip}
                  title={getToolbarTooltipTextForState('ask-ai', tabs.length > 0)}
                  disabled={tabs.length === 0}
                  onClick={() => {
                    handleToggleAskAi();
                    handleToolbarMenuClose();
                  }}
                  icon={<AiSparklesIcon fontSize="small" />}
                  label="Ask AI"
                />
              )}
              {!visibleButtons.has('treeAssistant') &&
                !effectiveDocumentReadOnly &&
                isFeatureEnabled(FEATURE_FLAG_KEYS.TREE_ASSISTANT) && (
                <ToolbarOverflowMenuItem
                  showTooltip={showToolbarOverflowTooltip}
                  title={getToolbarTooltipTextForState('tree-assistant', true)}
                  onClick={() => {
                    handleToggleTreeAssistant();
                    handleToolbarMenuClose();
                  }}
                  icon={<TreeAssistantIcon fontSize="small" />}
                  label="Structure builder"
                />
              )}
            </Menu>
        )}
          <Box
            ref={setToolbarPinnedRef}
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexShrink: 0,
              ml: isAdminRoute ? 0 : 1,
              gap: 0.25,
            }}
          >
          <Tooltip title="Help & Support">
            <span>
              <StyledIconButton
                onClick={() => setHelpDialogOpen(true)}
              >
                <HelpIcon />
              </StyledIconButton>
            </span>
          </Tooltip>
          <SettingsMenu
            onAutoPasteChange={handleAutoPasteChange}
            onToolbarDockMagnifyChange={handleToolbarDockMagnifyChange}
            onShowContentIndexInPaneChange={handleShowContentIndexInPaneChange}
            onClickToRenameCategoryInTreeChange={handleClickToRenameCategoryInTreeChange}
            clickToRenameCategoryInTree={clickToRenameCategoryInTree}
            workspaceChrome={workspaceChrome}
            onWorkspaceChromeChange={handleWorkspaceChromeChange}
            fontSize={fontSize}
            onFontSizeChange={handleFontSizeChange}
          />
          <IconButton
            color="inherit"
            onClick={handleClick}
            size="small"
            sx={{ ml: 2 }}
            aria-controls={open ? 'account-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={open ? 'true' : undefined}
          >

            {user ? (
              <Avatar
                src={getAvatarUrl()}
                sx={{ width: 40, height: 40, cursor: 'pointer' }}
              />
            ) : (
              <Avatar
                sx={{
                  width: 40,
                  height: 40,
                  bgcolor: 'primary.main',
                  cursor: 'pointer'
                }}
              >
                {getUserInitials()}
              </Avatar>
            )}
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            id="account-menu"
            open={open}
            onClose={handleClose}
            onClick={handleClose}
            PaperProps={{
              elevation: 0,
              sx: {
                overflow: 'visible',
                filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                mt: 1.5,
                '& .MuiAvatar-root': {
                  width: 32,
                  height: 32,
                  ml: -0.5,
                  mr: 1,
                },
              },
            }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            <Box sx={(theme) => ({ px: 2, py: 1.5, borderBottom: `1px solid ${theme.palette.divider}` })}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {user?.firstName && user?.lastName
                  ? `${user.firstName} ${user.lastName}`
                  : user?.username || 'User'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {user?.email || ''}
              </Typography>
            </Box>
            <MenuItem onClick={() => {
              handleClose();
              setProfileDialogOpen(true);
            }}>
              <ListItemIcon>
                <PersonIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Profile</ListItemText>
            </MenuItem>
            <MenuItem onClick={(e) => {
              e.stopPropagation();
              handleAvatarMenuClick(e);
            }}>
              <ListItemIcon>
                <AccountCircleIcon fontSize="small" />
              </ListItemIcon>
              Change Avatar
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Logout</ListItemText>
            </MenuItem>
            <Menu
              anchorEl={avatarMenuAnchorEl}
              open={avatarMenuOpen}
              onClose={handleAvatarMenuClose}
              onClick={(e) => e.stopPropagation()}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
              PaperProps={{
                sx: {
                  width: 280,
                  p: 1
                }
              }}
            >
              <MenuItem component="label">
                <ListItemIcon>
                  <UploadIcon fontSize="small" />
                </ListItemIcon>
                Upload New Avatar
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={handleAvatarChange}
                />
              </MenuItem>
              <Divider />
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 1fr)',
                gap: 1,
                p: 1,
                maxHeight: '300px',
                overflowY: 'auto'
              }}>
                {PREDEFINED_AVATARS.map((avatar) => (
                  <Box
                    key={avatar.id}
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePredefinedAvatarSelect(avatar.url);
                    }}
                    sx={{
                      cursor: 'pointer',
                      '&:hover': {
                        transform: 'scale(1.1)',
                        transition: 'transform 0.2s'
                      }
                    }}
                  >
                    <Avatar
                      src={predefinedAvatarDataUrls[avatar.id]}
                      sx={{
                        width: 60,
                        height: 60
                      }}
                    />
                  </Box>
                ))}
              </Box>
            </Menu>
          </Menu>
          {user?.admin && (
            <Button
              color="secondary"
              variant="contained"
              sx={{ ml: 2 }}
              onClick={() => navigate(location.pathname.startsWith('/admin') ? '/' : '/admin')}
            >
              {location.pathname.startsWith('/admin') ? 'Main UI' : 'Admin Console'}
            </Button>
          )}
          </Box>
          </Box>
        </Toolbar>
      </StyledAppBar>

      <MainContent sx={{ marginTop: `${effectiveAppBarHeight}px` }}>
        <SplitPaneContainer>
          {location.pathname.startsWith('/admin') ? (
            <Outlet />
          ) : (
            <>
              {isDocumentPanelVisible && (
                <Pane
                  style={{
                    width: `${DOCUMENT_PANEL_WIDTH_PX}px`,
                    position: 'fixed',
                    left: 0,
                    top: `${effectiveAppBarHeight}px`,
                    height: `calc(100vh - ${effectiveAppBarHeight}px)`,
                    zIndex: 1,
                  }}
                  sx={(theme) => ({
                    bgcolor: 'transparent',
                    borderRight: `1px solid ${theme.palette.divider}`,
                    boxShadow: theme.shadows[3],
                  })}
                >
                  <DocumentPanel
                    onNewDocument={handleNewDocument}
                    documents={documents.map(doc => ({
                      ...doc,
                      hasOpenTab: tabs.some(tab => tab.id === doc.id)
                    }))}
                    onOpenDocument={handleOpenDocument}
                    onOpenDocumentFully={openDocumentFully}
                    onOpenReadOnlyDocument={handleOpenReadOnlyDocument}
                    onRegisterReadAccessGrantRemover={registerRemoveReadAccessGrantFromList}
                    onRegisterIncomingShareRemover={registerRemoveIncomingShareFromList}
                    onCloseDocument={handleCloseDocument}
                    onDeleteDocument={handleDeleteDocument}
                    onRenameDocument={handleRenameDocument}
                    onDuplicateDocument={handleDuplicateDocument}
                    onMergeDocument={handleMergeDocument}
                    onCompareDocuments={handleCompareDocuments}
                    onSelectiveMerge={handleStartSelectiveMerge}
                    onSelectDocument={handleSelectDocument}
                    onLoadFromFile={handleLoadFromFile}
                    onExportDocument={handleExportDocument}
                    currentTab={currentTab}
                    currentDocument={currentDocument}
                    documentLastUsedAt={documentLastUsedAt}
                    setGlobalLoading={setLoadingSync}
                    refreshCurrentDocument={async () => {
                      if (currentDocument?.categoryId) {
                        const rootCategory = await fetchCategoryWithChildren(currentDocument.categoryId);
                        const tree = [rootCategory];
                        setCategories(tree);
                        // Keep tab cache in sync so later openDocumentFully / tab switches
                        // do not restore a pre-apply tree missing new categories.
                        persistDocumentCategoryTree(currentDocument.id, tree);
                        setExpandedNodes((prev) =>
                          prev.includes(rootCategory.id) ? prev : [...prev, rootCategory.id],
                        );
                        await refreshActiveDocumentSubcasesFromServer(currentDocument.categoryId);
                      }
                    }}
                    refreshDocuments={refreshDocuments}
                    tabs={tabs}
                    onIncomingSharesChanged={handleIncomingSharesChanged}
                    onOutgoingSharesChanged={handleOutgoingSharesChanged}
                    incomingSharesRefreshToken={incomingSharesRefreshToken}
                    outgoingSharesRefreshToken={outgoingSharesRefreshToken}
                    sharedWithMeFocusToken={sharedWithMeFocusToken}
                  />
                </Pane>
              )}
              <Pane
                style={{
                  width: isDocumentPanelVisible
                    ? `calc(100% - ${MAIN_PANE_LEFT_OFFSET_PX}px)`
                    : `calc(100% - ${MAIN_PANE_GAP_PX}px)`,
                  marginLeft: isDocumentPanelVisible ? MAIN_PANE_LEFT_OFFSET_PX : MAIN_PANE_GAP_PX,
                  height: '100%',
                  minWidth: 0,
                  maxWidth: isDocumentPanelVisible
                    ? `calc(100% - ${MAIN_PANE_LEFT_OFFSET_PX}px)`
                    : `calc(100% - ${MAIN_PANE_GAP_PX}px)`,
                  overflow: 'hidden',
                }}
                sx={(theme) => ({
                  pl: 0,
                  pr: theme.spacing(1.5),
                  pt: theme.spacing(1),
                  minWidth: 0,
                  maxWidth: '100%',
                  boxSizing: 'border-box',
                  transition: theme.transitions.create('margin', {
                    easing: theme.transitions.easing.sharp,
                    duration: theme.transitions.duration.leavingScreen,
                  }),
                })}
              >
                {documentSharingEnabled &&
                  incomingShareCount > 0 &&
                  !shareLoginNoticeDismissed &&
                  !location.pathname.startsWith('/admin') && (
                    <Alert
                      severity="info"
                      sx={{ mb: 1 }}
                      onClose={() => setShareLoginNoticeDismissed(true)}
                      action={
                        <Button color="inherit" size="small" onClick={handleViewSharedDocumentsNotice}>
                          View
                        </Button>
                      }
                    >
                      {incomingShareCount === 1
                        ? 'A document was shared with you. Open the document panel and check Shared with me or View only shared with me.'
                        : `${incomingShareCount} items were shared with you. Open the document panel and check Shared with me or View only shared with me.`}
                    </Alert>
                  )}
                {renderMainContent()}
              </Pane>
            </>
          )}
        </SplitPaneContainer>
      </MainContent>
      <Dialog
        open={splitDialogOpen}
        onClose={(_event, reason) => {
          if (reason === 'backdropClick') return;
          handleSplitDialogClose();
        }}
      >
        <DialogTitle>Split into two subcategories</DialogTitle>
        <DialogContent>
          {splitError && (
            <Typography color="error" sx={{ mb: 2 }}>
              {splitError}
          </Typography>
          )}
          <TextField
            autoFocus
            margin="dense"
            label="First Category Name"
            fullWidth
            value={splitNames.first}
            onChange={(e) => {
              const value = e.target.value;
              setSplitNames(prev => {
                const updated = { ...prev, first: value };
                validateSplitNames(updated.first, updated.second);
                return updated;
              });
            }}
            error={!!splitError}
          />
          <TextField
            margin="dense"
            label="Second Category Name"
            fullWidth
            value={splitNames.second}
            onChange={(e) => {
              const value = e.target.value;
              setSplitNames(prev => {
                const updated = { ...prev, second: value };
                validateSplitNames(updated.first, updated.second);
                return updated;
              });
            }}
            error={!!splitError}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSplitDialogClose}>Cancel</Button>
          <Button onClick={handleSplitDialogConfirm} variant="contained" color="primary" disabled={!!splitError}>
            Split
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={addCategoryDialogOpen}
        onClose={(_event, reason) => {
          if (reason === 'backdropClick') return;
          handleAddCategoryDialogClose();
        }}
      >
        <DialogTitle>Add New Category</DialogTitle>
        <DialogContent>
          {addError && (
             <Typography color="error" sx={{ mb: 2 }}>
                {addError}
          </Typography>
          )}
          <TextField
            autoFocus
            margin="dense"
            label="Category Name"
            fullWidth
            value={newName}
            onChange={(e) => handleNewCategoryNameChange(e.target.value)}
            error={!!addError}
          />
          <TextField
              margin="dense"
              label="Description"
              fullWidth
              value={newDescription}
              onChange={(e) => setNewDescription(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleAddCategoryDialogClose}>Cancel</Button>
          <Button onClick={handleAddCategoryDialogConfirm} variant="contained" color="primary">
            Add
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={groupCategoriesDialogOpen}
        onClose={(_event, reason) => {
          if (reason === 'backdropClick') return;
          handleGroupCategoriesDialogClose();
        }}
      >
          <DialogTitle>Group selected subcategories</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Creates a new category under{' '}
              <strong>{selectedCategory?.name ?? 'the selected branch'}</strong> and moves the
              subcategories you selected in the content pane into it. Leave at least one sibling
              unselected.
            </Typography>
            {addError && (
               <Typography color="error" sx={{ mb: 2 }}>
                  {addError}
            </Typography>
            )}
            <TextField
              autoFocus
              margin="dense"
              label="New group name"
        fullWidth
              value={newName}
              onChange={(e) => handleNewCategoryNameChange(e.target.value)}
              error={!!addError}
            />
            <TextField
                margin="dense"
                label="Description"
                fullWidth
                value={newDescription}
                onChange={(e) => setNewDescription(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleGroupCategoriesDialogClose}>Cancel</Button>
            <Button onClick={handleGroupCategoriesDialogConfirm} variant="contained" color="primary">
              Group
            </Button>
          </DialogActions>
        </Dialog>
      <Dialog
        open={mergeCategoriesDialogOpen}
        onClose={(_event, reason) => {
          if (reason === 'backdropClick') return;
          handleMergeCategoriesDialogClose();
        }}
      >
        <DialogTitle>Merge selected content categories</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Creates a new content category under{' '}
            <strong>{selectedCategory?.name ?? 'the selected branch'}</strong> with all rows from
            the content categories you selected in the content pane. Those source categories are
            removed. Leave at least one sibling unselected.
          </Typography>
          {addError && (
             <Typography color="error" sx={{ mb: 2 }}>
                {addError}
          </Typography>
          )}
          <TextField
            autoFocus
            margin="dense"
            label="New content category name"
      fullWidth
            value={newName}
            onChange={(e) => handleMergeCategoriesNameChange(e.target.value)}
            error={!!addError}
          />
          <TextField
              margin="dense"
              label="Description"
              fullWidth
              value={newDescription}
              onChange={(e) => setNewDescription(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleMergeCategoriesDialogClose}>Cancel</Button>
          <Button onClick={handleMergeCategoriesDialogConfirm} variant="contained" color="primary">
            Merge
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={renameDialogOpen}
        onClose={(_event, reason) => {
          if (reason === 'backdropClick') return;
          handleRenameDialogClose();
        }}
        TransitionProps={{
          onEntered: (node) => {
            const input = (node as HTMLElement).querySelector<HTMLInputElement>('input');
            if (!input) return;
            input.focus();
            input.select();
          },
        }}
      >
        <DialogTitle>
          Rename Category{selectedCategory?.name ? `: ${selectedCategory.name}` : ''}
        </DialogTitle>
        <DialogContent>
          {renameError && (
            <Typography color="error" sx={{ mb: 2 }}>
              {renameError}
          </Typography>
          )}
          <TextField
            autoFocus
            margin="dense"
            label="New Name"
            fullWidth
            value={newName}
            onChange={(e) => handleRenameCategoryNameChange(e.target.value)}
            onFocus={(e) => e.target.select()}
            error={!!renameError}
          />
          <TextField
              margin="dense"
              label="Description"
              fullWidth
              value={newDescription}
              onChange={(e) => setNewDescription(e.target.value)}
        />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRenameDialogClose}>Cancel</Button>
          <Button
            onClick={handleRenameDialogConfirm}
            variant="contained"
            color="primary"
            disabled={!!renameError || !newName.trim()}
          >
            Rename
          </Button>
        </DialogActions>
      </Dialog>
      <RenameSubcaseDialog
          open={openRenameSubcaseDialog}
          onClose={handleRenameSubcaseClose}
          onSubmit={handleRenameSubcaseSubmit}
          subcases={activeDocument?.subcases || []}
        />
      <DuplicateCategoryAsDocumentDialog
          open={duplicateCategoryDialogOpen}
          onClose={() => setDuplicateCategoryDialogOpen(false)}
          onConfirm={async (newName, selectedCategories, selectedSubcases) => {
            setLoading(true);
            try {
              // 1. Create the new document
              const newDocument = await createDocument(newName);
              if (!newDocument || !newDocument.categoryId) throw new Error('Failed to create document');

              // 2. Create a map of selected category IDs for quick lookup
              const selectedCategoryIds = new Set(selectedCategories.map(cat => cat.id));

              const sourceDocumentName = (activeDocument?.name ?? '').trim();
              const isSourceDocumentRootCategory = (cat: Category) =>
                sourceDocumentName.length > 0 &&
                normalizeCategoryName(cat.name) === normalizeCategoryName(sourceDocumentName);

              const postContentRowsForSelectedSubcases = async (
                parentCategoryId: string,
                contents: Category['contents']
              ) => {
                if (!Array.isArray(contents)) return;
                for (const content of contents) {
                  if (!selectedSubcases.includes(content.subcase)) continue;
                  await api.post('/content', buildContentCopyPayload(content, parentCategoryId));
                }
              };

              // 3. Helper function to traverse the original tree and create selected categories
              const createSelectedCategories = async (category: Category, parentId: string): Promise<void> => {
                // Skip if this category wasn't selected
                if (!selectedCategoryIds.has(category.id)) {
                  return;
                }

                // Create the category
                const response = await api.post('/categories', {
                  parentId,
                  name: category.name,
                  description: category.description || ''
                });
                const newCategory = response.data;

                if (category.contentCategory) {
                  await postContentRowsForSelectedSubcases(newCategory.id, category.contents);
                }

                // Recursively process child categories (content rows live in contents[], not children[])
                if (Array.isArray(category.children)) {
                  for (const child of category.children) {
                    await createSelectedCategories(child, newCategory.id);
                  }
                }
              };

              // Merge the selected category into the new document root (no extra folder named after the document).
              const populateSelectedSubtreeIntoParent = async (
                category: Category,
                parentId: string
              ): Promise<void> => {
                if (!selectedCategoryIds.has(category.id)) {
                  return;
                }
                if (category.contentCategory) {
                  await postContentRowsForSelectedSubcases(parentId, category.contents);
                }
                if (Array.isArray(category.children)) {
                  for (const child of category.children) {
                    await createSelectedCategories(child, parentId);
                  }
                }
              };

              // 4. Root category name matches the document (set at create). Copy selected subtree under it without an extra wrapper folder.
              // Prefer the fully loaded scope root from the dialog (lazy document tree may only have stubs).
              const scopeRoot =
                (selectedCategory
                  ? selectedCategories.find((c) => c.id === selectedCategory.id)
                  : undefined) ?? selectedCategories[0];
              if (scopeRoot) {
                if (isSourceDocumentRootCategory(scopeRoot)) {
                  if (Array.isArray(scopeRoot.children)) {
                    for (const child of scopeRoot.children) {
                      await createSelectedCategories(child, newDocument.categoryId);
                    }
                  }
                } else {
                  await populateSelectedSubtreeIntoParent(scopeRoot, newDocument.categoryId);
                }
              }

              await refreshDocuments();

              // 5. Load subcases for the new document (must use new doc root category id, not the source document)
              if (newDocument.categoryId) {
                const response = await api.get('/documents/subcases', {
                  params: { documentId: newDocument.categoryId },
                });
                newDocument.subcases = response.data;
              }

              setLoading(false);
              setDuplicateCategoryDialogOpen(false);
              // 6. Open the new document in a tab
              await handleOpenDocument(newDocument);
            } catch (error) {
              setLoading(false);
              alert(
                formatApiErrorMessage(
                  (error as { response?: { data?: unknown } })?.response?.data,
                  'Failed to duplicate as document',
                ),
              );
            }
          }}
          selectedCategory={selectedCategory}
          categories={categories}
          existingDocumentNames={ownedDocumentNames}
          documentId={activeDocument?.id || ''}
          subcaseGroups={subcaseGroups}
          documentSubcases={activeDocument?.subcases || []}
        />

      <TreeAssistantWindow
        open={
          !effectiveDocumentReadOnly &&
          treeAssistantOpen &&
          isFeatureEnabled(FEATURE_FLAG_KEYS.TREE_ASSISTANT)
        }
        onClose={() => setTreeAssistantOpen(false)}
        documentId={activeDocument?.id ?? null}
        selectedCategoryId={selectedCategory?.id}
        selectedContentIds={selectedContents.map((c) => c.id)}
        documentNamesForAutocomplete={documents.map((d) => d.name).filter((n): n is string => Boolean(n?.trim()))}
        libraryDocuments={documents
          .filter((d) => d.categoryId)
          .map((d) => ({ id: d.id, name: d.name, categoryId: d.categoryId! }))}
        openDocumentCategoryTree={categories}
        documentSubcasesForAutocomplete={activeDocument?.subcases ?? []}
        onApplied={async (applyResult) => {
          await refreshDocuments();
          if (applyResult?.deletedDocumentName) {
            const id = applyResult.deletedDocumentName;
            historyService.clearDocumentHistory(id);
            if (activeDocument?.id === id) {
              historyService.setCurrentDocument(null);
            }
            if (currentDocument?.id === id) {
              setCurrentDocument(undefined);
            }
            const placeholder: Document = {
              id,
              name: id,
              label: id,
              subcases: [],
            };
            handleCloseDocument(placeholder);
            return;
          }
          if (
            applyResult?.renamedDocumentOldName &&
            applyResult?.renamedDocumentNewName
          ) {
            const oldId = applyResult.renamedDocumentOldName;
            const newId = applyResult.renamedDocumentNewName;
            const rootId = applyResult.renamedDocumentRootCategoryId;
            historyService.rekeyDocumentHistory(oldId, newId);
            setTabs((prev) =>
              prev.map((t) =>
                t.id === oldId ? { ...t, id: newId, label: newId } : t,
              ),
            );
            if (currentTab === oldId) {
              setCurrentTab(newId);
            }
            const nextDoc =
              activeDocument &&
              (activeDocument.id === oldId || activeDocument.name === oldId)
                ? {
                    ...activeDocument,
                    id: newId,
                    name: newId,
                    label: newId,
                    ...(rootId ? { categoryId: rootId } : {}),
                  }
                : undefined;
            setCurrentDocument(nextDoc);
            setActiveDocument(nextDoc);
            historyService.setCurrentDocument(newId);
            if (nextDoc) {
              await reloadOpenDocumentCategoryTree(nextDoc);
            }
            return;
          }

          // Stale closure: after handleOpenDocument in runClientActions, activeDocument below would still be the old tab’s doc.
          let documentForPostApplyReload: Document | undefined =
            activeDocument !== null && activeDocument !== undefined ? activeDocument : undefined;

          const runClientActions = async () => {
            const actions = applyResult?.clientActions;
            if (!actions?.length) return;
            for (const action of actions) {
              if (action.type === 'OPEN_DOCUMENT') {
                const fromList = documents.find(
                  (d) => d.id === action.documentName || d.name === action.documentName,
                );
                const doc: Document = fromList
                  ? {
                      ...fromList,
                      categoryId: fromList.categoryId || action.categoryId,
                    }
                  : {
                      id: action.documentName,
                      name: action.documentName,
                      label: action.documentName,
                      categoryId: action.categoryId,
                      subcases: [],
                    };
                documentForPostApplyReload = doc;
                await handleOpenDocument(doc);
                await fetchSubcases(doc);
              } else if (action.type === 'CLOSE_DOCUMENT') {
                handleCloseDocument({
                  id: action.documentName,
                  name: action.documentName,
                  label: action.documentName,
                  subcases: [],
                });
              }
            }
          };

          let assistantHistoryRecorded = false;
          if (applyResult?.newDocumentName && applyResult?.newRootCategoryId) {
            const newDoc: Document = {
              id: applyResult.newDocumentName,
              name: applyResult.newDocumentName,
              label: applyResult.newDocumentName,
              categoryId: applyResult.newRootCategoryId,
              subcases: [],
            };
            await handleOpenDocument(newDoc, { forceExpandRoot: true });
            await fetchSubcases(newDoc);
            historyService.setCurrentDocument(newDoc.id);
            if (applyResult.assistantHistory?.undoSteps?.length) {
              historyService.recordOperation({
                type: 'treeAssistantBatch',
                data: {
                  ...applyResult.assistantHistory,
                  tooltipLabel: buildTreeAssistantBatchTooltip(applyResult.assistantHistory),
                },
              });
              assistantHistoryRecorded = true;
            }
            await runClientActions();
            return;
          }

          await runClientActions();

          if (documentForPostApplyReload) {
            await reloadOpenDocumentCategoryTree(documentForPostApplyReload);
            if (applyResult?.assistantHistory?.undoSteps?.length && !assistantHistoryRecorded) {
              historyService.setCurrentDocument(documentForPostApplyReload.id);
              historyService.recordOperation({
                type: 'treeAssistantBatch',
                data: {
                  ...applyResult.assistantHistory,
                  tooltipLabel: buildTreeAssistantBatchTooltip(applyResult.assistantHistory),
                },
              });
            }
          }
        }}
      />

      <ImportSubtreeFromDocumentDialog
        open={importSubtreeDialogOpen}
        onClose={() => setImportSubtreeDialogOpen(false)}
        documents={documents}
        excludeDocumentId={activeDocument?.id}
        targetDocumentLabel={activeDocument?.label || activeDocument?.name}
        targetCategoryName={selectedCategory?.name ?? ''}
        onRefreshDocuments={refreshDocuments}
        onConfirm={handleImportSubtreeFromDocumentConfirm}
      />

      <Dialog
        open={openDeleteCategoryDialog}
        onClose={(_event, reason) => {
          if (reason === 'backdropClick') return;
          setOpenDeleteCategoryDialog(false);
        }}
      >
        <DialogTitle>Delete Category</DialogTitle>
        <DialogContent>
          <Typography>
            {selectedCategories.length > 1
              ? `Are you sure you want to delete ${selectedCategories.length} categories and all their content?`
              : `Are you sure you want to delete the category ${selectedCategories.length === 0 ? selectedCategory?.name : selectedCategories[0].name} and all its content?`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteCategoryDialog(false)}>Cancel</Button>
          <Button
            onClick={async () => {
              setOpenDeleteCategoryDialog(false);
              await handleDeleteSelectedCategory();
            }}
            variant="contained"
            color="error"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={openDeleteContentDialog}
        onClose={(_event, reason) => {
          if (reason === 'backdropClick') return;
          setOpenDeleteContentDialog(false);
        }}
      >
        <DialogTitle>Delete Content</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete {selectedContents.length} content item(s)?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteContentDialog(false)}>Cancel</Button>
          <Button
            onClick={async () => {
              setOpenDeleteContentDialog(false);
              await handleDeleteContent();
            }}
            variant="contained"
            color="error"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={
          contentPaneFilterDialogOpen &&
          isFeatureEnabled(FEATURE_FLAG_KEYS.FILTER_CONTENT_PANE_BY_SUBCASE)
        }
        onClose={(_event, reason) => {
          if (reason === 'backdropClick') return;
          setContentPaneFilterDialogOpen(false);
        }}
      >
        <DialogTitle>Filter content by subcase</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1">Select Subcases</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              The category structure stays visible — only rows in the content pane are filtered. Group
              chips, Select all, and individual checkboxes (same as Category Structure Overview). The table updates
              while this dialog is open. Choosing every subcase clears the filter. Hidden rows are not
              deleted. Cancel restores the filter from when you opened this dialog.
            </Typography>
            {loadingSubcaseGroups ? (
              <Box sx={{ my: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={18} />
                <Typography variant="body2">Loading groups...</Typography>
              </Box>
            ) : (
              subcaseGroups.length > 0 && (
                <Box sx={{ my: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {subcaseGroups.map(group => (
                    <Chip
                      key={group.name}
                      label={group.name}
                      color={
                        contentPaneFilterDraftGroupNames.includes(group.name) ? 'primary' : 'default'
                      }
                      onClick={() => handleContentPaneFilterGroupSelect(group.name)}
                      sx={{ cursor: 'pointer' }}
                    />
                  ))}
                </Box>
              )
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={
                      contentPaneFilterDraftSubcases.length === (activeDocument?.subcases?.length || 0)
                    }
                    indeterminate={
                      contentPaneFilterDraftSubcases.length > 0 &&
                      contentPaneFilterDraftSubcases.length < (activeDocument?.subcases?.length || 0)
                    }
                    onChange={e => {
                      contentPaneFilterSubcasesBaselineRef.current = null;
                      if (e.target.checked) {
                        setContentPaneFilterDraftSubcases(activeDocument?.subcases || []);
                        setContentPaneFilterDraftGroupNames([]);
                      } else {
                        setContentPaneFilterDraftSubcases([]);
                        setContentPaneFilterDraftGroupNames([]);
                      }
                    }}
                  />
                }
                label="Select All"
              />
              <InvertSelectionButton
                disabled={(activeDocument?.subcases?.length || 0) === 0}
                onClick={() => {
                  const pool = activeDocument?.subcases || [];
                  contentPaneFilterSubcasesBaselineRef.current = null;
                  setContentPaneFilterDraftGroupNames([]);
                  setContentPaneFilterDraftSubcases(prev =>
                    invertSubcaseSelection(pool, prev),
                  );
                }}
              />
            </Box>
            <List sx={{ maxHeight: 300, overflow: 'auto' }}>
              {(activeDocument?.subcases || []).map((subcase: string) => (
                <ListItem key={subcase} disablePadding>
                  <ListItemButton
                    onClick={() => {
                      setContentPaneFilterDraftSubcases(prev =>
                        prev.includes(subcase)
                          ? prev.filter(s => s !== subcase)
                          : [...prev, subcase]
                      );
                    }}
                  >
                    <Checkbox
                      edge="start"
                      checked={contentPaneFilterDraftSubcases.includes(subcase)}
                      tabIndex={-1}
                      disableRipple
                    />
                    <ListItemText primary={subcase} />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              contentPaneFilterSubcasesBaselineRef.current = null;
              setContentPaneFilterDraftGroupNames([]);
              setContentPaneFilterDraftSubcases([...(activeDocument?.subcases || [])]);
            }}
          >
            Clear filter
          </Button>
          <Button onClick={cancelContentPaneFilterDialog}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setContentPaneFilterDialogOpen(false)}
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>
      <SubcaseGroupsDialog
        open={subcaseGroupsDialogOpen}
        onClose={() => setSubcaseGroupsDialogOpen(false)}
        onSaved={() => {
          const documentId = activeDocument?.categoryId;
          if (!documentId) return;
          setLoadingSubcaseGroups(true);
          api
            .get('/documents/subcase-groups', { params: { documentId } })
            .then(res => {
              const groupsArray = Object.entries(res.data as Record<string, string[]>).map(
                ([name, subcases]) => ({ name, subcases }),
              );
              setSubcaseGroups(groupsArray);
            })
            .catch(() => setSubcaseGroups([]))
            .finally(() => setLoadingSubcaseGroups(false));
        }}
        documentId={activeDocument?.categoryId || ''}
        documentSubcases={activeDocument?.subcases || []}
      />
      
      <FindCategoryDialog
        open={findDialogOpen}
        onClose={handleFindDialogClose}
        categories={categories}
        selectedCategory={selectedCategory}
        selectedContentId={currentContent?.id ?? selectedContents[0]?.id ?? null}
        onCategorySelect={setSelectedCategory}
        onExpandParents={expandParentsToMakeVisible}
        findCategoryInTree={findCategoryInTree}
        subcaseGroups={subcaseGroups}
        documentSubcases={activeDocument?.subcases || []}
        documentName={activeDocument?.name}
        onContentSelect={async (categoryId: string, contentId: string, searchString?: string, searchTerms?: string[], searchMode?: 'any' | 'all' | 'exact' | 'semantic', wholeWord?: boolean, caseSensitive?: boolean, allMatchingContentIds?: string[]) => {
          let category = findCategoryInTree(categories, categoryId);
          let content = category?.contents?.find(c => c.id === contentId);

          if (!content) {
            try {
              const fresh = await fetchCategoryForPane(categoryId);
              content = fresh.contents?.find(c => c.id === contentId);
              if (fresh.contents) {
                setCategories(prev =>
                  updateCategoryInTree(prev, categoryId, cat => ({ ...cat, contents: fresh.contents })),
                );
              }
              category = fresh;
            } catch (error) {
              console.error('Error loading category contents for find result:', error);
            }
          }

          if (!category || !content) {
            return;
          }

          setSelectedCategory(category);
          setSelectedContents([content]);
          setCurrentContent(content);
          const textToHighlight = searchString || findSearchString;
          setHighlightedText(textToHighlight);
          setSearchTerms(searchTerms || []);
          setSearchMode(searchMode || 'exact');
          setWholeWord(wholeWord || false);
          setCaseSensitive(caseSensitive || false);
          setMatchingContentIds(allMatchingContentIds || [contentId]);

          // Bring the content pane into view, then the specific result row (retries until mounted).
          requestAnimationFrame(() => {
            contentPaneRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            scrollToContent(contentId);
          });
        }}
      />
      <GenerateSubtreeDialog
          open={organizeCategoriesDialogOpen && isFeatureEnabled(FEATURE_FLAG_KEYS.GENERATE_SUBTREE_FROM_CONTENT)}
          onClose={handleOrganizeCategoriesClose}
          mode={organizeCategoriesMode}
          preset={organizeCategoriesPreset}
          category={organizeCategoriesMode === 'fromContent' ? selectedCategory : null}
          anchorCategory={organizeCategoriesMode === 'branch' ? selectedCategory : null}
          siblingCategoryIds={organizeBranchSiblingIds}
          splittableChildCategories={organizeSplittableChildren}
          splittableChildrenLoading={organizeSplittableLoading}
          categoriesTree={categories}
          onSuccess={handleGenerateSubtreeSuccess}
          setGlobalLoading={setLoadingSync}
        />
      <AddMatchingContentDialog
        open={addMatchingContentDialogOpen && isFeatureEnabled(FEATURE_FLAG_KEYS.SEMANTIC_FIND)}
        onClose={handleAddMatchingContentDialogClose}
        targetCategory={selectedCategory}
        categoriesTree={categories}
        sourceGroups={getAddMatchingContentSourceGroups(selectedCategory)}
        defaultSourceId={getAddMatchingContentDefaultSourceId(selectedCategory)}
        suggestedSourceExamples={(() => {
          const parent = selectedCategory?.parentId
            ? findCategoryInTree(categories, selectedCategory.parentId)
            : null;
          const examples: string[] = [];
          if (parent?.name?.trim()) {
            examples.push(`${TEMP_CONTENT_CATEGORY_PREFIX}${parent.name.trim()}`);
          }
          examples.push(ORIGINAL_FILES_CONTENT_CATEGORY_NAME);
          return examples;
        })()}
        onSuccess={handleAddMatchingContentSuccess}
        onSourceRowEdited={(categoryId, updated, previous) => {
          applyContentUpdateInTree(updated, categoryId);
          if (previous) {
            historyService.recordOperation({
              type: 'updateContent',
              data: { content: updated, prevContent: previous, categoryId },
            });
          }
        }}
      />
      <AdjustTextDialog
        open={adjustTextRow !== null}
        onClose={() => {
          setAdjustTextRow(null);
          setAdjustTextCategoryId(null);
        }}
        currentText={adjustTextRow?.text ?? ''}
        prevText={getAdjustTextNeighbours(adjustTextRow).prev}
        nextText={getAdjustTextNeighbours(adjustTextRow).next}
        onSave={handleAdjustTextSave}
      />
      <CompareContentDialog
        open={
          compareContentDialogOpen &&
          isFeatureEnabled(FEATURE_FLAG_KEYS.COMPARE_CONTENT_SELECTION)
        }
        onClose={handleCompareContentDialogClose}
        sourceCategory={selectedCategory}
        categoriesTree={categories}
        onSelectContentIds={handleCompareContentSelect}
        onSplitWithContentIds={handleCompareContentSplit}
      />
      <CategoryReportDialog
        open={
          categoryReportDialogOpen &&
          isFeatureEnabled(FEATURE_FLAG_KEYS.CATEGORY_AI_REPORT)
        }
        onClose={handleCategoryAiReportDialogClose}
        category={selectedCategory}
      />
      <ContentRichPreviewDialog
        open={contentPreviewOpen}
        onClose={() => setContentPreviewOpen(false)}
        html={contentPreviewHtml}
      />
      <HelpDialog
        open={helpDialogOpen}
        onClose={() => setHelpDialogOpen(false)}
      />
      <UserProfileDialog
        open={profileDialogOpen}
        onClose={() => setProfileDialogOpen(false)}
      />
      <Snackbar
        open={shareArrivalSnackbarOpen}
        autoHideDuration={10_000}
        onClose={() => setShareArrivalSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ bottom: shareAcceptedSnackbarOpen ? { xs: 88, sm: 96 } : undefined }}
      >
        <Alert
          severity="info"
          variant="filled"
          onClose={() => setShareArrivalSnackbarOpen(false)}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => {
                setShareArrivalSnackbarOpen(false);
                handleViewSharedDocumentsNotice();
              }}
            >
              View
            </Button>
          }
          sx={{ width: '100%' }}
        >
          {shareArrivalSnackbarMessage}
        </Alert>
      </Snackbar>
      <Snackbar
        open={shareAcceptedSnackbarOpen}
        autoHideDuration={10_000}
        onClose={() => setShareAcceptedSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="success"
          variant="filled"
          onClose={() => setShareAcceptedSnackbarOpen(false)}
          sx={{ width: '100%' }}
        >
          {shareAcceptedSnackbarMessage}
        </Alert>
      </Snackbar>
      <Dialog
        open={!!overviewCopySource}
        onClose={(_event, reason) => {
          if (reason === 'backdropClick') return;
          if (!overviewCopyBusy) setOverviewCopySource(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{overviewCopyMode === 'move' ? 'Move' : 'Copy'} row to another category</DialogTitle>
        <DialogContent dividers>
          {overviewCopySource && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                {overviewCopyMode === 'move' ? (
                  <>
                    Move this row from <strong>{overviewCopySource.parentName}</strong> into another content
                    category. It is removed from its current category, and you can revert with Undo.
                  </>
                ) : (
                  <>
                    Copy this row from <strong>{overviewCopySource.parentName}</strong> into another content
                    category. The original row stays where it is, and you can revert with Undo.
                  </>
                )}
              </Typography>
              <RadioGroup
                row
                value={overviewCopyMode}
                onChange={(e) => { setOverviewCopyMode(e.target.value as 'copy' | 'move'); setOverviewCopyError(null); }}
                sx={{ mb: 1.5 }}
              >
                <FormControlLabel value="copy" control={<Radio size="small" />} label="Copy (keep original)" />
                <FormControlLabel value="move" control={<Radio size="small" />} label="Move (remove from original)" />
              </RadioGroup>
              <Box
                sx={{
                  mb: 2,
                  p: 1,
                  borderRadius: 1,
                  border: theme => `1px solid ${theme.palette.divider}`,
                  bgcolor: 'action.hover',
                  maxHeight: 96,
                  overflow: 'auto',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {overviewCopySource.content.subcase && (
                  <Typography
                    variant="caption"
                    sx={{ display: 'block', mb: 0.5, color: 'text.secondary', fontWeight: 600 }}
                  >
                    {overviewCopySource.content.subcase}
                  </Typography>
                )}
                <Typography variant="body2">{overviewCopySource.content.text}</Typography>
              </Box>
              <Typography variant="subtitle2" sx={{ mb: 0.75 }}>
                Copy to
              </Typography>
              {(() => {
                const root =
                  (activeDocument?.categoryId
                    ? findCategoryInTree(categories, activeDocument.categoryId)
                    : null) ??
                  subTreeOverlayData.category ??
                  null;
                if (!root) return null;
                const renderNode = (category: Category): React.ReactNode | null => {
                  const selectable = overviewCopyTargetIds.has(category.id);
                  const isCurrent = category.id === overviewCopySource.parentId;
                  if (
                    !selectable &&
                    !isCurrent &&
                    !categorySubtreeHasAllowedCategoryId(category, overviewCopyTargetIds)
                  ) {
                    return null;
                  }
                  const rowCount = category.contentCategory
                    ? (category.contents?.length ?? 0)
                    : countContentRowsInCategorySubtree(category);
                  const nameSuffix = isCurrent ? ' (current)' : '';
                  const nameColor = isCurrent
                    ? 'text.disabled'
                    : selectable
                      ? 'text.primary'
                      : 'text.secondary';
                  return (
                    <TreeItem
                      key={category.id}
                      nodeId={category.id}
                      label={
                        <CategoryPickerTreeLabel
                          category={category}
                          contentCount={rowCount}
                          nameColor={nameColor}
                          nameSuffix={nameSuffix}
                        />
                      }
                      disabled={isCurrent}
                    >
                      {(category.children ?? []).map(child => renderNode(child)).filter(Boolean)}
                    </TreeItem>
                  );
                };
                return (
                  <Box
                    sx={{
                      maxHeight: 300,
                      overflow: 'auto',
                      border: theme => `1px solid ${theme.palette.divider}`,
                      borderRadius: 1,
                      p: 0.5,
                    }}
                  >
                    <TreeView
                      defaultCollapseIcon={<ExpandMoreIcon />}
                      defaultExpandIcon={<ChevronRightIcon />}
                      defaultExpanded={[root.id]}
                      selected={overviewCopyTargetId}
                      onNodeSelect={(_e, nodeId) => {
                        if (overviewCopyTargetIds.has(nodeId)) {
                          setOverviewCopyTargetId(nodeId);
                          setOverviewCopyError(null);
                        }
                      }}
                    >
                      {renderNode(root)}
                    </TreeView>
                  </Box>
                );
              })()}
              {overviewCopyError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {overviewCopyError}
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOverviewCopySource(null)} disabled={overviewCopyBusy}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={performOverviewCopy}
            disabled={!overviewCopyTargetId || overviewCopyBusy}
          >
            {overviewCopyBusy
              ? (overviewCopyMode === 'move' ? 'Moving…' : 'Copying…')
              : (overviewCopyMode === 'move' ? 'Move here' : 'Copy here')}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={!!overviewDeleteSource}
        onClose={(_event, reason) => {
          if (reason === 'backdropClick') return;
          if (!overviewDeleteBusy) setOverviewDeleteSource(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete content row</DialogTitle>
        <DialogContent dividers>
          {overviewDeleteSource && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Delete this row from <strong>{overviewDeleteSource.parentName}</strong>? You stay in
                Category Structure Overview, and the action can be reverted with Undo.
              </Typography>
              <Box
                sx={{
                  p: 1,
                  borderRadius: 1,
                  border: theme => `1px solid ${theme.palette.divider}`,
                  bgcolor: 'action.hover',
                  maxHeight: 96,
                  overflow: 'auto',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {overviewDeleteSource.content.subcase && (
                  <Typography
                    variant="caption"
                    sx={{ display: 'block', mb: 0.5, color: 'text.secondary', fontWeight: 600 }}
                  >
                    {overviewDeleteSource.content.subcase}
                  </Typography>
                )}
                <Typography variant="body2">{overviewDeleteSource.content.text}</Typography>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOverviewDeleteSource(null)} disabled={overviewDeleteBusy}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={performOverviewDelete}
            disabled={overviewDeleteBusy}
          >
            {overviewDeleteBusy ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={!!compareDeleteSource}
        onClose={(_event, reason) => {
          if (reason === 'backdropClick') return;
          if (!compareDeleteBusy) {
            setCompareDeleteSource(null);
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {compareDeleteSource?.kind === 'category'
            ? 'Delete category'
            : 'Delete content row'}
        </DialogTitle>
        <DialogContent dividers>
          {compareDeleteSource && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                {compareDeleteSource.kind === 'category' ? (
                  <>
                    Delete <strong>{compareDeleteSource.category?.name}</strong> from{' '}
                    <strong>{compareDeleteSource.parentName}</strong> in{' '}
                    <strong>{compareDeleteSource.targetDocumentName}</strong>? You stay in compare;
                    use <strong>Undo last action</strong> to restore it.
                  </>
                ) : (
                  <>
                    Delete this row from <strong>{compareDeleteSource.parentName}</strong> in{' '}
                    <strong>{compareDeleteSource.targetDocumentName}</strong>? You stay in compare;
                    use <strong>Undo last action</strong> to restore it.
                  </>
                )}
              </Typography>
              {compareDeleteSource.kind === 'content' && compareDeleteSource.content ? (
                <Box
                  sx={{
                    p: 1,
                    borderRadius: 1,
                    border: (theme) => `1px solid ${theme.palette.divider}`,
                    bgcolor: 'action.hover',
                    maxHeight: 96,
                    overflow: 'auto',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {compareDeleteSource.content.subcase && (
                    <Typography
                      variant="caption"
                      sx={{ display: 'block', mb: 0.5, color: 'text.secondary', fontWeight: 600 }}
                    >
                      {compareDeleteSource.content.subcase}
                    </Typography>
                  )}
                  <Typography variant="body2">{compareDeleteSource.content.text}</Typography>
                </Box>
              ) : null}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setCompareDeleteSource(null)}
            disabled={compareDeleteBusy}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => void performCompareDelete()}
            disabled={compareDeleteBusy}
          >
            {compareDeleteBusy ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={overviewCopySnackbarOpen}
        autoHideDuration={8_000}
        onClose={() => setOverviewCopySnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="success"
          variant="filled"
          onClose={() => setOverviewCopySnackbarOpen(false)}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => {
                setOverviewCopySnackbarOpen(false);
                handleUndo();
              }}
            >
              Undo
            </Button>
          }
          sx={{ width: '100%' }}
        >
          {overviewCopySnackbarMessage}
        </Alert>
      </Snackbar>
      <Snackbar
        open={!!categoryLoadFaultMessage}
        autoHideDuration={null}
        onClose={() => setCategoryLoadFaultMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="error"
          variant="filled"
          onClose={() => setCategoryLoadFaultMessage(null)}
          sx={{ width: '100%', maxWidth: 560 }}
        >
          {categoryLoadFaultMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MainLayout; 